import { formatIranSubmissionTimestamp } from "./utils/iranSubmissionTimestamp";
import { localizePersianDisplay } from "./utils/persianDisplay";
import JalaliDatePicker from "./components/JalaliDatePicker";
import { useState, useEffect, useRef, createContext, useContext } from "react";
import UiIcon from "./components/UiIcon";
import AccountMenu from "./components/AccountMenu";
import { organizePerformanceNavigation, NavigationItem, isNavigationActive } from "./components/PerformanceNavigation";
import UxProvider, { useUx } from "./components/UxProvider";
import FlexibleOrgPrototype, { OrgChartNode } from "./components/FlexibleOrgPrototype";
import IndividualGoalsPage from "./components/IndividualGoalsPage";
import HistoryArchivePage from "./components/HistoryArchivePage";
import CviAppShell from "./components/CviAppShell";
import { DashboardActionCenter, DetailDrawer, PeriodContextBar } from "./components/CviExperience";
import CycleManagement from "./core/cycle/CycleManagement";
import "./styles/cvi-design-system.css";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:3001").replace(/\/+$/, "");

/* ════════════════════════════════════════════════════════════════════════
   CVI — Contribution Value Index
   لاگین واحد + مسیردهی بر اساس نقش + پنل ادمین برای تکمیل داده‌های پایه
   همه‌ی داده‌ها (ساختار سازمانی، افراد، شاخص‌ها، ...) از یک Context مشترک
   خونده می‌شن تا تغییرات ادمین بلافاصله در پنل کارشناس/مدیر منعکس بشه.
   ════════════════════════════════════════════════════════════════════════ */

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const C = {
    navy: "#000064",
    cyan: "#00C8E6",
    cyanLt: "#F0FDFE",
    cyanBg: "rgba(0,200,230,0.1)",
    white: "#FFFFFF",
    bg: "#F0F2F5",
    card: "#FFFFFF",
    border: "#E8E8EE",
    borderMd: "#E4E4EC",
    text: "#111111",
    muted: "#888888",
    hint: "#AAAAAA",
    purple: "#534AB7",
    purpleLt: "#EEEDFE",
    teal: "#0F6E56",
    tealLt: "#E1F5EE",
    amber: "#854F0B",
    amberLt: "#FAEEDA",
    red: "#E24B4A",
    redLt: "#FCEBEB",
};


function uxNotify(message, tone = null) {
    if (!message) return;
    const inferred = tone || (/موفق|ذخیره شد|اعمال.*شد|ثبت شد|بازگشایی شد|نهایی.*شد/.test(String(message)) ? "success" : "error");
    window.dispatchEvent(new CustomEvent("cvi-notify", { detail: { message: String(message), tone: inferred } }));
}

function uxConfirm(message, options = {}) {
    return new Promise(resolve => window.dispatchEvent(new CustomEvent("cvi-confirm-request", {
        detail: { message: String(message || ""), resolve, ...options }
    })));
}

function uxPrompt(message, options = {}) {
    return new Promise(resolve => window.dispatchEvent(new CustomEvent("cvi-prompt-request", {
        detail: { message: String(message || ""), resolve, ...options }
    })));
}

// ── INTERNATIONALIZATION FOUNDATION ───────────────────────────────────────────
const SUPPORTED_LANGUAGES = {
    en: { label: "English", dir: "ltr" },
    fr: { label: "Français", dir: "ltr" },
    ar: { label: "العربية", dir: "rtl" },
    es: { label: "Español", dir: "ltr" },
    fa: { label: "فارسی", dir: "rtl" },
};

const UI_DICTIONARY = {
    en: {
        "common.back": "Back",
        "common.loading": "Loading...",
        "common.retry": "Try again",
        "common.noActivePeriod": "No active period",
        "common.completed": "Completed",
        "common.pending": "Pending",
        "common.responses": "responses",
        "common.response": "response",
        "common.questionWeight": "Question weight",
        "login.title": "Sign in",
        "login.subtitle": "Sign in with your account",
        "login.username": "Username",
        "login.password": "Password",
        "login.submit": "Sign in",
        "login.submitting": "Signing in...",
        "login.required": "Enter username and password",
        "login.invalid": "Incorrect username or password",
        "login.failed": "Sign in failed. Please try again",
        "login.invalidResponse": "Invalid response from server",
        "login.connectionError": "Could not connect to the server",
        "peer.title": "Peer Feedback",
        "peer.subtitle": "Assigned reviews and feedback received",
        "peer.myReviews": "My reviews",
        "peer.myResults": "My feedback result",
        "peer.feedbackAbout": "Feedback about {name}",
        "peer.allRequired": "All questions are required",
        "peer.submitFailed": "Could not submit peer feedback",
        "peer.loading": "Loading peer feedback...",
        "peer.submit": "Submit final feedback",
        "peer.submitting": "Submitting...",
        "peer.noAssignments": "No active review has been assigned to you.",
        "peer.waiting": "Awaiting review",
        "peer.start": "Start review",
        "peer.sent": "Submitted",
        "peer.notEnabled": "Peer feedback is not enabled for you.",
        "peer.threshold": "Results are shown after at least {min} responses are received.",
        "peer.progress": "So far, {done} of {total} reviews have been completed.",
        "peer.finalScore": "Final peer feedback score",
        "peer.validResponses": "{count} valid responses",
        "peer.anonymous": "Results are confidential and reviewer names are not displayed.",
        "rating.5": "Excellent",
        "rating.4": "Good",
        "rating.3": "Average",
        "rating.2": "Weak",
        "rating.1": "Very weak"
    },
    fr: {
        "common.back": "Retour", "common.loading": "Chargement...", "common.retry": "Réessayer",
        "common.noActivePeriod": "Aucune période active", "common.completed": "Terminé", "common.pending": "En attente",
        "common.responses": "réponses", "common.response": "réponse", "common.questionWeight": "Poids de la question",
        "login.title": "Connexion", "login.subtitle": "Connectez-vous avec votre compte", "login.username": "Nom d’utilisateur",
        "login.password": "Mot de passe", "login.submit": "Se connecter", "login.submitting": "Connexion...",
        "login.required": "Saisissez le nom d’utilisateur et le mot de passe", "login.invalid": "Nom d’utilisateur ou mot de passe incorrect",
        "login.failed": "Échec de la connexion. Réessayez", "login.invalidResponse": "Réponse du serveur invalide",
        "login.connectionError": "Impossible de se connecter au serveur",
        "peer.title": "Rétroaction des collègues", "peer.subtitle": "Évaluations attribuées et rétroactions reçues",
        "peer.myReviews": "Mes évaluations", "peer.myResults": "Mon résultat", "peer.feedbackAbout": "Rétroaction sur {name}",
        "peer.allRequired": "Toutes les questions sont obligatoires", "peer.submitFailed": "Impossible d’envoyer la rétroaction",
        "peer.loading": "Chargement des rétroactions...", "peer.submit": "Envoyer la rétroaction finale", "peer.submitting": "Envoi...",
        "peer.noAssignments": "Aucune évaluation active ne vous a été attribuée.", "peer.waiting": "En attente d’évaluation",
        "peer.start": "Commencer", "peer.sent": "Envoyé", "peer.notEnabled": "La rétroaction des collègues n’est pas activée pour vous.",
        "peer.threshold": "Le résultat s’affiche après réception d’au moins {min} réponses.",
        "peer.progress": "À ce jour, {done} évaluations sur {total} sont terminées.",
        "peer.finalScore": "Score final de rétroaction", "peer.validResponses": "{count} réponses valides",
        "peer.anonymous": "Les résultats sont confidentiels et les noms des évaluateurs ne sont pas affichés.",
        "rating.5": "Excellent", "rating.4": "Bon", "rating.3": "Moyen", "rating.2": "Faible", "rating.1": "Très faible"
    },
    ar: {
        "common.back": "رجوع", "common.loading": "جارٍ التحميل...", "common.retry": "حاول مجددًا",
        "common.noActivePeriod": "لا توجد فترة نشطة", "common.completed": "مكتمل", "common.pending": "قيد الانتظار",
        "common.responses": "ردود", "common.response": "رد", "common.questionWeight": "وزن السؤال",
        "login.title": "تسجيل الدخول", "login.subtitle": "سجّل الدخول باستخدام حسابك", "login.username": "اسم المستخدم",
        "login.password": "كلمة المرور", "login.submit": "دخول", "login.submitting": "جارٍ الدخول...",
        "login.required": "أدخل اسم المستخدم وكلمة المرور", "login.invalid": "اسم المستخدم أو كلمة المرور غير صحيحة",
        "login.failed": "فشل تسجيل الدخول. حاول مرة أخرى", "login.invalidResponse": "استجابة الخادم غير صالحة",
        "login.connectionError": "تعذر الاتصال بالخادم",
        "peer.title": "ملاحظات الزملاء", "peer.subtitle": "التقييمات الموكلة ونتائج الملاحظات المستلمة",
        "peer.myReviews": "تقييماتي", "peer.myResults": "نتيجة ملاحظاتي", "peer.feedbackAbout": "ملاحظات حول {name}",
        "peer.allRequired": "الإجابة على جميع الأسئلة إلزامية", "peer.submitFailed": "تعذر إرسال الملاحظات",
        "peer.loading": "جارٍ تحميل ملاحظات الزملاء...", "peer.submit": "إرسال الملاحظات النهائية", "peer.submitting": "جارٍ الإرسال...",
        "peer.noAssignments": "لم يتم إسناد أي تقييم نشط إليك.", "peer.waiting": "بانتظار التقييم",
        "peer.start": "بدء التقييم", "peer.sent": "تم الإرسال", "peer.notEnabled": "ملاحظات الزملاء غير مفعلة لك.",
        "peer.threshold": "تظهر النتيجة بعد استلام {min} ردود على الأقل.",
        "peer.progress": "حتى الآن اكتمل {done} من أصل {total} تقييمات.",
        "peer.finalScore": "النتيجة النهائية لملاحظات الزملاء", "peer.validResponses": "{count} ردود صالحة",
        "peer.anonymous": "النتائج سرية ولا يتم عرض أسماء المقيمين.",
        "rating.5": "ممتاز", "rating.4": "جيد", "rating.3": "متوسط", "rating.2": "ضعيف", "rating.1": "ضعيف جدًا"
    },
    es: {
        "common.back": "Volver", "common.loading": "Cargando...", "common.retry": "Intentar de nuevo",
        "common.noActivePeriod": "No hay un período activo", "common.completed": "Completado", "common.pending": "Pendiente",
        "common.responses": "respuestas", "common.response": "respuesta", "common.questionWeight": "Peso de la pregunta",
        "login.title": "Iniciar sesión", "login.subtitle": "Inicia sesión con tu cuenta", "login.username": "Usuario",
        "login.password": "Contraseña", "login.submit": "Entrar", "login.submitting": "Iniciando sesión...",
        "login.required": "Introduce usuario y contraseña", "login.invalid": "Usuario o contraseña incorrectos",
        "login.failed": "No se pudo iniciar sesión. Inténtalo de nuevo", "login.invalidResponse": "Respuesta del servidor no válida",
        "login.connectionError": "No se pudo conectar con el servidor",
        "peer.title": "Comentarios de colegas", "peer.subtitle": "Evaluaciones asignadas y comentarios recibidos",
        "peer.myReviews": "Mis evaluaciones", "peer.myResults": "Mi resultado", "peer.feedbackAbout": "Comentarios sobre {name}",
        "peer.allRequired": "Todas las preguntas son obligatorias", "peer.submitFailed": "No se pudieron enviar los comentarios",
        "peer.loading": "Cargando comentarios de colegas...", "peer.submit": "Enviar comentarios finales", "peer.submitting": "Enviando...",
        "peer.noAssignments": "No tienes evaluaciones activas asignadas.", "peer.waiting": "Pendiente de evaluación",
        "peer.start": "Iniciar evaluación", "peer.sent": "Enviado", "peer.notEnabled": "Los comentarios de colegas no están habilitados para ti.",
        "peer.threshold": "El resultado se muestra después de recibir al menos {min} respuestas.",
        "peer.progress": "Hasta ahora se han completado {done} de {total} evaluaciones.",
        "peer.finalScore": "Puntuación final de comentarios", "peer.validResponses": "{count} respuestas válidas",
        "peer.anonymous": "Los resultados son confidenciales y no se muestran los nombres de los evaluadores.",
        "rating.5": "Excelente", "rating.4": "Bueno", "rating.3": "Promedio", "rating.2": "Débil", "rating.1": "Muy débil"
    },
    fa: {
        "common.back": "بازگشت", "common.loading": "در حال بارگذاری...", "common.retry": "تلاش مجدد",
        "common.noActivePeriod": "دوره‌ای فعال نیست", "common.completed": "تکمیل‌شده", "common.pending": "در انتظار",
        "common.responses": "پاسخ", "common.response": "پاسخ", "common.questionWeight": "وزن سؤال",
        "login.title": "ورود به سامانه", "login.subtitle": "با اطلاعات کاربری خود وارد شوید", "login.username": "نام کاربری",
        "login.password": "رمز عبور", "login.submit": "ورود", "login.submitting": "در حال ورود...",
        "login.required": "نام کاربری و رمز عبور را وارد کنید", "login.invalid": "نام کاربری یا رمز عبور اشتباه است",
        "login.failed": "ورود به سامانه انجام نشد. دوباره تلاش کنید", "login.invalidResponse": "پاسخ دریافتی از سرور معتبر نیست",
        "login.connectionError": "ارتباط با سرور برقرار نشد",
        "peer.title": "بازخورد همکاران", "peer.subtitle": "ارزیابی‌های محول‌شده و نتیجه بازخوردهای دریافت‌شده",
        "peer.myReviews": "ارزیابی‌های من", "peer.myResults": "نتیجه بازخورد من", "peer.feedbackAbout": "بازخورد درباره {name}",
        "peer.allRequired": "پاسخ همه سؤال‌ها الزامی است", "peer.submitFailed": "ارسال بازخورد انجام نشد",
        "peer.loading": "در حال دریافت بازخورد همکاران...", "peer.submit": "ارسال نهایی بازخورد", "peer.submitting": "در حال ارسال...",
        "peer.noAssignments": "ارزیابی فعالی به شما محول نشده است.", "peer.waiting": "در انتظار ارزیابی",
        "peer.start": "شروع ارزیابی", "peer.sent": "ارسال‌شده", "peer.notEnabled": "بازخورد همکاران برای شما فعال نشده است.",
        "peer.threshold": "نتیجه پس از دریافت حداقل {min} پاسخ نمایش داده می‌شود.",
        "peer.progress": "تا این لحظه {done} از {total} ارزیابی تکمیل شده است.",
        "peer.finalScore": "امتیاز نهایی بازخورد همکاران", "peer.validResponses": "{count} پاسخ معتبر",
        "peer.anonymous": "نتایج محرمانه‌اند و نام ارزیاب‌ها نمایش داده نمی‌شود.",
        "rating.5": "عالی", "rating.4": "خوب", "rating.3": "متوسط", "rating.2": "ضعیف", "rating.1": "بسیار ضعیف"
    }
};

const SETTINGS_DICTIONARY = {
    en: {
        "language.label": "Language",
        "login.tagline": "Individual performance aligned with strategic indicators",
        "language.display": "Language & display",
        "language.description": "Choose the language used across CVI. The interface direction changes automatically.",
        "language.saved": "Language preference saved",
        "language.localOnly": "Language changed on this device. Server preference is not available yet.",
        "language.saveFailed": "The language changed, but the server preference could not be saved.",
        "settings.account": "Account settings",
        "settings.profile": "Profile",
        "settings.currentLanguage": "Interface language"
    },
    fr: {
        "language.label": "Langue",
        "login.tagline": "Performance individuelle alignée sur les indicateurs stratégiques",
        "language.display": "Langue et affichage",
        "language.description": "Choisissez la langue utilisée dans CVI. Le sens de l’interface s’adapte automatiquement.",
        "language.saved": "Préférence de langue enregistrée",
        "language.localOnly": "La langue a été modifiée sur cet appareil. La préférence serveur n’est pas encore disponible.",
        "language.saveFailed": "La langue a été modifiée, mais la préférence n’a pas pu être enregistrée sur le serveur.",
        "settings.account": "Paramètres du compte",
        "settings.profile": "Profil",
        "settings.currentLanguage": "Langue de l’interface"
    },
    ar: {
        "language.label": "اللغة",
        "login.tagline": "الأداء الفردي المتوافق مع المؤشرات الاستراتيجية",
        "language.display": "اللغة والعرض",
        "language.description": "اختر اللغة المستخدمة في CVI. يتغير اتجاه الواجهة تلقائيًا.",
        "language.saved": "تم حفظ تفضيل اللغة",
        "language.localOnly": "تم تغيير اللغة على هذا الجهاز. تفضيل الخادم غير متاح بعد.",
        "language.saveFailed": "تم تغيير اللغة، ولكن تعذر حفظ التفضيل على الخادم.",
        "settings.account": "إعدادات الحساب",
        "settings.profile": "الملف الشخصي",
        "settings.currentLanguage": "لغة الواجهة"
    },
    es: {
        "language.label": "Idioma",
        "login.tagline": "Desempeño individual alineado con los indicadores estratégicos",
        "language.display": "Idioma y visualización",
        "language.description": "Elige el idioma utilizado en CVI. La dirección de la interfaz cambia automáticamente.",
        "language.saved": "Preferencia de idioma guardada",
        "language.localOnly": "El idioma cambió en este dispositivo. La preferencia del servidor aún no está disponible.",
        "language.saveFailed": "El idioma cambió, pero no se pudo guardar la preferencia en el servidor.",
        "settings.account": "Configuración de la cuenta",
        "settings.profile": "Perfil",
        "settings.currentLanguage": "Idioma de la interfaz"
    },
    fa: {
        "language.label": "زبان",
        "login.tagline": "ارزش مشارکت؛ حاصل عملکرد فردی و موفقیت جمعی",
        "language.display": "زبان و نمایش",
        "language.description": "زبان مورد استفاده در کل CVI را انتخاب کنید. جهت رابط کاربری به‌صورت خودکار تغییر می‌کند.",
        "language.saved": "زبان انتخابی ذخیره شد",
        "language.localOnly": "زبان روی این دستگاه تغییر کرد. ذخیره ترجیح در سرور هنوز در دسترس نیست.",
        "language.saveFailed": "زبان تغییر کرد، اما ذخیره ترجیح در سرور انجام نشد.",
        "settings.account": "تنظیمات حساب",
        "settings.profile": "پروفایل",
        "settings.currentLanguage": "زبان رابط کاربری"
    }
};


const WORKFLOW_DICTIONARY = {
    en: {
        "workflow.title": "Work center", "workflow.tasks": "Pending tasks", "workflow.history": "History",
        "workflow.emptyTasks": "You have no pending tasks.", "workflow.emptyHistory": "No history is available for this period.",
        "workflow.loading": "Loading work center...", "workflow.error": "Could not load work center.", "workflow.overdue": "Overdue",
        "workflow.refresh": "Refresh", "workflow.close": "Close", "workflow.taskCount": "{count} pending",
        "workflow.activity": "Activity", "workflow.routine": "Routine", "workflow.peer": "Peer feedback",
        "workflow.indicator": "Strategic indicator", "workflow.period": "Period", "workflow.for": "For {name}",
        "file.download": "Download", "file.delete": "Delete", "file.deleting": "Deleting...", "file.failed": "File operation failed"
    },
    fr: {
        "workflow.title": "Centre de travail", "workflow.tasks": "Tâches en attente", "workflow.history": "Historique",
        "workflow.emptyTasks": "Vous n’avez aucune tâche en attente.", "workflow.emptyHistory": "Aucun historique pour cette période.",
        "workflow.loading": "Chargement du centre de travail...", "workflow.error": "Impossible de charger le centre de travail.", "workflow.overdue": "En retard",
        "workflow.refresh": "Actualiser", "workflow.close": "Fermer", "workflow.taskCount": "{count} en attente",
        "workflow.activity": "Activité", "workflow.routine": "Routine", "workflow.peer": "Rétroaction des collègues",
        "workflow.indicator": "Indicateur stratégique", "workflow.period": "Période", "workflow.for": "Pour {name}",
        "file.download": "Télécharger", "file.delete": "Supprimer", "file.deleting": "Suppression...", "file.failed": "Échec de l’opération sur le fichier"
    },
    ar: {
        "workflow.title": "مركز العمل", "workflow.tasks": "المهام المعلقة", "workflow.history": "السجل",
        "workflow.emptyTasks": "لا توجد مهام معلقة.", "workflow.emptyHistory": "لا يوجد سجل لهذه الفترة.",
        "workflow.loading": "جارٍ تحميل مركز العمل...", "workflow.error": "تعذر تحميل مركز العمل.", "workflow.overdue": "متأخر",
        "workflow.refresh": "تحديث", "workflow.close": "إغلاق", "workflow.taskCount": "{count} معلقة",
        "workflow.activity": "نشاط", "workflow.routine": "تقييم دوري", "workflow.peer": "ملاحظات الزملاء",
        "workflow.indicator": "مؤشر استراتيجي", "workflow.period": "الفترة", "workflow.for": "لـ {name}",
        "file.download": "تنزيل", "file.delete": "حذف", "file.deleting": "جارٍ الحذف...", "file.failed": "فشلت عملية الملف"
    },
    es: {
        "workflow.title": "Centro de trabajo", "workflow.tasks": "Tareas pendientes", "workflow.history": "Historial",
        "workflow.emptyTasks": "No tienes tareas pendientes.", "workflow.emptyHistory": "No hay historial para este período.",
        "workflow.loading": "Cargando centro de trabajo...", "workflow.error": "No se pudo cargar el centro de trabajo.", "workflow.overdue": "Vencido",
        "workflow.refresh": "Actualizar", "workflow.close": "Cerrar", "workflow.taskCount": "{count} pendientes",
        "workflow.activity": "Actividad", "workflow.routine": "Rutina", "workflow.peer": "Comentarios de colegas",
        "workflow.indicator": "Indicador estratégico", "workflow.period": "Período", "workflow.for": "Para {name}",
        "file.download": "Descargar", "file.delete": "Eliminar", "file.deleting": "Eliminando...", "file.failed": "Falló la operación del archivo"
    },
    fa: {
        "workflow.title": "مرکز کارها", "workflow.tasks": "کارهای در انتظار", "workflow.history": "تاریخچه یکپارچه",
        "workflow.emptyTasks": "کار در انتظاری ندارید.", "workflow.emptyHistory": "برای این دوره رویدادی ثبت نشده است.",
        "workflow.loading": "در حال دریافت مرکز کارها...", "workflow.error": "دریافت مرکز کارها انجام نشد.", "workflow.overdue": "سررسید گذشته",
        "workflow.refresh": "به‌روزرسانی", "workflow.close": "بستن", "workflow.taskCount": "{count} کار در انتظار",
        "workflow.activity": "فعالیت", "workflow.routine": "ارزیابی وظایف مستمر", "workflow.peer": "بازخورد همکاران",
        "workflow.indicator": "شاخص راهبردی", "workflow.period": "دوره", "workflow.for": "برای {name}",
        "file.download": "دانلود", "file.delete": "حذف", "file.deleting": "در حال حذف...", "file.failed": "عملیات فایل انجام نشد"
    }
};

// Self-reporting labels (employee declaration; zero direct CVI weight)
Object.assign(UI_DICTIONARY.fa, {
    "self.title": "خوداظهاری",
    "self.subtitle": "ثبت ارزیابی شما از عملکرد خود در دوره جاری؛ این بخش مستقیماً در امتیاز CVI وزن ندارد.",
    "self.month1": "ماه اول",
    "self.month2": "ماه دوم",
    "self.month3": "ماه سوم",
    "self.submitted": "ارسال‌شده",
    "self.evaluated": "ارزیابی‌شده",
    "self.draft": "در حال تکمیل",
    "self.deadline": "مهلت ثبت",
    "self.overdue": "با تأخیر",
    "self.pending": "در انتظار",
    "self.noQuestions": "برای این ماه سؤالی تعریف نشده است.",
    "self.history": "تاریخچه خوداظهاری"
});
Object.assign(UI_DICTIONARY.en, {
    "self.title": "Self-reporting",
    "self.subtitle": "Record your own assessment of your performance for the current period; this section has no direct weight in CVI.",
    "self.month1": "Month 1", "self.month2": "Month 2", "self.month3": "Month 3",
    "self.submitted": "Submitted", "self.evaluated": "Evaluated", "self.draft": "In progress",
    "self.deadline": "Submission deadline", "self.overdue": "Overdue", "self.pending": "Pending",
    "self.noQuestions": "No questions are defined for this month.", "self.history": "Self-reporting history"
});
Object.assign(UI_DICTIONARY.fr, {
    "self.title": "Auto-déclaration",
    "self.subtitle": "Consignez votre propre évaluation de votre performance pour la période en cours; cette section n’a pas de poids direct dans le CVI.",
    "self.month1": "Mois 1", "self.month2": "Mois 2", "self.month3": "Mois 3",
    "self.submitted": "Soumis", "self.evaluated": "Évalué", "self.draft": "En cours",
    "self.deadline": "Date limite", "self.overdue": "En retard", "self.pending": "En attente",
    "self.noQuestions": "Aucune question n’est définie pour ce mois.", "self.history": "Historique des auto-déclarations"
});
Object.assign(UI_DICTIONARY.es, {
    "self.title": "Autodeclaración",
    "self.subtitle": "Registra tu propia valoración del desempeño del período actual; esta sección no tiene peso directo en el CVI.",
    "self.month1": "Mes 1", "self.month2": "Mes 2", "self.month3": "Mes 3",
    "self.submitted": "Enviado", "self.evaluated": "Evaluado", "self.draft": "En curso",
    "self.deadline": "Fecha límite", "self.overdue": "Con retraso", "self.pending": "Pendiente",
    "self.noQuestions": "No hay preguntas definidas para este mes.", "self.history": "Historial de autodeclaración"
});
Object.assign(UI_DICTIONARY.ar, {
    "self.title": "الإقرار الذاتي",
    "self.subtitle": "سجّل تقييمك لأدائك في الفترة الحالية؛ لا يحمل هذا القسم وزناً مباشراً في CVI.",
    "self.month1": "الشهر الأول", "self.month2": "الشهر الثاني", "self.month3": "الشهر الثالث",
    "self.submitted": "تم الإرسال", "self.evaluated": "تم التقييم", "self.draft": "قيد الاستكمال",
    "self.deadline": "موعد التسجيل", "self.overdue": "متأخر", "self.pending": "قيد الانتظار",
    "self.noQuestions": "لا توجد أسئلة محددة لهذا الشهر.", "self.history": "سجل الإقرار الذاتي"
});

const LEGACY_UI_TRANSLATIONS = {"پیش‌نویس":{"en":"Draft","fr":"Brouillon","es":"Borrador","ar":"مسودة"},"در انتظار بررسی":{"en":"Pending review","fr":"En attente d’examen","es":"Pendiente de revisión","ar":"بانتظار المراجعة"},"نیازمند اصلاح":{"en":"Revision required","fr":"Révision requise","es":"Revisión requerida","ar":"يتطلب تعديلاً"},"ارسال مجدد":{"en":"Resubmitted","fr":"Soumis de nouveau","es":"Reenviado","ar":"أعيد الإرسال"},"تأییدشده":{"en":"Approved","fr":"Approuvé","es":"Aprobado","ar":"مُعتمد"},"ردشده":{"en":"Rejected","fr":"Rejeté","es":"Rechazado","ar":"مرفوض"},"کارشناس":{"en":"Specialist","fr":"Spécialiste","es":"Especialista","ar":"أخصائي"},"مدیر":{"en":"Manager","fr":"Gestionnaire","es":"Gerente","ar":"مدير"},"سالانه":{"en":"Annual","fr":"Annuel","es":"Anual","ar":"سنوي"},"فصلی":{"en":"Quarterly","fr":"Trimestriel","es":"Trimestral","ar":"ربع سنوي"},"بخش‌های صفحه":{"en":"Page sections","fr":"Sections de la page","es":"Secciones de la página","ar":"أقسام الصفحة"},"تأیید شده":{"en":"Approved","fr":"Approuvé","es":"Aprobado","ar":"تم الاعتماد"},"رد شده":{"en":"Rejected","fr":"Rejeté","es":"Rechazado","ar":"تم الرفض"},"عملکرد فردی":{"en":"Individual Performance","fr":"Performance individuelle","es":"Desempeño individual","ar":"الأداء الفردي"},"وظایف و فعالیت‌ها":{"en":"Achievements and activities","fr":"Réalisations et activités","es":"Logros y actividades","ar":"الإنجازات والأنشطة"},"فعالیت‌های مستمر":{"en":"Routine task performance","fr":"Performance des tâches courantes","es":"Desempeño de tareas rutinarias","ar":"أداء المهام الروتينية"},"بازخورد همکاران":{"en":"Peer feedback","fr":"Rétroaction des collègues","es":"Comentarios de colegas","ar":"ملاحظات الزملاء"},"مشارکت جمعی":{"en":"Collective Contribution","fr":"Contribution collective","es":"Contribución colectiva","ar":"المساهمة الجماعية"},"سازمانی":{"en":"Organization","fr":"Organisation","es":"Organización","ar":"المؤسسة"},"معاونت":{"en":"Division","fr":"Direction","es":"División","ar":"الإدارة"},"تیمی":{"en":"Team","fr":"Équipe","es":"Equipo","ar":"الفريق"},"لوگو":{"en":"Logo","fr":"Logo","es":"Logotipo","ar":"الشعار"},"خروج":{"en":"Log out","fr":"Déconnexion","es":"Cerrar sesión","ar":"تسجيل الخروج"},"رمز فعلی را وارد کنید":{"en":"Enter your current password","fr":"Saisissez votre mot de passe actuel","es":"Introduce tu contraseña actual","ar":"أدخل كلمة المرور الحالية"},"رمز فعلی اشتباه است":{"en":"The current password is incorrect","fr":"Le mot de passe actuel est incorrect","es":"La contraseña actual es incorrecta","ar":"كلمة المرور الحالية غير صحيحة"},"رمز جدید باید حداقل ۳ کاراکتر باشد":{"en":"The new password must be at least 3 characters","fr":"Le nouveau mot de passe doit contenir au moins 3 caractères","es":"La nueva contraseña debe tener al menos 3 caracteres","ar":"يجب أن تتكون كلمة المرور الجديدة من 3 أحرف على الأقل"},"تکرار رمز جدید مطابقت ندارد":{"en":"The new password confirmation does not match","fr":"La confirmation du nouveau mot de passe ne correspond pas","es":"La confirmación de la nueva contraseña no coincide","ar":"تأكيد كلمة المرور الجديدة غير متطابق"},"فقط فایل تصویری قابل بارگذاری است":{"en":"Only image files can be uploaded","fr":"Seuls les fichiers image peuvent être téléversés","es":"Solo se pueden cargar archivos de imagen","ar":"يمكن رفع ملفات الصور فقط"},"حجم عکس باید کمتر از ۳ مگابایت باشد":{"en":"The image must be smaller than 3 MB","fr":"L’image doit faire moins de 3 Mo","es":"La imagen debe pesar menos de 3 MB","ar":"يجب أن يكون حجم الصورة أقل من 3 ميغابايت"},"بارگذاری عکس انجام نشد؛ دوباره تلاش کنید":{"en":"Image upload failed; please try again","fr":"Le téléversement de l’image a échoué; réessayez","es":"No se pudo cargar la imagen; inténtalo de nuevo","ar":"فشل رفع الصورة؛ حاول مرة أخرى"},"تغییر عکس":{"en":"Change photo","fr":"Changer la photo","es":"Cambiar foto","ar":"تغيير الصورة"},"بارگذاری عکس":{"en":"Upload photo","fr":"Téléverser une photo","es":"Cargar foto","ar":"رفع صورة"},"بخش‌های بازخورد همکاران":{"en":"Peer feedback sections","fr":"Sections de rétroaction des collègues","es":"Secciones de comentarios de colegas","ar":"أقسام ملاحظات الزملاء"},"ثبت فعالیت انجام نشد":{"en":"Activity could not be saved","fr":"L’activité n’a pas pu être enregistrée","es":"No se pudo guardar la actividad","ar":"تعذر حفظ النشاط"},"داشبورد من":{"en":"My dashboard","fr":"Mon tableau de bord","es":"Mi panel","ar":"لوحة التحكم الخاصة بي"},"فعالیت‌ها":{"en":"Activities","fr":"Activités","es":"Actividades","ar":"الأنشطة"},"شاخص‌های من":{"en":"My indicators","fr":"Mes indicateurs","es":"Mis indicadores","ar":"مؤشراتي"},"تاریخچه":{"en":"History","fr":"Historique","es":"Historial","ar":"السجل"},"پروفایل":{"en":"Profile","fr":"Profil","es":"Perfil","ar":"الملف الشخصي"},"دوره‌ای فعال نیست":{"en":"No active period","fr":"Aucune période active","es":"No hay un período activo","ar":"لا توجد فترة نشطة"},"جدید":{"en":"New","fr":"Nouveau","es":"Nuevo","ar":"جديد"},"ثبت‌نشده":{"en":"Not submitted","fr":"Non soumis","es":"No enviado","ar":"غير مُرسل"},"ارسال‌شده":{"en":"Submitted","fr":"Soumis","es":"Enviado","ar":"تم الإرسال"},"قفل‌شده":{"en":"Locked","fr":"Verrouillé","es":"Bloqueado","ar":"مقفل"},"بسته‌شده بدون داده":{"en":"Closed without data","fr":"Fermé sans données","es":"Cerrado sin datos","ar":"مغلق بدون بيانات"},"نیازمند اقدام":{"en":"Action required","fr":"Action requise","es":"Acción requerida","ar":"يتطلب إجراءً"},"دوره نامشخص":{"en":"Unknown period","fr":"Période inconnue","es":"Período desconocido","ar":"فترة غير معروفة"},"هنوز تعیین نشده":{"en":"Not set yet","fr":"Pas encore défini","es":"Aún no definido","ar":"لم يُحدد بعد"},"علت انحراف":{"en":"Reason for variance","fr":"Raison de l’écart","es":"Motivo de la desviación","ar":"سبب الانحراف"},"اقدام اصلاحی":{"en":"Corrective action","fr":"Action corrective","es":"Acción correctiva","ar":"إجراء تصحيحي"},"بارگذاری مستند":{"en":"Upload document","fr":"Téléverser un document","es":"Cargar documento","ar":"رفع مستند"},"وضعیت شاخص‌های من":{"en":"My indicator status","fr":"État de mes indicateurs","es":"Estado de mis indicadores","ar":"حالة مؤشراتي"},"ثبت‌کننده":{"en":"Data submitter","fr":"Responsable de saisie","es":"Responsable de registro","ar":"مسجل البيانات"},"مالک":{"en":"Owner","fr":"Responsable","es":"Propietario","ar":"المالك"},"تأییدکننده":{"en":"Approver","fr":"Approbateur","es":"Aprobador","ar":"المعتمد"},"امروز آخرین مهلت است":{"en":"Today is the deadline","fr":"Aujourd’hui est la date limite","es":"Hoy es la fecha límite","ar":"اليوم هو الموعد النهائي"},"یادداشت بررسی (برای اصلاح الزامی)":{"en":"Review note (required for revision)","fr":"Note d’examen (obligatoire pour une révision)","es":"Nota de revisión (obligatoria para solicitar cambios)","ar":"ملاحظة المراجعة (مطلوبة للتعديل)"},"شاخص‌های راهبردی":{"en":"Strategic indicators","fr":"Indicateurs stratégiques","es":"Indicadores estratégicos","ar":"المؤشرات الاستراتيجية"},"نوع موارد صندوق بررسی":{"en":"Review inbox item type","fr":"Type d’élément de la boîte d’examen","es":"Tipo de elemento de la bandeja de revisión","ar":"نوع عنصر صندوق المراجعة"},"ثبت تصمیم انجام نشد":{"en":"Decision could not be saved","fr":"La décision n’a pas pu être enregistrée","es":"No se pudo guardar la decisión","ar":"تعذر حفظ القرار"},"بررسی فعالیت":{"en":"Activity review","fr":"Examen de l’activité","es":"Revisión de actividad","ar":"مراجعة النشاط"},"بازخورد یا توضیح برای کارشناس...":{"en":"Feedback or note for the specialist...","fr":"Rétroaction ou note pour le spécialiste...","es":"Comentario o nota para el especialista...","ar":"ملاحظة أو تعليق للأخصائي..."},"تیم":{"en":"Team","fr":"Équipe","es":"Equipo","ar":"الفريق"},"درآمد (نمایشی)":{"en":"Revenue (sample)","fr":"Revenu (exemple)","es":"Ingresos (ejemplo)","ar":"الإيرادات (مثال)"},"تومان":{"en":"Toman","fr":"Toman","es":"Toman","ar":"تومان"},"میانگین CVI":{"en":"Average CVI","fr":"CVI moyen","es":"CVI promedio","ar":"متوسط CVI"},"از ۱۰۰":{"en":"out of 100","fr":"sur 100","es":"de 100","ar":"من 100"},"تحقق اهداف":{"en":"Goal attainment","fr":"Atteinte des objectifs","es":"Cumplimiento de objetivos","ar":"تحقيق الأهداف"},"میانگین شاخص‌ها":{"en":"Indicator average","fr":"Moyenne des indicateurs","es":"Promedio de indicadores","ar":"متوسط المؤشرات"},"نفر در تیم":{"en":"team members","fr":"membres de l’équipe","es":"miembros del equipo","ar":"أعضاء الفريق"},"فعالیت تأیید شده":{"en":"approved activities","fr":"activités approuvées","es":"actividades aprobadas","ar":"أنشطة معتمدة"},"همه بررسی شده":{"en":"All reviewed","fr":"Tout a été examiné","es":"Todo revisado","ar":"تمت مراجعة الكل"},"ماه اول":{"en":"Month 1","fr":"Mois 1","es":"Mes 1","ar":"الشهر الأول"},"ماه دوم":{"en":"Month 2","fr":"Mois 2","es":"Mes 2","ar":"الشهر الثاني"},"ماه سوم":{"en":"Month 3","fr":"Mois 3","es":"Mes 3","ar":"الشهر الثالث"},"اعضای تیم":{"en":"Team members","fr":"Membres de l’équipe","es":"Miembros del equipo","ar":"أعضاء الفريق"},"هنوز عضوی به این مدیر متصل نشده":{"en":"No members are assigned to this manager yet","fr":"Aucun membre n’est encore rattaché à ce gestionnaire","es":"Aún no hay miembros asignados a este gerente","ar":"لم يتم تعيين أعضاء لهذا المدير بعد"},"فردی":{"en":"Individual","fr":"Individuel","es":"Individual","ar":"فردي"},"ثبت نشده":{"en":"Not submitted","fr":"Non soumis","es":"No enviado","ar":"غير مُرسل"},"عضوی با این فیلتر پیدا نشد":{"en":"No member matches this filter","fr":"Aucun membre ne correspond à ce filtre","es":"Ningún miembro coincide con este filtro","ar":"لا يوجد عضو مطابق لهذا المرشح"},"عملکرد بالا":{"en":"High performance","fr":"Haute performance","es":"Alto desempeño","ar":"أداء مرتفع"},"عملکرد قابل قبول":{"en":"Acceptable performance","fr":"Performance acceptable","es":"Desempeño aceptable","ar":"أداء مقبول"},"نیازمند توجه":{"en":"Needs attention","fr":"Nécessite une attention","es":"Requiere atención","ar":"يحتاج إلى اهتمام"},"هنوز سؤال وظایف مستمری توسط ادمین تعریف نشده است.":{"en":"No routine questions have been defined by the admin yet.","fr":"Aucune question de routine n’a encore été définie par l’administrateur.","es":"El administrador aún no ha definido preguntas rutinarias.","ar":"لم يحدد المسؤول أي أسئلة روتينية بعد."},"ذخیره ارزیابی وظایف مستمر انجام نشد":{"en":"Routine evaluation could not be saved","fr":"L’évaluation de routine n’a pas pu être enregistrée","es":"No se pudo guardar la evaluación rutinaria","ar":"تعذر حفظ التقييم الروتيني"},"عضوی یافت نشد":{"en":"No member found","fr":"Aucun membre trouvé","es":"No se encontró ningún miembro","ar":"لم يتم العثور على عضو"},"ارزیابی وظایف مستمر":{"en":"Routine evaluation","fr":"Évaluation de routine","es":"Evaluación rutinaria","ar":"التقييم الروتيني"},"هنوز سؤال وظایف مستمری برای تیم شما توسط ادمین تعریف نشده است.":{"en":"No routine questions have been defined for your team yet.","fr":"Aucune question de routine n’a encore été définie pour votre équipe.","es":"Aún no se han definido preguntas rutinarias para tu equipo.","ar":"لم يتم تحديد أسئلة روتينية لفريقك بعد."},"داشبورد تیم":{"en":"Team dashboard","fr":"Tableau de bord de l’équipe","es":"Panel del equipo","ar":"لوحة تحكم الفريق"},"صندوق بررسی":{"en":"Review inbox","fr":"Boîte d’examen","es":"Bandeja de revisión","ar":"صندوق المراجعة"},"ارزیابی وظایف مستمر مدیرها":{"en":"Managers’ routine evaluation","fr":"Évaluation de routine des gestionnaires","es":"Evaluación rutinaria de gerentes","ar":"التقييم الروتيني للمديرين"},"هنوز مدیری در این معاونت تعریف نشده":{"en":"No manager has been defined in this division yet","fr":"Aucun gestionnaire n’a encore été défini dans cette direction","es":"Aún no se ha definido ningún gerente en esta división","ar":"لم يتم تحديد مدير في هذه الإدارة بعد"},"هنوز سؤال وظایف مستمری برای این معاونت توسط ادمین تعریف نشده است.":{"en":"No routine questions have been defined for this division yet.","fr":"Aucune question de routine n’a encore été définie pour cette direction.","es":"Aún no se han definido preguntas rutinarias para esta división.","ar":"لم يتم تحديد أسئلة روتينية لهذه الإدارة بعد."},"اعضای معاونت":{"en":"Division members","fr":"Membres de la direction","es":"Miembros de la división","ar":"أعضاء الإدارة"},"هنوز عضوی در این معاونت تعریف نشده":{"en":"No members have been defined in this division yet","fr":"Aucun membre n’a encore été défini dans cette direction","es":"Aún no se han definido miembros en esta división","ar":"لم يتم تحديد أعضاء في هذه الإدارة بعد"},"داشبورد معاونت":{"en":"Division dashboard","fr":"Tableau de bord de la direction","es":"Panel de la división","ar":"لوحة تحكم الإدارة"},"نهایی":{"en":"Final","fr":"Final","es":"Final","ar":"نهائي"},"موقت":{"en":"Temporary","fr":"Temporaire","es":"Temporal","ar":"مؤقت"},"ارزیابی وظایف مستمر معاون‌ها":{"en":"Division heads’ routine evaluation","fr":"Évaluation de routine des responsables de direction","es":"Evaluación rutinaria de responsables de división","ar":"التقييم الروتيني لرؤساء الإدارات"},"هنوز معاونی تعریف نشده":{"en":"No division head has been defined yet","fr":"Aucun responsable de direction n’a encore été défini","es":"Aún no se ha definido ningún responsable de división","ar":"لم يتم تحديد رئيس إدارة بعد"},"هنوز سؤال وظایف مستمری برای سطح سازمان توسط ادمین تعریف نشده است.":{"en":"No organization-level routine questions have been defined yet.","fr":"Aucune question de routine au niveau de l’organisation n’a encore été définie.","es":"Aún no se han definido preguntas rutinarias a nivel de organización.","ar":"لم يتم تحديد أسئلة روتينية على مستوى المؤسسة بعد."},"اعضای شرکت":{"en":"Company members","fr":"Membres de l’entreprise","es":"Miembros de la empresa","ar":"أعضاء الشركة"},"هنوز عضوی در شرکت تعریف نشده":{"en":"No company members have been defined yet","fr":"Aucun membre de l’entreprise n’a encore été défini","es":"Aún no se han definido miembros de la empresa","ar":"لم يتم تحديد أعضاء في الشركة بعد"},"داشبورد کل شرکت":{"en":"Company dashboard","fr":"Tableau de bord de l’entreprise","es":"Panel de la empresa","ar":"لوحة تحكم الشركة"},"انتخاب کنید":{"en":"Select","fr":"Sélectionner","es":"Seleccionar","ar":"اختر"},"ذخیره":{"en":"Save","fr":"Enregistrer","es":"Guardar","ar":"حفظ"},"انصراف":{"en":"Cancel","fr":"Annuler","es":"Cancelar","ar":"إلغاء"},"ویرایش":{"en":"Edit","fr":"Modifier","es":"Editar","ar":"تعديل"},"حذف":{"en":"Delete","fr":"Supprimer","es":"Eliminar","ar":"حذف"},"ساختار سازمانی":{"en":"Organization structure","fr":"Structure organisationnelle","es":"Estructura organizativa","ar":"الهيكل التنظيمي"},"افراد":{"en":"People","fr":"Personnes","es":"Personas","ar":"الأفراد"},"نقش‌ها و وزن‌دهی":{"en":"Roles and weighting","fr":"Rôles et pondération","es":"Roles y ponderación","ar":"الأدوار والأوزان"},"هیچ نقشی تعریف نشده":{"en":"No roles have been defined","fr":"Aucun rôle n’a été défini","es":"No se han definido roles","ar":"لم يتم تحديد أدوار"},"جمع وزن‌های راهبردی یا فردی یک یا چند نقش ۱۰۰٪ نیست":{"en":"Strategic or individual weights for one or more roles do not total 100%","fr":"Les pondérations stratégiques ou individuelles d’un ou plusieurs rôles ne totalisent pas 100 %","es":"Las ponderaciones estratégicas o individuales de uno o más roles no suman 100 %","ar":"مجموع الأوزان الاستراتيجية أو الفردية لدور واحد أو أكثر لا يساوي 100٪"},"سؤالات وظایف مستمر تیم‌ها":{"en":"Team routine questions","fr":"Questions de routine des équipes","es":"Preguntas rutinarias de equipos","ar":"أسئلة الفرق الروتينية"},"ابتدا تیمی تعریف کنید":{"en":"Define a team first","fr":"Définissez d’abord une équipe","es":"Primero define un equipo","ar":"حدد فريقاً أولاً"},"سؤالات وظایف مستمر معاونت‌ها":{"en":"Division routine questions","fr":"Questions de routine des directions","es":"Preguntas rutinarias de divisiones","ar":"أسئلة الإدارات الروتينية"},"ابتدا معاونتی تعریف کنید":{"en":"Define a division first","fr":"Définissez d’abord une direction","es":"Primero define una división","ar":"حدد إدارة أولاً"},"سؤالات وظایف مستمر سازمان":{"en":"Organization routine questions","fr":"Questions de routine de l’organisation","es":"Preguntas rutinarias de la organización","ar":"أسئلة المؤسسة الروتينية"},"هنوز سؤالی تعریف نشده":{"en":"No questions have been defined yet","fr":"Aucune question n’a encore été définie","es":"Aún no se han definido preguntas","ar":"لم يتم تحديد أسئلة بعد"},"شاخص‌های سازمانی":{"en":"Organization indicators","fr":"Indicateurs de l’organisation","es":"Indicadores de la organización","ar":"مؤشرات المؤسسة"},"هدف‌گذاری دوره جاری":{"en":"Current-period target setting","fr":"Définition des cibles de la période en cours","es":"Definición de objetivos del período actual","ar":"تحديد أهداف الفترة الحالية"},"ثبت عملکرد دوره جاری":{"en":"Current-period performance entry","fr":"Saisie de la performance de la période en cours","es":"Registro de desempeño del período actual","ar":"إدخال أداء الفترة الحالية"},"تعیین نشده":{"en":"Not set","fr":"Non défini","es":"No definido","ar":"غير محدد"},"،":{"en":",","fr":",","es":",","ar":"،"},"نام معاونت":{"en":"Division name","fr":"Nom de la direction","es":"Nombre de la división","ar":"اسم الإدارة"},"مثال: فروش و بازاریابی":{"en":"Example: Sales and Marketing","fr":"Exemple : Ventes et marketing","es":"Ejemplo: Ventas y marketing","ar":"مثال: المبيعات والتسويق"},"معاون مربوطه":{"en":"Division head","fr":"Responsable de la direction","es":"Responsable de la división","ar":"رئيس الإدارة"},"نام تیم":{"en":"Team name","fr":"Nom de l’équipe","es":"Nombre del equipo","ar":"اسم الفريق"},"مثال: تیم فروش":{"en":"Example: Sales team","fr":"Exemple : Équipe des ventes","es":"Ejemplo: Equipo de ventas","ar":"مثال: فريق المبيعات"},"انتخاب معاونت":{"en":"Select division","fr":"Sélectionner une direction","es":"Seleccionar división","ar":"اختر الإدارة"},"مدیر تیم":{"en":"Team manager","fr":"Gestionnaire de l’équipe","es":"Gerente del equipo","ar":"مدير الفريق"},"معاونت‌ها":{"en":"Divisions","fr":"Directions","es":"Divisiones","ar":"الإدارات"},"تیم‌ها":{"en":"Teams","fr":"Équipes","es":"Equipos","ar":"الفرق"},"بخش‌های ساختار سازمانی":{"en":"Organization structure sections","fr":"Sections de la structure organisationnelle","es":"Secciones de la estructura organizativa","ar":"أقسام الهيكل التنظيمي"},"نام معاونت الزامی است":{"en":"Division name is required","fr":"Le nom de la direction est obligatoire","es":"El nombre de la división es obligatorio","ar":"اسم الإدارة مطلوب"},"نام تیم الزامی است":{"en":"Team name is required","fr":"Le nom de l’équipe est obligatoire","es":"El nombre del equipo es obligatorio","ar":"اسم الفريق مطلوب"},"انتخاب معاونت الزامی است":{"en":"Selecting a division is required","fr":"La sélection d’une direction est obligatoire","es":"Seleccionar una división es obligatorio","ar":"اختيار الإدارة مطلوب"},"معاون":{"en":"Division Head","fr":"Responsable de direction","es":"Responsable de división","ar":"رئيس إدارة"},"مدیرعامل":{"en":"CEO","fr":"PDG","es":"CEO","ar":"الرئيس التنفيذي"},"ادمین":{"en":"Admin","fr":"Administrateur","es":"Administrador","ar":"مسؤول النظام"},"نام و نام‌خانوادگی":{"en":"Full name","fr":"Nom complet","es":"Nombre completo","ar":"الاسم الكامل"},"مثال: علی رضایی":{"en":"Example: Ali Rezaei","fr":"Exemple : Ali Rezaei","es":"Ejemplo: Ali Rezaei","ar":"مثال: علي رضائي"},"کد پرسنلی":{"en":"Employee code","fr":"Code employé","es":"Código de empleado","ar":"رمز الموظف"},"کد ملی":{"en":"National ID","fr":"Identifiant national","es":"Identificación nacional","ar":"الرقم الوطني"},"ایمیل":{"en":"Email","fr":"Courriel","es":"Correo electrónico","ar":"البريد الإلكتروني"},"نام کاربری":{"en":"Username","fr":"Nom d’utilisateur","es":"Usuario","ar":"اسم المستخدم"},"رمز عبور":{"en":"Password","fr":"Mot de passe","es":"Contraseña","ar":"كلمة المرور"},"سطح دسترسی":{"en":"Access level","fr":"Niveau d’accès","es":"Nivel de acceso","ar":"مستوى الوصول"},"انتخاب سطح دسترسی":{"en":"Select access level","fr":"Sélectionner le niveau d’accès","es":"Seleccionar nivel de acceso","ar":"اختر مستوى الوصول"},"نقش":{"en":"Role","fr":"Rôle","es":"Rol","ar":"الدور"},"انتخاب سمت شغلی":{"en":"Select role","fr":"Sélectionner le rôle","es":"Seleccionar rol","ar":"اختر الدور"},"انتخاب تیم":{"en":"Select team","fr":"Sélectionner l’équipe","es":"Seleccionar equipo","ar":"اختر الفريق"},"مدیر مستقیم":{"en":"Direct manager","fr":"Gestionnaire direct","es":"Gerente directo","ar":"المدير المباشر"},"بدون مدیر مستقیم":{"en":"No direct manager","fr":"Aucun gestionnaire direct","es":"Sin gerente directo","ar":"بدون مدير مباشر"},"نام الزامی است":{"en":"Name is required","fr":"Le nom est obligatoire","es":"El nombre es obligatorio","ar":"الاسم مطلوب"},"کد پرسنلی الزامی است":{"en":"Employee code is required","fr":"Le code employé est obligatoire","es":"El código de empleado es obligatorio","ar":"رمز الموظف مطلوب"},"کد ملی الزامی است":{"en":"National ID is required","fr":"L’identifiant national est obligatoire","es":"La identificación nacional es obligatoria","ar":"الرقم الوطني مطلوب"},"کد ملی باید ۱۰ رقم باشد":{"en":"National ID must be 10 digits","fr":"L’identifiant national doit comporter 10 chiffres","es":"La identificación nacional debe tener 10 dígitos","ar":"يجب أن يتكون الرقم الوطني من 10 أرقام"},"ایمیل الزامی است":{"en":"Email is required","fr":"Le courriel est obligatoire","es":"El correo electrónico es obligatorio","ar":"البريد الإلكتروني مطلوب"},"ایمیل معتبر نیست":{"en":"Email is not valid","fr":"Le courriel n’est pas valide","es":"El correo electrónico no es válido","ar":"البريد الإلكتروني غير صالح"},"نام کاربری الزامی است":{"en":"Username is required","fr":"Le nom d’utilisateur est obligatoire","es":"El usuario es obligatorio","ar":"اسم المستخدم مطلوب"},"رمز عبور الزامی است":{"en":"Password is required","fr":"Le mot de passe est obligatoire","es":"La contraseña es obligatoria","ar":"كلمة المرور مطلوبة"},"سطح دسترسی الزامی است":{"en":"Access level is required","fr":"Le niveau d’accès est obligatoire","es":"El nivel de acceso es obligatorio","ar":"مستوى الوصول مطلوب"},"برای معاون، انتخاب معاونت الزامی است":{"en":"A division must be selected for a division head","fr":"Une direction doit être sélectionnée pour un responsable de direction","es":"Debe seleccionarse una división para un responsable de división","ar":"يجب اختيار إدارة لرئيس الإدارة"},"برای مدیر/کارشناس، انتخاب تیم الزامی است":{"en":"A team must be selected for a manager/specialist","fr":"Une équipe doit être sélectionnée pour un gestionnaire/spécialiste","es":"Debe seleccionarse un equipo para un gerente/especialista","ar":"يجب اختيار فريق للمدير/الأخصائي"},"نام گروه فعالیت":{"en":"Activity group name","fr":"Nom du groupe d’activités","es":"Nombre del grupo de actividades","ar":"اسم مجموعة الأنشطة"},"سقف امتیاز":{"en":"Maximum score","fr":"Score maximal","es":"Puntuación máxima","ar":"الحد الأقصى للدرجة"},"آیکون":{"en":"Icon (emoji)","fr":"Icône (emoji)","es":"Icono (emoji)","ar":"الأيقونة (رمز تعبيري)"},"زیرگروه‌ها (با کاما جدا کنید)":{"en":"Subgroups (comma-separated)","fr":"Sous-groupes (séparés par des virgules)","es":"Subgrupos (separados por comas)","ar":"المجموعات الفرعية (مفصولة بفواصل)"},"دوره تخصصی, دوره عمومی":{"en":"Specialized course, general course","fr":"Formation spécialisée, formation générale","es":"Curso especializado, curso general","ar":"دورة تخصصية، دورة عامة"},"عنوان سطح تأثیر":{"en":"Impact level title","fr":"Nom du niveau d’impact","es":"Título del nivel de impacto","ar":"عنوان مستوى التأثير"},"امتیاز":{"en":"Score","fr":"Score","es":"Puntuación","ar":"الدرجة"},"عنوان سطح":{"en":"Level title","fr":"Nom du niveau","es":"Título del nivel","ar":"عنوان المستوى"},"لوگو و برند":{"en":"Logo and branding","fr":"Logo et image de marque","es":"Logotipo y marca","ar":"الشعار والهوية"},"دسته‌های فعالیت":{"en":"Activity categories","fr":"Catégories d’activités","es":"Categorías de actividades","ar":"فئات الأنشطة"},"سطوح تأثیر":{"en":"Impact levels","fr":"Niveaux d’impact","es":"Niveles de impacto","ar":"مستويات التأثير"},"روتین":{"en":"Routine","fr":"Routine","es":"Rutina","ar":"روتيني"},"بخش‌های تنظیمات پایه":{"en":"Base settings sections","fr":"Sections des paramètres de base","es":"Secciones de configuración básica","ar":"أقسام الإعدادات الأساسية"},"تغییر لوگو":{"en":"Change logo","fr":"Changer le logo","es":"Cambiar logotipo","ar":"تغيير الشعار"},"آپلود لوگو":{"en":"Upload logo","fr":"Téléverser le logo","es":"Cargar logotipo","ar":"رفع الشعار"},"نام سمت شغلی جدید را وارد کنید:":{"en":"Enter the new role name:","fr":"Saisissez le nom du nouveau rôle :","es":"Introduce el nombre del nuevo rol:","ar":"أدخل اسم الدور الجديد:"},"سطح تیم":{"en":"Team level","fr":"Niveau équipe","es":"Nivel de equipo","ar":"مستوى الفريق"},"سطح معاونت":{"en":"Division level","fr":"Niveau direction","es":"Nivel de división","ar":"مستوى الإدارة"},"سطح سازمان":{"en":"Organization level","fr":"Niveau organisation","es":"Nivel de organización","ar":"مستوى المؤسسة"},"وظایف مستمر":{"en":"Routine tasks","fr":"Tâches courantes","es":"Tareas rutinarias","ar":"المهام الروتينية"},"نام سمت شغلی و جمع ۱۰۰٪ هر دو بخش را بررسی کنید":{"en":"Check the role name and ensure both sections total 100%","fr":"Vérifiez le nom du rôle et assurez-vous que les deux sections totalisent 100 %","es":"Comprueba el nombre del rol y que ambas secciones sumen 100 %","ar":"تحقق من اسم الدور وأن مجموع كل قسم يساوي 100٪"},"نام گروه الزامی است":{"en":"Group name is required","fr":"Le nom du groupe est obligatoire","es":"El nombre del grupo es obligatorio","ar":"اسم المجموعة مطلوب"},"عنوان الزامی است":{"en":"Title is required","fr":"Le titre est obligatoire","es":"El título es obligatorio","ar":"العنوان مطلوب"},"سازمان":{"en":"Organization","fr":"Organisation","es":"Organización","ar":"المؤسسة"},"نام شاخص":{"en":"Indicator name","fr":"Nom de l’indicateur","es":"Nombre del indicador","ar":"اسم المؤشر"},"مثال: درآمد تیم فروش":{"en":"Example: Sales team revenue","fr":"Exemple : Revenu de l’équipe des ventes","es":"Ejemplo: Ingresos del equipo de ventas","ar":"مثال: إيرادات فريق المبيعات"},"سطح":{"en":"Level","fr":"Niveau","es":"Nivel","ar":"المستوى"},"انتخاب سطح":{"en":"Select level","fr":"Sélectionner le niveau","es":"Seleccionar nivel","ar":"اختر المستوى"},"تیم مربوطه":{"en":"Related team","fr":"Équipe concernée","es":"Equipo correspondiente","ar":"الفريق المعني"},"معاونت مربوطه":{"en":"Related division","fr":"Direction concernée","es":"División correspondiente","ar":"الإدارة المعنية"},"واحد اندازه‌گیری":{"en":"Unit of measure","fr":"Unité de mesure","es":"Unidad de medida","ar":"وحدة القياس"},"تومان، نفر، درصد":{"en":"Toman, people, percent","fr":"Toman, personnes, pourcentage","es":"Toman, personas, porcentaje","ar":"تومان، أشخاص، نسبة مئوية"},"وزن در سطح خودش":{"en":"Weight within its level","fr":"Pondération dans son niveau","es":"Peso dentro de su nivel","ar":"الوزن ضمن مستواه"},"مالک شاخص":{"en":"Indicator owner","fr":"Responsable de l’indicateur","es":"Responsable del indicador","ar":"مالك المؤشر"},"انتخاب مالک":{"en":"Select owner","fr":"Sélectionner le responsable","es":"Seleccionar responsable","ar":"اختر المالك"},"ثبت‌کننده مقدار واقعی":{"en":"Actual-value submitter","fr":"Responsable de saisie de la valeur réelle","es":"Responsable de registrar el valor real","ar":"مسجل القيمة الفعلية"},"انتخاب ثبت‌کننده":{"en":"Select submitter","fr":"Sélectionner le responsable de saisie","es":"Seleccionar responsable de registro","ar":"اختر المسجل"},"انتخاب تأییدکننده":{"en":"Select approver","fr":"Sélectionner l’approbateur","es":"Seleccionar aprobador","ar":"اختر المعتمد"},"الگوی زمان‌بندی":{"en":"Schedule template","fr":"Modèle de calendrier","es":"Plantilla de programación","ar":"قالب الجدول الزمني"},"انتخاب الگو":{"en":"Select template","fr":"Sélectionner le modèle","es":"Seleccionar plantilla","ar":"اختر القالب"},"تجمیع نوبت‌ها":{"en":"Occurrence aggregation","fr":"Agrégation des occurrences","es":"Agregación de ocurrencias","ar":"تجميع الفترات"},"جمع":{"en":"Sum","fr":"Somme","es":"Suma","ar":"المجموع"},"میانگین":{"en":"Average","fr":"Moyenne","es":"Promedio","ar":"المتوسط"},"آخرین مقدار":{"en":"Latest value","fr":"Dernière valeur","es":"Último valor","ar":"آخر قيمة"},"روش تجمیع":{"en":"Aggregation method","fr":"Méthode d’agrégation","es":"Método de agregación","ar":"طريقة التجميع"},"مستند الزامی":{"en":"Document required","fr":"Document obligatoire","es":"Documento obligatorio","ar":"المستند مطلوب"},"بله":{"en":"Yes","fr":"Oui","es":"Sí","ar":"نعم"},"خیر":{"en":"No","fr":"Non","es":"No","ar":"لا"},"نام شاخص الزامی است":{"en":"Indicator name is required","fr":"Le nom de l’indicateur est obligatoire","es":"El nombre del indicador es obligatorio","ar":"اسم المؤشر مطلوب"},"انتخاب سطح الزامی است":{"en":"Selecting a level is required","fr":"La sélection d’un niveau est obligatoire","es":"Seleccionar un nivel es obligatorio","ar":"اختيار المستوى مطلوب"},"برای سطح تیم، انتخاب تیم الزامی است":{"en":"A team must be selected for team level","fr":"Une équipe doit être sélectionnée pour le niveau équipe","es":"Debe seleccionarse un equipo para el nivel de equipo","ar":"يجب اختيار فريق لمستوى الفريق"},"برای سطح معاونت، انتخاب معاونت الزامی است":{"en":"A division must be selected for division level","fr":"Une direction doit être sélectionnée pour le niveau direction","es":"Debe seleccionarse una división para el nivel de división","ar":"يجب اختيار إدارة لمستوى الإدارة"},"مالک، ثبت‌کننده و تأییدکننده باید مشخص شوند":{"en":"Owner, submitter, and approver must be specified","fr":"Le responsable, le responsable de saisie et l’approbateur doivent être définis","es":"Deben especificarse el responsable, el registrador y el aprobador","ar":"يجب تحديد المالك والمسجل والمعتمد"},"ثبت‌کننده نمی‌تواند تأییدکننده همان شاخص باشد":{"en":"The submitter cannot approve the same indicator","fr":"Le responsable de saisie ne peut pas approuver le même indicateur","es":"El registrador no puede aprobar el mismo indicador","ar":"لا يمكن للمسجل اعتماد المؤشر نفسه"},"انتخاب الگوی زمان‌بندی الزامی است":{"en":"Selecting a schedule template is required","fr":"La sélection d’un modèle de calendrier est obligatoire","es":"Seleccionar una plantilla de programación es obligatorio","ar":"اختيار قالب الجدول الزمني مطلوب"},"روش تجمیع نوبت‌ها الزامی است":{"en":"An occurrence aggregation method is required","fr":"Une méthode d’agrégation des occurrences est obligatoire","es":"Se requiere un método de agregación de ocurrencias","ar":"طريقة تجميع الفترات مطلوبة"},"نام الگو":{"en":"Template name","fr":"Nom du modèle","es":"Nombre de la plantilla","ar":"اسم القالب"},"گزارش فصلی استاندارد":{"en":"Standard quarterly report","fr":"Rapport trimestriel standard","es":"Informe trimestral estándar","ar":"تقرير ربع سنوي قياسي"},"تناوب":{"en":"Frequency","fr":"Fréquence","es":"Frecuencia","ar":"التكرار"},"ماهانه":{"en":"Monthly","fr":"Mensuel","es":"Mensual","ar":"شهري"},"شروع ثبت؛ روز بعد از پایان":{"en":"Submission start; days after period end","fr":"Début de saisie ; jours après la fin de la période","es":"Inicio del registro; días después del fin del período","ar":"بدء التسجيل؛ أيام بعد نهاية الفترة"},"مهلت ثبت؛ روز بعد از پایان":{"en":"Submission deadline; days after period end","fr":"Date limite de saisie ; jours après la fin de la période","es":"Fecha límite de registro; días después del fin del período","ar":"موعد التسجيل؛ أيام بعد نهاية الفترة"},"مهلت تأیید؛ روز پس از ثبت":{"en":"Approval deadline; days after submission","fr":"Date limite d’approbation ; jours après la saisie","es":"Fecha límite de aprobación; días después del registro","ar":"موعد الاعتماد؛ أيام بعد التسجيل"},"قفل؛ روز بعد از پایان":{"en":"Lock; days after period end","fr":"Verrouillage ; jours après la fin de la période","es":"Bloqueo; días después del fin del período","ar":"القفل؛ أيام بعد نهاية الفترة"},"یادآوری؛ چند روز قبل":{"en":"Reminder; days before","fr":"Rappel ; jours avant","es":"Recordatorio; días antes","ar":"التذكير؛ أيام قبل"},"این الگو به یک یا چند شاخص متصل است و قابل حذف نیست.":{"en":"This template is linked to one or more indicators and cannot be deleted.","fr":"Ce modèle est lié à un ou plusieurs indicateurs et ne peut pas être supprimé.","es":"Esta plantilla está vinculada a uno o más indicadores y no puede eliminarse.","ar":"هذا القالب مرتبط بمؤشر واحد أو أكثر ولا يمكن حذفه."},"نام و تناوب الگو الزامی است":{"en":"Template name and frequency are required","fr":"Le nom et la fréquence du modèle sont obligatoires","es":"El nombre y la frecuencia de la plantilla son obligatorios","ar":"اسم القالب وتكراره مطلوبان"},"تاریخ قفل باید بعد از پایان مهلت تأیید باشد":{"en":"The lock date must be after the approval deadline","fr":"La date de verrouillage doit être postérieure à la date limite d’approbation","es":"La fecha de bloqueo debe ser posterior a la fecha límite de aprobación","ar":"يجب أن يكون تاريخ القفل بعد موعد الاعتماد"},"شروع ثبت":{"en":"Submission start","fr":"Début de saisie","es":"Inicio del registro","ar":"بدء التسجيل"},"مهلت ثبت":{"en":"Submission deadline","fr":"Date limite de saisie","es":"Fecha límite de registro","ar":"موعد التسجيل"},"مهلت تأیید":{"en":"Approval deadline","fr":"Date limite d’approbation","es":"Fecha límite de aprobación","ar":"موعد الاعتماد"},"تاریخ قفل":{"en":"Lock date","fr":"Date de verrouillage","es":"Fecha de bloqueo","ar":"تاريخ القفل"},"دلیل استثنا (الزامی)":{"en":"Exception reason (required)","fr":"Motif de l’exception (obligatoire)","es":"Motivo de la excepción (obligatorio)","ar":"سبب الاستثناء (مطلوب)"},"متن سؤال":{"en":"Question text","fr":"Texte de la question","es":"Texto de la pregunta","ar":"نص السؤال"},"مثال: رعایت مهلت‌های تعیین‌شده":{"en":"Example: Meeting established deadlines","fr":"Exemple : Respect des échéances établies","es":"Ejemplo: Cumplimiento de los plazos establecidos","ar":"مثال: الالتزام بالمواعيد المحددة"},"وزن":{"en":"Weight","fr":"Pondération","es":"Peso","ar":"الوزن"},"٪":{"en":"%","fr":"%","es":"%","ar":"٪"},"متن سؤال الزامی است":{"en":"Question text is required","fr":"Le texte de la question est obligatoire","es":"El texto de la pregunta es obligatorio","ar":"نص السؤال مطلوب"},"مثال: همسویی با استراتژی سازمان":{"en":"Example: Alignment with organizational strategy","fr":"Exemple : Alignement sur la stratégie de l’organisation","es":"Ejemplo: Alineación con la estrategia de la organización","ar":"مثال: التوافق مع استراتيجية المؤسسة"},"سطح تیم (مدیر ← کارشناس)":{"en":"Team level (Manager ← Specialist)","fr":"Niveau équipe (Gestionnaire ← Spécialiste)","es":"Nivel de equipo (Gerente ← Especialista)","ar":"مستوى الفريق (المدير ← الأخصائي)"},"سطح معاونت (معاون ← مدیر)":{"en":"Division level (Division Head ← Manager)","fr":"Niveau direction (Responsable ← Gestionnaire)","es":"Nivel de división (Responsable ← Gerente)","ar":"مستوى الإدارة (رئيس الإدارة ← المدير)"},"سطح سازمان (مدیرعامل ← معاون)":{"en":"Organization level (CEO ← Division Head)","fr":"Niveau organisation (PDG ← Responsable de direction)","es":"Nivel de organización (CEO ← Responsable de división)","ar":"مستوى المؤسسة (الرئيس التنفيذي ← رئيس الإدارة)"},"سطوح پرسش‌های وظایف مستمر":{"en":"Routine question levels","fr":"Niveaux des questions de routine","es":"Niveles de preguntas rutinarias","ar":"مستويات الأسئلة الروتينية"},"آماده‌سازی":{"en":"Setup","fr":"Préparation","es":"Preparación","ar":"إعداد"},"فعال":{"en":"Active","fr":"Actif","es":"Activo","ar":"نشط"},"در حال نهایی‌سازی":{"en":"Finalizing","fr":"Finalisation en cours","es":"Finalizando","ar":"جارٍ الإنهاء"},"نهایی و قفل‌شده":{"en":"Final and locked","fr":"Final et verrouillé","es":"Final y bloqueado","ar":"نهائي ومقفل"},"دوره بسته نشد؛ ابتدا همه شاخص‌ها باید تأیید شوند.":{"en":"The period could not be closed; all indicators must be approved first.","fr":"La période n’a pas pu être clôturée ; tous les indicateurs doivent d’abord être approuvés.","es":"No se pudo cerrar el período; primero deben aprobarse todos los indicadores.","ar":"تعذر إغلاق الفترة؛ يجب اعتماد جميع المؤشرات أولاً."},"دلیل بازگشایی دوره را وارد کنید:":{"en":"Enter the reason for reopening the period:","fr":"Saisissez le motif de réouverture de la période :","es":"Introduce el motivo para reabrir el período:","ar":"أدخل سبب إعادة فتح الفترة:"},"فصل چهارم ۱۴۰۴":{"en":"Fourth quarter 1404","fr":"Quatrième trimestre 1404","es":"Cuarto trimestre 1404","ar":"الربع الرابع 1404"},"این دوره نهایی و قفل‌شده است":{"en":"This period is final and locked","fr":"Cette période est finale et verrouillée","es":"Este período es final y está bloqueado","ar":"هذه الفترة نهائية ومقفلة"},"مقدار واقعی فقط توسط ثبت‌کننده تعیین‌شده وارد می‌شود":{"en":"The actual value can only be entered by the designated submitter","fr":"La valeur réelle ne peut être saisie que par le responsable désigné","es":"El valor real solo puede ser registrado por el responsable designado","ar":"لا يمكن إدخال القيمة الفعلية إلا بواسطة المسجل المحدد"},"هدف":{"en":"Target","fr":"Cible","es":"Objetivo","ar":"الهدف"},"واقعی":{"en":"Actual","fr":"Réel","es":"Real","ar":"الفعلي"},"قفل دوره":{"en":"Lock period","fr":"Verrouiller la période","es":"Bloquear período","ar":"قفل الفترة"},"بازگشایی دوره":{"en":"Reopen period","fr":"Rouvrir la période","es":"Reabrir período","ar":"إعادة فتح الفترة"},"مثال: همکاری مؤثر با دیگران":{"en":"Example: Effective collaboration with others","fr":"Exemple : Collaboration efficace avec les autres","es":"Ejemplo: Colaboración eficaz con otros","ar":"مثال: التعاون الفعال مع الآخرين"},"بانک سؤال":{"en":"Question bank","fr":"Banque de questions","es":"Banco de preguntas","ar":"بنك الأسئلة"},"تخصیص ارزیاب‌ها":{"en":"Reviewer assignments","fr":"Affectation des évaluateurs","es":"Asignación de evaluadores","ar":"تعيين المقيّمين"},"تنظیمات بازخورد همکاران":{"en":"Peer feedback settings","fr":"Paramètres de rétroaction des collègues","es":"Configuración de comentarios de colegas","ar":"إعدادات ملاحظات الزملاء"},"— باید ۱۰۰٪ باشد":{"en":"— must equal 100%","fr":"— doit être égal à 100 %","es":"— debe ser igual a 100 %","ar":"— يجب أن يساوي 100٪"},"ارزیابی پیشنهاد نشد":{"en":"No review suggested","fr":"Aucune évaluation suggérée","es":"No se sugirió evaluación","ar":"لم يُقترح تقييم"},"معتبر":{"en":"Valid","fr":"Valide","es":"Válido","ar":"صالح"},"نیازمند تکمیل دستی":{"en":"Manual completion required","fr":"Complément manuel requis","es":"Se requiere completar manualmente","ar":"يتطلب إكمالاً يدوياً"},"بررسی وضعیت":{"en":"Check status","fr":"Vérifier l’état","es":"Revisar estado","ar":"فحص الحالة"},"تنظیمات پایه":{"en":"Base settings","fr":"Paramètres de base","es":"Configuración básica","ar":"الإعدادات الأساسية"},"سؤالات وظایف مستمر":{"en":"Routine questions","fr":"Questions de routine","es":"Preguntas rutinarias","ar":"الأسئلة الروتينية"},"شاخص‌ها":{"en":"Indicators","fr":"Indicateurs","es":"Indicadores","ar":"المؤشرات"},"زمان‌بندی شاخص‌ها":{"en":"Indicator scheduling","fr":"Planification des indicateurs","es":"Programación de indicadores","ar":"جدولة المؤشرات"},"برنامه‌ریزی دوره":{"en":"Period planning","fr":"Planification de la période","es":"Planificación del período","ar":"تخطيط الفترة"},"پایش ثبت عملکرد":{"en":"Performance entry monitoring","fr":"Suivi de la saisie de performance","es":"Monitoreo del registro de desempeño","ar":"متابعة إدخال الأداء"},"برای دوره‌ی انتخاب‌شده، مقدار هدف هر شاخص را وارد کنید":{"en":"Enter the target value for each indicator for the selected period","fr":"Saisissez la valeur cible de chaque indicateur pour la période sélectionnée","es":"Introduce el valor objetivo de cada indicador para el período seleccionado","ar":"أدخل القيمة المستهدفة لكل مؤشر للفترة المحددة"},"ثبت عملکرد":{"en":"Performance entry","fr":"Saisie de performance","es":"Registro de desempeño","ar":"إدخال الأداء"},"وضعیت ثبت و تأیید مقادیر واقعی را پایش کنید؛ ورود اطلاعات توسط مسئول هر شاخص انجام می‌شود":{"en":"Monitor the submission and approval status of actual values; data entry is performed by each indicator’s designated submitter","fr":"Suivez l’état de saisie et d’approbation des valeurs réelles ; la saisie est effectuée par le responsable désigné de chaque indicateur","es":"Supervisa el estado de registro y aprobación de los valores reales; los datos los registra el responsable designado de cada indicador","ar":"تابع حالة إدخال واعتماد القيم الفعلية؛ يقوم بإدخال البيانات المسجل المحدد لكل مؤشر"},"CVI دوره جاری":{"en":"Current-period CVI","fr":"CVI de la période en cours","es":"CVI del período actual","ar":"CVI للفترة الحالية"},"شاخص ارزیابی مشارکت":{"en":"Contribution Value Index","fr":"Indice de valeur de contribution","es":"Índice de Valor de Contribución","ar":"مؤشر قيمة المساهمة"},"خروج از سامانه":{"en":"Log out","fr":"Se déconnecter","es":"Cerrar sesión","ar":"تسجيل الخروج"},"عملکرد فردی":{"en":"Individual Performance","fr":"Performance individuelle","es":"Desempeño individual","ar":"الأداء الفردي"},"ورود به سامانه":{"en":"Sign in","fr":"Connexion","es":"Iniciar sesión","ar":"تسجيل الدخول"},"با اطلاعات کاربری خود وارد شوید":{"en":"Sign in with your account","fr":"Connectez-vous avec votre compte","es":"Inicia sesión con tu cuenta","ar":"سجّل الدخول باستخدام حسابك"},"ورود سریع (نمونه)":{"en":"Quick sign-in (demo)","fr":"Connexion rapide (démo)","es":"Acceso rápido (demo)","ar":"دخول سريع (تجريبي)"},"یک نقش نمونه را انتخاب کنید...":{"en":"Select a demo role...","fr":"Sélectionnez un rôle de démonstration...","es":"Selecciona un rol de demostración...","ar":"اختر دوراً تجريبياً..."},"تاریخچه فرایند فعالیت":{"en":"Activity process history","fr":"Historique du processus d’activité","es":"Historial del proceso de actividad","ar":"سجل عملية النشاط"},"فعالیت ارسال شد":{"en":"Activity submitted","fr":"Activité soumise","es":"Actividad enviada","ar":"تم إرسال النشاط"},"فعالیت شما برای بررسی به مدیر ارسال شد.":{"en":"Your activity was sent to your manager for review.","fr":"Votre activité a été envoyée à votre gestionnaire pour examen.","es":"Tu actividad se envió a tu gerente para revisión.","ar":"تم إرسال نشاطك إلى مديرك للمراجعة."},"پس از تأیید، امتیاز به داشبورد شما اضافه می‌شود.":{"en":"Once approved, the score will be added to your dashboard.","fr":"Après approbation, le score sera ajouté à votre tableau de bord.","es":"Una vez aprobada, la puntuación se añadirá a tu panel.","ar":"بعد الاعتماد، ستُضاف الدرجة إلى لوحة التحكم الخاصة بك."},"ثبت فعالیت جدید":{"en":"Add new activity","fr":"Ajouter une activité","es":"Registrar nueva actividad","ar":"إضافة نشاط جديد"},"بازگشت به داشبورد":{"en":"Back to dashboard","fr":"Retour au tableau de bord","es":"Volver al panel","ar":"العودة إلى لوحة التحكم"},"پاک‌کردن پیش‌نویس":{"en":"Clear draft","fr":"Effacer le brouillon","es":"Borrar borrador","ar":"مسح المسودة"},"گروه فعالیت *":{"en":"Activity group *","fr":"Groupe d’activités *","es":"Grupo de actividades *","ar":"مجموعة النشاط *"},"انتخاب گروه فعالیت":{"en":"Select activity group","fr":"Sélectionner le groupe d’activités","es":"Seleccionar grupo de actividades","ar":"اختر مجموعة النشاط"},"زیرگروه *":{"en":"Subgroup *","fr":"Sous-groupe *","es":"Subgrupo *","ar":"المجموعة الفرعية *"},"سطح تأثیر فعالیت توسط مدیر شما مشخص می‌شود. در توضیحات، تأثیر کار را شرح دهید.":{"en":"Your manager determines the activity’s impact level. Describe the impact of your work in the details.","fr":"Votre gestionnaire détermine le niveau d’impact de l’activité. Décrivez l’impact de votre travail dans les détails.","es":"Tu gerente determina el nivel de impacto de la actividad. Describe el impacto de tu trabajo en los detalles.","ar":"يحدد مديرك مستوى تأثير النشاط. صف تأثير عملك في التفاصيل."},"عنوان فعالیت *":{"en":"Activity title *","fr":"Titre de l’activité *","es":"Título de la actividad *","ar":"عنوان النشاط *"},"توضیحات *":{"en":"Description *","fr":"Description *","es":"Descripción *","ar":"الوصف *"},"تأثیر این فعالیت روی کار خود را توضیح دهید — مدیر بر اساس همین توضیحات سطح تأثیر را تعیین می‌کند.":{"en":"Explain how this activity affected your work — your manager will use this description to determine the impact level.","fr":"Expliquez l’impact de cette activité sur votre travail — votre gestionnaire utilisera cette description pour déterminer le niveau d’impact.","es":"Explica cómo afectó esta actividad a tu trabajo; tu gerente usará esta descripción para determinar el nivel de impacto.","ar":"اشرح تأثير هذا النشاط على عملك — سيستخدم مديرك هذا الوصف لتحديد مستوى التأثير."},"مستندات پشتیبان":{"en":"Supporting documents","fr":"Documents justificatifs","es":"Documentos de respaldo","ar":"المستندات الداعمة"},"این بخش اختیاری است، اما می‌تواند به ارزیابی بهتر فعالیت کمک کند.":{"en":"This section is optional, but it can support a better activity evaluation.","fr":"Cette section est facultative, mais elle peut faciliter une meilleure évaluation de l’activité.","es":"Esta sección es opcional, pero puede ayudar a evaluar mejor la actividad.","ar":"هذا القسم اختياري، لكنه قد يساعد في تقييم النشاط بشكل أفضل."},"برای آپلود کلیک کنید":{"en":"Click to upload","fr":"Cliquer pour téléverser","es":"Haz clic para cargar","ar":"انقر للرفع"},"گواهی، گزارش، تصویر — حداکثر ۵ مگابایت":{"en":"Certificate, report, image — max 5 MB","fr":"Certificat, rapport, image — 5 Mo max.","es":"Certificado, informe, imagen — máx. 5 MB","ar":"شهادة، تقرير، صورة — بحد أقصى 5 ميغابايت"},"خلاصه فعالیت":{"en":"Activity summary","fr":"Résumé de l’activité","es":"Resumen de la actividad","ar":"ملخص النشاط"},"ارسال برای مدیر ✓":{"en":"Send to manager ✓","fr":"Envoyer au gestionnaire ✓","es":"Enviar al gerente ✓","ar":"إرسال إلى المدير ✓"},"اصلاح فعالیت":{"en":"Revise activity","fr":"Réviser l’activité","es":"Revisar actividad","ar":"تعديل النشاط"},"توضیحات اصلاح‌شده *":{"en":"Revised description *","fr":"Description révisée *","es":"Descripción revisada *","ar":"الوصف المعدل *"},"مستندات":{"en":"Documents","fr":"Documents","es":"Documentos","ar":"المستندات"},"انتخاب مستند جدید":{"en":"📎 Select new document","fr":"📎 Sélectionner un nouveau document","es":"📎 Seleccionar nuevo documento","ar":"📎 اختر مستنداً جديداً"},"ثبت اصلاحات و ارسال مجدد":{"en":"Save revisions and resubmit","fr":"Enregistrer les révisions et soumettre de nouveau","es":"Guardar cambios y reenviar","ar":"حفظ التعديلات وإعادة الإرسال"},"ویرایش و ارسال مجدد":{"en":"Edit and resubmit","fr":"Modifier et soumettre de nouveau","es":"Editar y reenviar","ar":"تعديل وإعادة الإرسال"},"مقایسه با دوره قبل":{"en":"Compare with previous period","fr":"Comparer avec la période précédente","es":"Comparar con el período anterior","ar":"مقارنة بالفترة السابقة"},"ترکیب عملکرد فردی":{"en":"Individual contribution composition","fr":"Composition de la performance individuelle","es":"Composición de la desempeño individual","ar":"مكونات الأداء الفردي"},"فعالیت‌های غیرمستمر به تفکیک حوزه":{"en":"Non-routine activities by category","fr":"Activités non routinières par catégorie","es":"Actividades no rutinarias por categoría","ar":"الأنشطة غير الروتينية حسب الفئة"},"معیارهای ارزیابی وظایف مستمر":{"en":"Routine evaluation criteria","fr":"Critères d’évaluation de routine","es":"Criterios de evaluación rutinaria","ar":"معايير التقييم الروتيني"},"جزئیات ارزیابی وظایف مستمر برای این دوره ثبت نشده است":{"en":"Routine evaluation details have not been recorded for this period","fr":"Les détails de l’évaluation de routine n’ont pas été enregistrés pour cette période","es":"No se han registrado detalles de evaluación rutinaria para este período","ar":"لم يتم تسجيل تفاصيل التقييم الروتيني لهذه الفترة"},"جمع‌بندی این دوره":{"en":"Period summary","fr":"Résumé de la période","es":"Resumen del período","ar":"ملخص الفترة"},"نقطه قوت":{"en":"Strength","fr":"Point fort","es":"Fortaleza","ar":"نقطة قوة"},"فعالیت‌های ثبت‌شده دوره":{"en":"Activities recorded in this period","fr":"Activités enregistrées pour cette période","es":"Actividades registradas en este período","ar":"الأنشطة المسجلة في هذه الفترة"},"جزئیات فعالیت‌های این دوره در دسترس نیست":{"en":"Activity details for this period are not available","fr":"Les détails des activités de cette période ne sont pas disponibles","es":"Los detalles de las actividades de este período no están disponibles","ar":"تفاصيل أنشطة هذه الفترة غير متاحة"},"روند CVI — دوره‌های اخیر":{"en":"CVI trend — recent periods","fr":"Tendance du CVI — périodes récentes","es":"Tendencia del CVI — períodos recientes","ar":"اتجاه CVI — الفترات الأخيرة"},"جزئیات هر دوره":{"en":"Period details","fr":"Détails de la période","es":"Detalles del período","ar":"تفاصيل الفترة"},"جاری":{"en":"Current","fr":"En cours","es":"Actual","ar":"الحالية"},"امتیاز فعالیت‌های غیرمستمر از مجموع فعالیت‌های تأییدشده‌ی خودثبتی، به تفکیک گروه، تشکیل می‌شود. روی هر گروه بزنید تا فعالیت‌های آن را ببینید.":{"en":"The non-routine score is calculated from your approved self-reported activities by category. Select a category to view its activities.","fr":"Le score non routinier est calculé à partir de vos activités autodéclarées approuvées, par catégorie. Sélectionnez une catégorie pour voir ses activités.","es":"La puntuación no rutinaria se calcula a partir de tus actividades autodeclaradas aprobadas por categoría. Selecciona una categoría para ver sus actividades.","ar":"تُحسب درجة الأنشطة غير الروتينية من أنشطتك المعتمدة والمسجلة ذاتياً حسب الفئة. اختر فئة لعرض أنشطتها."},"فعالیت تأییدشده‌ای در این گروه ثبت نشده":{"en":"No approved activities have been recorded in this group","fr":"Aucune activité approuvée n’a été enregistrée dans ce groupe","es":"No se han registrado actividades aprobadas en este grupo","ar":"لم يتم تسجيل أنشطة معتمدة في هذه المجموعة"},"هنوز سؤال وظایف مستمری برای شما تعریف نشده است.":{"en":"No routine questions have been defined for you yet.","fr":"Aucune question de routine n’a encore été définie pour vous.","es":"Aún no se han definido preguntas rutinarias para ti.","ar":"لم يتم تحديد أسئلة روتينية لك بعد."},"شاخصی برای این سطح تعریف نشده است.":{"en":"No indicators have been defined for this level.","fr":"Aucun indicateur n’a été défini pour ce niveau.","es":"No se han definido indicadores para este nivel.","ar":"لم يتم تحديد مؤشرات لهذا المستوى."},"مشاهده نمودار روند دوره‌ها":{"en":"📈 View period trend chart ←","fr":"📈 Voir le graphique de tendance des périodes ←","es":"📈 Ver gráfico de tendencia de períodos ←","ar":"📈 عرض مخطط اتجاه الفترات ←"},"فعالیت‌های اخیر":{"en":"Recent activities","fr":"Activités récentes","es":"Actividades recientes","ar":"الأنشطة الأخيرة"},"مشاهده همه":{"en":"View all","fr":"Voir tout","es":"Ver todo","ar":"عرض الكل"},"هنوز فعالیتی ثبت نکرده‌اید":{"en":"You have not recorded any activities yet","fr":"Vous n’avez encore enregistré aucune activité","es":"Aún no has registrado actividades","ar":"لم تسجل أي أنشطة بعد"},"وضعیت":{"en":"Status","fr":"État","es":"Estado","ar":"الحالة"},"ثبت فعالیت جدید":{"en":"+ Add new activity","fr":"+ Ajouter une activité","es":"+ Registrar nueva actividad","ar":"+ إضافة نشاط جديد"},"فعالیت‌های من":{"en":"My activities","fr":"Mes activités","es":"Mis actividades","ar":"أنشطتي"},"جدید":{"en":"+ New","fr":"+ Nouveau","es":"+ Nuevo","ar":"+ جديد"},"تاریخچه دوره‌ها":{"en":"Period history","fr":"Historique des périodes","es":"Historial de períodos","ar":"سجل الفترات"},"نمودار رشد CVI":{"en":"CVI growth chart","fr":"Graphique d’évolution du CVI","es":"Gráfico de evolución del CVI","ar":"مخطط نمو CVI"},"حذف عکس":{"en":"Remove photo","fr":"Supprimer la photo","es":"Eliminar foto","ar":"إزالة الصورة"},"تغییر رمز عبور":{"en":"Change password","fr":"Changer le mot de passe","es":"Cambiar contraseña","ar":"تغيير كلمة المرور"},"رمز فعلی":{"en":"Current password","fr":"Mot de passe actuel","es":"Contraseña actual","ar":"كلمة المرور الحالية"},"رمز جدید":{"en":"New password","fr":"Nouveau mot de passe","es":"Nueva contraseña","ar":"كلمة المرور الجديدة"},"تکرار رمز جدید":{"en":"Confirm new password","fr":"Confirmer le nouveau mot de passe","es":"Confirmar nueva contraseña","ar":"تأكيد كلمة المرور الجديدة"},"رمز عبور با موفقیت تغییر کرد":{"en":"✅ Password changed successfully","fr":"✅ Mot de passe modifié avec succès","es":"✅ Contraseña cambiada correctamente","ar":"✅ تم تغيير كلمة المرور بنجاح"},"ذخیره‌ی رمز جدید":{"en":"Save new password","fr":"Enregistrer le nouveau mot de passe","es":"Guardar nueva contraseña","ar":"حفظ كلمة المرور الجديدة"},"ثبت فعالیت غیرفعال است":{"en":"Activity submission is disabled","fr":"La saisie d’activité est désactivée","es":"El registro de actividades está deshabilitado","ar":"إدخال الأنشطة معطل"},"دوره فعالی وجود ندارد؛ فعالیت جدید فقط در دوره فعال ثبت می‌شود.":{"en":"There is no active period; new activities can only be recorded during an active period.","fr":"Aucune période n’est active ; les nouvelles activités ne peuvent être enregistrées que pendant une période active.","es":"No hay un período activo; las nuevas actividades solo pueden registrarse durante un período activo.","ar":"لا توجد فترة نشطة؛ لا يمكن تسجيل أنشطة جديدة إلا خلال فترة نشطة."},"بازگشت":{"en":"Back","fr":"Retour","es":"Volver","ar":"رجوع"},"میانگین CVI تیم":{"en":"Team average CVI","fr":"CVI moyen de l’équipe","es":"CVI promedio del equipo","ar":"متوسط CVI للفريق"},"همه بررسی شده‌اند":{"en":"✅ All reviewed","fr":"✅ Tout a été examiné","es":"✅ Todo revisado","ar":"✅ تمت مراجعة الكل"},"تحقق اهداف تیم":{"en":"Team goal attainment","fr":"Atteinte des objectifs de l’équipe","es":"Cumplimiento de objetivos del equipo","ar":"تحقيق أهداف الفريق"},"شاخصی برای این تیم تعریف نشده":{"en":"No indicators have been defined for this team","fr":"Aucun indicateur n’a été défini pour cette équipe","es":"No se han definido indicadores para este equipo","ar":"لم يتم تحديد مؤشرات لهذا الفريق"},"تاریخچه تغییرات":{"en":"Timeline and audit log","fr":"Chronologie et journal d’audit","es":"Cronología y registro de auditoría","ar":"الخط الزمني وسجل التدقيق"},"هنوز رویدادی ثبت نشده است.":{"en":"No events have been recorded yet.","fr":"Aucun événement n’a encore été enregistré.","es":"Aún no se han registrado eventos.","ar":"لم يتم تسجيل أحداث بعد."},"بازگشت":{"en":"→ Back","fr":"→ Retour","es":"→ Volver","ar":"→ رجوع"},"هدف دوره:":{"en":"Period target:","fr":"Cible de la période :","es":"Objetivo del período:","ar":"هدف الفترة:"},"قفل خودکار":{"en":"Automatic lock","fr":"Verrouillage automatique","es":"Bloqueo automático","ar":"قفل تلقائي"},"پنجره ثبت هنوز باز نشده است.":{"en":"The submission window is not open yet.","fr":"La fenêtre de saisie n’est pas encore ouverte.","es":"La ventana de registro aún no está abierta.","ar":"نافذة التسجيل لم تُفتح بعد."},"این نوبت به‌صورت خودکار قفل شده است.":{"en":"This occurrence was locked automatically.","fr":"Cette occurrence a été verrouillée automatiquement.","es":"Esta ocurrencia se bloqueó automáticamente.","ar":"تم قفل هذه الفترة تلقائياً."},"مقدار واقعی *":{"en":"Actual value *","fr":"Valeur réelle *","es":"Valor real *","ar":"القيمة الفعلية *"},"توضیح یا منبع داده":{"en":"Description or data source","fr":"Description ou source de données","es":"Descripción o fuente de datos","ar":"الوصف أو مصدر البيانات"},"تحقق کمتر از ۸۰٪ است؛ علت انحراف و اقدام اصلاحی الزامی است.":{"en":"Achievement is below 80%; a reason for variance and corrective action are required.","fr":"L’atteinte est inférieure à 80 % ; la raison de l’écart et une action corrective sont obligatoires.","es":"El cumplimiento es inferior al 80 %; se requieren el motivo de la desviación y una acción correctiva.","ar":"نسبة التحقيق أقل من 80٪؛ يجب إدخال سبب الانحراف والإجراء التصحيحي."},"مستند برای ارسال الزامی است.":{"en":"A supporting document is required for submission.","fr":"Un document justificatif est requis pour la soumission.","es":"Se requiere un documento de respaldo para enviar.","ar":"مستند داعم مطلوب للإرسال."},"ذخیره پیش‌نویس":{"en":"Save draft","fr":"Enregistrer le brouillon","es":"Guardar borrador","ar":"حفظ المسودة"},"ارسال برای تأیید":{"en":"Submit for approval","fr":"Soumettre pour approbation","es":"Enviar para aprobación","ar":"إرسال للاعتماد"},"شاخص‌های مسئولیت من":{"en":"My assigned indicators","fr":"Mes indicateurs attribués","es":"Mis indicadores asignados","ar":"المؤشرات المسندة إليّ"},"مواردی که در آن مالک، ثبت‌کننده یا تأییدکننده هستید.":{"en":"Indicators where you are the owner, submitter, or approver.","fr":"Indicateurs pour lesquels vous êtes responsable, responsable de saisie ou approbateur.","es":"Indicadores en los que eres responsable, registrador o aprobador.","ar":"المؤشرات التي تكون فيها مالكاً أو مسجلاً أو معتمداً."},"دوره فعالی وجود ندارد.":{"en":"There is no active period.","fr":"Aucune période n’est active.","es":"No hay un período activo.","ar":"لا توجد فترة نشطة."},"در این بخش شاخصی برای شما وجود ندارد.":{"en":"There are no indicators for you in this section.","fr":"Aucun indicateur ne vous est attribué dans cette section.","es":"No hay indicadores para ti en esta sección.","ar":"لا توجد مؤشرات لك في هذا القسم."},"تأیید":{"en":"Approve","fr":"Approuver","es":"Aprobar","ar":"اعتماد"},"درخواست اصلاح":{"en":"Request revision","fr":"Demander une révision","es":"Solicitar revisión","ar":"طلب تعديل"},"هیچ شاخصی در انتظار تأیید شما نیست.":{"en":"No indicators are awaiting your approval.","fr":"Aucun indicateur n’attend votre approbation.","es":"No hay indicadores pendientes de tu aprobación.","ar":"لا توجد مؤشرات بانتظار اعتمادك."},"در انتظار تأیید":{"en":"Pending approval","fr":"En attente d’approbation","es":"Pendiente de aprobación","ar":"بانتظار الاعتماد"},"مواردی که برای تصمیم‌گیری شما ارسال شده‌اند.":{"en":"Items submitted for your decision.","fr":"Éléments soumis à votre décision.","es":"Elementos enviados para tu decisión.","ar":"العناصر المرسلة لاتخاذ قرارك."},"تصمیم مدیر":{"en":"Manager decision","fr":"Décision du gestionnaire","es":"Decisión del gerente","ar":"قرار المدير"},"سطح تأثیر *":{"en":"Impact level *","fr":"Niveau d’impact *","es":"Nivel de impacto *","ar":"مستوى التأثير *"},"یادداشت (برای درخواست اصلاح الزامی)":{"en":"Note (required when requesting revision)","fr":"Note (obligatoire pour demander une révision)","es":"Nota (obligatoria para solicitar revisión)","ar":"ملاحظة (مطلوبة عند طلب التعديل)"},"✓ تأیید":{"en":"✓ Approve","fr":"✓ Approuver","es":"✓ Aprobar","ar":"✓ اعتماد"},"اصلاح":{"en":"Revise","fr":"Réviser","es":"Revisar","ar":"تعديل"},"رد":{"en":"Reject","fr":"Rejeter","es":"Rechazar","ar":"رفض"},"همه بررسی شدند":{"en":"All reviewed","fr":"Tout a été examiné","es":"Todo revisado","ar":"تمت مراجعة الكل"},"هیچ فعالیتی در انتظار بررسی نیست":{"en":"No activities are awaiting review","fr":"Aucune activité n’attend d’être examinée","es":"No hay actividades pendientes de revisión","ar":"لا توجد أنشطة بانتظار المراجعة"},"تحقق شاخص‌های تیم":{"en":"Team indicator attainment","fr":"Atteinte des indicateurs de l’équipe","es":"Cumplimiento de indicadores del equipo","ar":"تحقيق مؤشرات الفريق"},"روند درآمد تیم (نمایشی)":{"en":"Team revenue trend (sample)","fr":"Tendance du revenu de l’équipe (exemple)","es":"Tendencia de ingresos del equipo (ejemplo)","ar":"اتجاه إيرادات الفريق (مثال)"},"سؤال وظایف مستمری برای این فرد تعریف نشده است.":{"en":"No routine questions have been defined for this person.","fr":"Aucune question de routine n’a été définie pour cette personne.","es":"No se han definido preguntas rutinarias para esta persona.","ar":"لم يتم تحديد أسئلة روتينية لهذا الشخص."},"ترکیب عملکرد فردی":{"en":"Individual score composition","fr":"Composition du score individuel","es":"Composición de la puntuación individual","ar":"مكونات الدرجة الفردية"},"جزئیات سؤالات وظایف مستمر":{"en":"Routine question details","fr":"Détails des questions de routine","es":"Detalles de preguntas rutinarias","ar":"تفاصيل الأسئلة الروتينية"},"امتیاز گروه‌ها":{"en":"Category scores","fr":"Scores par catégorie","es":"Puntuaciones por categoría","ar":"درجات الفئات"},"فعالیت‌های ثبت‌شده":{"en":"Recorded activities","fr":"Activités enregistrées","es":"Actividades registradas","ar":"الأنشطة المسجلة"},"هنوز فعالیتی ثبت نشده":{"en":"No activities have been recorded yet","fr":"Aucune activité n’a encore été enregistrée","es":"Aún no se han registrado actividades","ar":"لم يتم تسجيل أنشطة بعد"},"همه معاونت‌ها":{"en":"All divisions","fr":"Toutes les directions","es":"Todas las divisiones","ar":"كل الإدارات"},"همه تیم‌ها":{"en":"All teams","fr":"Toutes les équipes","es":"Todos los equipos","ar":"كل الفرق"},"میانگین CVI معاونت":{"en":"Division average CVI","fr":"CVI moyen de la direction","es":"CVI promedio de la división","ar":"متوسط CVI للإدارة"},"شاخص‌های معاونت":{"en":"Division indicators","fr":"Indicateurs de la direction","es":"Indicadores de la división","ar":"مؤشرات الإدارة"},"شاخصی در سطح معاونت تعریف نشده است":{"en":"No division-level indicators have been defined","fr":"Aucun indicateur au niveau direction n’a été défini","es":"No se han definido indicadores a nivel de división","ar":"لم يتم تحديد مؤشرات على مستوى الإدارة"},"مقایسه‌ی تیم‌ها":{"en":"Team comparison","fr":"Comparaison des équipes","es":"Comparación de equipos","ar":"مقارنة الفرق"},"تیمی برای این معاونت تعریف نشده است":{"en":"No teams have been defined for this division","fr":"Aucune équipe n’a été définie pour cette direction","es":"No se han definido equipos para esta división","ar":"لم يتم تحديد فرق لهذه الإدارة"},"میانگین CVI سازمان":{"en":"Organization average CVI","fr":"CVI moyen de l’organisation","es":"CVI promedio de la organización","ar":"متوسط CVI للمؤسسة"},"حداکثر CVI سازمان":{"en":"Highest organization CVI","fr":"CVI maximal de l’organisation","es":"CVI máximo de la organización","ar":"أعلى CVI في المؤسسة"},"تکمیل ارزیابی‌های وظایف مستمر":{"en":"Routine evaluation completion","fr":"Achèvement des évaluations de routine","es":"Finalización de evaluaciones rutinarias","ar":"اكتمال التقييمات الروتينية"},"منتظر تصمیم مدیرعامل":{"en":"Awaiting CEO decision","fr":"En attente de la décision du PDG","es":"A la espera de la decisión del CEO","ar":"بانتظار قرار الرئيس التنفيذي"},"شاخص سازمانی بحرانی":{"en":"Critical organization indicator","fr":"Indicateur critique de l’organisation","es":"Indicador crítico de la organización","ar":"مؤشر مؤسسي حرج"},"توزیع عملکرد کارکنان":{"en":"Employee performance distribution","fr":"Répartition de la performance des employés","es":"Distribución del desempeño de empleados","ar":"توزيع أداء الموظفين"},"شاخصی در سطح سازمان تعریف نشده است":{"en":"No organization-level indicators have been defined","fr":"Aucun indicateur au niveau organisation n’a été défini","es":"No se han definido indicadores a nivel de organización","ar":"لم يتم تحديد مؤشرات على مستوى المؤسسة"},"مقایسه‌ی معاونت‌ها":{"en":"Division comparison","fr":"Comparaison des directions","es":"Comparación de divisiones","ar":"مقارنة الإدارات"},"معاونتی تعریف نشده است":{"en":"No divisions have been defined","fr":"Aucune direction n’a été définie","es":"No se han definido divisiones","ar":"لم يتم تحديد إدارات"},"تیمی تعریف نشده است":{"en":"No teams have been defined","fr":"Aucune équipe n’a été définie","es":"No se han definido equipos","ar":"لم يتم تحديد فرق"},"در حال بارگذاری...":{"en":"Loading routine evaluations from the server...","fr":"Chargement des évaluations de routine depuis le serveur...","es":"Cargando evaluaciones rutinarias desde el servidor...","ar":"جارٍ تحميل التقييمات الروتينية من الخادم..."},"دوره فعالی وجود ندارد؛ ارزیابی‌های دوره‌های بسته فقط قابل مشاهده‌اند.":{"en":"There is no active period; evaluations from closed periods are view-only.","fr":"Aucune période n’est active ; les évaluations des périodes clôturées sont en lecture seule.","es":"No hay un período activo; las evaluaciones de períodos cerrados son de solo lectura.","ar":"لا توجد فترة نشطة؛ تقييمات الفترات المغلقة للعرض فقط."},"امتیاز وظایف مستمر فصل جاری":{"en":"Current-quarter routine score","fr":"Score de routine du trimestre en cours","es":"Puntuación rutinaria del trimestre actual","ar":"درجة الروتين للربع الحالي"},"سؤالی برای این ماه تعریف نشده است.":{"en":"No questions have been defined for this month.","fr":"Aucune question n’a été définie pour ce mois.","es":"No se han definido preguntas para este mes.","ar":"لم يتم تحديد أسئلة لهذا الشهر."},"هنوز پاسخ داده نشده":{"en":"Not answered yet","fr":"Pas encore répondu","es":"Aún sin respuesta","ar":"لم تتم الإجابة بعد"},"(باید توسط ادمین به ۱۰۰٪ برسد)":{"en":"(Admin must set this to 100%)","fr":"(L’administrateur doit régler ce total à 100 %)","es":"(El administrador debe llevar este total al 100 %)","ar":"(يجب على المسؤول ضبط المجموع إلى 100٪)"},"مشاهده داشبورد معاونت ←":{"en":"View division dashboard ←","fr":"Voir le tableau de bord de la direction ←","es":"Ver panel de la división ←","ar":"عرض لوحة تحكم الإدارة ←"},"تاریخچه عملکرد شرکت":{"en":"Company performance history","fr":"Historique de la performance de l’entreprise","es":"Historial de desempeño de la empresa","ar":"سجل أداء الشركة"},"روند میانگین CVI شرکت":{"en":"Company average CVI trend","fr":"Tendance du CVI moyen de l’entreprise","es":"Tendencia del CVI promedio de la empresa","ar":"اتجاه متوسط CVI للشركة"},"عملیات":{"en":"Actions","fr":"Actions","es":"Acciones","ar":"الإجراءات"},"هنوز موردی ثبت نشده":{"en":"No items have been recorded yet","fr":"Aucun élément n’a encore été enregistré","es":"Aún no se han registrado elementos","ar":"لم يتم تسجيل عناصر بعد"},"افزودن مورد جدید":{"en":"Add new item","fr":"Ajouter un élément","es":"Agregar nuevo elemento","ar":"إضافة عنصر جديد"},"افزودن":{"en":"+ Add","fr":"+ Ajouter","es":"+ Agregar","ar":"+ إضافة"},"بررسی وضعیت راه‌اندازی":{"en":"Review setup status","fr":"Vérifier l’état de configuration","es":"Revisar estado de configuración","ar":"مراجعة حالة الإعداد"},"برو":{"en":"Go ←","fr":"Aller ←","es":"Ir ←","ar":"انتقل ←"},"اول معاونت‌ها را بسازید، بعد تیم‌های هر معاونت را اضافه کنید. مسئولِ هر معاونت یا تیم نیازی به انتخاب دستی ندارد — وقتی از تب «افراد» به کسی نقش «معاون» یا «مدیر» بدهید و معاونت/تیمش را مشخص کنید، همین‌جا به‌صورت خودکار نمایش داده می‌شود.":{"en":"Create divisions first, then add the teams in each division. Division heads and team managers do not need to be selected manually — when you assign someone the Division Head or Manager role in the People tab and set their division/team, they will appear here automatically.","fr":"Créez d’abord les directions, puis ajoutez les équipes de chaque direction. Les responsables de direction et les gestionnaires d’équipe n’ont pas besoin d’être sélectionnés manuellement — lorsque vous attribuez à une personne le rôle de responsable de direction ou de gestionnaire dans l’onglet Personnes et définissez sa direction/équipe, elle apparaîtra ici automatiquement.","es":"Crea primero las divisiones y luego añade los equipos de cada división. No es necesario seleccionar manualmente a los responsables de división o gerentes de equipo: cuando asignes a alguien el rol de Responsable de división o Gerente en la pestaña Personas y definas su división/equipo, aparecerá aquí automáticamente.","ar":"أنشئ الإدارات أولاً، ثم أضف فرق كل إدارة. لا يلزم اختيار رؤساء الإدارات أو مديري الفرق يدوياً — عند تعيين دور رئيس إدارة أو مدير لشخص من تبويب الأفراد وتحديد إدارته/فريقه، سيظهر هنا تلقائياً."},"برای «معاون» فقط معاونت انتخاب می‌شود. برای «مدیر» و «کارشناس»، ابتدا معاونت را انتخاب کنید تا لیست تیم‌ها بر اساس همان معاونت فیلتر شود، سپس تیم را مشخص کنید.":{"en":"For a Division Head, only the division is selected. For a Manager or Specialist, first select the division to filter the team list, then select the team.","fr":"Pour un responsable de direction, seule la direction est sélectionnée. Pour un gestionnaire ou un spécialiste, sélectionnez d’abord la direction afin de filtrer la liste des équipes, puis sélectionnez l’équipe.","es":"Para un Responsable de división, solo se selecciona la división. Para un Gerente o Especialista, selecciona primero la división para filtrar la lista de equipos y luego selecciona el equipo.","ar":"بالنسبة لرئيس الإدارة، تُحدد الإدارة فقط. أما المدير أو الأخصائي، فاختر الإدارة أولاً لتصفية قائمة الفرق ثم اختر الفريق."},"لوگوی سازمان":{"en":"Organization logo","fr":"Logo de l’organisation","es":"Logotipo de la organización","ar":"شعار المؤسسة"},"این لوگو در نوار بالای همه‌ی پنل‌ها نمایش داده می‌شود.":{"en":"This logo appears in the top bar of all dashboards. ⚠️ Note: because this version has no server-side storage for the logo, it remains only for the current session and is cleared when the page reloads.","fr":"Ce logo apparaît dans la barre supérieure de tous les tableaux de bord. ⚠️ Remarque : comme cette version ne stocke pas le logo côté serveur, il reste uniquement pendant la session en cours et disparaît au rechargement de la page.","es":"Este logotipo aparece en la barra superior de todos los paneles. ⚠️ Nota: como esta versión no guarda el logotipo en el servidor, solo permanece durante la sesión actual y se elimina al recargar la página.","ar":"يظهر هذا الشعار في الشريط العلوي لجميع لوحات التحكم. ⚠️ ملاحظة: لأن هذه النسخة لا تحفظ الشعار على الخادم، فإنه يبقى خلال الجلسة الحالية فقط ويُمسح عند إعادة تحميل الصفحة."},"حذف لوگو":{"en":"Remove logo","fr":"Supprimer le logo","es":"Eliminar logotipo","ar":"إزالة الشعار"},"برای هر نقش، وزن مشارکت جمعی و مؤلفه‌های عملکرد فردی را جداگانه تعیین کنید. جمع هر بخش باید دقیقاً ۱۰۰٪ باشد.":{"en":"Set strategic-indicator and individual-performance weights separately for each role. Each section must total exactly 100%.","fr":"Définissez séparément les pondérations des indicateurs stratégiques et des composantes de performance individuelle pour chaque rôle. Chaque section doit totaliser exactement 100 %.","es":"Define por separado las ponderaciones de indicadores estratégicos y componentes de desempeño individual para cada rol. Cada sección debe sumar exactamente 100 %.","ar":"حدد أوزان المؤشرات الاستراتيجية ومكونات الأداء الفردي بشكل منفصل لكل دور. يجب أن يساوي مجموع كل قسم 100٪ بالضبط."},"سمت شغلی جدید":{"en":"+ New role","fr":"+ Nouveau rôle","es":"+ Nuevo rol","ar":"+ دور جديد"},"حذف نقش":{"en":"Delete role","fr":"Supprimer le rôle","es":"Eliminar rol","ar":"حذف الدور"},"نام سمت شغلی":{"en":"Role name","fr":"Nom du rôle","es":"Nombre del rol","ar":"اسم الدور"},"وزن مشارکت جمعی":{"en":"Strategic indicator weights","fr":"Pondération des indicateurs stratégiques","es":"Ponderaciones de indicadores estratégicos","ar":"أوزان المؤشرات الاستراتيجية"},"ترکیب سطوح تیم، معاونت و سازمان":{"en":"Combination of team, division, and organization levels","fr":"Combinaison des niveaux équipe, direction et organisation","es":"Combinación de niveles de equipo, división y organización","ar":"مزيج مستويات الفريق والإدارة والمؤسسة"},"وزن عملکرد فردی":{"en":"Individual contribution weights","fr":"Pondération de la performance individuelle","es":"Ponderaciones de desempeño individual","ar":"أوزان الأداء الفردي"},"ترکیب سه مؤلفه عملکرد فردی":{"en":"Combination of the three individual evaluation components","fr":"Combinaison des trois composantes de l’évaluation individuelle","es":"Combinación de los tres componentes de evaluación individual","ar":"مزيج مكونات التقييم الفردي الثلاثة"},"ذخیره تنظیمات سمت شغلی":{"en":"Save role settings","fr":"Enregistrer les paramètres du rôle","es":"Guardar configuración del rol","ar":"حفظ إعدادات الدور"},"ذخیره پس از تکمیل نام سمت شغلی و رسیدن جمع هر دو بخش به ۱۰۰٪ فعال می‌شود.":{"en":"Saving is enabled after the role name is completed and both sections total 100%.","fr":"L’enregistrement est activé lorsque le nom du rôle est renseigné et que les deux sections totalisent 100 %.","es":"El guardado se habilita cuando se completa el nombre del rol y ambas secciones suman 100 %.","ar":"يتم تفعيل الحفظ بعد إدخال اسم الدور ووصول مجموع كل قسم إلى 100٪."},"سطوح کیفیِ ارزیابی وظایف مستمر":{"en":"Routine evaluation rating levels","fr":"Niveaux qualitatifs de l’évaluation de routine","es":"Niveles cualitativos de evaluación rutinaria","ar":"مستويات التقييم الروتيني"},"مدیر برای پاسخ به هر سؤال وظایف مستمر یکی از این سطوح را انتخاب می‌کند (حداکثر تا ۵ سطح توصیه می‌شود).":{"en":"The manager selects one of these levels for each routine question (up to 5 levels are recommended).","fr":"Le gestionnaire sélectionne l’un de ces niveaux pour chaque question de routine (un maximum de 5 niveaux est recommandé).","es":"El gerente selecciona uno de estos niveles para cada pregunta rutinaria (se recomiendan hasta 5 niveles).","ar":"يختار المدير أحد هذه المستويات لكل سؤال روتيني (يوصى بحد أقصى 5 مستويات)."},"شاخص‌های سازمانی — تعریف":{"en":"Organization indicators — definition","fr":"Indicateurs de l’organisation — définition","es":"Indicadores de la organización — definición","ar":"مؤشرات المؤسسة — التعريف"},"ادمین ساختار، مسئولیت‌ها و زمان‌بندی شاخص را تعریف می‌کند. مقدار واقعی فقط توسط ثبت‌کننده تعیین‌شده وارد و پس از تأیید رسمی وارد محاسبات می‌شود.":{"en":"The admin defines each indicator’s structure, responsibilities, and schedule. Actual values are entered only by the designated submitter and included in calculations after formal approval.","fr":"L’administrateur définit la structure, les responsabilités et le calendrier de chaque indicateur. Les valeurs réelles sont saisies uniquement par le responsable désigné et intégrées aux calculs après approbation formelle.","es":"El administrador define la estructura, responsabilidades y programación de cada indicador. Los valores reales solo los registra el responsable designado y se incluyen en los cálculos tras la aprobación formal.","ar":"يحدد المسؤول هيكل كل مؤشر ومسؤولياته وجدوله الزمني. تُدخل القيم الفعلية فقط بواسطة المسجل المحدد وتدخل في الحسابات بعد الاعتماد الرسمي."},"تقویم و الگوهای زمان‌بندی شاخص‌ها":{"en":"Indicator calendar and schedule templates","fr":"Calendrier des indicateurs et modèles de planification","es":"Calendario de indicadores y plantillas de programación","ar":"تقويم المؤشرات وقوالب الجدولة"},"تاریخ‌های هر نوبت از پایان بازه به‌صورت خودکار محاسبه می‌شوند. تغییر یک الگو روی دوره‌های آینده اثر دارد؛ تاریخ‌های ذخیره‌شده در رکوردهای قبلی ثابت می‌مانند.":{"en":"Dates for each occurrence are calculated automatically from the end of its interval. Changes to a template affect future periods; dates already stored in previous records remain unchanged.","fr":"Les dates de chaque occurrence sont calculées automatiquement à partir de la fin de son intervalle. Les modifications d’un modèle affectent les périodes futures ; les dates déjà enregistrées restent inchangées.","es":"Las fechas de cada ocurrencia se calculan automáticamente desde el final de su intervalo. Los cambios en una plantilla afectan a períodos futuros; las fechas ya guardadas permanecen sin cambios.","ar":"تُحسب تواريخ كل فترة تلقائياً من نهاية نطاقها. تؤثر تغييرات القالب على الفترات المستقبلية؛ أما التواريخ المحفوظة سابقاً فتبقى ثابتة."},"استثنای یک شاخص در یک دوره":{"en":"Indicator exception for a period","fr":"Exception d’indicateur pour une période","es":"Excepción de indicador para un período","ar":"استثناء مؤشر لفترة"},"انتخاب شاخص":{"en":"Select indicator","fr":"Sélectionner l’indicateur","es":"Seleccionar indicador","ar":"اختر المؤشر"},"انتخاب دوره":{"en":"Select period","fr":"Sélectionner la période","es":"Seleccionar período","ar":"اختر الفترة"},"ثبت استثنا":{"en":"Save exception","fr":"Enregistrer l’exception","es":"Guardar excepción","ar":"حفظ الاستثناء"},"این سؤالات مربوط به عملکرد وظایف مستمر و تکراری افراد است که توسط سرپرست مستقیم — نه خودِ فرد — در پایان هر ماه پاسخ داده می‌شود. در هر سطح، ۹ سؤال (۳ سؤال × ۳ ماه) با جمع وزن دقیقاً ۱۰۰٪ تعریف می‌شود.":{"en":"These questions assess recurring routine performance and are answered by the direct supervisor — not the employee — at the end of each month. At each level, 9 questions (3 questions × 3 months) are defined with weights totaling exactly 100%.","fr":"Ces questions évaluent la performance routinière et récurrente et sont répondues par le supérieur direct — et non par l’employé — à la fin de chaque mois. À chaque niveau, 9 questions (3 questions × 3 mois) sont définies avec une pondération totale de 100 %.","es":"Estas preguntas evalúan el desempeño rutinario y recurrente y las responde el supervisor directo — no el empleado — al final de cada mes. En cada nivel se definen 9 preguntas (3 preguntas × 3 meses) con una ponderación total exacta de 100 %.","ar":"تقيّم هذه الأسئلة الأداء الروتيني والمتكرر ويجيب عنها المشرف المباشر — وليس الموظف نفسه — في نهاية كل شهر. في كل مستوى تُحدد 9 أسئلة (3 أسئلة × 3 أشهر) بمجموع أوزان يساوي 100٪ بالضبط."},"دوره":{"en":"Period","fr":"Période","es":"Período","ar":"الفترة"},"فعال‌کردن این دوره به‌عنوان دوره جاری":{"en":"Set this as the current period","fr":"Définir comme période en cours","es":"Establecer como período actual","ar":"تعيين هذه الفترة كفترة حالية"},"بستن، محاسبه نهایی و قفل دوره":{"en":"Close, finalize calculations, and lock period","fr":"Clôturer, finaliser les calculs et verrouiller la période","es":"Cerrar, finalizar cálculos y bloquear el período","ar":"إغلاق الفترة وإنهاء الحسابات وقفلها"},"بازگشایی کنترل‌شده برای اصلاح":{"en":"Controlled reopening for corrections","fr":"Réouverture contrôlée pour corrections","es":"Reapertura controlada para correcciones","ar":"إعادة فتح مضبوطة للتصحيح"},"اطلاعات این دوره فقط‌خواندنی است. بازگشایی فقط با ثبت دلیل انجام می‌شود و Snapshot قبلی تا بستن مجدد محفوظ می‌ماند.":{"en":"This period is read-only. Reopening requires a recorded reason, and the previous snapshot is retained until the period is closed again.","fr":"Cette période est en lecture seule. La réouverture nécessite un motif enregistré et le snapshot précédent est conservé jusqu’à la prochaine clôture.","es":"Este período es de solo lectura. La reapertura requiere un motivo registrado y el snapshot anterior se conserva hasta volver a cerrar el período.","ar":"هذه الفترة للقراءة فقط. تتطلب إعادة الفتح تسجيل سبب، وتبقى اللقطة السابقة محفوظة حتى إغلاق الفترة مرة أخرى."},"برچسب دوره جدید":{"en":"New period label","fr":"Libellé de la nouvelle période","es":"Etiqueta del nuevo período","ar":"تسمية الفترة الجديدة"},"شروع":{"en":"Start","fr":"Début","es":"Inicio","ar":"البداية"},"پایان":{"en":"End","fr":"Fin","es":"Fin","ar":"النهاية"},"دوره جدید":{"en":"+ New period","fr":"+ Nouvelle période","es":"+ Nuevo período","ar":"+ فترة جديدة"},"هنوز شاخصی تعریف نشده — اول از تب «شاخص‌ها» چند شاخص بسازید.":{"en":"No indicators have been defined yet — create a few from the Indicators tab first.","fr":"Aucun indicateur n’a encore été défini — créez-en d’abord dans l’onglet Indicateurs.","es":"Aún no se han definido indicadores — crea algunos primero desde la pestaña Indicadores.","ar":"لم يتم تحديد مؤشرات بعد — أنشئ بعض المؤشرات أولاً من تبويب المؤشرات."},"هیچ دوره‌ای ثبت نشده — از فرم بالا یک دوره بسازید.":{"en":"No periods have been created — use the form above to create one.","fr":"Aucune période n’a été créée — utilisez le formulaire ci-dessus pour en créer une.","es":"No se han creado períodos — usa el formulario de arriba para crear uno.","ar":"لم يتم إنشاء فترات — استخدم النموذج أعلاه لإنشاء فترة."},"تاریخچه تغییرات دوره":{"en":"Period audit log","fr":"Journal d’audit de la période","es":"Registro de auditoría del período","ar":"سجل تدقيق الفترة"},"سؤال‌های مشترک، ارزیاب‌های هر فرد و سطح محرمانگی را مدیریت کنید.":{"en":"Manage shared questions, each person’s reviewers, and confidentiality level.","fr":"Gérez les questions communes, les évaluateurs de chaque personne et le niveau de confidentialité.","es":"Gestiona las preguntas comunes, los evaluadores de cada persona y el nivel de confidencialidad.","ar":"أدر الأسئلة المشتركة ومقيّمي كل شخص ومستوى السرية."},"تخصیص خودکار گروهی":{"en":"Bulk automatic assignment","fr":"Affectation automatique en lot","es":"Asignación automática masiva","ar":"تعيين تلقائي جماعي"},"سیستم ابتدا همکاران هم‌سطح همان تیم، سپس همان معاونت و در نهایت سایر همکاران هم‌سطح را انتخاب می‌کند. مدیر مستقیم همیشه حذف می‌شود و بار ارزیابی بین افراد متعادل خواهد شد.":{"en":"The system first selects same-level colleagues from the same team, then from the same division, and finally other same-level colleagues. Direct managers are always excluded, and review workload is balanced across people.","fr":"Le système sélectionne d’abord les collègues de même niveau dans la même équipe, puis dans la même direction, et enfin les autres collègues de même niveau. Le gestionnaire direct est toujours exclu et la charge d’évaluation est répartie équitablement.","es":"El sistema selecciona primero colegas del mismo nivel en el mismo equipo, luego en la misma división y finalmente otros colegas del mismo nivel. El gerente directo siempre se excluye y la carga de evaluación se equilibra entre las personas.","ar":"يختار النظام أولاً الزملاء من المستوى نفسه في الفريق نفسه، ثم في الإدارة نفسها، وأخيراً زملاء آخرين من المستوى نفسه. يُستبعد المدير المباشر دائماً وتُوازن أعباء التقييم بين الأشخاص."},"همه نقش‌ها":{"en":"All roles","fr":"Tous les rôles","es":"Todos los roles","ar":"كل الأدوار"},"تعداد ارزیاب":{"en":"Number of reviewers","fr":"Nombre d’évaluateurs","es":"Número de evaluadores","ar":"عدد المقيّمين"},"پیشنهاد سیستمی ارزیاب‌ها":{"en":"System-recommended reviewers","fr":"Évaluateurs recommandés par le système","es":"Evaluadores recomendados por el sistema","ar":"المقيّمون المقترحون من النظام"},"پیش‌نمایش تخصیص‌ها":{"en":"Assignment preview","fr":"Aperçu des affectations","es":"Vista previa de asignaciones","ar":"معاينة التعيينات"},"تأیید و اعمال همه":{"en":"Confirm and apply all","fr":"Confirmer et tout appliquer","es":"Confirmar y aplicar todo","ar":"تأكيد وتطبيق الكل"},"فرد مورد ارزیابی":{"en":"Person being reviewed","fr":"Personne évaluée","es":"Persona evaluada","ar":"الشخص محل التقييم"},"فعال‌سازی بازخورد در عملکرد فردی":{"en":"Include peer feedback in individual score","fr":"Inclure la rétroaction des collègues dans le score individuel","es":"Incluir comentarios de colegas en la puntuación individual","ar":"تضمين ملاحظات الزملاء في الدرجة الفردية"},"نمایش نتیجه":{"en":"Result visibility","fr":"Visibilité du résultat","es":"Visibilidad del resultado","ar":"عرض النتيجة"},"محرمانه — فقط میانگین":{"en":"Confidential — average only","fr":"Confidentiel — moyenne seulement","es":"Confidencial — solo promedio","ar":"سري — المتوسط فقط"},"گروهی — بدون نام":{"en":"Grouped — no names","fr":"Groupé — sans noms","es":"Agrupado — sin nombres","ar":"مجمّع — بدون أسماء"},"شفاف — همراه نام":{"en":"Transparent — with names","fr":"Transparent — avec noms","es":"Transparente — con nombres","ar":"شفاف — مع الأسماء"},"حداقل پاسخ برای نمایش":{"en":"Minimum responses to show results","fr":"Nombre minimal de réponses pour afficher le résultat","es":"Respuestas mínimas para mostrar resultados","ar":"الحد الأدنى من الردود لعرض النتيجة"},"هم‌تیمی":{"en":"Teammate","fr":"Coéquipier","es":"Compañero de equipo","ar":"زميل في الفريق"},"تعداد ارزیاب‌ها از حداقل پاسخ لازم کمتر است.":{"en":"The number of reviewers is below the minimum responses required.","fr":"Le nombre d’évaluateurs est inférieur au minimum de réponses requis.","es":"El número de evaluadores es inferior al mínimo de respuestas requerido.","ar":"عدد المقيّمين أقل من الحد الأدنى المطلوب من الردود."},"در حال دریافت اطلاعات سازمان...":{"en":"Loading organization data...","fr":"Chargement des données de l’organisation...","es":"Cargando datos de la organización...","ar":"جارٍ تحميل بيانات المؤسسة..."},"تلاش مجدد":{"en":"Try again","fr":"Réessayer","es":"Intentar de nuevo","ar":"حاول مجدداً"},"روز تا پایان":{"en":"days until end of","fr":"jours avant la fin de","es":"días hasta el final de","ar":"أيام حتى نهاية"},"به پایان رسیده":{"en":"has ended","fr":"est terminée","es":"ha finalizado","ar":"انتهت"},"روز تأخیر":{"en":"days overdue","fr":"jours de retard","es":"días de retraso","ar":"أيام تأخير"},"روز تا مهلت ثبت":{"en":"days until submission deadline","fr":"jours avant la date limite de saisie","es":"días hasta la fecha límite de registro","ar":"أيام حتى موعد التسجيل"},"در انتظار":{"en":"pending","fr":"en attente","es":"pendiente","ar":"قيد الانتظار"},"تکمیل‌شده":{"en":"completed","fr":"terminé","es":"completado","ar":"مكتمل"},"روز":{"en":"day","fr":"jour","es":"día","ar":"يوم"},"نفر ثبت شده":{"en":"people registered","fr":"personnes enregistrées","es":"personas registradas","ar":"أشخاص مسجلون"},"نقش، هر دو گروه وزن معتبر است":{"en":"roles, both weight groups are valid","fr":"rôles, les deux groupes de pondération sont valides","es":"roles, ambos grupos de ponderación son válidos","ar":"أدوار، مجموعتا الأوزان صالحتان"},"شاخص تعریف شده":{"en":"indicators defined","fr":"indicateurs définis","es":"indicadores definidos","ar":"مؤشرات محددة"},"شاخص هدف‌گذاری شده":{"en":"indicators have targets","fr":"indicateurs ont une cible","es":"indicadores tienen objetivo","ar":"مؤشرات لها أهداف"},"شاخص تأیید شده":{"en":"indicators approved","fr":"indicateurs approuvés","es":"indicadores aprobados","ar":"مؤشرات معتمدة"}};
Object.assign(LEGACY_UI_TRANSLATIONS, {"فعالیت‌های مستمر":{"en":"Routine activities","fr":"Activités récurrentes","ar":"الأنشطة المستمرة","es":"Actividades recurrentes"},"ثبت فعالیت جدید":{"en":"Register new activity","fr":"Enregistrer une nouvelle activité","ar":"تسجيل نشاط جديد","es":"Registrar nueva actividad"},"فهرست فعالیت‌های غیرمستمر ثبت‌شده":{"en":"Recorded achievements and non-routine activities","fr":"Réalisations et activités non récurrentes enregistrées","ar":"الإنجازات والأنشطة غير المستمرة المسجلة","es":"Logros y actividades no recurrentes registrados"}});
Object.assign(LEGACY_UI_TRANSLATIONS, {"پروفایل و تنظیمات":{"en":"Profile and settings","fr":"Profil et paramètres","ar":"الملف الشخصي والإعدادات","es":"Perfil y configuración"},"اطلاعات شخصی، حساب کاربری و ترجیحات شما":{"en":"Your personal details, account and preferences","fr":"Vos informations personnelles, compte et préférences","ar":"معلوماتك الشخصية وحسابك وتفضيلاتك","es":"Tus datos personales, cuenta y preferencias"},"اطلاعات فردی":{"en":"Personal details","fr":"Informations personnelles","ar":"المعلومات الشخصية","es":"Datos personales"},"اطلاعات سازمانی":{"en":"Organization","fr":"Organisation","ar":"المؤسسة","es":"Organización"},"ترجیحات":{"en":"Preferences","fr":"Préférences","ar":"التفضيلات","es":"Preferencias"}});
const LEGACY_UI_TRANSLATION_KEYS = Object.keys(LEGACY_UI_TRANSLATIONS)
  .filter(key => key.length >= 3)
  .sort((a, b) => b.length - a.length);

function translateLegacyUiText(value, language) {
  if (value == null || language === "fa") return value;
  const source = String(value);
  const exact = LEGACY_UI_TRANSLATIONS[source]?.[language];
  if (exact != null) return exact;
  let translated = source;
  for (const key of LEGACY_UI_TRANSLATION_KEYS) {
    if (!translated.includes(key)) continue;
    const replacement = LEGACY_UI_TRANSLATIONS[key]?.[language];
    if (replacement != null) translated = translated.split(key).join(replacement);
  }
  return translated;
}

const LEGACY_UI_ORIGINAL_TEXT = new WeakMap();
const LEGACY_UI_RENDERED_TEXT = new WeakMap();
const LEGACY_UI_ORIGINAL_ATTRIBUTES = new WeakMap();
const LEGACY_UI_RENDERED_ATTRIBUTES = new WeakMap();

function LegacyUiTranslator() {
  const { language } = useI18n();
  useEffect(() => {
    if (typeof document === "undefined" || !document.body) return undefined;
    const hasPersian = value => /[\u0600-\u06FF]/.test(String(value || ""));
    const translatableAttributes = ["placeholder", "title", "aria-label"];
    const translateTextNode = node => {
      if (!node || node.nodeType !== Node.TEXT_NODE) return;
      if (node.parentElement?.closest?.('[data-no-legacy-translate="true"], [contenteditable="true"], code, pre, script, style, kbd, samp')) return;
      const current = node.data;
      const previous = LEGACY_UI_RENDERED_TEXT.get(node);
      // A React re-render may replace the same Text node. Preserve the new original.
      if (current !== previous) {
        LEGACY_UI_ORIGINAL_TEXT.set(node, current);
      }
      const original = LEGACY_UI_ORIGINAL_TEXT.get(node) ?? current;
      const next = language === "fa"
        ? localizePersianDisplay(original)
        : translateLegacyUiText(original, language);
      LEGACY_UI_RENDERED_TEXT.set(node, next);
      if (current !== next) node.data = next;
    };
    const translateElementAttributes = element => {
      if (!element || element.nodeType !== Node.ELEMENT_NODE) return;
      if (element.closest?.('[data-no-legacy-translate="true"]')) return;
      let originals = LEGACY_UI_ORIGINAL_ATTRIBUTES.get(element);
      if (!originals) {
        originals = {};
        LEGACY_UI_ORIGINAL_ATTRIBUTES.set(element, originals);
      }
      let rendered = LEGACY_UI_RENDERED_ATTRIBUTES.get(element);
      if (!rendered) {
        rendered = {};
        LEGACY_UI_RENDERED_ATTRIBUTES.set(element, rendered);
      }
      for (const attribute of translatableAttributes) {
        const current = element.getAttribute(attribute);
        if (current == null) continue;
        if (current !== originals[attribute] && current !== rendered[attribute]) originals[attribute] = current;
        const original = originals[attribute] ?? current;
        const next = language === "fa"
          ? (element.matches?.('input[type="email"], input[type="url"], input[type="password"], input[type="number"], input[type="date"], input[type="datetime-local"]')
              ? original : localizePersianDisplay(original))
          : translateLegacyUiText(original, language);
        rendered[attribute] = next;
        if (current !== next) element.setAttribute(attribute, next);
      }
    };
    const translateTree = root => {
      if (!root) return;
      if (root.nodeType === Node.TEXT_NODE) {
        translateTextNode(root);
        return;
      }
      if (root.nodeType === Node.ELEMENT_NODE) translateElementAttributes(root);
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        if (node.nodeType === Node.TEXT_NODE) translateTextNode(node);
        else if (node.nodeType === Node.ELEMENT_NODE) translateElementAttributes(node);
        node = walker.nextNode();
      }
    };
    translateTree(document.body);
    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.type === "characterData") {
          translateTextNode(record.target);
          continue;
        }
        if (record.type === "attributes") {
          translateElementAttributes(record.target);
          continue;
        }
        for (const node of record.addedNodes) translateTree(node);
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: translatableAttributes,
    });
    return () => observer.disconnect();
  }, [language]);
  return null;
}

const I18nContext = createContext(null);

function I18nProvider({ children }) {
    const [language, setLanguage] = useState(() => {
        try {
            const saved = localStorage.getItem("cvi_language");
            return SUPPORTED_LANGUAGES[saved] ? saved : "fa";
        } catch {
            return "fa";
        }
    });

    const direction = SUPPORTED_LANGUAGES[language]?.dir || "ltr";

    useEffect(() => {
        try { localStorage.setItem("cvi_language", language); } catch { }
        if (typeof document !== "undefined") {
            document.documentElement.lang = language;
            document.documentElement.dir = direction;
        }
    }, [language, direction]);

    const t = (key, params = {}) => {
        const template = WORKFLOW_DICTIONARY[language]?.[key] ?? SETTINGS_DICTIONARY[language]?.[key] ?? UI_DICTIONARY[language]?.[key] ?? WORKFLOW_DICTIONARY.en?.[key] ?? SETTINGS_DICTIONARY.en?.[key] ?? UI_DICTIONARY.en?.[key] ?? key;
        return Object.entries(params).reduce(
            (value, [name, replacement]) => value.replaceAll(`{${name}}`, String(replacement)),
            template
        );
    };

    return <I18nContext.Provider value={{ language, setLanguage, direction, t, languages:SUPPORTED_LANGUAGES }}>
    <LegacyUiTranslator />
    <GlobalResponsiveStyles />
    {children}
  </I18nContext.Provider>;
}

function useI18n() {
    const context = useContext(I18nContext);
    if (!context) throw new Error("useI18n must be used inside I18nProvider.");
    return context;
}

function LanguageSelector({ compact = false, onChange, value }) {
    const { language, setLanguage, languages } = useI18n();
    const selectedValue = value || language;
    const handleChange = event => {
        const next = event.target.value;
        if (onChange) onChange(next);
        else setLanguage(next);
    };
    return <select value={selectedValue} onChange={handleChange} aria-label="Language"
        style={{
            border: "1px solid rgba(255,255,255,0.22)", background: compact ? "rgba(255,255,255,0.08)" : "#fff",
            color: compact ? "#fff" : C.navy, borderRadius: 7, padding: compact ? "5px 7px" : "8px 10px",
            fontSize: compact ? 10 : 12, fontFamily: "inherit", cursor: "pointer", direction: "ltr"
        }}>
        {Object.entries(languages).map(([code, meta]) => <option key={code} value={code} style={{ color: C.text }}>{meta.label}</option>)}
    </select>;
}


// ── SEED DATA (حالت اولیه — همه‌ی این‌ها بعداً از پنل ادمین قابل ویرایشن) ──────
const INIT_USERS = [
    { id: "u1", name: "علی رضایی", role: "ceo", posId: "manager", teamId: null, deptId: null, managerId: null, username: "ali.r", password: "123", employeeCode: "1001", nationalId: "0011223344", email: "ali.r@donex.co" },
    { id: "u2", name: "مریم صادقی", role: "admin", posId: "specialist", teamId: null, deptId: null, managerId: null, username: "maryam.s", password: "123", employeeCode: "1002", nationalId: "0022334455", email: "maryam.s@donex.co" },
    { id: "u3", name: "حسین محمدی", role: "deputy", posId: "deputy", teamId: null, deptId: "d1", managerId: "u1", username: "hossein.m", password: "123", employeeCode: "1003", nationalId: "0033445566", email: "hossein.m@donex.co" },
    { id: "u4", name: "سارا کریمی", role: "manager", posId: "manager", teamId: "t1", deptId: "d1", managerId: "u3", username: "sara.k", password: "123", employeeCode: "1004", nationalId: "0044556677", email: "sara.k@donex.co" },
    { id: "u5", name: "رضا احمدی", role: "manager", posId: "manager", teamId: "t2", deptId: "d1", managerId: "u3", username: "reza.a", password: "123", employeeCode: "1005", nationalId: "0055667788", email: "reza.a@donex.co" },
    { id: "u6", name: "نگار تهرانی", role: "deputy", posId: "deputy", teamId: null, deptId: "d2", managerId: "u1", username: "negar.t", password: "123", employeeCode: "1006", nationalId: "0066778899", email: "negar.t@donex.co" },
    { id: "u7", name: "کامران نوری", role: "manager", posId: "manager", teamId: "t3", deptId: "d2", managerId: "u6", username: "kamran.n", password: "123", employeeCode: "1007", nationalId: "0077889900", email: "kamran.n@donex.co" },
    { id: "u8", name: "فاطمه موسوی", role: "specialist", posId: "specialist", teamId: "t1", deptId: "d1", managerId: "u4", username: "fateme.m", password: "123", employeeCode: "1008", nationalId: "0088990011", email: "fateme.m@donex.co" },
    { id: "u9", name: "داود عزیزی", role: "specialist", posId: "specialist", teamId: "t1", deptId: "d1", managerId: "u4", username: "davood.a", password: "123", employeeCode: "1009", nationalId: "0099001122", email: "davood.a@donex.co" },
    { id: "u10", name: "لیلا نصیری", role: "specialist", posId: "specialist", teamId: "t2", deptId: "d1", managerId: "u5", username: "leila.n", password: "123", employeeCode: "1010", nationalId: "0010111213", email: "leila.n@donex.co" },
    { id: "u11", name: "نیلوفر رشیدی", role: "specialist", posId: "specialist", teamId: "t1", deptId: "d1", managerId: "u4", username: "niloofar.r", password: "123", employeeCode: "1011", nationalId: "0011121314", email: "niloofar.r@donex.co" },
];

const INIT_TEAMS = [
    { id: "t1", label: "تیم فروش", deptId: "d1" },
    { id: "t2", label: "تیم بازاریابی", deptId: "d1" },
    { id: "t3", label: "تیم توسعه", deptId: "d2" },
];

const INIT_DEPARTMENTS = [
    { id: "d1", label: "فروش و بازاریابی" },
    { id: "d2", label: "فناوری اطلاعات" },
];

// وزن‌ها به‌صورت درصد (۰ تا ۱۰۰) نگه داشته می‌شن تا برای ادمین قابل‌فهم باشن؛
// جمع سه‌تا باید همیشه ۱۰۰ باشه (در فرم ادمین اعتبارسنجی می‌شه)
const INIT_POSITIONS = [
    { id: "specialist", label: "کارشناس", wTeam: 67, wDept: 23, wOrg: 10, wNonRoutine: 50, wRoutine: 30, wPeerFeedback: 20, wResponsibilities: 60, wGoals: 40 },
    { id: "team_lead", label: "تیم لید", wTeam: 50, wDept: 35, wOrg: 15, wNonRoutine: 45, wRoutine: 35, wPeerFeedback: 20, wResponsibilities: 60, wGoals: 40 },
    { id: "manager", label: "مدیر", wTeam: 30, wDept: 50, wOrg: 20, wNonRoutine: 40, wRoutine: 35, wPeerFeedback: 25 },
    { id: "deputy", label: "معاون", wTeam: 15, wDept: 45, wOrg: 40, wNonRoutine: 30, wRoutine: 40, wPeerFeedback: 30 },
];

const INIT_ACTIVITY_GROUPS = [
    {
        id: "learning", label: "یادگیری و توسعه", maxScore: 25, icon: "activity",
        subs: ["دوره تخصصی", "دوره عمومی", "شرکت در کنفرانس", "مطالعه مستقل", "وبینار"]
    },
    {
        id: "project", label: "پروژه و کار تخصصی", maxScore: 30, icon: "target",
        subs: ["مشارکت در پروژه بین‌تیمی", "کار اورژانسی", "جایگزینی همکار", "کیفیت بالاتر از استاندارد"]
    },
    {
        id: "innovation", label: "نوآوری و بهبود", maxScore: 25, icon: "performance",
        subs: ["بهبود اجراشده", "بهبود در دست بررسی", "ساخت ابزار جدید", "مستندسازی دانش"]
    },
    {
        id: "impact", label: "اثرگذاری بر دیگران", maxScore: 20, icon: "team",
        subs: ["آموزش مستقیم به همکار", "جلسه اشتراک دانش", "منتورینگ عضو جدید", "تهیه مستند آموزشی"]
    },
];

const INIT_IMPACT_LEVELS = [
    { id: "very_high", label: "خیلی زیاد", score: 4 },
    { id: "high", label: "زیاد", score: 3 },
    { id: "medium", label: "متوسط", score: 2 },
    { id: "low", label: "کم", score: 1 },
];

// ── بخش روتین (ارزیابی ماهانه‌ی مدیر) ──────────────────────────────────────
// سطوح کیفیِ پاسخ به سؤالات وظایف مستمر — مشابه سطوح تأثیر، ولی مستقل و قابل‌ویرایش
const INIT_ROUTINE_LEVELS = [
    { id: "r_excellent", label: "عالی", score: 5 },
    { id: "r_good", label: "خوب", score: 4 },
    { id: "r_medium", label: "متوسط", score: 3 },
    { id: "r_weak", label: "ضعیف", score: 2 },
    { id: "r_poor", label: "بسیار ضعیف", score: 1 },
];

// وزن سراسری سه مؤلفه عملکرد فردی؛ جمع سه مقدار باید همیشه ۱۰۰ باشد.
// logo: تصویر لوگوی سازمان به‌صورت base64 data-URL؛ فقط در حافظه‌ی همین session باقی می‌ماند.
// وزن‌های عمومی فقط برای سازگاری با نقش‌های قدیمیِ فاقد وزن اختصاصی نگه داشته می‌شوند.
const INIT_ORG_SETTINGS = {
    wNonRoutine: 50,
    wRoutine: 30,
    wPeerFeedback: 20,
    name: "",
    logo: null,
    defaultLanguage: "en",
    accentColor: "#00C8E6",
    timezone: "UTC",
    dateFormat: "YYYY-MM-DD",
    calendar: "gregorian"
};

// بانک مشترک سؤالات بازخورد همکاران و تنظیم ارزیاب‌های هر فرد
const INIT_PEER_QUESTIONS = [
    { id: "pq1", label: "همکاری مؤثر و رفتار حرفه‌ای", weight: 25 },
    { id: "pq2", label: "مسئولیت‌پذیری و قابل اتکا بودن", weight: 25 },
    { id: "pq3", label: "کیفیت ارتباط و انتقال اطلاعات", weight: 25 },
    { id: "pq4", label: "مشارکت در حل مسئله و بهبود کار", weight: 25 },
];
const INIT_PEER_CONFIGS = [
    { userId: "u8", enabled: true, reviewerIds: ["u9", "u11", "u10"], visibility: "anonymous", minResponses: 2 },
];
// هر رکورد، پاسخ نهایی یک ارزیاب درباره یک فرد در یک دوره است.
const INIT_PEER_REVIEWS = [];

// بانک سؤالات وظایف مستمر — به ازای هر تیم، ۳ سؤال برای هر ماه از فصل (month: 1|2|3)
// وزن هر سؤال به‌صورت درصد است؛ جمع وزن ۹ سؤالِ یک تیم باید ۱۰۰ شود.
const INIT_ROUTINE_QUESTIONS = [
    { id: "rq1", teamId: "t1", month: 1, label: "رعایت مهلت‌های تعیین‌شده در کارهای روزمره", weight: 12 },
    { id: "rq2", teamId: "t1", month: 1, label: "کیفیت ارتباط و هماهنگی با اعضای تیم", weight: 11 },
    { id: "rq3", teamId: "t1", month: 1, label: "پیگیری و نظم در انجام وظایف ثابت", weight: 10 },
    { id: "rq4", teamId: "t1", month: 2, label: "رعایت مهلت‌های تعیین‌شده در کارهای روزمره", weight: 12 },
    { id: "rq5", teamId: "t1", month: 2, label: "کیفیت ارتباط و هماهنگی با اعضای تیم", weight: 11 },
    { id: "rq6", teamId: "t1", month: 2, label: "پیگیری و نظم در انجام وظایف ثابت", weight: 10 },
    { id: "rq7", teamId: "t1", month: 3, label: "رعایت مهلت‌های تعیین‌شده در کارهای روزمره", weight: 12 },
    { id: "rq8", teamId: "t1", month: 3, label: "کیفیت ارتباط و هماهنگی با اعضای تیم", weight: 11 },
    { id: "rq9", teamId: "t1", month: 3, label: "پیگیری و نظم در انجام وظایف ثابت", weight: 11 },
];

// جواب‌های ثبت‌شده توسط مدیر — هر رکورد یک (سؤال × فرد × دوره)
const INIT_ROUTINE_ANSWERS = [
    { id: "ra1", questionId: "rq1", userId: "u8", periodId: "Q3-1404", levelId: "r_good", date: "1404/07/30" },
    { id: "ra2", questionId: "rq2", userId: "u8", periodId: "Q3-1404", levelId: "r_excellent", date: "1404/07/30" },
    { id: "ra3", questionId: "rq3", userId: "u8", periodId: "Q3-1404", levelId: "r_good", date: "1404/07/30" },
];

// بانک سؤالات وظایف مستمر در سطح معاونت — معاون به همین شکل برای مدیرهای زیرمجموعه‌اش پاسخ می‌دهد
const INIT_DEPT_ROUTINE_QUESTIONS = [
    { id: "dq1", deptId: "d1", month: 1, label: "مدیریت و پیگیری اهداف تیم", weight: 12 },
    { id: "dq2", deptId: "d1", month: 1, label: "کیفیت گزارش‌دهی به معاونت", weight: 11 },
    { id: "dq3", deptId: "d1", month: 1, label: "هماهنگی با سایر تیم‌های معاونت", weight: 10 },
    { id: "dq4", deptId: "d1", month: 2, label: "مدیریت و پیگیری اهداف تیم", weight: 12 },
    { id: "dq5", deptId: "d1", month: 2, label: "کیفیت گزارش‌دهی به معاونت", weight: 11 },
    { id: "dq6", deptId: "d1", month: 2, label: "هماهنگی با سایر تیم‌های معاونت", weight: 10 },
    { id: "dq7", deptId: "d1", month: 3, label: "مدیریت و پیگیری اهداف تیم", weight: 12 },
    { id: "dq8", deptId: "d1", month: 3, label: "کیفیت گزارش‌دهی به معاونت", weight: 11 },
    { id: "dq9", deptId: "d1", month: 3, label: "هماهنگی با سایر تیم‌های معاونت", weight: 11 },
];

// جواب‌های ثبت‌شده توسط معاون درباره‌ی مدیرهای زیرمجموعه‌اش
const INIT_DEPT_ROUTINE_ANSWERS = [];

// بانک سؤالات وظایف مستمر در سطح سازمان — مدیرعامل به همین شکل برای معاون‌ها پاسخ می‌دهد (بدون نیاز به تفکیک معاونت)
const INIT_ORG_ROUTINE_QUESTIONS = [
    { id: "oq1", month: 1, label: "همسویی معاونت با استراتژی سازمان", weight: 12 },
    { id: "oq2", month: 1, label: "کیفیت گزارش‌دهی به مدیرعامل", weight: 11 },
    { id: "oq3", month: 1, label: "هماهنگی بین‌معاونتی", weight: 10 },
    { id: "oq4", month: 2, label: "همسویی معاونت با استراتژی سازمان", weight: 12 },
    { id: "oq5", month: 2, label: "کیفیت گزارش‌دهی به مدیرعامل", weight: 11 },
    { id: "oq6", month: 2, label: "هماهنگی بین‌معاونتی", weight: 10 },
    { id: "oq7", month: 3, label: "همسویی معاونت با استراتژی سازمان", weight: 12 },
    { id: "oq8", month: 3, label: "کیفیت گزارش‌دهی به مدیرعامل", weight: 11 },
    { id: "oq9", month: 3, label: "هماهنگی بین‌معاونتی", weight: 11 },
];

// جواب‌های ثبت‌شده توسط مدیرعامل درباره‌ی معاون‌ها
const INIT_ORG_ROUTINE_ANSWERS = [];

const INIT_SCHEDULE_TEMPLATES = [
    {
        id: "schedule_monthly", label: "گزارش ماهانه استاندارد", frequency: "monthly",
        openOffsetDays: 0, submitAfterDays: 5, approveAfterDays: 3, lockAfterDays: 10, reminderDays: 3
    },
    {
        id: "schedule_quarterly", label: "گزارش فصلی استاندارد", frequency: "quarterly",
        openOffsetDays: 0, submitAfterDays: 5, approveAfterDays: 3, lockAfterDays: 10, reminderDays: 3
    },
    {
        id: "schedule_annual", label: "گزارش سالانه استاندارد", frequency: "annual",
        openOffsetDays: 0, submitAfterDays: 10, approveAfterDays: 5, lockAfterDays: 20, reminderDays: 5
    },
];
const INIT_SCHEDULE_EXCEPTIONS = [];

// لایه‌ی ۱: تعریف شاخص — یک‌بار ساخته می‌شه، بین دوره‌ها ثابت می‌مونه (بدون هدف/واقعی)
const INIT_INDICATOR_DEFS = [
    { id: "i1", label: "درآمد تیم فروش", level: "team", teamId: "t1", unit: "M تومان", weight: 0.40, ownerId: "u4", registrarId: "u8", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "sum", requiresEvidence: true },
    { id: "i2", label: "مشتری جدید", level: "team", teamId: "t1", unit: "نفر", weight: 0.25, ownerId: "u4", registrarId: "u9", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "sum", requiresEvidence: false },
    { id: "i3", label: "نرخ حفظ مشتری", level: "team", teamId: "t1", unit: "%", weight: 0.20, ownerId: "u4", registrarId: "u8", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "average", requiresEvidence: true },
    { id: "i4", label: "کنترل هزینه", level: "team", teamId: "t1", unit: "M تومان", weight: 0.15, ownerId: "u4", registrarId: "u9", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "sum", requiresEvidence: true },
    { id: "i5", label: "تیکت‌های بسته‌شده", level: "team", teamId: "t2", unit: "عدد", weight: 0.30, ownerId: "u5", registrarId: "u10", approverId: "u3", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "sum", requiresEvidence: false },
    { id: "i6", label: "رضایت مشتری معاونت", level: "dept", deptId: "d1", unit: "/5", weight: 0.35, ownerId: "u3", registrarId: "u4", approverId: "u1", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "average", requiresEvidence: true },
    { id: "i7", label: "رشد درآمد سازمان", level: "org", unit: "%", weight: 0.25, ownerId: "u1", registrarId: "u3", approverId: "u1", scheduleTemplateId: "schedule_quarterly", aggregationMethod: "latest", requiresEvidence: true },
];

// لایه‌ی ۲: داده‌ی دوره‌ای — هر رکورد یک (شاخص × دوره) با هدف و واقعی
const INIT_INDICATOR_DATA = [
    { id: "i1_Q3-1404", periodId: "Q3-1404", indicatorId: "i1", target: 500, actual: 425, status: "approved", submittedBy: "u8", approvedBy: "u3", history: [{ type: "approved", date: "1404/08/20", by: "حسین محمدی", note: "مقدار تأیید شد" }] },
    { id: "i2_Q3-1404", periodId: "Q3-1404", indicatorId: "i2", target: 30, actual: 28, status: "approved", submittedBy: "u9", approvedBy: "u3", history: [{ type: "approved", date: "1404/08/20", by: "حسین محمدی", note: "مقدار تأیید شد" }] },
    { id: "i3_Q3-1404", periodId: "Q3-1404", indicatorId: "i3", target: 90, actual: 94, status: "approved", submittedBy: "u8", approvedBy: "u3", history: [{ type: "approved", date: "1404/08/20", by: "حسین محمدی", note: "مقدار تأیید شد" }] },
    { id: "i4_Q3-1404", periodId: "Q3-1404", indicatorId: "i4", target: 150, actual: 186, status: "approved", submittedBy: "u9", approvedBy: "u3", history: [{ type: "approved", date: "1404/08/20", by: "حسین محمدی", note: "مقدار تأیید شد" }] },
    { id: "i5_Q3-1404", periodId: "Q3-1404", indicatorId: "i5", target: 200, actual: 185, status: "approved", submittedBy: "u10", approvedBy: "u3", history: [{ type: "approved", date: "1404/08/20", by: "حسین محمدی", note: "مقدار تأیید شد" }] },
    { id: "i6_Q3-1404", periodId: "Q3-1404", indicatorId: "i6", target: 5, actual: 4, status: "approved", submittedBy: "u4", approvedBy: "u1", history: [{ type: "approved", date: "1404/08/20", by: "علی رضایی", note: "مقدار تأیید شد" }] },
    { id: "i7_Q3-1404", periodId: "Q3-1404", indicatorId: "i7", target: 20, actual: 16, status: "approved", submittedBy: "u3", approvedBy: "u1", history: [{ type: "approved", date: "1404/08/20", by: "علی رضایی", note: "مقدار تأیید شد" }] },
];

const INIT_PERIODS = [
    { id: "Q1-1404", label: "فصل اول ۱۴۰۴", start: "1404/01/01", end: "1404/03/31", active: false, status: "locked" },
    { id: "Q2-1404", label: "فصل دوم ۱۴۰۴", start: "1404/04/01", end: "1404/06/30", active: false, status: "locked" },
    { id: "Q3-1404", label: "فصل سوم ۱۴۰۴", start: "1404/07/01", end: "1404/09/30", active: true, status: "active" },
];

const INIT_ACTIVITIES = [
    { id: "a1", userId: "u8", periodId: "Q3-1404", groupId: "learning", sub: "دوره تخصصی", title: "دوره Bizagi BPMS", desc: "دوره جامع مدیریت فرآیند با ابزار Bizagi. مستقیماً در پروژه جاری پیاده‌سازی شد.", doc: "cert_bizagi.pdf", status: "approved", impactLevel: "very_high", score: 4, managerNote: "", date: "1404/07/15" },
    { id: "a2", userId: "u8", periodId: "Q3-1404", groupId: "innovation", sub: "بهبود اجراشده", title: "بهینه‌سازی فرآیند گزارش‌گیری", desc: "زمان گزارش‌گیری هفتگی را از ۳ ساعت به ۴۵ دقیقه کاهش دادم.", doc: "", status: "pending_review", impactLevel: null, score: 0, managerNote: "", date: "1404/08/02" },
    {
        id: "a3", userId: "u8", periodId: "Q3-1404", groupId: "impact", sub: "منتورینگ عضو جدید", title: "منتورینگ همکار جدید", desc: "۳ هفته آموزش مستقیم به داود عزیزی برای آشنایی با فرآیندهای تیم.", doc: "", status: "revision_requested", impactLevel: null, score: 0, managerNote: "لطفاً ساعات دقیق آموزش را مشخص کنید", date: "1404/08/10", revisionRequestedAt: "1404/08/12",
        history: [{ type: "submitted", date: "1404/08/10", by: "فاطمه موسوی" },
        { type: "revision_requested", date: "1404/08/12", by: "سارا کریمی", note: "لطفاً ساعات دقیق آموزش را مشخص کنید" }]
    },
    { id: "a4", userId: "u9", periodId: "Q3-1404", groupId: "project", sub: "کیفیت بالاتر از استاندارد", title: "تحویل پروژه زودتر از موعد", desc: "پروژه ۲ هفته زودتر از موعد مقرر با کیفیت بالا تحویل داده شد.", doc: "report.pdf", status: "approved", impactLevel: "high", score: 3, managerNote: "", date: "1404/07/20" },
    { id: "a5", userId: "u9", periodId: "Q3-1404", groupId: "learning", sub: "دوره تخصصی", title: "دوره Python پیشرفته", desc: "دوره برنامه‌نویسی پیشرفته با تأثیر زیاد روی اتوماسیون گزارش‌ها.", doc: "py_cert.pdf", status: "pending_review", impactLevel: null, score: 0, managerNote: "", date: "1404/08/05" },
    { id: "a6", userId: "u10", periodId: "Q3-1404", groupId: "learning", sub: "شرکت در کنفرانس", title: "کنفرانس مارکتینگ دیجیتال", desc: "شرکت در کنفرانس و ارائه خلاصه به تیم.", doc: "conf_cert.pdf", status: "approved", impactLevel: "medium", score: 2, managerNote: "", date: "1404/07/25" },
    { id: "a7", userId: "u11", periodId: "Q3-1404", groupId: "innovation", sub: "ساخت ابزار جدید", title: "ساخت داشبورد Excel فروش تیم", desc: "داشبورد اتوماتیک هفتگی برای پیگیری اهداف فروش طراحی کردم.", doc: "dashboard.xlsx", status: "approved", impactLevel: "very_high", score: 4, managerNote: "", date: "1404/07/28" },
    { id: "a8", userId: "u4", periodId: "Q3-1404", groupId: "impact", sub: "جلسه اشتراک دانش", title: "جلسه هفتگی اشتراک دانش تیم", desc: "برگزاری جلسه هفتگی ۱ ساعته برای اشتراک تجربیات اعضا.", doc: "", status: "approved", impactLevel: "high", score: 3, managerNote: "", date: "1404/07/18" },
];

// نمودار روند درآمد تیم (صرفاً نمایشی/دکوراتیو، بخشی از داده‌ی قابل‌ویرایش ادمین نیست)

// روند تاریخی CVI برای دوره‌های قدیمی که داده‌ی شاخص برایشان نداریم (صرفاً دکوراتیو)
const HISTORY_CVI = [
    {
        periodId: "Q1-1404", label: "فصل اول ۱۴۰۴", individual: 68, org: 61.7, cvi: 64.8,
        nonRoutine: 64, routine: 77,
        activityGroupScores: { learning: 15, project: 18, innovation: 17, impact: 14 },
        activityCounts: { learning: 2, project: 2, innovation: 2, impact: 1 },
        routineCriteria: [
            { label: "رعایت مهلت‌های تعیین‌شده", score: 80, weight: 36 },
            { label: "کیفیت ارتباط و هماهنگی", score: 60, weight: 33 },
            { label: "پیگیری و نظم در انجام وظایف", score: 90, weight: 31 },
        ]
    },
    {
        periodId: "Q2-1404", label: "فصل دوم ۱۴۰۴", individual: 74, org: 65.2, cvi: 69.5,
        nonRoutine: 78, routine: 64,
        activityGroupScores: { learning: 20, project: 22, innovation: 20, impact: 16 },
        activityCounts: { learning: 3, project: 2, innovation: 3, impact: 2 },
        routineCriteria: [
            { label: "رعایت مهلت‌های تعیین‌شده", score: 70, weight: 36 },
            { label: "کیفیت ارتباط و هماهنگی", score: 55, weight: 33 },
            { label: "پیگیری و نظم در انجام وظایف", score: 68, weight: 31 },
        ]
    },
];

// Snapshot نهایی دوره: پس از قفل‌شدن، تغییر فعالیت‌ها/فرمول‌ها روی این داده اثر نمی‌گذارد.
const INIT_PERIOD_SNAPSHOTS = INIT_USERS
    .filter(u => !["admin", "ceo"].includes(u.role))
    .flatMap(u => HISTORY_CVI.map(p => ({
        ...p, id: `snapshot_${p.periodId}_${u.id}`, userId: u.id,
        status: "final", locked: true, formulaVersion: "CVI-1.0",
        weights: { individual: 50, organizational: 50, nonRoutine: 70, routine: 30 },
        calculatedAt: `پایان ${p.label}`, version: 1,
    })));

const INIT_PERIOD_AUDIT = INIT_PERIODS.filter(p => p.status === "locked").map(p => ({
    id: `period_audit_${p.id}`, periodId: p.id, type: "locked", by: "سیستم",
    date: `پایان ${p.label}`, note: "دوره نهایی و Snapshot آن قفل شد"
}));

function latestPeriodSnapshotsForUser(snapshots, userId) {
    return Object.values(snapshots.filter(s => s.userId === userId).reduce((latest, s) => {
        const current = latest[s.periodId];
        if (!current || Number(s.version || 1) >= Number(current.version || 1)) latest[s.periodId] = s;
        return latest;
    }, {}));
}

// ── DATA CONTEXT (منبع واحد — همه‌ی پنل‌ها از همینجا می‌خونن/می‌نویسن) ─────────
const DataContext = createContext(null);
function useData() { return useContext(DataContext); }
// بسته‌ی کامل داده‌های روتین (سه سطح: تیم/معاونت/سازمان) — برای محاسبه‌ی امتیاز فردیِ هر نقش لازم است
function useRoutineCtx() {
    const { routineAnswers, routineQuestions, deptRoutineAnswers, deptRoutineQuestions,
        orgRoutineAnswers, orgRoutineQuestions, routineLevels,
        peerQuestions, peerConfigs, peerReviews, peerResult, positions } = useData();
    return {
        routineAnswers, routineQuestions, deptRoutineAnswers, deptRoutineQuestions,
        orgRoutineAnswers, orgRoutineQuestions, routineLevels, peerQuestions, peerConfigs, peerReviews, peerResult, positions
    };
}

function DataProvider({ children }) {
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [orgUnits, setOrgUnits] = useState([]);
    const [positions, setPositions] = useState([]);
    const [activityGroups, setActivityGroups] = useState([]);
    const [impactLevels, setImpactLevels] = useState([]);
    const [indicatorDefs, setIndicatorDefs] = useState([]);
    const [indicatorData, setIndicatorData] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [activities, setActivities] = useState([]);
    const [routineLevels, setRoutineLevels] = useState([]);
    const [routineQuestions, setRoutineQuestions] = useState([]);
    const [routineAnswers, setRoutineAnswers] = useState([]);
    const [deptRoutineQuestions, setDeptRoutineQuestions] = useState([]);
    const [deptRoutineAnswers, setDeptRoutineAnswers] = useState([]);
    const [orgRoutineQuestions, setOrgRoutineQuestions] = useState([]);
    const [orgRoutineAnswers, setOrgRoutineAnswers] = useState([]);
    const [orgSettings, setOrgSettings] = useState(INIT_ORG_SETTINGS);
    const [periodSnapshots, setPeriodSnapshots] = useState([]);
    const [periodAudit, setPeriodAudit] = useState([]);
    const [scheduleTemplates, setScheduleTemplates] = useState([]);
    const [scheduleExceptions, setScheduleExceptions] = useState(INIT_SCHEDULE_EXCEPTIONS);
    const [peerQuestions, setPeerQuestions] = useState([]);
    const [peerConfigs, setPeerConfigs] = useState([]);
    const [peerReviews, setPeerReviews] = useState([]);
    const [peerAssignments, setPeerAssignments] = useState([]);
    const [peerResult, setPeerResult] = useState(null);
    const [peerLoading, setPeerLoading] = useState(false);
    const [peerError, setPeerError] = useState("");
    const [peerReady, setPeerReady] = useState(false);
    const [baseDataLoading, setBaseDataLoading] = useState(false);
    const [baseDataError, setBaseDataError] = useState("");
    const [baseDataReady, setBaseDataReady] = useState(false);
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [activitiesError, setActivitiesError] = useState("");
    const [activitiesReady, setActivitiesReady] = useState(false);
    const [routineTargets, setRoutineTargets] = useState([]);
    const [routineLoading, setRoutineLoading] = useState(false);
    const [routineError, setRoutineError] = useState("");
    const [routineReady, setRoutineReady] = useState(false);
    const [indicatorLoading, setIndicatorLoading] = useState(false);
    const [indicatorError, setIndicatorError] = useState("");
    const [indicatorReady, setIndicatorReady] = useState(false);

    // Invalidate every session-scoped cache synchronously before a user/session switch.
    // This prevents a single render from showing the previous user's dashboard data
    // while the new API requests are still starting.
    const invalidateSessionData = () => {
        setBaseDataReady(false);
        setActivitiesReady(false);
        setRoutineReady(false);
        setIndicatorReady(false);
        setPeerReady(false);
        setBaseDataError("");
        setActivitiesError("");
        setRoutineError("");
        setIndicatorError("");
        setPeerError("");

        setUsers([]);
        setTeams([]);
        setDepartments([]);
        setOrgUnits([]);
        setPositions([]);
        setActivityGroups([]);
        setImpactLevels([]);
        setPeriods([]);
        setActivities([]);
        setRoutineLevels([]);
        setRoutineQuestions([]);
        setRoutineAnswers([]);
        setDeptRoutineQuestions([]);
        setDeptRoutineAnswers([]);
        setOrgRoutineQuestions([]);
        setOrgRoutineAnswers([]);
        setRoutineTargets([]);
        setIndicatorDefs([]);
        setIndicatorData([]);
        setPeriodSnapshots([]);
        setPeriodAudit([]);
        setScheduleTemplates([]);
        setScheduleExceptions(INIT_SCHEDULE_EXCEPTIONS);
        setPeerQuestions([]);
        setPeerConfigs([]);
        setPeerReviews([]);
        setPeerAssignments([]);
        setPeerResult(null);
        setOrgSettings(INIT_ORG_SETTINGS);
    };

    const normalizeApiActivity = (activity) => {
        const firstAttachment = activity.attachments?.[0] || null;
        const activityDate = activity.submittedAt || activity.createdAt || null;

        return {
            id: activity.id,
            userId: activity.userId,
            periodId: activity.periodId,
            groupId: activity.groupId,
            sub: activity.subtype?.name || activity.subtype?.label || "",
            subtypeId: activity.subtypeId || null,
            title: activity.title || "",
            desc: activity.description || "",
            doc: firstAttachment?.fileName || "",
            docDataUrl: "",
            status: String(activity.status || "").toLowerCase(),
            impactLevel: activity.impactLevelId || null,
            score: Number(activity.score) || 0,
            managerNote: activity.managerNote || "",
            blockerCategory: activity.blockerCategory || "",
            blockerText: activity.blockerText || "",
            supportNeed: activity.supportNeed || "",
            suggestedAction: activity.suggestedAction || "",
            blockerStatus: String(activity.blockerStatus || "none").toLowerCase(),
            date: activityDate ? jalaliDateFromGregorian(new Date(activityDate)) : "",
            submittedAt: activity.submittedAt || null,
            revisionRequestedAt: activity.revisionRequestedAt || null,
            resubmittedAt: activity.resubmittedAt || null,
            approvedAt: activity.approvedAt || null,
            rejectedAt: activity.rejectedAt || null,
            attachments: activity.attachments || [],
            history: (activity.history || []).map(event => ({
                id: event.id,
                type: String(event.type || event.eventType || "").toLowerCase(),
                eventType: String(event.eventType || event.type || "").toLowerCase(),
                date: event.createdAt ? jalaliDateFromGregorian(new Date(event.createdAt)) : "",
                by: event.actor?.name || "سیستم",
                actorId: event.actorId || event.actor?.id || null,
                actorRole: event.actor?.role ? String(event.actor.role).toLowerCase() : null,
                fromStatus: event.fromStatus ? String(event.fromStatus).toLowerCase() : null,
                toStatus: event.toStatus ? String(event.toStatus).toLowerCase() : null,
                note: event.note || "",
            })),
        };
    };

    const handleUnauthorized = () => {
        invalidateSessionData();
        sessionStorage.removeItem("cvi_user");
        sessionStorage.removeItem("cvi_token");
        window.dispatchEvent(new Event("cvi-session-expired"));
    };

    const uploadActivityFileApi = async (activityId, file) => {
        if (!file) return null;
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(`${API_BASE_URL}/api/activities/${activityId}/files`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        if (response.status === 401) handleUnauthorized();
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || `File upload failed with status ${response.status}.`);
        return payload;
    };

    const activityApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");

        const response = await fetch(`${API_BASE_URL}/api/activities${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });

        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || `Activity request failed with status ${response.status}.`);
        }
        return payload;
    };

    const loadActivities = async () => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            setActivities([]);
            setActivitiesLoading(false);
            setActivitiesError("");
            setActivitiesReady(false);
            return;
        }

        setActivitiesLoading(true);
        setActivitiesError("");
        setActivitiesReady(false);

        try {
            const rows = await activityApiRequest("");
            setActivities(rows.map(normalizeApiActivity));
            setActivitiesReady(true);
        } catch (error) {
            console.error("Activities load failed:", error);
            setActivitiesError("دریافت فعالیت‌ها انجام نشد. دوباره تلاش کنید");
        } finally {
            setActivitiesReady(true);
            setActivitiesLoading(false);
        }
    };

    const resolveSubtypeId = (groupId, subtypeLabel) => {
        if (!groupId || !subtypeLabel) return null;
        const group = activityGroups.find(item => item.id === groupId);
        const index = group?.subs?.findIndex(label => label === subtypeLabel) ?? -1;
        return index >= 0 ? `${groupId}_sub_${index + 1}` : null;
    };

    const createActivityApi = async (form, periodId) => {
        const created = await activityApiRequest("", {
            method: "POST",
            body: JSON.stringify({
                periodId,
                groupId: form.groupId,
                subtypeId: resolveSubtypeId(form.groupId, form.sub),
                title: form.title,
                description: form.desc,
                blockerCategory: form.blockerCategory || null,
                blockerText: form.blockerText || null,
                supportNeed: form.supportNeed || null,
                suggestedAction: form.suggestedAction || null,
                blockerStatus: form.blockerStatus || "none",
                submit: false,
            }),
        });

        if (form._file) await uploadActivityFileApi(created.id, form._file);
        const submitted = await activityApiRequest(`/${created.id}/submit`, {
            method: "POST",
            body: JSON.stringify({}),
        });
        const normalized = normalizeApiActivity(submitted);
        setActivities(prev => [normalized, ...prev.filter(item => item.id !== normalized.id)]);
        return normalized;
    };

    const resubmitActivityApi = async (activityId, form) => {
        await activityApiRequest(`/${activityId}`, {
            method: "PATCH",
            body: JSON.stringify({
                title: form.title,
                description: form.desc,
                blockerCategory: form.blockerCategory || null,
                blockerText: form.blockerText || null,
                supportNeed: form.supportNeed || null,
                suggestedAction: form.suggestedAction || null,
                blockerStatus: form.blockerStatus || "none",
            }),
        });

        if (form._file) await uploadActivityFileApi(activityId, form._file);
        const submitted = await activityApiRequest(`/${activityId}/submit`, {
            method: "POST",
            body: JSON.stringify({}),
        });

        const normalized = normalizeApiActivity(submitted);
        setActivities(prev => prev.map(item => item.id === normalized.id ? normalized : item));
        return normalized;
    };

    const reviewActivityApi = async (activityId, decision, impactLevelId, note) => {
        const reviewed = await activityApiRequest(`/${activityId}/review`, {
            method: "POST",
            body: JSON.stringify({
                decision,
                impactLevelId: impactLevelId || null,
                note: note?.trim() || null,
            }),
        });

        const normalized = normalizeApiActivity(reviewed);
        setActivities(prev => prev.map(item => item.id === normalized.id ? normalized : item));
        return normalized;
    };

    const normalizeRoutineQuestion = (question) => ({
        id: question.id,
        scope: String(question.scope || "").toLowerCase(),
        teamId: question.teamId ?? null,
        deptId: question.departmentId ?? question.deptId ?? null,
        departmentId: question.departmentId ?? question.deptId ?? null,
        orgUnitId: question.orgUnitId ?? null,
        month: Number(question.month) || 1,
        label: question.label || question.text || "",
        text: question.text || question.label || "",
        weight: Number(question.weight) || 0,
        isActive: question.isActive !== false,
        sortOrder: Number(question.sortOrder) || 0,
    });

    const normalizeRoutineLevel = (level) => ({
        id: level.id,
        label: level.label || level.name || "",
        name: level.name || level.label || "",
        score: Number(level.score) || 0,
        isActive: level.isActive !== false,
        sortOrder: Number(level.sortOrder) || 0,
    });

    const normalizeRoutineAnswer = (answer) => ({
        id: answer.id,
        questionId: answer.questionId,
        userId: answer.subjectUserId || answer.userId,
        subjectUserId: answer.subjectUserId || answer.userId,
        evaluatorId: answer.evaluatorId || null,
        periodId: answer.periodId,
        levelId: answer.levelId,
        note: answer.note || "",
        answeredAt: answer.answeredAt || null,
        date: answer.answeredAt ? jalaliDateFromGregorian(new Date(answer.answeredAt)) : "",
        question: answer.question ? normalizeRoutineQuestion(answer.question) : null,
        level: answer.level ? normalizeRoutineLevel(answer.level) : null,
    });

    const routineApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");

        const response = await fetch(`${API_BASE_URL}/api/routine${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });

        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || `Routine request failed with status ${response.status}.`);
        }
        return payload;
    };

    const setRoutineQuestionsForScope = (scope, rows) => {
        const normalized = rows.map(normalizeRoutineQuestion);
        if (scope === "team") setRoutineQuestions(normalized);
        if (scope === "department") setDeptRoutineQuestions(normalized);
        if (scope === "org_unit") {
            const current = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
            if (String(current?.role || "").toLowerCase() === "deputy") setDeptRoutineQuestions(normalized);
            else setRoutineQuestions(normalized);
        }
        if (scope === "organization") setOrgRoutineQuestions(normalized);
    };

    const setRoutineAnswersForScope = (scope, rows) => {
        const normalized = rows.map(normalizeRoutineAnswer);
        if (scope === "team") setRoutineAnswers(normalized);
        if (scope === "department") setDeptRoutineAnswers(normalized);
        if (scope === "org_unit") {
            const current = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
            if (String(current?.role || "").toLowerCase() === "deputy") setDeptRoutineAnswers(normalized);
            else setRoutineAnswers(normalized);
        }
        if (scope === "organization") setOrgRoutineAnswers(normalized);
    };

    const loadRoutineData = async () => {
        const token = sessionStorage.getItem("cvi_token");
        const active = periods.find(period => period.active && period.status === "active") || null;

        if (!token) {
            setRoutineTargets([]);
            setRoutineLoading(false);
            setRoutineError("");
            setRoutineReady(false);
            return;
        }
        if (!baseDataReady) {
            setRoutineReady(false);
            return;
        }

        let sessionUser = null;
        try {
            sessionUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
        } catch { }
        const directoryUser = users.find(item => item.id === sessionUser?.id);
        if (directoryUser) sessionUser = { ...sessionUser, ...directoryUser };

        const role = String(sessionUser?.role || "").toLowerCase();
        setRoutineLoading(true);
        setRoutineError("");
        setRoutineReady(false);

        try {
            const levels = await routineApiRequest("/levels");
            setRoutineLevels(levels.map(normalizeRoutineLevel));

            if (role === "admin") {
                const adminQuestions = await adminApiRequest("/routine-questions");
                const normalizedQuestions = adminQuestions.map(normalizeRoutineQuestion);
                setRoutineQuestions(normalizedQuestions.filter(question => question.scope === "org_unit"));
                setDeptRoutineQuestions([]);
                setOrgRoutineQuestions(normalizedQuestions.filter(question => question.scope === "organization"));
                setRoutineAnswers([]);
                setDeptRoutineAnswers([]);
                setOrgRoutineAnswers([]);
                setRoutineTargets([]);
                return;
            }

            if (!active) {
                setRoutineTargets([]);
                return;
            }

            const evaluatorScope = sessionUser?.orgUnitId && ["manager","deputy"].includes(role) ? "org_unit"
                : role === "manager" ? "team"
                    : role === "deputy" ? "department"
                        : role === "ceo" ? "organization" : null;

            if (evaluatorScope) {
                const [questions, targets, answers] = await Promise.all([
                    routineApiRequest("/questions"),
                    routineApiRequest("/targets"),
                    routineApiRequest(`/answers?periodId=${encodeURIComponent(active.id)}`),
                ]);

                setRoutineQuestionsForScope(evaluatorScope, questions);
                setRoutineAnswersForScope(evaluatorScope, answers);
                setRoutineTargets(targets.map(target => ({
                    id: target.id,
                    name: target.name,
                    role: String(target.role || "").toLowerCase(),
                    teamId: target.teamId ?? null,
                    deptId: target.departmentId ?? null,
                    departmentId: target.departmentId ?? null,
                    orgUnitId: target.orgUnitId ?? null,
                    managerId: target.managerId ?? null,
                })));
            } else {
                setRoutineTargets([]);
            }

            const mine = await routineApiRequest(`/mine?periodId=${encodeURIComponent(active.id)}`);
            const myAnswers = (mine.answers || []).map(normalizeRoutineAnswer);
            const myQuestions = myAnswers.map(answer => answer.question).filter(Boolean);
            const myScope = myQuestions[0]?.scope || null;

            if (myScope) {
                if (myScope !== evaluatorScope) {
                    setRoutineQuestionsForScope(myScope, myQuestions);
                    setRoutineAnswersForScope(myScope, myAnswers);
                } else if (!evaluatorScope) {
                    setRoutineQuestionsForScope(myScope, myQuestions);
                    setRoutineAnswersForScope(myScope, myAnswers);
                }
            }
        } catch (error) {
            console.error("Routine data load failed:", error);
            setRoutineError("دریافت اطلاعات ارزیابی وظایف مستمر انجام نشد. دوباره تلاش کنید");
        } finally {
            setRoutineReady(true);
            setRoutineLoading(false);
        }
    };

    const saveRoutineAnswerApi = async (questionId, subjectUserId, levelId, note = "") => {
        const active = periods.find(period => period.active && period.status === "active") || null;
        if (!active) throw new Error("No active period.");

        const saved = await routineApiRequest("/answers", {
            method: "POST",
            body: JSON.stringify({
                questionId,
                subjectUserId,
                periodId: active.id,
                levelId,
                note: note?.trim() || null,
            }),
        });

        const normalized = normalizeRoutineAnswer(saved);
        const scope = normalized.question?.scope || null;
        let updater = scope === "team" ? setRoutineAnswers
            : scope === "department" ? setDeptRoutineAnswers
                : scope === "organization" ? setOrgRoutineAnswers : null;
        if (scope === "org_unit") {
            let current = null;
            try { current = JSON.parse(sessionStorage.getItem("cvi_user") || "null"); } catch { current = null; }
            updater = String(current?.role || "").toLowerCase() === "deputy" ? setDeptRoutineAnswers : setRoutineAnswers;
        }

        if (updater) {
            updater(prev => {
                const exists = prev.some(answer => answer.id === normalized.id || (
                    answer.questionId === normalized.questionId &&
                    answer.userId === normalized.userId &&
                    answer.periodId === normalized.periodId
                ));
                if (!exists) return [...prev, normalized];
                return prev.map(answer =>
                    answer.id === normalized.id || (
                        answer.questionId === normalized.questionId &&
                        answer.userId === normalized.userId &&
                        answer.periodId === normalized.periodId
                    ) ? normalized : answer
                );
            });
        }

        return normalized;
    };

    const normalizePeerQuestion = (question) => ({
        id: question.id,
        label: question.label || question.text || "",
        text: question.text || question.label || "",
        weight: Number(question.weight) || 0,
        isActive: question.isActive !== false,
        sortOrder: Number(question.sortOrder) || 0,
    });

    const normalizePeerConfig = (config) => ({
        id: config.id,
        userId: config.userId || config.subjectUserId,
        subjectUserId: config.subjectUserId || config.userId,
        enabled: Boolean(config.enabled),
        visibility: String(config.visibility || "anonymous").toLowerCase() === "identified" ? "identified" : "anonymous",
        minResponses: Math.max(1, Number(config.minResponses) || 3),
        reviewerIds: Array.isArray(config.reviewerIds) ? config.reviewerIds : [],
    });

    const normalizePeerAssignment = (assignment) => ({
        id: assignment.id,
        periodId: assignment.periodId,
        subjectUserId: assignment.subjectUserId,
        reviewerId: assignment.reviewerId,
        status: String(assignment.status || "").toLowerCase(),
        assignedAt: assignment.assignedAt || null,
        completedAt: assignment.completedAt || null,
        subjectUser: assignment.subjectUser ? {
            id: assignment.subjectUser.id,
            name: assignment.subjectUser.name,
            role: String(assignment.subjectUser.role || "").toLowerCase(),
            teamId: assignment.subjectUser.teamId ?? null,
            deptId: assignment.subjectUser.departmentId ?? null,
            departmentId: assignment.subjectUser.departmentId ?? null,
        } : null,
        period: assignment.period ? {
            id: assignment.period.id,
            label: assignment.period.name,
            name: assignment.period.name,
            status: String(assignment.period.status || "").toLowerCase(),
        } : null,
        review: assignment.review ? {
            id: assignment.review.id,
            submittedAt: assignment.review.submittedAt || null,
            answers: (assignment.review.answers || []).map(answer => ({
                id: answer.id,
                questionId: answer.questionId,
                score: Number(answer.score),
                note: answer.note || "",
            })),
        } : null,
    });

    const normalizePeerResult = (result) => result ? ({
        period: result.period ? {
            id: result.period.id,
            label: result.period.name,
            name: result.period.name,
            status: String(result.period.status || "").toLowerCase(),
        } : null,
        subjectUserId: result.subjectUserId,
        config: result.config ? {
            enabled: Boolean(result.config.enabled),
            visibility: String(result.config.visibility || "anonymous").toLowerCase(),
            minResponses: Number(result.config.minResponses) || 1,
        } : null,
        summary: {
            assignedCount: Number(result.summary?.assignedCount) || 0,
            responseCount: Number(result.summary?.responseCount) || 0,
            enoughResponses: Boolean(result.summary?.enoughResponses),
            overallScore: result.summary?.overallScore == null ? null : Number(result.summary.overallScore),
        },
        questions: (result.questions || []).map(question => ({
            questionId: question.questionId,
            text: question.text || "",
            label: question.text || "",
            weight: Number(question.weight) || 0,
            responseCount: Number(question.responseCount) || 0,
            averageScore: question.averageScore == null ? null : Number(question.averageScore),
        })),
    }) : null;

    const peerApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");

        const response = await fetch(`${API_BASE_URL}/api/peer${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });

        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.message || `Peer feedback request failed with status ${response.status}.`);
        }
        return payload;
    };

    const loadPeerData = async () => {
        const token = sessionStorage.getItem("cvi_token");
        const active = periods.find(period => period.active && period.status === "active") || null;

        if (!token) {
            setPeerAssignments([]);
            setPeerResult(null);
            setPeerLoading(false);
            setPeerError("");
            setPeerReady(false);
            return;
        }
        if (!baseDataReady) {
            setPeerReady(false);
            return;
        }

        setPeerLoading(true);
        setPeerError("");
        setPeerReady(false);

        try {
            const questions = await peerApiRequest("/questions");
            setPeerQuestions(questions.map(normalizePeerQuestion));

            let sessionUser = null;
            try { sessionUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null"); } catch { sessionUser = null; }
            if (String(sessionUser?.role || "").toLowerCase() === "admin") {
                const configs = await peerApiRequest("/admin/configs");
                setPeerConfigs(configs.map(normalizePeerConfig));
            }

            if (!active) {
                setPeerAssignments([]);
                setPeerResult(null);
                setPeerReady(true);
                return;
            }

            const [assignments, mine] = await Promise.all([
                peerApiRequest(`/assignments?periodId=${encodeURIComponent(active.id)}`),
                peerApiRequest(`/mine?periodId=${encodeURIComponent(active.id)}`),
            ]);

            setPeerAssignments(assignments.map(normalizePeerAssignment));
            setPeerResult(normalizePeerResult(mine));
            setPeerReady(true);
        } catch (error) {
            console.error("Peer feedback load failed:", error);
            setPeerError("دریافت اطلاعات بازخورد همکاران انجام نشد. دوباره تلاش کنید");
        } finally {
            setPeerReady(true);
            setPeerLoading(false);
        }
    };

    const submitPeerReviewApi = async (assignmentId, answers) => {
        const payload = Object.entries(answers).map(([questionId, score]) => ({
            questionId,
            score: Number(score),
        }));

        const saved = await peerApiRequest(`/assignments/${assignmentId}/submit`, {
            method: "POST",
            body: JSON.stringify({ answers: payload }),
        });

        const normalized = normalizePeerAssignment(saved);
        setPeerAssignments(prev => prev.map(item => item.id === normalized.id ? normalized : item));

        const active = periods.find(period => period.active && period.status === "active") || null;
        if (active) {
            const mine = await peerApiRequest(`/mine?periodId=${encodeURIComponent(active.id)}`);
            setPeerResult(normalizePeerResult(mine));
        }

        return normalized;
    };

    const normalizeScheduleTemplate = (template) => ({
        id: template.id,
        label: template.label || template.name || "",
        name: template.name || template.label || "",
        frequency: String(template.frequency || "").toLowerCase(),
        openOffsetDays: Number(template.openOffsetDays) || 0,
        submitAfterDays: Number(template.submitAfterDays) || 0,
        approveAfterDays: Number(template.approveAfterDays) || 0,
        lockAfterDays: Number(template.lockAfterDays) || 0,
        reminderDays: Number(template.reminderDays) || 0,
        isActive: template.isActive !== false,
    });

    const normalizeApiIndicator = (indicator) => ({
        id: indicator.id,
        label: indicator.label || indicator.name || "",
        name: indicator.name || indicator.label || "",
        level: String(indicator.level || "").toLowerCase() === "department" ? "dept"
            : String(indicator.level || "").toLowerCase() === "organization" ? "org"
                : String(indicator.level || "").toLowerCase(),
        teamId: indicator.teamId ?? indicator.team?.id ?? null,
        deptId: indicator.departmentId ?? indicator.department?.id ?? null,
        departmentId: indicator.departmentId ?? indicator.department?.id ?? null,
        orgUnitId: indicator.orgUnitId ?? indicator.orgUnit?.id ?? null,
        orgUnitLabel: indicator.orgUnit?.name || indicator.orgUnit?.label || "",
        orgUnit: indicator.orgUnit || null,
        unit: indicator.unit || "",
        weight: Number(indicator.weight) || 0,
        ownerId: indicator.ownerId,
        registrarId: indicator.registrarId,
        approverId: indicator.approverId,
        scheduleTemplateId: indicator.scheduleTemplateId,
        aggregationMethod: String(indicator.aggregationMethod || "").toLowerCase(),
        formulaTemplateId: indicator.formulaTemplateId || "standard:higher",
        requiresEvidence: Boolean(indicator.requiresEvidence),
        isActive: indicator.isActive !== false,
        owner: indicator.owner || null,
        registrar: indicator.registrar || null,
        approver: indicator.approver || null,
        team: indicator.team || null,
        department: indicator.department || null,
        scheduleTemplate: indicator.scheduleTemplate ? normalizeScheduleTemplate(indicator.scheduleTemplate) : null,
    });

    const normalizeApiIndicatorValue = (value) => {
        const firstAttachment = value.attachments?.[0] || null;
        const latestRevision = (value.history || []).slice().reverse().find(item =>
            String(item.toStatus || "").toLowerCase() === "revision_requested" ||
            String(item.eventType || "").toLowerCase() === "revision_requested");
        return {
            id: value.id,
            indicatorId: value.indicatorId,
            periodId: value.periodId,
            occurrenceId: value.occurrenceId === "period" ? null : (value.occurrenceId ?? null),
            target: value.target == null ? null : Number(value.target),
            actual: value.actual == null ? null : Number(value.actual),
            status: String(value.status || "not_started").toLowerCase(),
            submittedBy: value.submittedById ?? value.submittedBy?.id ?? null,
            approvedBy: value.approvedById ?? value.approvedBy?.id ?? null,
            note: value.note || "",
            deviationReason: value.deviationReason || "",
            correctiveAction: value.correctiveAction || "",
            reviewNote: latestRevision?.note || "",
            scheduleSnapshot: value.scheduleSnapshot || null,
            submittedAt: value.submittedAt || null,
            approvedAt: value.approvedAt || null,
            lockedAt: value.lockedAt || null,
            doc: firstAttachment?.fileName || "",
            docDataUrl: "",
            attachments: value.attachments || [],
            history: (value.history || []).map(item => ({
                id: item.id,
                type: String(item.eventType || item.toStatus || "").toLowerCase(),
                eventType: String(item.eventType || "").toLowerCase(),
                fromStatus: item.fromStatus ? String(item.fromStatus).toLowerCase() : null,
                toStatus: item.toStatus ? String(item.toStatus).toLowerCase() : null,
                oldValue: item.oldValue,
                newValue: item.newValue,
                note: item.note || "",
                actorId: item.actorId || item.actor?.id || null,
                actorRole: item.actor?.role ? String(item.actor.role).toLowerCase() : null,
                by: item.actor?.name || "سیستم",
                date: item.createdAt ? jalaliDateFromGregorian(new Date(item.createdAt)) : "",
            })),
        };
    };

    const indicatorApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api/indicators${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });
        if (response.status === 401) {
            handleUnauthorized();
            throw new Error("Session expired.");
        }
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || `Indicator request failed with status ${response.status}.`);
        return payload;
    };

    const uploadIndicatorFileApi = async (valueId, file) => {
        if (!file) return null;
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(`${API_BASE_URL}/api/indicators/values/${valueId}/files`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        if (response.status === 401) handleUnauthorized();
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || `Evidence upload failed with status ${response.status}.`);
        return payload;
    };

    const mergeIndicatorValue = (value) => {
        const normalized = normalizeApiIndicatorValue(value);
        setIndicatorData(prev => {
            const exists = prev.some(item => item.id === normalized.id);
            return exists ? prev.map(item => item.id === normalized.id ? normalized : item) : [...prev, normalized];
        });
        return normalized;
    };

    const loadIndicatorData = async () => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            setIndicatorDefs([]);
            setIndicatorData([]);
            setScheduleTemplates([]);
            setIndicatorLoading(false);
            setIndicatorError("");
            setIndicatorReady(false);
            return;
        }
        setIndicatorLoading(true);
        setIndicatorError("");
        setIndicatorReady(false);
        try {
            const [definitions, templates, values] = await Promise.all([
                indicatorApiRequest("/"),
                indicatorApiRequest("/schedule-templates"),
                indicatorApiRequest("/values/list"),
            ]);
            setIndicatorDefs(definitions.map(normalizeApiIndicator));
            setScheduleTemplates(templates.map(normalizeScheduleTemplate));
            setIndicatorData(values.map(normalizeApiIndicatorValue));
            setIndicatorReady(true);
        } catch (error) {
            console.error("Indicator data load failed:", error);
            setIndicatorError("دریافت اطلاعات شاخص‌های مشارکت جمعی انجام نشد. دوباره تلاش کنید");
        } finally {
            setIndicatorReady(true);
            setIndicatorLoading(false);
        }
    };

    const indicatorDefinitionPayload = (form) => ({
        name: String(form.label || form.name || "").trim(),
        level: form.level === "dept" ? "department" : form.level === "org" ? "organization" : form.level,
        teamId: form.level === "team" ? (form.teamId || null) : null,
        departmentId: form.level === "dept" ? (form.deptId || form.departmentId || null) : null,
        orgUnitId: form.level === "org_unit" ? (form.orgUnitId || null) : null,
        unit: String(form.unit || "").trim(),
        weight: Number(form.weight) || 0,
        ownerId: form.ownerId,
        registrarId: form.registrarId,
        approverId: form.approverId,
        scheduleTemplateId: form.scheduleTemplateId,
        aggregationMethod: form.aggregationMethod,
        formulaTemplateId: form.formulaTemplateId || "standard:higher",
        requiresEvidence: form.requiresEvidence === true || String(form.requiresEvidence) === "true",
    });

    const createIndicatorApi = async (form) => {
        const saved = await indicatorApiRequest("/", { method: "POST", body: JSON.stringify(indicatorDefinitionPayload(form)) });
        const normalized = normalizeApiIndicator(saved);
        setIndicatorDefs(prev => [...prev.filter(item => item.id !== normalized.id), normalized]);
        return normalized;
    };

    const updateIndicatorApi = async (id, form) => {
        const saved = await indicatorApiRequest(`/${id}`, { method: "PATCH", body: JSON.stringify(indicatorDefinitionPayload(form)) });
        const normalized = normalizeApiIndicator(saved);
        setIndicatorDefs(prev => prev.map(item => item.id === id ? normalized : item));
        return normalized;
    };

    const archiveIndicatorApi = async (id) => {
        const current = indicatorDefs.find(item => item.id === id);
        if (!current) return null;
        const saved = await indicatorApiRequest(`/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ ...indicatorDefinitionPayload(current), isActive: false }),
        });
        setIndicatorDefs(prev => prev.filter(item => item.id !== id));
        return normalizeApiIndicator(saved);
    };

    const createScheduleTemplateApi = async (form) => {
        const saved = await indicatorApiRequest("/schedule-templates", {
            method: "POST",
            body: JSON.stringify({
                name: String(form.label || form.name || "").trim(),
                frequency: form.frequency,
                openOffsetDays: Number(form.openOffsetDays) || 0,
                submitAfterDays: Number(form.submitAfterDays) || 0,
                approveAfterDays: Number(form.approveAfterDays) || 0,
                lockAfterDays: Number(form.lockAfterDays) || 0,
                reminderDays: Number(form.reminderDays) || 0,
            }),
        });
        const normalized = normalizeScheduleTemplate(saved);
        setScheduleTemplates(prev => [...prev.filter(item => item.id !== normalized.id), normalized]);
        return normalized;
    };

    const setIndicatorTargetApi = async (indicatorId, periodId, target, occurrenceId = "period") => {
        const saved = await indicatorApiRequest(`/${indicatorId}/periods/${periodId}/target`, {
            method: "PUT",
            body: JSON.stringify({ target: Number(target), occurrenceId: occurrenceId || "period" }),
        });
        return mergeIndicatorValue(saved);
    };

    const saveIndicatorValueApi = async (indicatorId, periodId, form, submit = false, occurrenceId = "period") => {
        const savedDraft = await indicatorApiRequest(`/${indicatorId}/periods/${periodId}/actual`, {
            method: "PUT",
            body: JSON.stringify({
                actual: Number(form.actual),
                occurrenceId: occurrenceId || "period",
                note: form.note?.trim() || null,
                deviationReason: form.deviationReason?.trim() || null,
                correctiveAction: form.correctiveAction?.trim() || null,
            }),
        });
        let normalized = mergeIndicatorValue(savedDraft);
        if (form._file) await uploadIndicatorFileApi(savedDraft.id, form._file);
        if (submit) {
            const submitted = await indicatorApiRequest(`/values/${savedDraft.id}/submit`, {
                method: "POST",
                body: JSON.stringify({ note: form.note?.trim() || null }),
            });
            normalized = mergeIndicatorValue(submitted);
        }
        return normalized;
    };

    const reviewIndicatorValueApi = async (valueId, decision, note = "") => {
        const path = decision === "approved" ? "approve" : "revision";
        const saved = await indicatorApiRequest(`/values/${valueId}/${path}`, {
            method: "POST",
            body: JSON.stringify({ note: note?.trim() || null }),
        });
        return mergeIndicatorValue(saved);
    };

    const periodApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api/periods${path}`, {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });
        let payload = null;
        try { payload = await response.json(); } catch { payload = null; }
        if (response.status === 401) {
            sessionStorage.removeItem("cvi_user");
            sessionStorage.removeItem("cvi_token");
            window.dispatchEvent(new Event("cvi-session-expired"));
        }
        if (!response.ok) {
            const error = new Error(payload?.message || `Period request failed with status ${response.status}.`);
            error.status = response.status;
            error.blockers = payload?.blockers || null;
            throw error;
        }
        return payload;
    };

    const normalizePeriodSnapshot = (snapshot) => ({
        id: snapshot.id,
        periodId: snapshot.periodId,
        label: snapshot.label,
        userId: snapshot.userId,
        individual: Number(snapshot.individualScore) || 0,
        org: Number(snapshot.organizationalScore) || 0,
        cvi: Number(snapshot.cviScore) || 0,
        nonRoutine: snapshot.nonRoutineScore == null ? null : Number(snapshot.nonRoutineScore),
        routine: snapshot.routineScore == null ? null : Number(snapshot.routineScore),
        peerFeedback: snapshot.peerFeedbackScore == null ? null : Number(snapshot.peerFeedbackScore),
        activityGroupScores: snapshot.activityGroupScores || {},
        activityCounts: snapshot.activityCounts || {},
        routineCriteria: snapshot.routineCriteria || [],
        status: String(snapshot.status || "final").toLowerCase(),
        locked: Boolean(snapshot.locked),
        formulaVersion: snapshot.formulaVersion,
        weights: snapshot.weights || {},
        calculatedAt: snapshot.calculatedAt,
        version: Number(snapshot.version) || 1,
    });

    const loadPeriodLifecycleData = async (periodList = periods) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token || !periodList?.length) {
            setPeriodSnapshots([]);
            setPeriodAudit([]);
            return;
        }
        let sessionUser = null;
        try { sessionUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null"); } catch { sessionUser = null; }
        const role = String(sessionUser?.role || "").toLowerCase();
        const canSeeAll = ["admin", "ceo"].includes(role);
        try {
            const snapshotGroups = await Promise.all(periodList.map(period =>
                periodApiRequest(`/${period.id}/snapshots${canSeeAll ? "" : "/me"}`)
                    .catch(error => error.status === 404 ? [] : Promise.reject(error))
            ));
            setPeriodSnapshots(snapshotGroups.flat().map(normalizePeriodSnapshot));

            if (canSeeAll) {
                const auditGroups = await Promise.all(periodList.map(period =>
                    periodApiRequest(`/${period.id}/audit`).catch(error => error.status === 404 ? [] : Promise.reject(error))
                ));
                setPeriodAudit(auditGroups.flat());
            } else {
                setPeriodAudit([]);
            }
        } catch (error) {
            console.error("Period lifecycle data load failed:", error);
        }
    };

    const adminApiRequest = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api/admin${path}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        if (response.status === 401) { handleUnauthorized(); throw new Error("Session expired."); }
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) { const error = new Error(payload.message || `Admin request failed with status ${response.status}.`); error.blockers = payload.blockers || null; throw error; }
        return payload;
    };

    const reloadAdminBaseData = async () => { await Promise.all([loadBaseData(), loadRoutineData()]); };

    const updateOrganizationSettingsApi = async (patch) => {
        const saved = await adminApiRequest("/organization/settings", { method: "PATCH", body: JSON.stringify(patch) });
        setOrgSettings(prev => ({
            ...prev,
            name: patch.organizationName ?? prev.name,
            logo: patch.logoUrl !== undefined ? patch.logoUrl : prev.logo,
            defaultLanguage: saved.defaultLanguage ?? patch.defaultLanguage ?? prev.defaultLanguage,
            accentColor: saved.accentColor ?? patch.accentColor ?? prev.accentColor,
            timezone: saved.timezone ?? patch.timezone ?? prev.timezone,
            dateFormat: saved.dateFormat ?? patch.dateFormat ?? prev.dateFormat,
            calendar: saved.calendar ?? patch.calendar ?? prev.calendar,
        }));
        return saved;
    };

    const createDepartmentApi = async f => { await adminApiRequest("/departments", { method: "POST", body: JSON.stringify({ name: f.label }) }); await loadBaseData(); };
    const updateDepartmentApi = async (id, f) => { await adminApiRequest(`/departments/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label }) }); await loadBaseData(); };
    const archiveDepartmentApi = async id => { await adminApiRequest(`/departments/${id}`, { method: "DELETE" }); await loadBaseData(); };
    const createTeamApi = async f => { await adminApiRequest("/teams", { method: "POST", body: JSON.stringify({ name: f.label, departmentId: f.deptId }) }); await loadBaseData(); };
    const updateTeamApi = async (id, f) => { await adminApiRequest(`/teams/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label, departmentId: f.deptId }) }); await loadBaseData(); };
    const archiveTeamApi = async id => { await adminApiRequest(`/teams/${id}`, { method: "DELETE" }); await loadBaseData(); };

    const positionPayload = f => ({ name: f.label, teamWeight: Number(f.wTeam) || 0, departmentWeight: Number(f.wDept) || 0, organizationWeight: Number(f.wOrg) || 0, tasksActivitiesWeight: Number(f.wTasksActivities) || 0, nonRoutineWeight: Number(f.wNonRoutine) || 0, routineWeight: Number(f.wRoutine) || 0, peerFeedbackWeight: Number(f.wPeerFeedback) || 0, responsibilityWeight: 100, individualGoalWeight: Number(f.wGoals) || 0 });
    const createPositionApi = async f => { const row = await adminApiRequest("/positions", { method: "POST", body: JSON.stringify(positionPayload(f)) }); await loadBaseData(); return row; };
    const updatePositionApi = async (id, f) => { const row = await adminApiRequest(`/positions/${id}`, { method: "PATCH", body: JSON.stringify(positionPayload(f)) }); await loadBaseData(); return row; };
    const archivePositionApi = async id => { await adminApiRequest(`/positions/${id}`, { method: "DELETE" }); await loadBaseData(); };

    const userPayload = f => ({ name: f.name, employeeCode: f.employeeCode, nationalId: f.nationalId, email: f.email, username: f.username, password: f.password || undefined, role: f.role, positionId: f.posId || null, orgUnitId: f.orgUnitId || null, departmentId: f.orgUnitId ? null : (f.deptId || null), teamId: f.orgUnitId ? null : (f.teamId || null), managerId: f.managerId || null });
    const createUserApi = async f => { await adminApiRequest("/users", { method: "POST", body: JSON.stringify(userPayload(f)) }); await loadBaseData(); };
    const updateUserApi = async (id, f) => { await adminApiRequest(`/users/${id}`, { method: "PATCH", body: JSON.stringify(userPayload(f)) }); await loadBaseData(); };
    const archiveUserApi = async id => { await adminApiRequest(`/users/${id}`, { method: "DELETE" }); await loadBaseData(); };

    const groupPayload = f => ({ name: f.label, maxScore: Number(f.maxScore) || 0, icon: f.icon || null, subtypes: (f.subsText || "").split(",").map(x => x.trim()).filter(Boolean) });
    const createActivityGroupApi = async f => { await adminApiRequest("/activity-groups", { method: "POST", body: JSON.stringify(groupPayload(f)) }); await loadBaseData(); };
    const updateActivityGroupApi = async (id, f) => { await adminApiRequest(`/activity-groups/${id}`, { method: "PATCH", body: JSON.stringify(groupPayload(f)) }); await loadBaseData(); };
    const archiveActivityGroupApi = async id => { await adminApiRequest(`/activity-groups/${id}`, { method: "DELETE" }); await loadBaseData(); };
    const createImpactLevelApi = async f => { await adminApiRequest("/impact-levels", { method: "POST", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadBaseData(); };
    const updateImpactLevelApi = async (id, f) => { await adminApiRequest(`/impact-levels/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadBaseData(); };
    const archiveImpactLevelApi = async id => { await adminApiRequest(`/impact-levels/${id}`, { method: "DELETE" }); await loadBaseData(); };
    const createRoutineLevelApi = async f => { await adminApiRequest("/routine-levels", { method: "POST", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadRoutineData(); };
    const updateRoutineLevelApi = async (id, f) => { await adminApiRequest(`/routine-levels/${id}`, { method: "PATCH", body: JSON.stringify({ name: f.label, score: Number(f.score) || 0 }) }); await loadRoutineData(); };
    const archiveRoutineLevelApi = async id => { await adminApiRequest(`/routine-levels/${id}`, { method: "DELETE" }); await loadRoutineData(); };

    const loadBaseData = async () => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            setUsers([]);
            setTeams([]);
            setDepartments([]);
            setOrgUnits([]);
            setPositions([]);
            setPeriods([]);
            setBaseDataLoading(false);
            setBaseDataError("");
            setBaseDataReady(false);
            return;
        }

        setBaseDataLoading(true);
        setBaseDataError("");
        setBaseDataReady(false);

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const endpoints = ["users", "teams", "departments", "org-units", "positions", "periods", "organization", "activity-groups", "impact-levels"];
            const responses = await Promise.all(
                endpoints.map(endpoint => fetch(`${API_BASE_URL}/api/${endpoint}`, { headers }))
            );

            const unauthorized = responses.some(response => response.status === 401);
            if (unauthorized) {
                sessionStorage.removeItem("cvi_user");
                sessionStorage.removeItem("cvi_token");
                window.dispatchEvent(new Event("cvi-session-expired"));
                throw new Error("Session expired.");
            }

            const failed = responses.find(response => !response.ok);
            if (failed) throw new Error(`Base data request failed with status ${failed.status}.`);

            const [apiUsers, apiTeams, apiDepartments, apiOrgUnitsResponse, apiPositions, apiPeriods, apiOrganization, apiActivityGroups, apiImpactLevels] =
                await Promise.all(responses.map(response => response.json()));
            const apiOrgUnits = Array.isArray(apiOrgUnitsResponse) ? apiOrgUnitsResponse : (apiOrgUnitsResponse?.items || []);

            setUsers(apiUsers.map(user => ({
                id: user.id,
                name: user.name,
                role: String(user.role || "").toLowerCase(),
                posId: user.position?.id ?? null,
                teamId: user.team?.id ?? null,
                deptId: user.department?.id ?? null,
                orgUnitId: user.orgUnit?.id ?? null,
                orgUnit: user.orgUnit ?? null,
                managerId: user.managerId ?? null,
                username: user.username,
                employeeCode: user.employeeCode ?? "",
                nationalId: user.nationalId ?? "",
                email: user.email,
                avatarUrl: user.avatarUrl ?? null,
                preferredLanguage: user.preferredLanguage ?? null,
            })));

            setTeams(apiTeams.map(team => ({
                id: team.id,
                label: team.name,
                deptId: team.departmentId ?? team.department?.id ?? null,
            })));

            setDepartments(apiDepartments.map(department => ({
                id: department.id,
                label: department.name,
            })));

            setOrgUnits(apiOrgUnits.map(unit => ({
                id: unit.id,
                label: unit.name,
                name: unit.name,
                type: unit.type || "",
                parentId: unit.parentId ?? null,
                managerUserId: unit.managerUserId ?? unit.manager?.id ?? null,
                isActive: unit.isActive !== false,
            })));

            setPositions(apiPositions.map(position => ({
                id: position.id,
                label: position.name,
                wTeam: Number(position.teamWeight) || 0,
                wDept: Number(position.departmentWeight) || 0,
                wOrg: Number(position.organizationWeight) || 0,
                wTasksActivities: Number(position.tasksActivitiesWeight ?? 0),
                wNonRoutine: Number(position.nonRoutineWeight) || 0,
                wRoutine: Number(position.routineWeight) || 0,
                wPeerFeedback: Number(position.peerFeedbackWeight) || 0,
                wResponsibilities: 100,
                wGoals: Number(position.individualGoalWeight ?? 0),
            })));

            setPeriods(apiPeriods.map(period => ({
                id: period.id,
                label: period.name,
                start: jalaliDateFromGregorian(new Date(period.startDate)),
                end: jalaliDateFromGregorian(new Date(period.endDate)),
                active: Boolean(period.isActive),
                status: String(period.status || "").toLowerCase(),
                closedAt: period.closedAt ?? null,
                reopenedAt: period.reopenedAt ?? null,
            })));

            setActivityGroups(apiActivityGroups.map(group => ({ id: group.id, label: group.name, maxScore: Number(group.maxScore) || 0, icon: group.icon || "⭐", subs: (group.subtypes || []).map(sub => sub.name) })));
            setImpactLevels(apiImpactLevels.map(level => ({ id: level.id, label: level.name, score: Number(level.score) || 0 })));

            if (apiOrganization?.settings) {
                setOrgSettings(prev => ({
                    ...prev,
                    wNonRoutine: Number(apiOrganization.settings.defaultNonRoutineWeight) || 0,
                    wRoutine: Number(apiOrganization.settings.defaultRoutineWeight) || 0,
                    wPeerFeedback: Number(apiOrganization.settings.defaultPeerFeedbackWeight) || 0,
                    name: apiOrganization.name || prev.name || "",
                    logo: apiOrganization.logoUrl ?? prev.logo,
                    defaultLanguage: apiOrganization.settings.defaultLanguage || prev.defaultLanguage || "en",
                    accentColor: apiOrganization.settings.accentColor || prev.accentColor || "#00C8E6",
                    timezone: apiOrganization.settings.timezone || prev.timezone || "UTC",
                    dateFormat: apiOrganization.settings.dateFormat || prev.dateFormat || "YYYY-MM-DD",
                    calendar: apiOrganization.settings.calendar || prev.calendar || "gregorian",
                }));
            }
            setBaseDataReady(true);
        } catch (error) {
            console.error("Base data load failed:", error);
            setBaseDataError("دریافت اطلاعات انجام نشد. دوباره تلاش کنید");
        } finally {
            setBaseDataLoading(false);
        }
    };

    useEffect(() => {
        loadBaseData();
        const handleAuthChanged = () => loadBaseData();
        window.addEventListener("cvi-auth-changed", handleAuthChanged);
        return () => window.removeEventListener("cvi-auth-changed", handleAuthChanged);
    }, []);

    useEffect(() => {
        loadActivities();
        const handleAuthChanged = () => loadActivities();
        window.addEventListener("cvi-auth-changed", handleAuthChanged);
        return () => window.removeEventListener("cvi-auth-changed", handleAuthChanged);
    }, []);

    useEffect(() => {
        loadRoutineData();
    }, [periods, users, baseDataReady]);

    useEffect(() => {
        loadPeerData();
    }, [periods, baseDataReady]);

    useEffect(() => {
        loadIndicatorData();
        const handleAuthChanged = () => loadIndicatorData();
        window.addEventListener("cvi-auth-changed", handleAuthChanged);
        return () => window.removeEventListener("cvi-auth-changed", handleAuthChanged);
    }, []);

    useEffect(() => {
        loadPeriodLifecycleData(periods);
    }, [periods]);

    const activePeriod = periods.find(p => p.active && p.status === "active") || null;

    useEffect(() => {
        const today = todayJalaliStr();
        setIndicatorData(prev => {
            let changed = false;
            const next = prev.map(data => {
                if (data.status !== "approved") return data;
                const def = indicatorDefs.find(i => i.id === data.indicatorId), period = periods.find(p => p.id === data.periodId);
                const computed = data.scheduleSnapshot || indicatorScheduleOccurrences(def, period, scheduleTemplates, scheduleExceptions)
                    .find(o => o.occurrenceId === data.occurrenceId) || indicatorScheduleOccurrences(def, period, scheduleTemplates, scheduleExceptions)[0];
                if (!computed?.lockDate || daysBetweenJalali(computed.lockDate, today) < 0) return data;
                changed = true;
                return {
                    ...data, status: "locked", lockedAt: currentPersianDateTime(), scheduleSnapshot: data.scheduleSnapshot || computed,
                    history: [...(data.history || []), {
                        type: "locked", toStatus: "locked", date: currentPersianDateTime(), by: "سیستم",
                        oldValue: data.actual, newValue: data.actual, note: "قفل خودکار طبق تقویم شاخص"
                    }]
                };
            });
            if (activePeriod) {
                indicatorDefs.flatMap(def => indicatorScheduleOccurrences(def, activePeriod, scheduleTemplates, scheduleExceptions)
                    .map(schedule => ({ def, schedule }))).forEach(({ def, schedule }) => {
                        const exists = next.some(d => d.indicatorId === def.id && d.periodId === activePeriod.id &&
                            (d.occurrenceId === schedule.occurrenceId || (!d.occurrenceId && schedule.occurrenceId.endsWith(":1"))));
                        if (!exists && daysBetweenJalali(schedule.lockDate, today) >= 0) {
                            changed = true; next.push({
                                id: `${def.id}_${schedule.occurrenceId}_missed`, indicatorId: def.id, periodId: activePeriod.id,
                                occurrenceId: schedule.occurrenceId, status: "closed_without_data", actual: null, target: null,
                                scheduleSnapshot: schedule, history: [{
                                    type: "closed_without_data", toStatus: "closed_without_data",
                                    date: currentPersianDateTime(), by: "سیستم", note: "پنجره ثبت بدون دریافت داده بسته شد"
                                }]
                            });
                        }
                    });
            }
            return changed ? next : prev;
        });
    }, [indicatorDefs, periods, scheduleTemplates, scheduleExceptions]);

    const closePeriod = async (periodId) => {
        const period = periods.find(p => p.id === periodId);
        if (!period || period.status === "locked") return false;

        const status = await periodApiRequest(`/${periodId}/finalization-status`);
        if (!status.ready) {
            const error = new Error("Period has incomplete items and cannot be finalized.");
            error.blockers = status.blockers;
            throw error;
        }

        await periodApiRequest(`/${periodId}/finalize`, { method: "POST", body: JSON.stringify({}) });
        await Promise.all([loadBaseData(), loadIndicatorData()]);
        await loadPeriodLifecycleData(periods);
        return true;
    };

    const reopenPeriod = async (periodId, actorName = "ادمین", reason = "") => {
        if (!reason.trim()) return false;
        await periodApiRequest(`/${periodId}/reopen`, {
            method: "POST",
            body: JSON.stringify({ reason: reason.trim() }),
        });
        await Promise.all([loadBaseData(), loadIndicatorData()]);
        await loadPeriodLifecycleData(periods);
        return true;
    };

    const createPeriodApi = async (form) => {
        const row = await adminApiRequest("/periods", {
            method: "POST",
            body: JSON.stringify({ name: form.label, startDate: form.start, endDate: form.end }),
        });
        await loadBaseData();
        return row;
    };
    const activatePeriodApi = async (id) => {
        const row = await adminApiRequest(`/periods/${id}/activate`, { method: "POST" });
        await loadBaseData();
        return row;
    };
    const createRoutineQuestionApi = async (form) => {
        const row = await adminApiRequest("/routine-questions", { method: "POST", body: JSON.stringify(form) });
        await loadRoutineData();
        return row;
    };
    const updateRoutineQuestionApi = async (id, form) => {
        const row = await adminApiRequest(`/routine-questions/${id}`, { method: "PATCH", body: JSON.stringify(form) });
        await loadRoutineData();
        return row;
    };
    const archiveRoutineQuestionApi = async (id) => {
        const row = await adminApiRequest(`/routine-questions/${id}`, { method: "DELETE" });
        await loadRoutineData();
        return row;
    };
    const createPeerQuestionAdminApi = async (form) => {
        const row = await peerApiRequest("/admin/questions", { method: "POST", body: JSON.stringify(form) });
        await loadPeerData();
        return row;
    };
    const updatePeerQuestionAdminApi = async (id, form) => {
        const row = await peerApiRequest(`/admin/questions/${id}`, { method: "PATCH", body: JSON.stringify(form) });
        await loadPeerData();
        return row;
    };
    const archivePeerQuestionAdminApi = async (id) => {
        const row = await peerApiRequest(`/admin/questions/${id}`, { method: "DELETE" });
        await loadPeerData();
        return row;
    };
    const savePeerConfigAdminApi = async (subjectUserId, config) => {
        const visibility = config.visibility === "identified" ? "IDENTIFIED" : "ANONYMOUS";
        const row = await peerApiRequest(`/admin/subjects/${subjectUserId}/config`, {
            method: "PUT",
            body: JSON.stringify({
                enabled: Boolean(config.enabled), visibility,
                minResponses: Math.max(1, Number(config.minResponses) || 1),
                reviewerIds: config.reviewerIds || [],
            }),
        });
        await loadPeerData();
        return row;
    };
    const createPeerAssignmentsAdminApi = async (subjectUserId, periodId, reviewerIds) => {
        const row = await peerApiRequest(`/admin/subjects/${subjectUserId}/assignments`, {
            method: "POST",
            body: JSON.stringify({ periodId, reviewerIds }),
        });
        await loadPeerData();
        return row;
    };

    const value = {
        users, setUsers, teams, setTeams, departments, setDepartments, orgUnits, setOrgUnits,
        positions, setPositions, activityGroups, setActivityGroups,
        impactLevels, setImpactLevels, indicatorDefs, setIndicatorDefs,
        indicatorData, setIndicatorData, periods, setPeriods, activePeriod,
        activities, setActivities,
        routineLevels, setRoutineLevels, routineQuestions, setRoutineQuestions,
        routineAnswers, setRoutineAnswers, orgSettings, setOrgSettings,
        deptRoutineQuestions, setDeptRoutineQuestions, deptRoutineAnswers, setDeptRoutineAnswers,
        orgRoutineQuestions, setOrgRoutineQuestions, orgRoutineAnswers, setOrgRoutineAnswers,
        periodSnapshots, setPeriodSnapshots, periodAudit, closePeriod, reopenPeriod,
        scheduleTemplates, setScheduleTemplates, scheduleExceptions, setScheduleExceptions,
        peerQuestions, setPeerQuestions, peerConfigs, setPeerConfigs, peerReviews, setPeerReviews,
        peerAssignments, peerResult, peerLoading, peerError, reloadPeerData: loadPeerData, submitPeerReviewApi,
        invalidateSessionData,
        sessionDataReady: baseDataReady && activitiesReady && routineReady && indicatorReady && peerReady,
        baseDataLoading, baseDataError, reloadBaseData: loadBaseData,
        activitiesLoading, activitiesError, reloadActivities: loadActivities,
        createActivityApi, resubmitActivityApi, reviewActivityApi,
        routineTargets, routineLoading, routineError, reloadRoutineData: loadRoutineData,
        saveRoutineAnswerApi,
        indicatorLoading, indicatorError, reloadIndicatorData: loadIndicatorData,
        createIndicatorApi, updateIndicatorApi, archiveIndicatorApi, createScheduleTemplateApi,
        setIndicatorTargetApi, saveIndicatorValueApi, reviewIndicatorValueApi,
        updateOrganizationSettingsApi,
        createDepartmentApi, updateDepartmentApi, archiveDepartmentApi, createTeamApi, updateTeamApi, archiveTeamApi,
        createPositionApi, updatePositionApi, archivePositionApi, createUserApi, updateUserApi, archiveUserApi,
        createActivityGroupApi, updateActivityGroupApi, archiveActivityGroupApi, createImpactLevelApi, updateImpactLevelApi, archiveImpactLevelApi,
        createRoutineLevelApi, updateRoutineLevelApi, archiveRoutineLevelApi,
        createPeriodApi, activatePeriodApi,
        createRoutineQuestionApi, updateRoutineQuestionApi, archiveRoutineQuestionApi,
        createPeerQuestionAdminApi, updatePeerQuestionAdminApi, archivePeerQuestionAdminApi,
        savePeerConfigAdminApi, createPeerAssignmentsAdminApi,
    };
    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

// ── SHARED CALC (منبع واحد محاسبه CVI) ────────────────────────────────────────
function groupLabel(activityGroups, groupId) {
    return activityGroups.find(g => g.id === groupId)?.label || groupId;
}

const ACTIVITY_STATUS = {
    DRAFT: "draft",
    PENDING_REVIEW: "pending_review",
    REVISION_REQUESTED: "revision_requested",
    RESUBMITTED: "resubmitted",
    APPROVED: "approved",
    REJECTED: "rejected",
};

function isReviewableActivity(activity) {
    return [ACTIVITY_STATUS.PENDING_REVIEW, ACTIVITY_STATUS.RESUBMITTED].includes(activity.status);
}

function activityStatusLabel(status) {
    return ({
        draft: "پیش‌نویس", pending_review: "در انتظار بررسی",
        revision_requested: "نیازمند اصلاح", resubmitted: "ارسال مجدد",
        approved: "تأییدشده", rejected: "ردشده"
    })[status] || status || "—";
}

// فقط مدیر مستقیم ثبت‌کننده مجاز به تصمیم‌گیری درباره فعالیت است.
function canReviewActivity(evaluator, activity, users) {
    const owner = users.find(u => u.id === activity.userId);
    return Boolean(owner && owner.managerId === evaluator.id);
}

function currentPersianDateTime() {
    try {
        return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
            year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
        }).format(new Date());
    } catch {
        return new Date().toLocaleString("fa-IR");
    }
}

function activityTimeline(activity) {
    if (activity.history?.length) return activity.history;
    const events = [{ type: "submitted", date: activity.date, by: "کارشناس" }];
    if (activity.status === ACTIVITY_STATUS.REVISION_REQUESTED) events.push({
        type: "revision_requested", date: activity.revisionRequestedAt || activity.date,
        by: "مدیر", note: activity.managerNote
    });
    return events;
}

// امتیاز «غیر روتین» — دقیقاً همان محاسبه‌ی قبلی: فعالیت‌های خودثبتیِ تأییدشده
function calcNonRoutineScore(activities, activityGroups, userId, periodId) {
    const approved = activities.filter(a => a.userId === userId &&
        a.periodId === periodId && a.status === ACTIVITY_STATUS.APPROVED);
    const groupMap = {};
    approved.forEach(a => { groupMap[a.groupId] = (groupMap[a.groupId] || 0) + a.score; });
    let total = 0;
    activityGroups.forEach(g => { total += Math.min(groupMap[g.id] || 0, g.maxScore); });
    return Math.min(100, total);
}

// امتیاز «روتین» — میانگین وزنیِ پاسخ‌های ارزیاب (مدیر/معاون/مدیرعامل) به سؤالات ۹گانه‌ی
// دامنه‌ی مربوطه (تیم/معاونت/سازمان) در دوره‌ی جاری. اگر هنوز سؤالی تعریف نشده یا پاسخی
// ثبت نشده باشد، null برمی‌گرداند (نه صفر) تا در محاسبه‌ی نهایی کلاً از این بخش صرف‌نظر شود.
// scopeKey=null یعنی بانک سؤال سراسری است (بدون فیلتر بر اساس دامنه — مثل سطح سازمان).
function calcScopedRoutineScore(answers, questions, routineLevels, userId, scopeKey, scopeValue, periodId) {
    const qs = scopeKey ? questions.filter(q => q[scopeKey] === scopeValue) : questions;
    if (!qs.length) return null;
    const maxLevelScore = routineLevels.length ? Math.max(...routineLevels.map(l => l.score)) : 0;
    if (!maxLevelScore) return null;
    let t = 0, w = 0;
    qs.forEach(q => {
        const ans = answers.find(a => a.questionId === q.id && a.userId === userId && a.periodId === periodId);
        if (!ans) return;
        const lvl = routineLevels.find(l => l.id === ans.levelId);
        if (!lvl) return;
        const pct = Math.min(100, (lvl.score / maxLevelScore) * 100);
        t += pct * (Number(q.weight) || 0);
        w += (Number(q.weight) || 0);
    });
    return w > 0 ? t / w : null;
}

// سطح تیم — مدیر برای کارشناس‌های تیمش پاسخ می‌دهد
function calcRoutineScore(routineAnswers, routineQuestions, routineLevels, userId, teamId, periodId) {
    return calcScopedRoutineScore(routineAnswers, routineQuestions, routineLevels, userId, "teamId", teamId, periodId);
}
// سطح معاونت — معاون برای مدیرهای زیرمجموعه‌اش پاسخ می‌دهد
function calcDeptRoutineScore(deptRoutineAnswers, deptRoutineQuestions, routineLevels, userId, deptId, periodId) {
    return calcScopedRoutineScore(deptRoutineAnswers, deptRoutineQuestions, routineLevels, userId, "deptId", deptId, periodId);
}
// سطح سازمان — مدیرعامل برای معاون‌ها پاسخ می‌دهد (بانک سؤال سراسری و واحد است)
function calcOrgRoutineScore(orgRoutineAnswers, orgRoutineQuestions, routineLevels, userId, periodId) {
    return calcScopedRoutineScore(orgRoutineAnswers, orgRoutineQuestions, routineLevels, userId, null, null, periodId);
}

// بر اساس نقش فرد، بانک سؤالِ روتینِ مناسب را انتخاب و امتیاز وظایف مستمرش را برمی‌گرداند.
// کارشناس ← سؤالات تیم (پاسخ‌دهنده: مدیر) | مدیر ← سؤالات معاونت (پاسخ‌دهنده: معاون)
// معاون ← سؤالات سازمان (پاسخ‌دهنده: مدیرعامل) | مدیرعامل/ادمین ← فاقد ارزیابی وظایف مستمر
function calcRoutineScoreForUser(user, routineCtx, periodId) {
    if (user.role === "specialist") {
        if (user.orgUnitId) {
            return calcScopedRoutineScore(routineCtx.routineAnswers, routineCtx.routineQuestions,
                routineCtx.routineLevels, user.id, "orgUnitId", user.orgUnitId, periodId);
        }
        return calcRoutineScore(routineCtx.routineAnswers, routineCtx.routineQuestions,
            routineCtx.routineLevels, user.id, user.teamId, periodId);
    }
    if (user.role === "manager") {
        if (user.orgUnitId) {
            return calcScopedRoutineScore(routineCtx.deptRoutineAnswers, routineCtx.deptRoutineQuestions,
                routineCtx.routineLevels, user.id, "orgUnitId", user.orgUnitId, periodId);
        }
        return calcDeptRoutineScore(routineCtx.deptRoutineAnswers, routineCtx.deptRoutineQuestions,
            routineCtx.routineLevels, user.id, user.deptId, periodId);
    }
    if (user.role === "deputy") {
        if (user.orgUnitId) {
            return calcScopedRoutineScore(routineCtx.orgRoutineAnswers, routineCtx.orgRoutineQuestions,
                routineCtx.routineLevels, user.id, "orgUnitId", user.orgUnitId, periodId);
        }
        return calcOrgRoutineScore(routineCtx.orgRoutineAnswers, routineCtx.orgRoutineQuestions,
            routineCtx.routineLevels, user.id, periodId);
    }
    return null;
}

function calcPeerQuestionResults(peerQuestions, peerReviews, routineLevels, targetUserId, periodId) {
    const submitted = peerReviews.filter(r => r.targetUserId === targetUserId && r.periodId === periodId && r.status === "submitted");
    const maxScore = routineLevels.length ? Math.max(...routineLevels.map(l => Number(l.score) || 0)) : 0;
    return peerQuestions.map(q => {
        const values = submitted.map(r => {
            const level = routineLevels.find(l => l.id === r.answers?.[q.id]);
            return level && maxScore ? (Number(level.score) / maxScore) * 100 : null;
        }).filter(v => v != null);
        return {
            questionId: q.id, label: q.label, weight: Number(q.weight) || 0, count: values.length,
            average: values.length ? values.reduce((a, b) => a + b, 0) / values.length : null
        };
    });
}

function calcPeerFeedbackScore(peerQuestions, peerReviews, routineLevels, targetUserId, periodId) {
    const rows = calcPeerQuestionResults(peerQuestions, peerReviews, routineLevels, targetUserId, periodId)
        .filter(r => r.average != null);
    const weight = rows.reduce((s, r) => s + r.weight, 0);
    return weight ? rows.reduce((s, r) => s + r.average * r.weight, 0) / weight : null;
}

function calcPeerReviewRecordScore(peerQuestions, routineLevels, review) {
    const maxScore = routineLevels.length ? Math.max(...routineLevels.map(l => Number(l.score) || 0)) : 0;
    const rows = peerQuestions.map(q => ({
        weight: Number(q.weight) || 0,
        score: Number(routineLevels.find(l => l.id === review.answers?.[q.id])?.score) || 0
    })).filter(r => r.weight > 0);
    const weight = rows.reduce((s, r) => s + r.weight, 0);
    return weight && maxScore ? rows.reduce((s, r) => s + (r.score / maxScore) * 100 * r.weight, 0) / weight : null;
}

function calcEligiblePeerFeedbackScore(routineCtx, targetUserId, periodId) {
    const apiResult = routineCtx.peerResult;
    if (apiResult?.subjectUserId === targetUserId && apiResult?.period?.id === periodId) {
        if (!apiResult.config?.enabled || !apiResult.summary?.enoughResponses || apiResult.summary?.overallScore == null) return null;
        return Math.min(100, Math.max(0, (Number(apiResult.summary.overallScore) / 5) * 100));
    }
    const config = (routineCtx.peerConfigs || []).find(c => c.userId === targetUserId && c.enabled);
    if (!config) return null;
    const responseCount = (routineCtx.peerReviews || []).filter(r => r.targetUserId === targetUserId &&
        r.periodId === periodId && r.status === "submitted").length;
    if (responseCount < (Number(config.minResponses) || 1)) return null;
    return calcPeerFeedbackScore(routineCtx.peerQuestions || [], routineCtx.peerReviews || [],
        routineCtx.routineLevels || [], targetUserId, periodId);
}

// امتیاز فردی نهایی = ترکیب وزنیِ دستاوردها، وظایف مستمر و بازخورد همکاران.
// مؤلفه‌های فاقد داده از محاسبه کنار می‌روند و وزن مؤلفه‌های موجود خودکار نرمال می‌شود.
function calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, user, periodId) {
    const nonRoutine = calcNonRoutineScore(activities, activityGroups, user.id, periodId);
    const routine = calcRoutineScoreForUser(user, routineCtx, periodId);
    const peerFeedback = calcEligiblePeerFeedbackScore(routineCtx, user.id, periodId);
    const roleWeights = (routineCtx.positions || []).find(p => p.id === user.posId) || {};
    const components = [
        { score: nonRoutine, weight: Number(roleWeights.wNonRoutine ?? orgSettings?.wNonRoutine) || 0 },
        { score: routine, weight: Number(roleWeights.wRoutine ?? orgSettings?.wRoutine) || 0 },
        { score: peerFeedback, weight: Number(roleWeights.wPeerFeedback ?? orgSettings?.wPeerFeedback) || 0 },
    ].filter(c => c.score != null && c.weight > 0);
    const weightSum = components.reduce((s, c) => s + c.weight, 0);
    if (!weightSum) return 0;
    return Math.min(100, Math.max(0, components.reduce((s, c) => s + c.score * c.weight, 0) / weightSum));
}

// شاخص‌های سازمانی بر اساس teamId/deptId خودِ کاربر فیلتر می‌شن، نه همه‌ی شاخص‌های آن سطح.
// اگر برای دوره‌ی جاری هنوز هدف/واقعی وارد نشده باشه، آن شاخص از محاسبه کنار گذاشته می‌شه
// (نه اینکه صفر در نظر گرفته بشه) — چون هنوز داده‌ای برایش نیست.
function calcCVI(ind, org) { return Math.sqrt(Math.max(0, ind) * Math.max(0, org)); }

// نسخه‌ی تفکیکی از calcOrgScore که علاوه بر جمع نهایی، امتیاز هر سه سطح (تیمی/معاونتی/سازمانی)
// و وزن هرکدوم را هم برمی‌گرداند — برای نمایش جزئیات (drill-down) در داشبورد.
function calcOrgLevelScore(level, scopeId, indicatorDefs, indicatorData, periodId) {
    const inds = indicatorDefs.filter(i => {
        if (i.level !== level) return false;
        if (level === "org") return true;
        if (level === "team") return i.teamId === scopeId;
        if (level === "dept") return i.deptId === scopeId;
        return false;
    });
    if (!inds.length) return null;
    let t = 0, w = 0;
    inds.forEach(i => {
        const rows = indicatorData.filter(d => d.periodId === periodId && d.indicatorId === i.id &&
            ["approved", "locked"].includes(d.status) && d.actual != null);
        if (!rows.length) return;
        const target = rows.find(d => d.target != null)?.target;
        if (target == null || target === 0) return;
        const values = rows.map(d => Number(d.actual) || 0);
        const actual = i.aggregationMethod === "sum" ? values.reduce((a, b) => a + b, 0)
            : i.aggregationMethod === "average" ? values.reduce((a, b) => a + b, 0) / values.length : values[values.length - 1];
        t += Math.min(100, (actual / target) * 100) * i.weight;
        w += i.weight;
    });
    return w > 0 ? t / w : null;
}

function calcOrgScoreBreakdown(user, positions, indicatorDefs, indicatorData, periodId) {
    const pos = positions.find(p => p.id === user.posId);
    if (!pos) return { team: null, dept: null, org: null, wTeam: 0, wDept: 0, wOrg: 0, total: 0 };
    const team = calcOrgLevelScore("team", user.teamId, indicatorDefs, indicatorData, periodId);
    const dept = calcOrgLevelScore("dept", user.deptId, indicatorDefs, indicatorData, periodId);
    const org = calcOrgLevelScore("org", null, indicatorDefs, indicatorData, periodId);
    let total = 0;
    if (team !== null) total += team * (pos.wTeam / 100);
    if (dept !== null) total += dept * (pos.wDept / 100);
    if (org !== null) total += org * (pos.wOrg / 100);
    return { team, dept, org, wTeam: pos.wTeam, wDept: pos.wDept, wOrg: pos.wOrg, total };
}

function calcOrgScore(user, positions, indicatorDefs, indicatorData, periodId) {
    return calcOrgScoreBreakdown(user, positions, indicatorDefs, indicatorData, periodId).total;
}

// ── تبدیل تقویم میلادی به شمسی (بدون نیاز به کتابخانه‌ی خارجی) ────────────────
function gregorianToJalali(gy, gm, gd) {
    const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    let jy = (gy <= 1600) ? 0 : 979;
    gy -= (gy <= 1600) ? 621 : 1600;
    const gy2 = (gm > 2) ? (gy + 1) : gy;
    let days = (365 * gy) + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) + Math.floor((gy2 + 399) / 400)
        - 80 + gd + g_d_m[gm - 1];
    jy += 33 * Math.floor(days / 12053); days %= 12053;
    jy += 4 * Math.floor(days / 1461); days %= 1461;
    jy += Math.floor((days - 1) / 365);
    if (days > 365) days = (days - 1) % 365;
    const jm = (days < 186) ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
    const jd = 1 + ((days < 186) ? (days % 31) : ((days - 186) % 30));
    return [jy, jm, jd];
}
function jalaliToGregorian(jy, jm, jd) {
    let gy = (jy <= 979) ? 621 : 1600;
    jy -= (jy <= 979) ? 0 : 979;
    const days = 365 * jy + Math.floor(jy / 33) * 8 + Math.floor(((jy % 33) + 3) / 4)
        + 78 + jd + ((jm < 7) ? (jm - 1) * 31 : ((jm - 7) * 30 + 186));
    gy += 400 * Math.floor(days / 146097); let d2 = days % 146097;
    if (d2 > 36524) { gy += 100 * Math.floor(--d2 / 36524); d2 %= 36524; if (d2 >= 365) d2++; }
    gy += 4 * Math.floor(d2 / 1461); d2 %= 1461;
    if (d2 > 365) { gy += Math.floor((d2 - 1) / 365); d2 = (d2 - 1) % 365; }
    let gd = d2 + 1;
    const sal_a = [0, 31, (((gy % 4 === 0) && (gy % 100 !== 0)) || (gy % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let gm = 0;
    while (gm < 13 && gd > sal_a[gm]) { gd -= sal_a[gm]; gm++; }
    return new Date(gy, gm - 1, gd);
}
function todayJalaliStr() {
    const now = new Date();
    const [jy, jm, jd] = gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
    return `${jy}/${String(jm).padStart(2, "0")}/${String(jd).padStart(2, "0")}`;
}
function jalaliDateFromGregorian(date) {
    const [jy, jm, jd] = gregorianToJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
    return `${jy}/${String(jm).padStart(2, "0")}/${String(jd).padStart(2, "0")}`;
}
function addDaysToJalali(dateStr, days = 0) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("/").map(Number);
    const date = jalaliToGregorian(y, m, d); date.setDate(date.getDate() + Number(days || 0));
    return jalaliDateFromGregorian(date);
}
function daysBetweenJalali(fromStr, toStr) {
    if (!fromStr || !toStr) return null;
    const parse = s => { const [y, m, d] = s.split("/").map(Number); return jalaliToGregorian(y, m, d).getTime(); };
    return Math.ceil((parse(toStr) - parse(fromStr)) / 86400000);
}
function monthEndJalali(year, month) {
    const nextMonth = month === 12 ? 1 : month + 1, nextYear = month === 12 ? year + 1 : year;
    const firstNext = jalaliToGregorian(nextYear, nextMonth, 1); firstNext.setDate(firstNext.getDate() - 1);
    return jalaliDateFromGregorian(firstNext);
}
function indicatorScheduleOccurrences(def, period, templates, exceptions = []) {
    if (!def || !period) return [];
    const template = templates.find(t => t.id === def.scheduleTemplateId) || templates.find(t => t.frequency === "quarterly");
    if (!template) return [];
    const [sy, sm] = period.start.split("/").map(Number), [ey, em] = period.end.split("/").map(Number);
    let ends = [];
    if (template.frequency === "monthly") {
        let y = sy, m = sm; while (y < ey || (y === ey && m <= em)) { ends.push(monthEndJalali(y, m)); m++; if (m > 12) { m = 1; y++; } }
    } else if (template.frequency === "annual") {
        if (em === 12) ends = [period.end];
    } else ends = [period.end];
    return ends.map((rangeEnd, index) => {
        const occurrenceId = `${period.id}:${template.frequency}:${index + 1}`;
        const exception = exceptions.find(e => e.indicatorId === def.id && e.periodId === period.id &&
            (!e.occurrenceId || e.occurrenceId === occurrenceId));
        const openDate = exception?.openDate || addDaysToJalali(rangeEnd, template.openOffsetDays);
        const submitDeadline = exception?.submitDeadline || addDaysToJalali(rangeEnd, template.submitAfterDays);
        const approvalDeadline = exception?.approvalDeadline || addDaysToJalali(submitDeadline, template.approveAfterDays);
        const lockDate = exception?.lockDate || addDaysToJalali(rangeEnd, template.lockAfterDays);
        return {
            occurrenceId, rangeEnd, openDate, submitDeadline, approvalDeadline, lockDate,
            label: template.frequency === "monthly" ? `ماه ${index + 1}` : template.frequency === "annual" ? "سالانه" : "فصلی",
            reminderDays: Number(template.reminderDays) || 3, templateId: template.id, exception: exception || null
        };
    });
}
function daysRemainingInPeriod(period) {
    if (!period?.end) return null;
    const [ey, em, ed] = period.end.split("/").map(Number);
    const endGregorian = jalaliToGregorian(ey, em, ed);
    const now = new Date();
    const diffMs = endGregorian.setHours(23, 59, 59, 999) - now.getTime();
    return Math.ceil(diffMs / 86400000);
}

// یک شاخص تعریف‌شده را با هدف/واقعیِ دوره‌ی مشخص ترکیب می‌کنه (برای نمایش در پنل مدیر)
function withPeriodData(defs, indicatorData, periodId) {
    return defs.map(def => {
        const d = indicatorData.find(x => x.periodId === periodId && x.indicatorId === def.id);
        const approved = d && ["approved", "locked"].includes(d.status);
        return {
            ...def, target: d?.target ?? 0, actual: approved ? (d.actual ?? 0) : 0,
            dataStatus: d?.status || "not_started"
        };
    });
}

function useIsMobile() {
    const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : true);
    useEffect(() => {
        const h = () => setM(window.innerWidth < 768);
        window.addEventListener("resize", h);
        return () => window.removeEventListener("resize", h);
    }, []);
    return m;
}

// ── SHARED ATOMS ────────────────────────────────────────────────────────────
function workflowTaskNav(task, role) {
    const target = task?.actionTarget || task?.module || "";
    if (target === "routine-self") return "self-assessment";
    if (target === "routine-evaluation") return role === "manager" ? "employee-evaluation" : "routine";
    if (target === "activities") return "activities";
    if (target === "review-inbox") return "inbox";
    if (target === "peer-feedback") return "peer-feedback";
    if (target === "indicators") return "my-indicators";
    if (target === "period") return "cycle-management";
    if (task?.module === "activity") return "activities";
    if (task?.module === "peer") return "peer-feedback";
    if (task?.module === "indicator") return "my-indicators";
    if (task?.module === "routine") return role === "specialist" ? "self-assessment" : "routine";
    return "dashboard";
}

function Badge({ color = "gray", children, small }) {
    const map = {
        gray: [C.bg, "#ccc", C.muted],
        blue: ["#E6F1FB", "#185FA5", "#0C447C"],
        teal: [C.tealLt, C.teal, C.teal],
        purple: [C.purpleLt, C.purple, C.purple],
        amber: [C.amberLt, C.amber, C.amber],
        red: [C.redLt, C.red, C.red],
        green: [C.tealLt, C.teal, C.teal],
        cyan: [C.cyanBg, C.cyan, "#007A8C"],
    };
    const [bg, border, text] = map[color] || map.gray;
    return (
        <span style={{
            background: bg, border: `1px solid ${border}`, color: text,
            borderRadius: 20, padding: small ? "2px 7px" : "3px 10px",
            fontSize: small ? 10 : 11, fontWeight: 500, display: "inline-block", whiteSpace: "nowrap"
        }}>
            {children}
        </span>
    );
}

function ProfessionalTabs({ items, value, active, onChange, ariaLabel = "بخش‌های صفحه" }) {
    const selectedValue = value ?? active ?? items?.[0]?.id;
    return (
        <div role="tablist" aria-label={ariaLabel} style={{
            display: "flex", alignItems: "flex-end", gap: 22,
            borderBottom: `1px solid ${C.border}`, overflowX: "auto", overflowY: "hidden", marginBottom: 16,
            padding: "0 2px", scrollbarWidth: "thin"
        }}>
            {items.map(item => {
                const isActive = item.id === selectedValue;
                return <button key={item.id} role="tab" aria-selected={isActive} onClick={() => onChange(item.id)}
                    style={{
                        position: "relative", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
                        padding: "10px 2px 11px", border: "none", background: "transparent", fontFamily: "inherit",
                        fontSize: 12.5, fontWeight: isActive ? 600 : 400, color: isActive ? C.navy : C.muted, cursor: "pointer"
                    }}>
                    {item.icon && <span style={{ display: "inline-flex", alignItems: "center", color: "currentColor" }}><UiIcon name={item.icon} size={15} /></span>}<span>{item.label}</span>
                    {item.count != null && <span style={{
                        minWidth: 18, height: 18, padding: "0 5px", boxSizing: "border-box",
                        borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9.5,
                        background: isActive ? C.cyanBg : C.bg, color: isActive ? C.navy : C.hint
                    }}>{item.count}</span>}
                    {isActive && <span style={{
                        position: "absolute", right: 0, left: 0, bottom: -1, height: 3,
                        background: C.cyan, borderRadius: "3px 3px 0 0"
                    }} />}
                </button>;
            })}
        </div>
    );
}

function UserAvatar({ user, size = 30 }) {
    const { users } = useData();
    const currentUser = users.find(u => u.id === user.id) || user;
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%", overflow: "hidden",
            background: C.cyan, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: size <= 30 ? 12 : 16, fontWeight: 700, color: C.navy, flexShrink: 0
        }}>
            {currentUser.photoDataUrl
                ? <img src={currentUser.photoDataUrl} alt={`عکس ${currentUser.name}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : currentUser.name?.[0]}
        </div>
    );
}


function CviHelpCenter() {
    const topics = [
        { id: "start", title: "CVI چیست؟", keywords: "شروع شاخص ارزش مشارکت عملکرد فردی مشارکت جمعی", body: "CVI یا شاخص ارزش مشارکت، تصویری یکپارچه از مشارکت فرد در موفقیت سازمان ارائه می‌کند. این شاخص از ترکیب عملکرد فردی و مشارکت جمعی شکل می‌گیرد.", points: ["عملکرد فردی: وظایف و فعالیت‌ها، اهداف فردی و بازخورد همکاران", "مشارکت جمعی: ارتباط عملکرد فرد با شاخص‌های تیم، معاونت و سازمان"] },
        { id: "tasks", title: "وظایف و فعالیت‌ها", keywords: "فعالیت مستمر غیرمستمر وظایف خوداظهاری", body: "این بخش کارهای فرد را در دو گروه فعالیت‌های مستمر و فعالیت‌های غیرمستمر دنبال می‌کند.", points: ["فعالیت‌های مستمر: کارهای معمول و تکرارشونده نقش", "فعالیت‌های غیرمستمر: کارهای موردی، پروژه‌ای یا خارج از روال", "خوداظهاری فعالیت‌های مستمر برای مقایسه و گفت‌وگوست و مستقیماً در CVI وزن ندارد؛ ارزیابی رسمی توسط ارزیاب انجام می‌شود."] },
        { id: "goals", title: "اهداف فردی", keywords: "هدف اهداف مدیر زیرمجموعه target deadline تحقق دوره قبل", body: "اهداف فردی نتایجی هستند که برای یک دوره مشخص تعریف و پیگیری می‌شوند.", points: ["کاربر اهداف خودش را در «اهداف من» می‌بیند.", "مدیر در «اهداف افراد زیرمجموعه» ابتدا فرد را انتخاب و سپس اهداف او را مدیریت می‌کند.", "هدف می‌تواند عنوان، معیار موفقیت، Target، وزن و Deadline داشته باشد.", "برای دوره جدید می‌توان از تعریف اهداف دوره‌های قبل استفاده کرد؛ عملکرد و امتیاز قبلی به هدف جدید منتقل نمی‌شود."] },
        { id: "peer", title: "بازخورد همکاران", keywords: "peer feedback همکار ارزیاب ناشناس", body: "بازخورد همکاران دیدگاه چند ارزیاب هم‌سطح را درباره همکاری و رفتار کاری فرد جمع‌آوری می‌کند.", points: ["مدیر مستقیم در این بخش ارزیاب همکار محسوب نمی‌شود.", "نمایش هویت ارزیاب‌ها تابع تنظیمات محرمانگی سازمان است.", "نتیجه طبق وزن تعیین‌شده در عملکرد فردی اثر می‌گذارد."] },
        { id: "strategic", title: "شاخص‌های راهبردی و شاخص‌های مرتبط با من", keywords: "شاخص راهبردی شاخص های من مالک ثبت کننده تایید کننده مشارکت جمعی", body: "شاخص‌های راهبردی عملکرد تیم، معاونت یا سازمان را اندازه‌گیری می‌کنند و مبنای مشارکت جمعی هستند. در بخش شاخص‌های مرتبط با شما، شاخص‌هایی نمایش داده می‌شوند که در آن‌ها نقشی دارید.", points: ["ثبت‌کننده: مقدار واقعی شاخص را ثبت و ارسال می‌کند.", "تأییدکننده: مقدار ارسال‌شده را بررسی می‌کند.", "مالک: مسئول پیگیری عملکرد شاخص است.", "فرایند معمول: ثبت عملکرد ← ارسال برای بررسی ← تأیید یا درخواست اصلاح"] },
        { id: "inbox", title: "صندوق بررسی", keywords: "صندوق بررسی فعالیت غیرمستمر شاخص راهبردی تایید اصلاح", body: "صندوق بررسی محل مواردی است که برای تصمیم‌گیری شما ارسال شده‌اند؛ بنابراین فقط موارد نیازمند اقدام شما در آن ظاهر می‌شوند.", points: ["فعالیت‌های غیرمستمر: فعالیت ارسال‌شده فرد زیرمجموعه را بررسی، تأیید، رد یا برای اصلاح بازمی‌گردانید.", "شاخص‌های راهبردی: وقتی شما تأییدکننده شاخص باشید، مقدار ارسال‌شده ثبت‌کننده برای بررسی اینجا نمایش داده می‌شود."] },
        { id: "collective", title: "مشارکت جمعی", keywords: "مشارکت جمعی سازمان تیم معاونت چرا عملکرد سازمان", body: "مشارکت جمعی نشان می‌دهد فرد تا چه اندازه در نتایج واحدها و ساختاری که عضوی از آن است سهیم است. این بخش از شاخص‌های راهبردی تیم، معاونت و سازمان شکل می‌گیرد.", points: ["هدف آن جایگزین‌کردن ارزیابی فردی نیست؛ بلکه اضافه‌کردن بُعد موفقیت مشترک به CVI است.", "وزن سطوح سازمانی بر اساس نقش و تنظیمات مدل ارزیابی تعیین می‌شود."] },
        { id: "history", title: "تاریخچه", keywords: "تاریخچه دوره گذشته آرشیو قفل read only", body: "تاریخچه آرشیو فقط‌خواندنی دوره‌های گذشته است و امکان مشاهده جزئیات عملکرد فردی، مشارکت جمعی و CVI همان دوره را فراهم می‌کند.", points: ["داده دوره قفل‌شده تغییر نمی‌کند.", "فرمول و نتایج تاریخی با تغییرات دوره‌های بعد دوباره محاسبه نمی‌شوند."] },
        { id: "score", title: "CVI چگونه شکل می‌گیرد؟", keywords: "فرمول امتیاز وزن CVI محاسبه", body: "CVI از ترکیب عملکرد فردی و مشارکت جمعی به دست می‌آید. عملکرد فردی نیز از وظایف و فعالیت‌ها، اهداف فردی و بازخورد همکاران تشکیل می‌شود.", points: ["وزن مؤلفه‌ها بر اساس نقش قابل تنظیم است.", "خوداظهاری فعالیت‌های مستمر وزن مستقیم در CVI ندارد.", "نتایج دوره نهایی‌شده به‌صورت Snapshot نگهداری می‌شوند."] },
    ];
    const [query,setQuery]=useState("");
    const [openId,setOpenId]=useState("start");
    const q=query.trim().toLowerCase();
    const visible=q ? topics.filter(t => (t.title+" "+t.keywords+" "+t.body+" "+t.points.join(" ")).toLowerCase().includes(q)) : topics;
    const selected=topics.find(x=>x.id===openId && visible.some(v=>v.id===x.id)) || visible[0];
    return <div className="cvi-dashboard-page">
        <DashboardPageIntro eyebrow="CVI Help Center" title="راهنمای CVI" subtitle="مفاهیم، نقش‌ها و فرایندهای سیستم را سریع پیدا کنید" />
        <Card style={{marginBottom:14}}><div style={{padding:16}}><div style={{fontSize:12,fontWeight:600,marginBottom:8}}>درباره کدام بخش CVI سؤال دارید؟</div><input value={query} onChange={e=>setQuery(e.target.value)} placeholder="مثلاً: شاخص‌های راهبردی، خوداظهاری، صندوق بررسی..." style={{width:"100%",boxSizing:"border-box",padding:"11px 12px",border:"1.5px solid #E4E4EC",borderRadius:9,fontFamily:"inherit",fontSize:12,outline:"none"}}/></div></Card>
        <div style={{display:"grid",gridTemplateColumns:"minmax(210px,280px) minmax(0,1fr)",gap:12,alignItems:"start"}}>
            <Card><div style={{padding:8}}>{visible.length===0?<div style={{padding:20,textAlign:"center",fontSize:11,color:C.muted}}>موضوعی با این عبارت پیدا نشد.</div>:visible.map(t=><button key={t.id} onClick={()=>setOpenId(t.id)} style={{width:"100%",border:"none",borderRadius:8,padding:"10px 11px",marginBottom:3,textAlign:"right",fontFamily:"inherit",cursor:"pointer",background:openId===t.id?C.cyanBg:"transparent",color:openId===t.id?C.navy:C.text,fontSize:12,fontWeight:openId===t.id?600:400}}>{t.title}</button>)}</div></Card>
            {selected ? <Card><div style={{padding:20}}><h2 style={{fontSize:16,margin:"0 0 12px",color:C.navy}}>{selected.title}</h2><p style={{fontSize:12.5,lineHeight:1.9,color:"#444",margin:"0 0 14px"}}>{selected.body}</p><div style={{display:"grid",gap:8}}>{selected.points.map((point,i)=><div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",background:C.bg,borderRadius:8,padding:"9px 11px",fontSize:11.5,lineHeight:1.8}}><span style={{color:C.teal}}><UiIcon name="check" size={14}/></span><span>{point}</span></div>)}</div></div></Card> : <Card><div style={{padding:30,textAlign:"center",color:C.muted}}>عبارت دیگری را جست‌وجو کنید.</div></Card>}
        </div>
    </div>;
}
function EmptyState({ title, description, icon = "activity" }) {
    return <div className="cvi-empty-state">
        <span className="cvi-empty-state-icon"><UiIcon name={icon} size={22} /></span>
        <strong>{title}</strong>
        {description && <span>{description}</span>}
    </div>;
}

function AppSkeleton() {
    return <div className="cvi-app-skeleton" aria-label="در حال بارگذاری">
        <div className="cvi-skeleton-header" />
        <div className="cvi-skeleton-body">
            <div className="cvi-skeleton-sidebar" />
            <div className="cvi-skeleton-content">
                <div className="cvi-skeleton-line wide" />
                <div className="cvi-skeleton-line" />
                <div className="cvi-skeleton-grid">{[0,1,2,3].map(i => <div key={i} className="cvi-skeleton-card" />)}</div>
                <div className="cvi-skeleton-panel" />
            </div>
        </div>
    </div>;
}

function TabTransitionContent({ transitionKey, children }) {
    // Keep the wrapper identity stable across local tab changes.
    // A key tied to the active tab forces React to unmount/remount the whole subtree,
    // which resets child state and re-runs mount-only data loading effects.
    return <div className="cvi-tab-content">{children}</div>;
}

function UserMenu({ user, onLogout, onSettings }) {
    const { language } = useI18n();
    return <AccountMenu user={user} language={language} avatar={<UserAvatar user={user} size={30} />}
        onLogout={onLogout} onSettings={onSettings} />;
}

function StatusBadge({ status }) {
    const map = {
        draft: ["gray", "پیش‌نویس"],
        approved: ["teal", "تأیید شده"],
        pending_review: ["amber", "در انتظار بررسی"],
        revision_requested: ["purple", "نیازمند اصلاح"],
        resubmitted: ["blue", "ارسال مجدد"],
        rejected: ["red", "رد شده"],
        submitted: ["blue", "ارسال‌شده"],
        locked: ["purple", "قفل‌شده"],
        active: ["teal", "فعال"],
        inactive: ["gray", "غیرفعال"],
        pending: ["amber", "در انتظار"],
        completed: ["teal", "تکمیل‌شده"],
    };
    const [color, label] = map[status] || ["gray", status];
    return <Badge color={color}>{label}</Badge>;
}

function ScoreRing({ value, size = 72, color = C.navy }) {
    const r = (size - 8) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (Math.min(100, Math.max(0, value)) / 100) * circ;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.bg} strokeWidth={6} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`} />
            <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
                fontSize={size < 60 ? 11 : 14} fontWeight={500} fill={color}>
                {Math.round(value)}
            </text>
        </svg>
    );
}

function MiniBar({ value, max = 100, color = C.navy, height = 5 }) {
    return (
        <div style={{ height, background: C.bg, borderRadius: height / 2, overflow: "hidden", marginTop: 6 }}>
            <div style={{
                width: `${Math.min(100, (value / max) * 100)}%`, height: "100%",
                background: color, borderRadius: height / 2
            }} />
        </div>
    );
}

function Card({ children, style }) {
    return (
        <div className="cvi-card" style={style}>
            {children}
        </div>
    );
}

// نمای کلی امتیاز (لایه اول drill-down) — روی داشبورد اصلیِ هر نقش استفاده می‌شه.
// امتیاز فردی به «روتین/غیر روتین» و امتیاز سازمانی به «سازمانی/معاونتی/تیمی» تفکیک می‌شه؛
// با کلیک روی هرکدوم، لایه دوم (جزئیات کامل) باز می‌شه.
function DashboardPageIntro({ title, subtitle, period, periodStart = null, periodEnd = null, eyebrow, actions, periodInline = false, subtitleInline = false,
    attentionCount = null, onAttentionClick = null }) {
    const hasAttention = attentionCount !== null && attentionCount !== undefined && Number.isFinite(Number(attentionCount));
    const formatPeriodDate = (value) => {
        const match = String(value || "").match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
        if (!match) return value || "";
        const months = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور", "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"];
        const toFa = (v) => String(v).replace(/\d/g, d => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
        const [, year, month, day] = match;
        return `${toFa(Number(day))} ${months[Number(month) - 1] || toFa(month)} ${toFa(year)}`;
    };
    const periodRange = periodStart && periodEnd ? `${formatPeriodDate(periodStart)} تا ${formatPeriodDate(periodEnd)}` : "";
    return (
        <div className={`cvi-dashboard-intro${periodInline ? " is-compact" : ""}`}>
            <div style={{ minWidth: 0, width: "100%" }}>
                {eyebrow && <div className="cvi-dashboard-eyebrow">{eyebrow}</div>}
                <div className="cvi-dashboard-title-row">
                    <div className="cvi-dashboard-heading-main">
                        <div className="cvi-dashboard-identity-line">
                            <h1 className="cvi-dashboard-title">{title}</h1>
                            {subtitleInline && subtitle && <span className="cvi-dashboard-inline-role">({subtitle})</span>}
                        </div>
                        {periodInline && period && <div className="cvi-dashboard-period-inline">
                            <div><span>دوره ارزیابی:</span> <strong>{period}</strong></div>
                            {periodRange && <small>{periodRange}</small>}
                        </div>}
                    </div>
                    {hasAttention && <button type="button" className={`cvi-dashboard-attention-card${Number(attentionCount) > 0 ? " has-items" : ""}`}
                        onClick={onAttentionClick || undefined}>
                        <span>موارد نیازمند توجه</span>
                        <strong>{Number(attentionCount)}</strong>
                        <small>{Number(attentionCount) > 0 ? "نیازمند پیگیری" : "مورد فوری ندارید"}</small>
                    </button>}
                </div>
                {!subtitleInline && subtitle && <div className="cvi-dashboard-subtitle">{subtitle}</div>}
            </div>
            <div className="cvi-dashboard-intro-actions">
                {!periodInline && period && <span className="cvi-dashboard-period">{period}</span>}
                {actions}
            </div>
        </div>
    );
}

// Presentation-only formatting: do not change numeric values sent to the API or entered in forms.
function formatFaDisplay(value) {
    if (value === null || value === undefined) return value;
    if (typeof value !== "string" && typeof value !== "number") return value;
    return String(value).replace(/[0-9]/g, digit => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)])
        .replace(/([۰-۹]),(?=[۰-۹]{3}(?:[^۰-۹]|$))/g, "$1٬");
}

function DashboardMetricCard({ label, value, helper, tone = "navy", icon, onClick }) {
    const toneMap = {
        navy: [C.navy, C.cyanBg], teal: [C.teal, C.tealLt], purple: [C.purple, C.purpleLt],
        amber: [C.amber, C.amberLt], red: [C.red, C.redLt], cyan: ["#007A8C", C.cyanLt],
    };
    const [color, soft] = toneMap[tone] || toneMap.navy;
    return (
        <div className={`cvi-dashboard-metric${onClick ? " is-clickable" : ""}`} onClick={onClick}
            role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}>
            <div className="cvi-dashboard-metric-top">
                <span className="cvi-dashboard-metric-label">{label}</span>
                {icon && <span className="cvi-dashboard-metric-icon" style={{ color, background: soft }}>{icon}</span>}
            </div>
            <div className="cvi-dashboard-metric-value" style={{ color }}>{formatFaDisplay(value)}</div>
            {helper && <div className="cvi-dashboard-metric-helper">{formatFaDisplay(helper)}</div>}
        </div>
    );
}

function DashboardSection({ title, action, children, className = "" }) {
    return (
        <section className={`cvi-dashboard-section ${className}`.trim()}>
            <div className="cvi-dashboard-section-head">
                <h2>{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function DashboardLinkButton({ children, onClick, primary = false }) {
    return <button type="button" onClick={onClick}
        className={`cvi-dashboard-link-button${primary ? " is-primary" : ""}`}>{children}</button>;
}

function ScoreOverview({ user, cviScore, indivScore, nonRoutineScore, routineScore, orgScore, orgBreakdown, periodDelta,
    onOpenNonRoutine, onOpenRoutine, onOpenTasksActivities, onOpenGoals, onOpenPeerFeedback, onOpenOrgLevel }) {
    const { orgSettings, positions, peerQuestions, peerReviews, peerConfigs, peerResult, routineLevels, activePeriod, indicatorDefs, indicatorData } = useData();
    const individualWeights = positions.find(p => p.id === user?.posId) || orgSettings;
    const peerFeedbackScore = user ? calcEligiblePeerFeedbackScore({ peerQuestions, peerReviews, peerConfigs, peerResult, routineLevels }, user.id, activePeriod?.id) : null;
    const [goalScore, setGoalScore] = useState(null);
    const [liveCvi, setLiveCvi] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedSubDriver, setSelectedSubDriver] = useState(null);
    const [explainOpen, setExplainOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const loadGoalScore = async () => {
            if (!user?.id || !activePeriod?.id) { setGoalScore(null); return; }
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/goals/?periodId=${encodeURIComponent(activePeriod.id)}`, { headers: { Authorization: `Bearer ${token}` } });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "Goal score load failed");
                const goals = (payload.goals || []).filter(goal => goal.subjectUserId === user.id && goal.managerScore != null && Number(goal.weight) > 0 && ["ACTIVE", "COMPLETED"].includes(String(goal.status).toUpperCase()));
                const totalWeight = goals.reduce((sum, goal) => sum + Number(goal.weight || 0), 0);
                const score = totalWeight ? goals.reduce((sum, goal) => sum + Number(goal.managerScore) * Number(goal.weight || 0), 0) / totalWeight : null;
                if (!cancelled) setGoalScore(score);
            } catch { if (!cancelled) setGoalScore(null); }
        };
        loadGoalScore();
        return () => { cancelled = true; };
    }, [user?.id, activePeriod?.id]);

    useEffect(() => {
        let cancelled = false;
        const loadLiveCvi = async () => {
            if (!user?.id || !activePeriod?.id) { setLiveCvi(null); return; }
            setLiveCvi(null);
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/cvi/periods/${encodeURIComponent(activePeriod.id)}/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "CVI load failed");
                if (!cancelled) setLiveCvi(payload);
            } catch {
                if (!cancelled) setLiveCvi(null);
            }
        };
        loadLiveCvi();
        return () => { cancelled = true; };
    }, [user?.id, activePeriod?.id]);

    const scoreText = value => value == null || !Number.isFinite(Number(value)) ? "—" : `${formatFaDisplay(Number(value).toFixed(0))}٪`;
    const weightText = value => value == null || !Number.isFinite(Number(value)) ? "—" : formatFaDisplay(Number(value).toFixed(0));

    const taskActivityParts = [
        { label: "مستمر", value: liveCvi?.scores ? liveCvi.scores.routine : routineScore, weight: liveCvi?.weights?.tasksActivitiesSplit?.continuous ?? individualWeights?.wRoutine ?? 60 },
        { label: "غیرمستمر", value: liveCvi?.scores ? liveCvi.scores.nonRoutine : nonRoutineScore, weight: liveCvi?.weights?.tasksActivitiesSplit?.nonContinuous ?? individualWeights?.wNonRoutine ?? 40 },
    ];
    const eligibleTaskParts = taskActivityParts.filter(item => item.value != null && Number(item.weight) > 0);
    const taskPartWeight = eligibleTaskParts.reduce((sum, item) => sum + Number(item.weight), 0);
    const tasksActivitiesScore = taskPartWeight
        ? eligibleTaskParts.reduce((sum, item) => sum + Number(item.value) * Number(item.weight), 0) / taskPartWeight
        : null;

    const components = [
        { id:"tasks", label: "وظایف و فعالیت‌ها", value: liveCvi?.scores ? liveCvi.scores.tasksActivities : tasksActivitiesScore, weight: liveCvi?.weights?.individualComponents?.tasksActivities ?? individualWeights?.wTasksActivities ?? 50, subLabel: `مستمر ${liveCvi?.weights?.tasksActivitiesSplit?.continuous ?? individualWeights?.wRoutine ?? 60}٪ • غیرمستمر ${liveCvi?.weights?.tasksActivitiesSplit?.nonContinuous ?? individualWeights?.wNonRoutine ?? 40}٪`, onOpenFull: onOpenTasksActivities },
        { id:"goals", label: "اهداف فردی", value: liveCvi?.scores ? liveCvi.scores.individualGoals : goalScore, weight: liveCvi?.weights?.individualComponents?.individualGoals ?? individualWeights?.wGoals ?? 30, onOpenFull: onOpenGoals },
        { id:"peer", label: "بازخورد همکاران", value: liveCvi?.scores ? liveCvi.scores.peerFeedback : peerFeedbackScore, weight: liveCvi?.weights?.individualComponents?.peerFeedback ?? individualWeights?.wPeerFeedback ?? 20, onOpenFull: onOpenPeerFeedback },
    ];
    const eligibleIndividual = components.filter(item => item.value != null && Number(item.weight) > 0);
    const eligibleIndividualWeight = eligibleIndividual.reduce((sum, item) => sum + Number(item.weight), 0);
    const fallbackIndividualScore = eligibleIndividualWeight
        ? eligibleIndividual.reduce((sum, item) => sum + Number(item.value) * Number(item.weight), 0) / eligibleIndividualWeight
        : (indivScore == null ? null : Number(indivScore));
    const displayedIndividualScore = liveCvi?.scores ? liveCvi.scores.individual : fallbackIndividualScore;
    const displayedOrgScore = liveCvi?.scores ? liveCvi.scores.organizational : orgScore;
    const displayedCviScore = (liveCvi?.scores ? liveCvi.scores.cvi : (displayedIndividualScore == null || displayedOrgScore == null
        ? (cviScore == null ? null : Number(cviScore))
        : Math.sqrt(Math.max(0, displayedIndividualScore) * Math.max(0, Number(displayedOrgScore)))));

    const liveOrg = liveCvi?.breakdown?.organizational;
    const strategic = [
        { id:"team", label: "تیمی", value: liveOrg?.levels?.team?.score ?? orgBreakdown?.team, weight: liveOrg?.levels?.team?.effectiveWeight ?? liveOrg?.levels?.team?.weight ?? orgBreakdown?.wTeam, level: "team", onOpenFull:()=>onOpenOrgLevel?.("team") },
        { id:"upstream", label: "واحدهای بالادستی", value: liveOrg?.levels?.upstream?.score ?? liveOrg?.levels?.department?.score ?? orgBreakdown?.upstream ?? orgBreakdown?.dept, weight: liveOrg?.levels?.upstream?.effectiveWeight ?? liveOrg?.levels?.upstream?.weight ?? liveOrg?.levels?.department?.effectiveWeight ?? liveOrg?.levels?.department?.weight ?? orgBreakdown?.wUpstream ?? orgBreakdown?.wDept, level: "dept", onOpenFull:()=>onOpenOrgLevel?.("dept") },
        { id:"org", label: "کلان سازمان", value: liveOrg?.levels?.organization?.score ?? orgBreakdown?.org, weight: liveOrg?.levels?.organization?.effectiveWeight ?? liveOrg?.levels?.organization?.weight ?? orgBreakdown?.wOrg, level: "org", onOpenFull:()=>onOpenOrgLevel?.("org") },
    ];

    const ScoreRow = ({ item }) => {
        const numeric = Number(item.value);
        const fill = Number.isFinite(numeric) ? Math.max(0, Math.min(100, numeric)) : 0;
        const isSelected = selectedDriver?.id === item.id;
        return <button type="button" onClick={() => setSelectedDriver(current => current?.id === item.id ? null : item)} style={{
            width: "100%", border: `1px solid ${isSelected ? "#BEC7E8" : "#E7E9F1"}`,
            background: isSelected ? "#F8F9FE" : "#fff", borderRadius: 15,
            padding: "10px 11px", cursor: "pointer", textAlign: "right",
            fontFamily: "inherit", boxShadow: "0 2px 8px rgba(0,0,64,.03)"
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 6, height: 30, borderRadius: 7, background: "#E8EAF5", overflow: "hidden", display: "flex", alignItems: "end", flexShrink: 0 }}>
                    <span style={{ width: "100%", height: `${fill}%`, background: C.navy, borderRadius: 7 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                        <b style={{ fontSize: 11, color: "#252638" }}>{item.label}</b>
                        <span className="cvi-score-weight"><span>وزن</span><strong>{weightText(item.weight)}</strong><span>٪</span></span>
                    </div>
                    {item.subLabel && <div style={{ fontSize: 8.8, color: C.muted, marginTop: 4 }}>{item.subLabel}</div>}
                </div>
                <strong style={{ fontSize: 16, color: C.navy, minWidth: 27, textAlign: "center" }}>{scoreText(item.value)}</strong>
                <span style={{ color: C.hint, fontSize: 12, transform: isSelected ? "rotate(90deg)" : "none", transition: ".15s" }}>←</span>
            </div>
        </button>;
    };

    const collectiveIndicatorItems = selectedSubDriver && selectedDriver?.id === "collective-summary" ? (() => {
        const level = selectedSubDriver.id === "team" ? "team" : selectedSubDriver.id === "org" ? "org" : "dept";
        const isFlexible = liveOrg?.mode === "flexible_org_units" && Boolean(user?.orgUnitId);
        const upstreamIds = new Set((liveOrg?.levels?.upstream?.units || []).map(row => row.unit?.id).filter(Boolean));
        // The local unit and its ancestors are different scopes. Never use the
        // local unit ID when the user selects the upstream level.
        const matching = (def) => {
            if (level === "org") {
                if (isFlexible) {
                    const rootId = liveOrg?.levels?.organization?.unit?.id;
                    return rootId ? def.level === "org_unit" && def.orgUnitId === rootId : def.level === "org";
                }
                return def.level === "org";
            }
            if (isFlexible) {
                if (level === "team") return def.level === "org_unit" && def.orgUnitId === user.orgUnitId;
                return def.level === "org_unit" && upstreamIds.has(def.orgUnitId);
            }
            if (level === "team") return def.level === "team" && def.teamId === user?.teamId;
            return def.level === "dept" && def.deptId === user?.deptId;
        };
        // No calculated score means there is no evaluated evidence for this
        // level; do not show unrelated indicators as though they caused it.
        if (selectedSubDriver.value == null) return [];
        return (indicatorDefs || []).filter(matching).map(def => {
            const rows = (indicatorData || []).filter(row =>
                row.periodId === activePeriod?.id &&
                row.indicatorId === def.id &&
                ["approved", "locked"].includes(String(row.status || "").toLowerCase()) &&
                row.actual != null
            );
            const target = rows.find(row => row.target != null)?.target;
            let actual = null;
            if (rows.length) {
                const values = rows.map(row => Number(row.actual) || 0);
                actual = def.aggregationMethod === "sum"
                    ? values.reduce((sum, value) => sum + value, 0)
                    : def.aggregationMethod === "average"
                        ? values.reduce((sum, value) => sum + value, 0) / values.length
                        : values[values.length - 1];
            }
            const score = actual != null && Number(target) > 0 ? Math.min(100, (Number(actual) / Number(target)) * 100) : null;
            return {
                id: def.id,
                label: def.label || def.name || def.title || "شاخص",
                weight: Number(def.weight) || 0,
                actual,
                target: target == null ? null : Number(target),
                score
            };
        });
    })() : [];

    const selectedDriverDetails = selectedSubDriver ? (() => {
        const scoreOrDash = value => value == null || !Number.isFinite(Number(value)) ? "—" : `${Number(value).toFixed(0)}٪`;
        if (selectedSubDriver.id === "tasks") return {
            title: "وظایف و فعالیت‌ها",
            description: "نمای فعلی این مؤلفه بر اساس وظایف مستمر و فعالیت‌های غیرمستمر دوره جاری.",
            facts: [
                {
                    label:"وظایف مستمر",
                    value:`${scoreOrDash(liveCvi?.scores?.routine ?? routineScore)} · وزن ${weightText(liveCvi?.weights?.tasksActivitiesSplit?.continuous ?? individualWeights?.wRoutine ?? 60)}٪`
                },
                {
                    label:"فعالیت‌های غیرمستمر",
                    value:`${scoreOrDash(liveCvi?.scores?.nonRoutine ?? nonRoutineScore)} · وزن ${weightText(liveCvi?.weights?.tasksActivitiesSplit?.nonContinuous ?? individualWeights?.wNonRoutine ?? 40)}٪`
                },
                { label:"امتیاز ترکیبی", value:scoreOrDash(selectedSubDriver.value) },
            ]
        };
        if (selectedSubDriver.id === "goals") return {
            title:"اهداف فردی",
            description:"خلاصه وضعیت اهداف فردی این دوره و امتیاز ثبت‌شده برای این مؤلفه.",
            facts:[
                {label:"امتیاز این مؤلفه",value:scoreOrDash(selectedSubDriver.value)},
                {label:"وضعیت",value:selectedSubDriver.value==null?"هنوز داده کافی برای محاسبه ثبت نشده":"امتیاز دوره محاسبه شده"}
            ]
        };
        if (selectedSubDriver.id === "peer") return {
            title:"بازخورد همکاران",
            description:"خلاصه نتیجه بازخوردهای واجد شرایط ثبت‌شده برای این دوره.",
            facts:[
                {label:"امتیاز این مؤلفه",value:scoreOrDash(selectedSubDriver.value)},
                {label:"وضعیت",value:selectedSubDriver.value==null?"هنوز پاسخ کافی برای نتیجه نهایی وجود ندارد":"نتیجه دوره قابل استفاده است"}
            ]
        };
        return {
            title:selectedSubDriver.label,
            description:selectedSubDriver.value == null ? "برای این سطح هنوز ارزیابی معتبر ثبت نشده است؛ وزن آن فعلاً از محاسبه کنار گذاشته شده و شاخص سطح دیگری نمایش داده نمی‌شود." : "شاخص‌های تشکیل‌دهنده این سطح و امتیاز هرکدام در دوره جاری.",
            facts:[
                {label:"امتیاز این سطح",value:scoreOrDash(selectedSubDriver.value)},
                {label:"تعداد شاخص‌ها",value:String(collectiveIndicatorItems.length)}
            ],
            indicators: collectiveIndicatorItems
        };
    })() : null;

    const deltaCopy = liveCvi?.comparison?.previousPeriodDelta;
    const displayedDelta = Number.isFinite(Number(periodDelta))
        ? Number(periodDelta)
        : (Number.isFinite(Number(deltaCopy)) ? Number(deltaCopy) : null);

    return (
        <section className="cvi-personal-performance">
            <div className="cvi-personal-hero">
                <div className="cvi-personal-hero-copy cvi-dashboard-card-heading">
                    <h2>تصویر کلی عملکرد این دوره</h2>
                    <p>ترکیب عملکرد فردی و مشارکت جمعی؛ با امکان مشاهده جزئیات و منطق محاسبه امتیاز.</p>
                    {liveCvi?.assessmentCoverage?.overall != null && <div title="پوشش ارزیابی بیانگر نسبت وزن داده‌های معتبر به وزن اجزای مورد انتظار است و از CVI کسر نمی‌شود." style={{display:"flex",alignItems:"center",gap:6,marginTop:10,fontSize:11,color:C.muted,fontWeight:400}}>
                        <span style={{width:6,height:6,borderRadius:"50%",background:"#adb6c7",flexShrink:0}} />
                        <span>پوشش ارزیابی: {liveCvi.assessmentCoverage.overall.toFixed(0)}٪</span>
                        <span style={{opacity:.75}}>· میزان تکمیل اطلاعات، نه امتیاز عملکرد</span>
                    </div>}
                    {displayedDelta != null && <div className={`cvi-premium-delta ${displayedDelta >= 0 ? "up" : "down"}`}>
                        {displayedDelta >= 0 ? "↑" : "↓"} {Math.abs(displayedDelta).toFixed(1)} نسبت به دوره قبل
                    </div>}
                </div>

                <button type="button" className="cvi-personal-side-score" onClick={() => { setSelectedSubDriver(null); setSelectedDriver({ id: "individual-summary", label: "عملکرد فردی", value: displayedIndividualScore, weight: 100 }); }}>
                    <span>عملکرد فردی</span>
                    <strong>{scoreText(displayedIndividualScore)}</strong>
                    
                    <em>مشاهده اجزا</em>
                </button>

                <div className="cvi-personal-cvi-zone">
                    <button type="button" className="cvi-personal-cvi-button" onClick={() => setExplainOpen(true)} aria-label="نمایش نحوه محاسبه CVI">
                        <div className="cvi-personal-cvi-ring" style={{ "--cvi-progress": `${Math.max(0, Math.min(100, Number(displayedCviScore) || 0))}%` }}>
                            <div>
                                <span>CVI دوره جاری</span>
                                <strong>{displayedCviScore == null ? "—" : `${Number(displayedCviScore).toFixed(1)}٪`}</strong>
                                
                            </div>
                        </div>
                        <b>نحوه محاسبه CVI</b>
                    </button>
                </div>

                <button type="button" className="cvi-personal-side-score collective" onClick={() => { setSelectedSubDriver(null); setSelectedDriver({ id: "collective-summary", label: "مشارکت جمعی", value: displayedOrgScore, weight: 100 }); }}>
                    <span>مشارکت جمعی</span>
                    <strong>{scoreText(displayedOrgScore)}</strong>
                    
                    <em>مشاهده اجزا</em>
                </button>
            </div>

            <DetailDrawer
                open={selectedDriver?.id === "individual-summary" || selectedDriver?.id === "collective-summary"}
                title={selectedDriver?.id === "individual-summary" ? "عملکرد فردی" : "مشارکت جمعی"}
                eyebrow="جزئیات امتیاز"
                description={selectedDriver?.id === "individual-summary"
                    ? "اجزای عملکرد فردی و امتیاز هر مؤلفه در دوره جاری."
                    : "اجزای مشارکت جمعی و امتیاز هر سطح در دوره جاری."}
                onClose={() => { setSelectedSubDriver(null); setSelectedDriver(null); }}
                footer={selectedSubDriver?.onOpenFull ? <button type="button" className="cvi-score-full-detail" onClick={() => { const openFull = selectedSubDriver.onOpenFull; setSelectedSubDriver(null); setSelectedDriver(null); openFull(); }}>مشاهده جزئیات کامل ←</button> : null}
            >
                <div className="cvi-drawer-master-detail">
                    <div className="cvi-explain-section cvi-drawer-master-list">
                        {(selectedDriver?.id === "individual-summary" ? components : strategic).map(item =>
                            <button type="button" key={item.id}
                                className={`cvi-drawer-score-row${selectedSubDriver?.id === item.id ? " is-active" : ""}`}
                                onClick={() => setSelectedSubDriver(current => current?.id === item.id ? null : item)}>
                                <span>{item.label}</span>
                                <b>{scoreText(item.value)}</b>
                                <small>وزن {weightText(item.weight)}٪</small>
                                <UiIcon name="chevronDown" size={14} />
                            </button>
                        )}
                    </div>

                    <div className={`cvi-drawer-inline-detail${selectedSubDriver ? " is-open" : ""}`}>
                        {selectedSubDriver && selectedDriverDetails ? <>
                            {selectedDriverDetails.indicators ? (
                                <div className="cvi-collective-indicator-list is-standalone">
                                    <div className="cvi-collective-indicator-list-head">
                                        <strong>شاخص‌های {selectedDriverDetails.title}</strong>
                                    </div>
                                    {selectedDriverDetails.indicators.length ? selectedDriverDetails.indicators.map(item => <div key={item.id} className="cvi-collective-indicator-row">
                                        <div className="cvi-collective-indicator-copy">
                                            <strong>{item.label}</strong>
                                            <small>
                                                {item.actual != null && item.target != null ? `عملکرد ${Number(item.actual).toLocaleString("fa-IR")} از هدف ${Number(item.target).toLocaleString("fa-IR")}` : "داده تأییدشده‌ای برای این دوره ثبت نشده"}
                                                {item.weight > 0 ? ` · وزن ${weightText(item.weight)}٪` : ""}
                                            </small>
                                        </div>
                                        <b className={item.score == null ? "is-empty" : ""}>{item.score == null ? "—" : Number(item.score).toFixed(0)}</b>
                                    </div>) : <div className="cvi-collective-indicator-empty">شاخصی برای این سطح تعریف نشده است.</div>}
                                </div>
                            ) : <>
                                <div className="cvi-drawer-inline-detail-head">
                                    <div>
                                        <span>اطلاعات بیشتر</span>
                                        <h4>{selectedDriverDetails.title}</h4>
                                    </div>
                                    <strong>{scoreText(selectedSubDriver.value)}</strong>
                                </div>
                                <p>{selectedDriverDetails.description}</p>
                                <div className="cvi-score-quick-facts">
                                    {selectedDriverDetails.facts?.map(fact => <div key={fact.label}>
                                        <span>{fact.label}</span>
                                        <strong>{fact.value}</strong>
                                    </div>)}
                                </div>
                            </>}
                        </> : <div className="cvi-drawer-inline-placeholder">برای مشاهده اطلاعات بیشتر، یکی از مؤلفه‌های بالا را انتخاب کنید.</div>}
                    </div>
                </div>
            </DetailDrawer>

            <DetailDrawer
                open={explainOpen}
                title="نحوه محاسبه CVI و پوشش ارزیابی"
                eyebrow="شفافیت محاسبات"
                description="CVI دوره جاری از ترکیب عملکرد فردی و مشارکت جمعی با میانگین هندسی ساخته می‌شود."
                onClose={() => setExplainOpen(false)}
            >
                <div style={{padding:"12px 14px",background:"#F7F9FC",borderRadius:12,marginBottom:14,fontSize:12,lineHeight:1.9,color:"#38425D"}}>
                    <strong style={{display:"block",fontSize:13,marginBottom:4}}>۱. شاخص CVI چگونه محاسبه می‌شود؟</strong>
                    CVI میانگین هندسی دو بُعد «عملکرد فردی» و «مشارکت جمعی» است؛ یعنی ریشه دوم حاصل‌ضرب این دو امتیاز. بنابراین پایین بودن هرکدام، نتیجه نهایی را کاهش می‌دهد.
                    <div style={{direction:"ltr",textAlign:"center",marginTop:8,fontWeight:600,fontSize:13}}>CVI = √(Individual Performance × Collective Contribution)</div>
                </div>
                <div className="cvi-explain-formula">
                    <span>عملکرد فردی</span><strong>{scoreText(displayedIndividualScore)}</strong>
                    <b>×</b>
                    <span>مشارکت جمعی</span><strong>{scoreText(displayedOrgScore)}</strong>
                    <em>√</em>
                    <span>CVI</span><strong>{displayedCviScore == null ? "—" : `${Number(displayedCviScore).toFixed(1)}٪`}</strong>
                </div>
                <div className="cvi-explain-section">
                    <h4>ساخت عملکرد فردی</h4>
                    {components.map(item => <div key={item.id} className="cvi-explain-row"><span>{item.label}</span><b>{scoreText(item.value)}</b><small>وزن {weightText(item.weight)}٪</small></div>)}
                </div>
                <div className="cvi-explain-section">
                    <h4>ساخت مشارکت جمعی</h4>
                    {strategic.map(item => <div key={item.id} className="cvi-explain-row"><span>{item.label}</span><b>{scoreText(item.value)}</b><small>وزن {weightText(item.weight)}٪</small></div>)}
                </div>
                <div className="cvi-explain-note">اگر یک مؤلفه هنوز داده معتبر نداشته باشد، امتیاز آن صفر فرض نمی‌شود؛ وزن مؤثر میان مؤلفه‌های دارای داده توزیع می‌شود. امتیاز صفرِ ارزیابی‌شده با «ثبت‌نشده» متفاوت است.</div>
                <div className="cvi-explain-section" style={{marginTop:14}}>
                    <h4>۲. پوشش ارزیابی چیست؟</h4>
                    <p style={{fontSize:12,lineHeight:1.9,color:C.muted,margin:"8px 0"}}>
                        پوشش ارزیابی نشان می‌دهد چه سهمی از وزن مؤلفه‌های مورد انتظار، اطلاعات معتبر و قابل‌محاسبه دارد. این درصد کیفیت تکمیل داده‌ها را توضیح می‌دهد و مستقیماً از امتیاز CVI کم نمی‌کند.
                    </p>
                    <div style={{background:"#F7F9FC",borderRadius:10,padding:12,fontSize:12,lineHeight:2}}>
                        <div>پوشش فردی: <strong>{liveCvi?.assessmentCoverage?.individual?.percent == null ? "—" : `${liveCvi.assessmentCoverage.individual.percent.toFixed(0)}٪`}</strong></div>
                        <div>پوشش مشارکت جمعی: <strong>{liveCvi?.assessmentCoverage?.organizational?.percent == null ? "—" : `${liveCvi.assessmentCoverage.organizational.percent.toFixed(0)}٪`}</strong></div>
                        <div style={{borderTop:"1px solid #E3E7EE",marginTop:6,paddingTop:6}}>پوشش کلی: <strong>{liveCvi?.assessmentCoverage?.overall == null ? "—" : `${liveCvi.assessmentCoverage.overall.toFixed(0)}٪`}</strong></div>
                    </div>
                    <p style={{fontSize:11,lineHeight:1.85,color:C.muted,margin:"8px 0 0"}}>روش محاسبه: در هر بُعد، مجموع وزنِ دارای داده معتبر بر مجموع وزنِ مورد انتظار تقسیم می‌شود؛ در اجزای چندمرحله‌ای پوشش جزئی هم منظور می‌شود. سپس میانگین ساده پوشش فردی و جمعی، پوشش کلی است. برای مثال، پوشش ۳۵٪ یعنی هنوز همه اطلاعات مورد انتظار تکمیل نشده‌اند؛ نه اینکه ۶۵ امتیاز از CVI کم شده باشد.</p>
                </div>
            </DetailDrawer>
        </section>
    );
}

function GlobalResponsiveStyles() {
    return <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body, #root { width: 100%; min-height: 100%; margin: 0; }
        body { overflow-x: hidden; }
        button, input, select, textarea { max-width: 100%; }
        table { max-width: 100%; }
        @media (max-width: 720px) {
            button { min-height: 36px; }
            input, select, textarea { font-size: 16px !important; }
            .cvi-workflow-modal { width: calc(100vw - 20px) !important; max-height: 82vh !important; }
            .cvi-workflow-grid { grid-template-columns: 1fr !important; }
            .cvi-attachment-row { align-items: flex-start !important; flex-wrap: wrap !important; }
        }
        @media (max-width: 420px) {
            .cvi-hide-xs { display: none !important; }
        }
    `}</style>;
}

async function protectedFileDownload(url, fileName) {
    const token = sessionStorage.getItem("cvi_token");
    if (!token) throw new Error("Authentication required.");
    const response = await fetch(`${API_BASE_URL}${url}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new Error(`Download failed (${response.status}).`);
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = fileName || "attachment";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(href);
}

function ProtectedAttachmentList({ module, entityId, attachments = [], canDelete = false }) {
    const { t } = useI18n();
    const [rows, setRows] = useState(attachments);
    const [busyId, setBusyId] = useState(null);
    useEffect(() => setRows(attachments), [attachments]);
    if (!entityId || !rows?.length) return null;
    const pathFor = (attachmentId, download = false) => module === "indicator"
        ? `/api/indicators/values/${entityId}/files/${attachmentId}${download ? "/download" : ""}`
        : `/api/activities/${entityId}/files/${attachmentId}${download ? "/download" : ""}`;
    const remove = async attachment => {
        if (!canDelete || busyId) return;
        setBusyId(attachment.id);
        try {
            const token = sessionStorage.getItem("cvi_token");
            const response = await fetch(`${API_BASE_URL}${pathFor(attachment.id)}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) throw new Error("delete failed");
            setRows(current => current.filter(item => item.id !== attachment.id));
        } catch (error) {
            console.error("Attachment delete failed:", error);
            uxNotify(t("file.failed"));
        } finally { setBusyId(null); }
    };
    return <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
        {rows.map(attachment => <div key={attachment.id} className="cvi-attachment-row" style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", background: C.bg, borderRadius: 7, fontSize: 11 }}>
            <span aria-hidden="true"><UiIcon name="paperclip" size={14} /></span>
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.fileName}</span>
            {attachment.sizeBytes != null && <span style={{ color: C.hint, fontSize: 9 }}>{Math.max(1, Math.round(Number(attachment.sizeBytes) / 1024))} KB</span>}
            <button onClick={() => protectedFileDownload(pathFor(attachment.id, true), attachment.fileName).catch(() => uxNotify(t("file.failed")))} style={{ border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>{t("file.download")}</button>
            {canDelete && <button disabled={busyId === attachment.id} onClick={() => remove(attachment)} style={{ border: `1px solid ${C.red}`, color: C.red, background: C.redLt, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>{busyId === attachment.id ? t("file.deleting") : t("file.delete")}</button>}
        </div>)}
    </div>;
}

function WorkflowCenterButton({ hidden = false }) {
    const { activePeriod } = useData();
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("tasks");
    const [tasks, setTasks] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const token = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("cvi_token") : null;
    const query = activePeriod?.id ? `?periodId=${encodeURIComponent(activePeriod.id)}` : "";
    const load = async (quiet = false) => {
        if (!token) return;
        if (!quiet) setLoading(true);
        setError("");
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [taskResponse, historyResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/api/workflow/tasks${query}`, { headers }),
                fetch(`${API_BASE_URL}/api/workflow/history${query}${query ? "&" : "?"}limit=100`, { headers }),
            ]);
            const taskPayload = await taskResponse.json().catch(() => ({}));
            const historyPayload = await historyResponse.json().catch(() => ({}));
            if (!taskResponse.ok || !historyResponse.ok) throw new Error("workflow load failed");
            setTasks(taskPayload.tasks || []);
            setHistory(historyPayload.events || []);
        } catch (loadError) {
            console.error("Workflow center load failed:", loadError);
            if (!quiet) setError(t("workflow.error"));
        } finally { if (!quiet) setLoading(false); }
    };
    useEffect(() => {
        if (hidden || !token) return undefined;
        load(true);
        const timer = setInterval(() => load(true), 60000);
        return () => clearInterval(timer);
    }, [hidden, token, activePeriod?.id]);
    if (hidden || !token) return null;
    const moduleLabel = module => t(`workflow.${module}`);
    const overdueTasks = tasks.filter(task => task.overdue);
    const currentTasks = tasks.filter(task => !task.overdue);
    const taskSections = [
        { id: "overdue", label: t("workflow.overdue"), rows: overdueTasks, urgent: true },
        { id: "current", label: t("workflow.tasks"), rows: currentTasks, urgent: false },
    ].filter(section => section.rows.length);
    return <>
        <button className="cvi-icon-button" aria-label={t("workflow.title")} title={t("workflow.title")} onClick={() => { setOpen(true); load(); }} style={{ position: "relative", fontSize: 16 }}>
            <UiIcon name="bell" size={17} />
            {tasks.length > 0 && <span style={{ position: "absolute", top: -4, insetInlineEnd: -4, minWidth: 17, height: 17, borderRadius: 10, background: C.red, color: "#fff", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", border: "2px solid #000064" }}>{tasks.length > 99 ? "99+" : tasks.length}</span>}
        </button>
        {open && <div role="dialog" aria-modal="true" aria-label={t("workflow.title")} onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,20,0.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 10 }}>
            <div className="cvi-workflow-modal cvi-workflow-dialog" onClick={event => event.stopPropagation()}>
                <div style={{ padding: "13px 14px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${C.border}` }}>
                    <strong style={{ flex: 1, fontSize: 14 }}>{t("workflow.title")}</strong>
                    <button onClick={() => load()} style={{ border: `1px solid ${C.border}`, background: "#fff", borderRadius: 7, padding: "6px 9px", cursor: "pointer", fontFamily: "inherit" }}>{t("workflow.refresh")}</button>
                    <button aria-label={t("workflow.close")} onClick={() => setOpen(false)} style={{ border: "none", background: C.bg, borderRadius: 7, width: 32, height: 32, cursor: "pointer" }}><UiIcon name="close" size={16} /></button>
                </div>
                <div style={{ display: "flex", padding: "8px 12px 0", gap: 6 }}>
                    {[['tasks', t("workflow.tasks")], ['history', t("workflow.history")]].map(([id, label]) => <button key={id} onClick={() => setTab(id)} style={{ border: "none", borderBottom: `2px solid ${tab === id ? C.cyan : "transparent"}`, background: "transparent", padding: "8px 12px", color: tab === id ? C.navy : C.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: tab === id ? 600 : 400 }}>{label}{id === "tasks" && tasks.length ? ` (${tasks.length})` : ""}</button>)}
                </div>
                <div style={{ overflowY: "auto", padding: 12, flex: 1 }}>
                    {loading && <div style={{ padding: 24, textAlign: "center", color: C.muted }}>{t("workflow.loading")}</div>}
                    {!loading && error && <div style={{ padding: 24, textAlign: "center", color: C.red }}>{error}</div>}
                    {!loading && !error && tab === "tasks" && (tasks.length === 0 ? <EmptyState title={t("workflow.emptyTasks")} description={t("workflow.title")} icon="check" /> : <div className="cvi-workflow-sections">{taskSections.map(section => <section key={section.id} className={`cvi-workflow-section${section.urgent ? " is-urgent" : ""}`}><div className="cvi-workflow-section-head"><strong>{section.label}</strong><span>{section.rows.length}</span></div><div className="cvi-workflow-grid">{section.rows.map(task => <div key={task.id} className={`cvi-workflow-task${task.overdue ? " is-overdue" : ""}`}><div className="cvi-workflow-task-meta"><span>{moduleLabel(task.module)}</span>{task.overdue && <StatusBadge status="pending" />}</div><div className="cvi-workflow-task-title">{task.title}</div>{task.description && <div className="cvi-workflow-task-description">{task.description}</div>}{task.subjectName && <div className="cvi-workflow-task-subject">{t("workflow.for", { name: task.subjectName })}</div>}</div>)}</div></section>)}</div>)}
                    {!loading && !error && tab === "history" && (history.length === 0 ? <div style={{ padding: 28, textAlign: "center", color: C.muted }}>{t("workflow.emptyHistory")}</div> : <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>{history.map(event => <div key={event.id} style={{ display: "flex", gap: 10, borderBottom: `1px solid ${C.bg}`, padding: "8px 4px" }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: C.cyan, marginTop: 5, flexShrink: 0 }} /><div style={{ flex: 1, minWidth: 0 }}><div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}><span style={{ fontSize: 11.5, fontWeight: 600 }}>{event.title}</span><span style={{ fontSize: 9, color: C.muted }}>{moduleLabel(event.module)}</span></div><div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{event.eventType}{event.subjectName ? ` · ${event.subjectName}` : ""}</div>{event.note && <div style={{ fontSize: 10, color: C.text, marginTop: 3 }}>{event.note}</div>}<div style={{ fontSize: 9, color: C.hint, marginTop: 3 }}>{event.actorName || "System"} · {event.createdAt ? formatIranSubmissionTimestamp(event.createdAt) : ""}</div></div></div>)}</div>)}
                </div>
            </div>
        </div>}
    </>;
}

// TopBar واحد: هم برای هدر اصلی و هم صفحات فرعی
function TopBar({ onBack, title, right, stepDots, onSearch, searchItems = [] }) {
    const { orgSettings, activePeriod } = useData();
    const { t, language } = useI18n();
    const [searchText, setSearchText] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    // Search the actual navigation for the active role; never offer inaccessible destinations.
    const normalizedQuery = String(searchText).trim().replace(/ي/g, "ی").replace(/ك/g, "ک").replace(/\u200c/g, " ").toLowerCase();
    const searchMatches = searchItems
        .filter(item => item && item.id && item.label)
        .filter(item => [item.label, item.group, ...(item.id === "teamdash" || item.id === "dept" ? ["داشبورد تیم", "عملکرد تیم"] : []), ...(item.id === "company" ? ["داشبورد شرکت", "داشبورد سازمان"] : [])]
            .some(value => String(value || "").replace(/ي/g, "ی").replace(/ك/g, "ک").replace(/\u200c/g, " ").toLowerCase().includes(normalizedQuery)))
        .slice(0, 8)
        .map(item => ({...item, nav: item.id}));
    const quickSearchRef = useRef(null);
    useEffect(() => {
        if (onBack || !onSearch) return undefined;
        const focusOnShortcut = event => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                quickSearchRef.current?.focus();
            }
        };
        window.addEventListener("keydown", focusOnShortcut);
        return () => window.removeEventListener("keydown", focusOnShortcut);
    }, [onBack, onSearch]);
    const daysLeft = !onBack ? daysRemainingInPeriod(activePeriod) : null;
    const periodStatusText = daysLeft == null ? ""
        : language === "fa" ? (daysLeft > 0 ? `${Number(daysLeft).toLocaleString("fa-IR")} روز باقی‌مانده` : daysLeft === 0 ? "امروز آخرین روز" : "پایان‌یافته")
            : language === "fr" ? (daysLeft > 0 ? `${daysLeft} jours restants` : daysLeft === 0 ? "dernier jour aujourd’hui" : "terminée")
                : language === "ar" ? (daysLeft > 0 ? `${daysLeft} يوم متبقٍ` : daysLeft === 0 ? "اليوم هو اليوم الأخير" : "منتهية")
                    : language === "es" ? (daysLeft > 0 ? `${daysLeft} días restantes` : daysLeft === 0 ? "hoy es el último día" : "finalizado")
                        : (daysLeft > 0 ? `${daysLeft} days left` : daysLeft === 0 ? "last day today" : "ended");

    return (
        <div className="cvi-shell-header">
            <div className="cvi-brand">
                {onBack ? (
                    <>
                        <button className="cvi-icon-button" onClick={onBack} aria-label={t("common.back")} style={{ width: "auto", paddingInline: 11, fontSize: 12 }}>
                            ← {t("common.back")}
                        </button>
                        <span style={{ color: "#fff", fontSize: 14, fontWeight: 650, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span>
                    </>
                ) : (
                    <>
                        {orgSettings?.logo ? (
                            <img className="cvi-brand-logo" src={orgSettings.logo} alt="CVI" />
                        ) : (
                            <div className="cvi-brand-mark">CVI</div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <div className="cvi-brand-title">{language === "fa" ? "شاخص ارزیابی مشارکت" : "Contribution Value Index"}</div>
                            <div className="cvi-brand-meta">Contribution Value Index</div>
                        </div>
                        {orgSettings?.name && (
                            <div className="cvi-org-pill cvi-hide-xs" title={orgSettings.name}>
                                <UiIcon name="building" size={13} />
                                <span>{orgSettings.name}</span>
                            </div>
                        )}
                        {activePeriod && (
                            <div className="cvi-period-pill cvi-hide-xs">
                                <span aria-hidden="true">●</span>
                                <span>{activePeriod.label}</span>
                                {daysLeft != null && <span>· {periodStatusText}</span>}
                            </div>
                        )}
                    </>
                )}
            </div>
            {!onBack && onSearch && <div className="cvi-header-quick-search">
                <form onSubmit={e=>{e.preventDefault();const target=searchMatches[0];if(target){onSearch(target.nav);setSearchOpen(false);setSearchText("");}}} role="search">
                    <UiIcon name="search" size={15}/>
                    <input ref={quickSearchRef} aria-label="جستجوی بخش‌های CVI" placeholder="جستجوی بخش‌ها و صفحات..." value={searchText} onChange={e=>{setSearchText(e.target.value);setSearchOpen(true);}} onFocus={()=>setSearchOpen(true)} onBlur={()=>setTimeout(()=>setSearchOpen(false),150)} />

                </form>
                {searchOpen&&searchText.trim()&&<div className="cvi-header-search-results">{searchMatches.length?searchMatches.map(item=><button type="button" key={item.nav} onMouseDown={e=>e.preventDefault()} onClick={()=>{onSearch(item.nav);setSearchText("");setSearchOpen(false);}}>{item.label}</button>):<small>بخشی یافت نشد</small>}</div>}
            </div>}
            <div className="cvi-header-actions">
                {stepDots && (
                    <div style={{ display: "flex", gap: 4 }}>
                        {[0, 1, 2].map(i => (
                            <div key={i} style={{
                                height: 6, borderRadius: 3,
                                background: i === stepDots - 1 ? C.cyan : i < stepDots - 1 ? "rgba(0,200,230,0.5)" : "rgba(255,255,255,0.2)",
                                width: i === stepDots - 1 ? 16 : 6, transition: "all 0.2s"
                            }} />
                        ))}
                    </div>
                )}
                <WorkflowCenterButton hidden={Boolean(onBack)} />
                {right}
            </div>
        </div>
    );
}

function SubBar({ title, onBack }) {
    const { t } = useI18n();
    return (
        <div style={{
            background: C.navy, padding: "10px 16px", display: "flex",
            alignItems: "center", gap: 10, flexShrink: 0
        }}>
            <button onClick={onBack}
                style={{
                    background: "rgba(255,255,255,0.1)", border: "none", color: "#fff",
                    borderRadius: 7, padding: "5px 10px", cursor: "pointer", fontSize: 12
                }}>
                ← {t("common.back")}
            </button>
            <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>{title}</span>
        </div>
    );
}

// ── APP LAYOUT (مشترک بین همه‌ی پنل‌ها — دسکتاپ: Sidebar / موبایل: BottomNav) ──
// items: [{ id, label, icon, badge? }]
function Sidebar({ items, nav, onNav, onLogout }) {
    const { language } = useI18n();
    const groupNames = {
        en: { overview: "Overview", performance: "Performance", workflow: "Workflow", people: "People", account: "Account" },
        fr: { overview: "Aperçu", performance: "Performance", workflow: "Flux de travail", people: "Équipe", account: "Compte" },
        fa: { overview: "داشبورد", performance: "عملکرد", workflow: "گردش کار", people: "افراد", account: "حساب کاربری" },
        ar: { overview: "نظرة عامة", performance: "الأداء", workflow: "سير العمل", people: "الأفراد", account: "الحساب" },
        es: { overview: "Resumen", performance: "Rendimiento", workflow: "Flujo de trabajo", people: "Personas", account: "Cuenta" },
    }[language] || { overview: "Overview", performance: "Performance", workflow: "Workflow", people: "People", account: "Account" };

    const classify = item => {
        if (item.section) return item.section;
        const id = String(item.id || "").toLowerCase();
        if (id.includes("dashboard") || id === "home" || id === "company" || id === "dept" || id === "overview") return "overview";
        if (id.includes("member") || id.includes("people") || id.includes("user")) return "people";
        if (id.includes("history") || id.includes("review") || id.includes("inbox")) return "workflow";
        if (id === "help") return "account";
        if (id.includes("profile") || id.includes("setting")) return "account";
        return "performance";
    };

    let groups;
    if (items.some(item => item.group)) {
        const seen = [];
        items.forEach(item => { if (item.group && !seen.includes(item.group)) seen.push(item.group); });
        groups = seen.map(label => ({ id: label, label, items: items.filter(item => item.group === label) }));
    } else {
        const order = ["overview", "performance", "workflow", "people", "account"];
        groups = order.map(id => ({ id, label: groupNames[id], items: items.filter(item => classify(item) === id) })).filter(group => group.items.length);
    }

    return (
        <aside className="cvi-sidebar" aria-label="Primary navigation">
            {groups.map(group => (
                <div className="cvi-sidebar-group" key={group.id}>
                    <div className="cvi-sidebar-label">{group.label}</div>
                    {group.items.map(item => <NavigationItem key={item.id} item={item} active={nav} onNav={onNav} />)}
                </div>
            ))}
            <div style={{ flex: 1, minHeight: 18 }} />
            <button onClick={onLogout} className="cvi-sidebar-logout"><UiIcon name="logout" size={16} />{language === "fa" ? "خروج از سامانه" : language === "fr" ? "Déconnexion" : language === "ar" ? "تسجيل الخروج" : language === "es" ? "Cerrar sesión" : "Sign out"}</button>
        </aside>
    );
}

// items بدون آیتم خروج داده می‌شن — این کامپوننت خودش دکمه‌ی خروج رو به انتها اضافه می‌کنه
export function AppBottomNav({ items, active, onNav, onLogout }) {
    const [openMenu, setOpenMenu] = useState(null);
    const hierarchical = items.some(item => item.id === "tasks-activities");
    const performanceItems = items.filter(item => item.section === "performance");
    const primary = items.filter(item => ["dashboard", "company", "inbox"].includes(item.id));
    const moreItems = items.filter(item => !primary.includes(item) && !performanceItems.includes(item));
    const allItems = hierarchical ? [
        ...primary.slice(0, 1),
        { id: "performance-menu", label: "عملکرد", icon: "performance", children: performanceItems, badge: performanceItems.reduce((sum, item) => sum + (item.badge || 0), 0) },
        ...primary.slice(1),
        { id: "more-menu", label: "بیشتر", icon: "more", children: [...moreItems, { id: "logout", label: "خروج", icon: "logout" }] },
    ] : [...items, { id: "logout", label: "خروج", icon: "logout" }];
    const menu = allItems.find(item => item.id === openMenu);
    const navigate = id => { setOpenMenu(null); id === "logout" ? onLogout() : onNav(id); };
    return (
        <>
        {menu && <section className="cvi-mobile-navigation-panel" aria-label={menu.label}>
            <button type="button" className="cvi-navigation-close" onClick={() => setOpenMenu(null)}>بستن</button>
            {menu.children.map(item => <NavigationItem key={item.id} item={item} active={active} onNav={navigate} />)}
        </section>}
        <nav className="cvi-bottom-nav" aria-label="Mobile navigation">
            {allItems.map(item => (
                <button key={item.id}
                    onClick={() => item.children ? setOpenMenu(openMenu === item.id ? null : item.id) : navigate(item.id)}
                    aria-expanded={item.children ? openMenu === item.id : undefined}
                    className={`cvi-bottom-nav-item${isNavigationActive(item, active) ? " is-active" : ""}`}
                    aria-current={active === item.id ? "page" : undefined}>
                    {item.badge > 0 && (
                        <span style={{
                            position: "absolute", top: 6, insetInlineEnd: "calc(50% - 18px)",
                            minWidth: 8, height: 8, background: C.red, borderRadius: 10, border: "2px solid #fff"
                        }} />
                    )}
                    <span className="cvi-bottom-nav-icon" aria-hidden="true"><UiIcon name={item.icon} size={19} /></span>
                    <span className="cvi-bottom-nav-label">{item.label}</span>
                </button>
            ))}
        </nav>
        </>
    );
}

// ── LOGIN PAGE (واحد برای همه نقش‌ها) ──────────────────────────────────────────
function LoginPage({ onLogin }) {
    const { t, language, setLanguage } = useI18n();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (language !== "fa") setLanguage("fa");
        try {
            localStorage.setItem("cvi_language", "fa");
            sessionStorage.setItem("cvi_login_language", "fa");
        } catch {}
        if (typeof document !== "undefined") {
            document.documentElement.lang = "fa";
            document.documentElement.dir = "rtl";
        }
    }, []);

    const handle = async () => {
        if (loading) return;

        const cleanUsername = username.trim();
        if (!cleanUsername || !password) {
            setError("نام کاربری و رمز عبور را وارد کنید.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: cleanUsername, password }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setError(response.status === 401 ? "نام کاربری یا رمز عبور صحیح نیست." : "ورود به سامانه انجام نشد. دوباره تلاش کنید.");
                return;
            }

            if (!data?.user || !data?.token) {
                setError("پاسخ دریافتی از سامانه معتبر نیست.");
                return;
            }

            const apiUser = data.user;
            const normalizedUser = {
                ...apiUser,
                role: String(apiUser.role || "").toLowerCase(),
                deptId: apiUser.department?.id ?? null,
                teamId: apiUser.team?.id ?? null,
                posId: apiUser.position?.id ?? null,
            };

            const resolvedLanguage = SUPPORTED_LANGUAGES[apiUser.preferredLanguage]
                ? apiUser.preferredLanguage
                : language;
            setLanguage(resolvedLanguage);
            normalizedUser.preferredLanguage = apiUser.preferredLanguage || resolvedLanguage;

            onLogin(normalizedUser, data.token);
        } catch (err) {
            console.error("Login request failed:", err);
            setError("ارتباط با سرور برقرار نشد.");
        } finally {
            setLoading(false);
        }
    };

    const openCviGuide = () => {
        const overlay = document.querySelector("#cvi-login-guide-fa-root .cvi-fa-overlay");
        if (!overlay) return;
        overlay.classList.add("is-open");
        document.body.style.overflow = "hidden";
        overlay.querySelector(".cvi-fa-close")?.focus();
    };

    return (
        <main className="cvi-ir-login-page" data-no-legacy-translate="true">
            <style>{`
                html, body, #root {
                    height: 100%;
                    margin: 0;
                    overflow: hidden;
                }
                * { box-sizing: border-box; }
                .cvi-ir-login-page {
                    height: 100vh;
                    overflow: hidden;
                    display: grid;
                    grid-template-columns: minmax(440px, .95fr) minmax(0, 1.05fr);
                    direction: ltr;
                    background: #fff;
                    color: #111827;
                    font-family: Vazirmatn, "IRANSansX", "Segoe UI", Tahoma, Arial, sans-serif;
                }
                .cvi-ir-brand-panel {
                    grid-column: 2;
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    min-height: 100vh;
                    overflow: hidden;
                    padding: clamp(36px, 5vw, 72px);
                    direction: rtl;
                    text-align: right;
                    background:
                        radial-gradient(circle at 84% 12%, rgba(0,200,230,.11), transparent 32%),
                        linear-gradient(215deg, #F7FBFF 0%, #F8FAFC 56%, #F4F7FB 100%);
                    border-left: 1px solid #E8EDF4;
                }
                .cvi-ir-brand-panel::after {
                    content: "";
                    position: absolute;
                    width: 420px;
                    height: 420px;
                    border-radius: 50%;
                    left: -210px;
                    bottom: -220px;
                    border: 1px solid rgba(0,0,100,.07);
                    box-shadow: 0 0 0 58px rgba(0,200,230,.025), 0 0 0 116px rgba(0,0,100,.018);
                    pointer-events: none;
                }
                .cvi-ir-brand {
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                    width: fit-content;
                    position: relative;
                    z-index: 1;
                }
                .cvi-ir-logo {
                    width: 42px;
                    height: 42px;
                    display: grid;
                    place-items: center;
                    border-radius: 11px;
                    background: #000064;
                    color: #fff;
                    font: 800 13px/1 "Segoe UI", sans-serif;
                    letter-spacing: .03em;
                    box-shadow: 0 8px 24px rgba(0,0,100,.12);
                }
                .cvi-ir-brand-copy strong {
                    display: block;
                    color: #000064;
                    font-size: 15px;
                    font-weight: 700;
                    line-height: 1.3;
                }
                .cvi-ir-brand-copy span {
                    display: block;
                    margin-top: 4px;
                    color: #778197;
                    font: 600 10px/1.3 "Segoe UI", sans-serif;
                    letter-spacing: .05em;
                    direction: ltr;
                    text-align: right;
                }
                .cvi-ir-hero {
                    position: relative;
                    z-index: 1;
                    max-width: 520px;
                    margin: auto 0;
                    padding: 56px 0;
                }
                .cvi-ir-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 10px;
                    color: #0B6F79;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: .02em;
                }
                .cvi-ir-eyebrow::before {
                    content: "";
                    width: 22px;
                    height: 2px;
                    border-radius: 99px;
                    background: #00C8E6;
                }
                .cvi-ir-hero h1 {
                    margin: 0;
                    max-width: 430px;
                    color: #07184A;
                    font-size: clamp(28px, 2.15vw, 36px);
                    line-height: 1.6;
                    letter-spacing: -.01em;
                    font-weight: 700;
                    text-wrap: balance;
                }
                .cvi-ir-hero p {
                    max-width: 455px;
                    margin: 14px 0 0;
                    color: #667085;
                    font-size: 13.5px;
                    line-height: 1.95;
                }
                .cvi-ir-form-panel {
                    grid-column: 1;
                    grid-row: 1;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 100vh;
                    overflow: hidden;
                    padding: 40px;
                    background: #fff;
                    direction: rtl;
                }
                .cvi-ir-form-wrap {
                    width: min(100%, 345px);
                }
                .cvi-ir-form h2 {
                    margin: 0;
                    color: #101828;
                    font-size: 23px;
                    line-height: 1.55;
                    font-weight: 700;
                    text-align: right;
                }
                .cvi-ir-form-subtitle {
                    margin: 5px 0 24px;
                    color: #7A8599;
                    font-size: 12.5px;
                    line-height: 1.9;
                    text-align: right;
                }
                .cvi-ir-field {
                    margin-bottom: 18px;
                }
                .cvi-ir-field label {
                    display: block;
                    margin-bottom: 7px;
                    color: #344054;
                    font-size: 11.5px;
                    font-weight: 650;
                }
                .cvi-ir-input {
                    box-sizing: border-box;
                    width: 100%;
                    height: 42px;
                    padding: 0 13px;
                    border: 1px solid #D8DEE8;
                    border-radius: 8px;
                    background: #fff;
                    color: #101828;
                    outline: none;
                    font: 400 13px/1 Vazirmatn, "IRANSansX", "Segoe UI", Tahoma, sans-serif;
                    transition: border-color .15s ease, box-shadow .15s ease;
                }
                .cvi-ir-input::placeholder { color: #B0B8C6; }
                .cvi-ir-input:focus {
                    border-color: #00AFC9;
                    box-shadow: 0 0 0 3px rgba(0,200,230,.10);
                }
                .cvi-ir-error {
                    margin: -4px 0 14px;
                    padding: 9px 11px;
                    border-radius: 8px;
                    background: #FFF5F5;
                    color: #C33A3A;
                    font-size: 11.5px;
                    line-height: 1.7;
                }
                .cvi-ir-submit {
                    width: 100%;
                    height: 46px;
                    margin-top: 4px;
                    border: 0;
                    border-radius: 9px;
                    background: #000064;
                    color: #fff;
                    font: 700 12.5px/1 Vazirmatn, "IRANSansX", "Segoe UI", Tahoma, sans-serif;
                    cursor: pointer;
                    transition: transform .15s ease, background .15s ease, box-shadow .15s ease;
                    box-shadow: 0 6px 18px rgba(0,0,100,.12);
                }
                .cvi-ir-submit:hover:not(:disabled) {
                    background: #080878;
                    transform: translateY(-1px);
                    box-shadow: 0 8px 22px rgba(0,0,100,.16);
                }
                .cvi-ir-submit:disabled { opacity: .65; cursor: wait; }
                .cvi-ir-guide-link {
                    margin-top: 16px;
                    text-align: center;
                }
                .cvi-ir-guide-link button {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 0;
                    border: 0;
                    background: transparent;
                    color: #667085;
                    font: 700 11.5px/1.5 Vazirmatn, "IRANSansX", "Segoe UI", Tahoma, sans-serif;
                    cursor: pointer;
                    transition: color .15s ease;
                }
                .cvi-ir-guide-link button:hover { color: #000064; }
                .cvi-ir-guide-icon {
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    display: inline-grid;
                    place-items: center;
                    background: #EEF4FF;
                    color: #000064;
                    font: 800 11px/1 Georgia, serif;
                }
                .cvi-ir-powered {
                    position: absolute;
                    left: 28px;
                    bottom: 22px;
                    color: #B8BEC8;
                    font: 400 9.5px/1.4 "Segoe UI", Arial, sans-serif;
                    letter-spacing: .015em;
                    direction: ltr;
                    white-space: nowrap;
                    user-select: none;
                }
                @media (max-width: 900px) {
                    html, body, #root { overflow: auto; }
                    .cvi-ir-login-page {
                        grid-template-columns: 1fr;
                        height: auto;
                        min-height: 100vh;
                        overflow: visible;
                    }
                    .cvi-ir-brand-panel {
                        grid-column: 1;
                        grid-row: 1;
                        min-height: auto;
                        padding: 28px 28px 34px;
                        border-left: 0;
                        border-bottom: 1px solid #E8EDF4;
                    }
                    .cvi-ir-hero { padding: 48px 0 18px; }
                    .cvi-ir-hero h1 { font-size: clamp(30px, 7vw, 42px); }
                    .cvi-ir-form-panel {
                        grid-column: 1;
                        grid-row: 2;
                        min-height: auto;
                        padding: 38px 24px 34px;
                        flex-direction: column;
                    }
                    .cvi-ir-powered {
                        position: static;
                        align-self: flex-start;
                        margin-top: 28px;
                        margin-left: 0;
                    }
                }
                @media (max-width: 560px) {
                    .cvi-ir-brand-panel { padding: 24px 20px 28px; }
                    .cvi-ir-brand-copy span { display: none; }
                    .cvi-ir-hero { padding: 40px 0 10px; }
                    .cvi-ir-hero h1 { font-size: 31px; line-height: 1.55; }
                    .cvi-ir-hero p { font-size: 13px; margin-top: 12px; }
                    .cvi-ir-form-panel { padding: 30px 20px 44px; align-items: flex-start; }
                }
            `}</style>

            <section className="cvi-ir-brand-panel" aria-label="CVI">
                <div className="cvi-ir-brand">
                    <div className="cvi-ir-logo">CVI</div>
                    <div className="cvi-ir-brand-copy">
                        <strong>شاخص ارزش مشارکت</strong>
                        <span>سامانه ارزیابی مشارکت و عملکرد</span>
                    </div>
                </div>

                <div className="cvi-ir-hero">
                    <div className="cvi-ir-eyebrow">عملکرد و اثرگذاری</div>
                    <h1>اثرگذاری شما، فراتر از نتایج فردی شماست.</h1>
                </div>
            </section>

            <section className="cvi-ir-form-panel">
                <div className="cvi-ir-form-wrap">
                    <div className="cvi-ir-form">
                        <h2>ورود به سامانه</h2>
                        <p className="cvi-ir-form-subtitle">برای ورود به سامانه، نام کاربری و رمز عبور خود را وارد کنید.</p>

                        <div className="cvi-ir-field">
                            <label htmlFor="cvi-ir-username">نام کاربری</label>
                            <input
                                id="cvi-ir-username"
                                className="cvi-ir-input"
                                type="text"
                                autoComplete="username"
                                value={username}
                                onChange={e => { setUsername(e.target.value); setError(""); }}
                                placeholder="نام کاربری"
                                onKeyDown={e => e.key === "Enter" && handle()}
                            />
                        </div>

                        <div className="cvi-ir-field">
                            <label htmlFor="cvi-ir-password">رمز عبور</label>
                            <input
                                id="cvi-ir-password"
                                className="cvi-ir-input"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={e => { setPassword(e.target.value); setError(""); }}
                                placeholder="••••••••"
                                onKeyDown={e => e.key === "Enter" && handle()}
                            />
                        </div>

                        {error && <div className="cvi-ir-error" role="alert">{error}</div>}

                        <button className="cvi-ir-submit" onClick={handle} disabled={loading}>
                            {loading ? "در حال ورود..." : "ورود"}
                        </button>

                        <div className="cvi-ir-guide-link">
                            <button type="button" onClick={openCviGuide}>
                                <span className="cvi-ir-guide-icon">i</span>
                                <span>CVI چیست؟</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="cvi-ir-powered">Powered by DoneX</div>
            </section>
        </main>
    );
}

function ActivityTimeline({ activity }) {
    const labels = {
        submitted: ["ثبت اولیه فعالیت", "📝"],
        revision_requested: ["درخواست اصلاح توسط مدیر", "↩️"],
        revised: ["اصلاح توسط کارشناس", "✏️"],
        resubmitted: ["ارسال مجدد برای بررسی", "📤"],
        approved: ["تأیید نهایی", "✅"],
        rejected: ["رد فعالیت", "❌"],
    };
    const events = activityTimeline(activity);
    return (
        <Card style={{ marginBottom: 12 }}>
            <div style={{ padding: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 12 }}>تاریخچه فرایند فعالیت</div>
                {events.map((event, i) => {
                    const [label, icon] = labels[event.type] || [event.type, "•"];
                    return (
                        <div key={`${event.type}-${i}`} style={{
                            display: "flex", gap: 10, position: "relative",
                            paddingBottom: i < events.length - 1 ? 16 : 0
                        }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: "50%", background: i === events.length - 1 ? C.cyanBg : C.bg,
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0,
                                border: `1px solid ${i === events.length - 1 ? C.cyan : C.border}`
                            }}>{icon}</div>
                            {i < events.length - 1 && <div style={{
                                position: "absolute", right: 13, top: 28, bottom: 0,
                                width: 1, background: C.border
                            }} />}
                            <div style={{ flex: 1, paddingTop: 2 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                    <span style={{ fontSize: 11.5, fontWeight: 500 }}>{label}</span>
                                    <span style={{ fontSize: 9.5, color: C.hint, whiteSpace: "nowrap" }}>{event.date || "—"}</span>
                                </div>
                                <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                                    توسط {event.by || "—"}{event.actorRole ? ` · ${event.actorRole}` : ""}
                                </div>
                                {event.fromStatus && event.toStatus && (
                                    <div style={{ fontSize: 9.5, color: C.hint, marginTop: 2 }}>
                                        {activityStatusLabel(event.fromStatus)} ← {activityStatusLabel(event.toStatus)}
                                    </div>
                                )}
                                {event.note && <div style={{
                                    background: C.purpleLt, color: C.purple,
                                    borderRadius: 6, padding: "6px 8px", fontSize: 10.5, lineHeight: 1.6, marginTop: 6
                                }}>
                                    {event.note}
                                </div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}



// ── ACTIVITY FORM (کارشناس) ────────────────────────────────────────────────────
function ActivityForm({ onBack, onSubmit, draftKey = "activity-draft" }) {
    const { activityGroups } = useData();
    const emptyForm = { groupId: "", sub: "", title: "", desc: "", blockerCategory: "", blockerText: "", supportNeed: "", suggestedAction: "", blockerStatus: "none", doc: "", docDataUrl: "", docType: "", docSize: 0, _file: null };
    const [form, setForm] = useState(() => {
        try {
            const saved = typeof localStorage !== "undefined" ? localStorage.getItem(draftKey) : null;
            return saved ? { ...emptyForm, ...JSON.parse(saved) } : emptyForm;
        } catch { return emptyForm; }
    });
    const [submitted, setSubmitted] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [draftSavedAt, setDraftSavedAt] = useState("");

    const selectedGroup = activityGroups.find(g => g.id === form.groupId);
    const canSubmit = Boolean(form.groupId && form.sub && form.title.trim() && form.desc.trim().length >= 10);
    const validationMessages = [
        !form.groupId && "گروه فعالیت را انتخاب کنید",
        form.groupId && !form.sub && "زیرگروه را انتخاب کنید",
        !form.title.trim() && "عنوان فعالیت را وارد کنید",
        form.desc.trim().length < 10 && "توضیحات باید حداقل ۱۰ کاراکتر باشد",
    ].filter(Boolean);

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                if (typeof localStorage !== "undefined") {
                    const { _file, ...serializableForm } = form;
                    localStorage.setItem(draftKey, JSON.stringify(serializableForm));
                    setDraftSavedAt(currentPersianDateTime());
                }
            } catch { }
        }, 400);
        return () => clearTimeout(timer);
    }, [form, draftKey]);

    const handleFileSelect = (file) => {
        setUploadError("");
        if (!file) return;
        const allowed = ["application/pdf", "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/jpeg", "image/png"];
        if (!allowed.includes(file.type)) return setUploadError("فرمت فایل مجاز نیست؛ PDF، Word، JPG یا PNG انتخاب کنید");
        if (file.size > 5 * 1024 * 1024) return setUploadError("حجم فایل باید کمتر از ۵ مگابایت باشد");
        const reader = new FileReader();
        reader.onload = () => setForm(f => ({
            ...f, doc: file.name, docDataUrl: String(reader.result || ""),
            docType: file.type, docSize: file.size, _file: file
        }));
        reader.onerror = () => setUploadError("خواندن فایل ناموفق بود؛ دوباره تلاش کنید");
        reader.readAsDataURL(file);
    };
    const handleSubmit = () => {
        if (!canSubmit) return;
        try { if (typeof localStorage !== "undefined") localStorage.removeItem(draftKey); } catch { }
        setSubmitted(true);
        onSubmit && onSubmit(form);
    };

    const inputStyle = {
        width: "100%", padding: "10px 13px", border: `1.5px solid ${C.borderMd}`,
        borderRadius: 9, fontSize: 13, color: C.text, outline: "none",
        background: "#fff", fontFamily: "inherit", direction: "inherit",
        boxSizing: "border-box"
    };

    if (submitted) return (
        <div className="cvi-workflow-form-shell" style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "inherit", direction: "inherit" }}>
            <TopBar onBack={onBack} title="ثبت فعالیت" />
            <div style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center",
                background: C.bg
            }}>
                <div style={{
                    width: 64, height: 64, borderRadius: "50%", background: C.tealLt,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 28, margin: "0 auto 16px"
                }}>✅</div>
                <div style={{ fontSize: 17, fontWeight: 500, marginBottom: 6 }}>فعالیت ارسال شد</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>
                    فعالیت شما برای بررسی به مدیر ارسال شد.<br />
                    پس از تأیید، امتیاز به داشبورد شما اضافه می‌شود.
                </div>
                <Card style={{ width: "100%", marginBottom: 16 }}>
                    <div style={{ padding: 14 }}>
                        {[["گروه", selectedGroup?.label || form.groupId], ["زیرگروه", form.sub],
                        ["عنوان", form.title], ["مستندات", form.doc || "ندارد"]].map(([l, v]) => (
                            <div key={l} style={{
                                display: "flex", justifyContent: "space-between",
                                padding: "7px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 12
                            }}>
                                <span style={{ color: C.muted }}>{l}</span>
                                <span style={{ fontWeight: 500 }}>{v}</span>
                            </div>
                        ))}
                    </div>
                </Card>
                <button onClick={() => { setSubmitted(false); setForm(emptyForm); }}
                    style={{
                        width: "100%", padding: 12, background: C.navy, color: "#fff",
                        border: "none", borderRadius: 9, fontSize: 13, fontWeight: 500,
                        cursor: "pointer", marginBottom: 8, fontFamily: "inherit"
                    }}>
                    ثبت فعالیت جدید
                </button>
                <button onClick={onBack} style={{
                    width: "100%", padding: 12, background: "#fff",
                    color: C.navy, border: `1.5px solid ${C.border}`, borderRadius: 9,
                    fontSize: 13, cursor: "pointer", fontFamily: "inherit"
                }}>
                    بازگشت به داشبورد
                </button>
            </div>
        </div>
    );

    return (
        <div style={{
            display: "flex", flexDirection: "column", height: "100%",
            fontFamily: "inherit", direction: "inherit"
        }}>
            <TopBar onBack={onBack} title="ثبت فعالیت جدید" />

            <div style={{
                flex: 1, overflowY: "auto", padding: 16, display: "flex",
                flexDirection: "column", gap: 12, background: C.bg
            }}>

                <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    fontSize: 10.5, color: C.hint
                }}>
                    <span>{draftSavedAt ? `پیش‌نویس خودکار ذخیره شد: ${draftSavedAt}` : "پیش‌نویس به‌صورت خودکار ذخیره می‌شود"}</span>
                    <button onClick={() => {
                        setForm(emptyForm);
                        try { if (typeof localStorage !== "undefined") localStorage.removeItem(draftKey); } catch { }
                    }} style={{
                        border: "none", background: "none", color: C.red, fontSize: 10.5,
                        cursor: "pointer", fontFamily: "inherit"
                    }}>
                        پاک‌کردن پیش‌نویس
                    </button>
                </div>

                <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                        <div>
                            <label style={{
                                display: "block", fontSize: 11, fontWeight: 600, color: "#666",
                                marginBottom: 5, letterSpacing: "0.4px"
                            }}>گروه فعالیت *</label>
                            <select value={form.groupId}
                                onChange={e => setForm(f => ({ ...f, groupId: e.target.value, sub: "" }))}
                                style={{ ...inputStyle, cursor: "pointer" }}>
                                <option value="">انتخاب گروه فعالیت</option>
                                {activityGroups.map(g => (
                                    <option key={g.id} value={g.id}>{g.icon} {g.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: "block", fontSize: 11, fontWeight: 600, color: "#666",
                                marginBottom: 5, letterSpacing: "0.4px"
                            }}>زیرگروه *</label>
                            <select value={form.sub} disabled={!selectedGroup}
                                onChange={e => setForm(f => ({ ...f, sub: e.target.value }))}
                                style={{
                                    ...inputStyle, cursor: selectedGroup ? "pointer" : "not-allowed",
                                    background: selectedGroup ? "#fff" : "#F4F4F6", color: selectedGroup ? C.text : C.hint
                                }}>
                                <option value="">{selectedGroup ? "انتخاب زیرگروه" : "ابتدا گروه را انتخاب کنید"}</option>
                                {(selectedGroup?.subs || []).map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={{
                        background: "#E8F4FF", borderRadius: 8, padding: "10px 12px",
                        fontSize: 11, color: "#185FA5", lineHeight: 1.6, display: "flex", gap: 8
                    }}>
                        <span style={{ flexShrink: 0, display: "inline-flex" }}><UiIcon name="performance" size={16} /></span>
                        <span>سطح تأثیر فعالیت توسط مدیر شما مشخص می‌شود. در توضیحات، تأثیر کار را شرح دهید.</span>
                    </div>
                </>

                <>
                    <div>
                        <label style={{
                            display: "block", fontSize: 11, fontWeight: 600, color: "#666",
                            letterSpacing: "0.4px", marginBottom: 5
                        }}>عنوان فعالیت *</label>
                        <input style={inputStyle} type="text" value={form.title}
                            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                            placeholder="مثال: شرکت در دوره Bizagi BPMS" />
                    </div>
                    <div>
                        <div style={{
                            display: "flex", justifyContent: "space-between",
                            fontSize: 11, fontWeight: 600, color: "#666", letterSpacing: "0.4px", marginBottom: 5
                        }}>
                            <span>توضیحات *</span>
                            <span style={{ color: C.hint, fontWeight: 400 }}>{form.desc.length} / ۵۰۰</span>
                        </div>
                        <textarea style={{ ...inputStyle, resize: "none", lineHeight: 1.6 }}
                            rows={5} value={form.desc}
                            onChange={e => setForm(f => ({ ...f, desc: e.target.value.slice(0, 500) }))}
                            placeholder="چه کاری انجام دادید؟ چه تأثیری روی کار یا تیم داشت؟" />
                    </div>
                    <div style={{
                        background: "#E8F4FF", borderRadius: 8, padding: "10px 12px",
                        fontSize: 11, color: "#185FA5", lineHeight: 1.6, display: "flex", gap: 8
                    }}>
                        <span>✍️</span>
                        <span>تأثیر این فعالیت روی کار خود را توضیح دهید — مدیر بر اساس همین توضیحات سطح تأثیر را تعیین می‌کند.</span>
                    </div>
                </>

                <Card>
                    <div style={{ padding: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>موانع و نیازهای حمایتی <span style={{fontWeight:400,color:C.hint}}>(اختیاری)</span></div>
                        <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.7, marginBottom: 10 }}>اگر برای انجام این فعالیت مانعی دارید، همین‌جا ثبت کنید تا مدیر بتواند آن را پیگیری کند. این بخش در امتیاز فعالیت اثر مستقیم ندارد.</div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:8 }}>
                            <label><span style={{display:"block",fontSize:10.5,color:C.muted,marginBottom:4}}>نوع مانع</span><select value={form.blockerCategory} onChange={e=>setForm(f=>({...f,blockerCategory:e.target.value,blockerStatus:e.target.value&&f.blockerStatus==="none"?"open":f.blockerStatus}))} style={inputStyle}>
                                <option value="">بدون مانع</option><option value="EXTERNAL_DEPENDENCY">وابستگی بیرونی / بین‌واحدی</option><option value="RESOURCE_LIMITATION">محدودیت منابع</option><option value="PROCESS_ISSUE">مسئله فرایندی</option><option value="SYSTEM_TOOL">سیستم / ابزار</option><option value="MANAGEMENT_DECISION">تصمیم مدیریتی</option><option value="OTHER">سایر</option>
                            </select></label>
                            <label><span style={{display:"block",fontSize:10.5,color:C.muted,marginBottom:4}}>وضعیت</span><select value={form.blockerStatus} onChange={e=>setForm(f=>({...f,blockerStatus:e.target.value}))} style={inputStyle}>
                                <option value="none">بدون مانع</option><option value="open">باز</option><option value="in_progress">در حال پیگیری</option><option value="resolved">حل‌شده</option>
                            </select></label>
                            <label style={{gridColumn:"1/-1"}}><span style={{display:"block",fontSize:10.5,color:C.muted,marginBottom:4}}>شرح مانع</span><textarea value={form.blockerText} onChange={e=>setForm(f=>({...f,blockerText:e.target.value}))} style={{...inputStyle,minHeight:58,resize:"vertical"}} /></label>
                            <label><span style={{display:"block",fontSize:10.5,color:C.muted,marginBottom:4}}>نیاز حمایتی</span><textarea value={form.supportNeed} onChange={e=>setForm(f=>({...f,supportNeed:e.target.value}))} style={{...inputStyle,minHeight:58,resize:"vertical"}} /></label>
                            <label><span style={{display:"block",fontSize:10.5,color:C.muted,marginBottom:4}}>اقدام پیشنهادی</span><textarea value={form.suggestedAction} onChange={e=>setForm(f=>({...f,suggestedAction:e.target.value}))} style={{...inputStyle,minHeight:58,resize:"vertical"}} /></label>
                        </div>
                    </div>
                </Card>

                <div style={{
                    fontSize: 11, fontWeight: 600, color: "#666",
                    marginTop: 2, letterSpacing: "0.4px"
                }}>مستندات پشتیبان</div>
                <div style={{ fontSize: 10.5, color: C.hint, marginTop: -8 }}>
                    این بخش اختیاری است، اما می‌تواند به ارزیابی بهتر فعالیت کمک کند.
                </div>
                <>
                    {!form.doc ? (
                        <label style={{
                            border: `1.5px dashed ${C.borderMd}`, borderRadius: 10,
                            padding: 24, textAlign: "center", cursor: "pointer", background: "#FAFAFA",
                            fontFamily: "inherit"
                        }}>
                            <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={e => handleFileSelect(e.target.files?.[0])} style={{ display: "none" }} />
                            <div style={{ fontSize: 24, color: C.hint, marginBottom: 6 }}>📎</div>
                            <div style={{ fontSize: 12, color: C.muted }}>برای آپلود کلیک کنید</div>
                            <div style={{ fontSize: 10, color: C.hint, marginTop: 2 }}>
                                گواهی، گزارش، تصویر — حداکثر ۵ مگابایت
                            </div>
                        </label>
                    ) : (
                        <div style={{
                            background: "#fff", border: `1px solid ${C.border}`,
                            borderRadius: 8, padding: "9px 12px", display: "flex",
                            alignItems: "center", gap: 8, fontSize: 12
                        }}>
                            <span style={{ color: C.navy, fontSize: 16 }}>📄</span>
                            {form.docDataUrl ? <a href={form.docDataUrl} download={form.doc}
                                style={{ color: C.navy, textDecoration: "none" }}>{form.doc}</a> : <span>{form.doc}</span>}
                            <span style={{ fontSize: 10, color: C.hint }}>
                                {form.docSize ? `${(form.docSize / 1024).toFixed(0)} KB` : ""}
                            </span>
                            <button onClick={() => setForm(f => ({ ...f, doc: "", docDataUrl: "", docType: "", docSize: 0, _file: null }))}
                                style={{
                                    marginRight: "auto", color: C.red, border: "none",
                                    background: "none", cursor: "pointer", fontSize: 11, fontFamily: "inherit"
                                }}>حذف</button>
                        </div>
                    )}
                    {uploadError && <div style={{ fontSize: 10.5, color: C.red, marginTop: -6 }}>{uploadError}</div>}

                    <Card>
                        <div style={{ padding: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>خلاصه فعالیت</div>
                            {[["گروه", selectedGroup?.label || form.groupId], ["زیرگروه", form.sub],
                            ["عنوان", form.title], ["مستندات", form.doc || "ندارد"]].map(([l, v]) => (
                                <div key={l} style={{
                                    display: "flex", justifyContent: "space-between",
                                    padding: "7px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 12
                                }}>
                                    <span style={{ color: C.muted }}>{l}</span>
                                    <span style={{ fontWeight: 500 }}>{v}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </>
            </div>

            <div style={{
                background: "#fff", borderTop: `1px solid ${C.border}`,
                padding: "10px 16px", flexShrink: 0
            }}>
                {!canSubmit && <div style={{
                    background: C.redLt, borderRadius: 7, padding: "7px 10px",
                    marginBottom: 8, fontSize: 10.5, color: C.red, lineHeight: 1.7
                }}>
                    {validationMessages.join(" • ")}
                </div>}
                <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={handleSubmit} disabled={!canSubmit}
                        title={!canSubmit ? validationMessages.join("، ") : ""}
                        style={{
                            flex: 1, padding: 12, background: canSubmit ? C.navy : "#E4E4EC",
                            color: canSubmit ? "#fff" : C.hint, border: "none", borderRadius: 9,
                            fontSize: 13, fontWeight: 500, cursor: canSubmit ? "pointer" : "not-allowed",
                            fontFamily: "inherit"
                        }}>
                        ارسال برای مدیر ✓
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── SPECIALIST DETAIL VIEWS (جزئیات فعالیت / دوره / نمودار CVI — تمام‌صفحه با دکمه بازگشت) ──
function SpecialistDetailView({ view, setView, selectedAct, setSelectedAct,
    selectedPeriod, setSelectedPeriod, periodDetailOrigin, setPeriodDetailOrigin, myActs, allPeriods, activityGroups, impactLevels,
    user, positions, indicatorDefs, indicatorData, activePeriod,
    routineCtx, selectedOrgLevel }) {
    const { orgSettings, resubmitActivityApi } = useData();
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const [editingRevision, setEditingRevision] = useState(false);
    const [revisionForm, setRevisionForm] = useState({ title: "", desc: "", doc: "" });

    if (view === "act-detail" && selectedAct) {
        const a = selectedAct;
        const canResubmit = revisionForm.title.trim() && revisionForm.desc.trim().length >= 10;
        const submitRevision = async () => {
            if (!canResubmit) return;
            try {
                const updated = await resubmitActivityApi(a.id, revisionForm);
                setSelectedAct(updated);
                setEditingRevision(false);
            } catch (error) {
                console.error("Activity resubmission failed:", error);
                uxNotify(error.message || "ارسال مجدد فعالیت انجام نشد");
            }
        };
        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "inherit", direction: "inherit"
            }}>
                <TopBar onBack={() => { setView(null); setSelectedAct(null); }} title="جزئیات فعالیت" />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ background: C.navy, padding: "12px 14px" }}>
                            <div style={{ color: "#fff", fontSize: 14, fontWeight: 500, marginBottom: 3 }}>{a.title}</div>
                            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>{groupLabel(activityGroups, a.groupId)} · {a.sub}</div>
                        </div>
                        <div style={{ padding: 14 }}>
                            <p style={{ fontSize: 12, color: "#555", lineHeight: 1.7, marginBottom: 12 }}>{a.desc}</p>
                            <ProtectedAttachmentList module="activity" entityId={a.id}
                                attachments={a.attachments || []}
                                canDelete={[ACTIVITY_STATUS.DRAFT, ACTIVITY_STATUS.REVISION_REQUESTED].includes(a.status)} />
                            {a.managerNote && (
                                <div style={{
                                    background: C.purpleLt, borderRadius: 8, padding: "8px 12px",
                                    fontSize: 12, color: C.purple, marginBottom: 12
                                }}>
                                    💬 بازخورد مدیر: {a.managerNote}
                                </div>
                            )}
                            {a.blockerStatus && a.blockerStatus !== "none" && <div style={{background:"#FFF8E8",border:"1px solid #F2DFC0",borderRadius:8,padding:"9px 11px",marginBottom:12}}>
                                <div style={{fontSize:10.5,fontWeight:700,color:C.amber}}>مانع و نیاز حمایتی · {a.blockerStatus==="resolved"?"حل‌شده":a.blockerStatus==="in_progress"?"در حال پیگیری":"باز"}</div>
                                {a.blockerText&&<div style={{fontSize:11,color:"#555",marginTop:4}}>{a.blockerText}</div>}
                                {a.supportNeed&&<div style={{fontSize:10.5,color:C.muted,marginTop:4}}>نیاز حمایتی: {a.supportNeed}</div>}
                                {a.suggestedAction&&<div style={{fontSize:10.5,color:C.muted,marginTop:3}}>اقدام پیشنهادی: {a.suggestedAction}</div>}
                            </div>}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                {[["وضعیت", <StatusBadge status={a.status} />],
                                ["سطح تأثیر", a.impactLevel ? impactLevels.find(l => l.id === a.impactLevel)?.label : "—"],
                                ["امتیاز", a.score || "—"],
                                ["تاریخ", a.date]
                                ].map(([l, v]) => (
                                    <div key={l} style={{ background: C.bg, borderRadius: 8, padding: "8px 10px" }}>
                                        <div style={{ fontSize: 10, color: C.hint, marginBottom: 3 }}>{l}</div>
                                        <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                    <ActivityTimeline activity={a} />
                    {a.status === ACTIVITY_STATUS.REVISION_REQUESTED && (
                        editingRevision ? (
                            <Card>
                                <div style={{ padding: 14 }}>
                                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 12 }}>اصلاح فعالیت</div>
                                    <label style={{ display: "block", fontSize: 10.5, color: C.muted, marginBottom: 5 }}>عنوان فعالیت *</label>
                                    <input value={revisionForm.title}
                                        onChange={e => setRevisionForm(f => ({ ...f, title: e.target.value }))}
                                        style={{
                                            width: "100%", padding: "9px 11px", border: `1.5px solid ${C.borderMd}`,
                                            borderRadius: 8, fontSize: 12, boxSizing: "border-box", fontFamily: "inherit",
                                            direction: "inherit", marginBottom: 10
                                        }} />
                                    <label style={{ display: "block", fontSize: 10.5, color: C.muted, marginBottom: 5 }}>توضیحات اصلاح‌شده *</label>
                                    <textarea value={revisionForm.desc} rows={5}
                                        onChange={e => setRevisionForm(f => ({ ...f, desc: e.target.value.slice(0, 500) }))}
                                        style={{
                                            width: "100%", padding: "9px 11px", border: `1.5px solid ${C.borderMd}`,
                                            borderRadius: 8, fontSize: 12, boxSizing: "border-box", fontFamily: "inherit",
                                            direction: "inherit", resize: "none", lineHeight: 1.6, marginBottom: 10
                                        }} />
                                    <label style={{ display: "block", fontSize: 10.5, color: C.muted, marginBottom: 5 }}>مستندات</label>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                                        <label style={{
                                            padding: "8px 11px", background: "#fff", border: `1px dashed ${C.borderMd}`,
                                            borderRadius: 8, fontSize: 11, cursor: "pointer"
                                        }}>
                                            انتخاب مستند جدید
                                            <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }}
                                                onChange={e => {
                                                    const file = e.target.files?.[0];
                                                    if (!file || file.size > 5 * 1024 * 1024) return;
                                                    const reader = new FileReader();
                                                    reader.onload = () => setRevisionForm(f => ({
                                                        ...f, doc: file.name,
                                                        docDataUrl: String(reader.result || ""), docType: file.type, docSize: file.size, _file: file
                                                    }));
                                                    reader.readAsDataURL(file);
                                                }} />
                                        </label>
                                        <span style={{ fontSize: 10.5, color: C.muted }}>{revisionForm.doc || "بدون مستند"}</span>
                                    </div>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <button onClick={() => setEditingRevision(false)}
                                            style={{
                                                padding: "10px 14px", background: "#fff", color: C.muted,
                                                border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer"
                                            }}>انصراف</button>
                                        <button onClick={submitRevision} disabled={!canResubmit}
                                            style={{
                                                flex: 1, padding: 10, background: canResubmit ? C.navy : "#E4E4EC",
                                                color: canResubmit ? "#fff" : C.hint, border: "none", borderRadius: 8,
                                                fontSize: 12, fontWeight: 500, cursor: canResubmit ? "pointer" : "not-allowed"
                                            }}>
                                            ثبت اصلاحات و ارسال مجدد
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ) : (
                            <button onClick={() => {
                                setRevisionForm({
                                    title: a.title, desc: a.desc, doc: a.doc || "",
                                    docDataUrl: a.docDataUrl || "", docType: a.docType || "", docSize: a.docSize || 0, _file: null
                                });
                                setEditingRevision(true);
                            }} style={{
                                width: "100%", padding: 12, background: C.navy, color: "#fff",
                                border: "none", borderRadius: 9, fontSize: 13, fontWeight: 500, cursor: "pointer"
                            }}>
                                ویرایش و ارسال مجدد
                            </button>
                        )
                    )}
                </div>
            </div>
        );
    }

    if (view === "period-detail" && selectedPeriod) {
        const periodActs = myActs.filter(a => a.periodId === selectedPeriod.periodId);
        const approvedActs = periodActs.filter(a => a.status === "approved");
        const groupRows = activityGroups.map(g => {
            const acts = approvedActs.filter(a => a.groupId === g.id);
            const liveScore = Math.min(acts.reduce((sum, a) => sum + (Number(a.score) || 0), 0), g.maxScore);
            return {
                ...g,
                score: selectedPeriod.activityGroupScores?.[g.id] ?? liveScore,
                count: selectedPeriod.activityCounts?.[g.id] ?? acts.length
            };
        });
        const nonRoutine = selectedPeriod.nonRoutine ?? Math.min(100, groupRows.reduce((sum, g) => sum + g.score, 0));
        const routine = selectedPeriod.routine ?? calcRoutineScoreForUser(user, routineCtx, selectedPeriod.periodId);
        const peerFeedback = selectedPeriod.peerFeedback ?? calcEligiblePeerFeedbackScore(routineCtx, user.id, selectedPeriod.periodId);
        const periodRoleWeights = positions.find(p => p.id === user.posId) || orgSettings;
        const periodIndex = allPeriods.findIndex(p => p.periodId === selectedPeriod.periodId);
        const previousPeriod = periodIndex > 0 ? allPeriods[periodIndex - 1] : null;
        const delta = previousPeriod ? selectedPeriod.cvi - previousPeriod.cvi : null;

        let liveQuestions = [], liveAnswers = [];
        if (user.role === "manager") {
            liveQuestions = routineCtx.deptRoutineQuestions.filter(q => q.deptId === user.deptId);
            liveAnswers = routineCtx.deptRoutineAnswers;
        } else if (user.role === "deputy") {
            liveQuestions = routineCtx.orgRoutineQuestions;
            liveAnswers = routineCtx.orgRoutineAnswers;
        } else {
            liveQuestions = routineCtx.routineQuestions.filter(q => q.teamId === user.teamId);
            liveAnswers = routineCtx.routineAnswers;
        }
        const maxRoutine = routineCtx.routineLevels.length
            ? Math.max(...routineCtx.routineLevels.map(l => l.score)) : 0;
        const criteriaMap = {};
        liveQuestions.forEach(q => {
            const ans = liveAnswers.find(a => a.questionId === q.id && a.userId === user.id && a.periodId === selectedPeriod.periodId);
            const level = ans && routineCtx.routineLevels.find(l => l.id === ans.levelId);
            if (!level || !maxRoutine) return;
            if (!criteriaMap[q.label]) criteriaMap[q.label] = { label: q.label, total: 0, weight: 0 };
            criteriaMap[q.label].total += (level.score / maxRoutine) * 100 * (Number(q.weight) || 0);
            criteriaMap[q.label].weight += Number(q.weight) || 0;
        });
        const routineCriteria = selectedPeriod.routineCriteria || Object.values(criteriaMap).map(c => ({
            label: c.label, score: c.weight ? c.total / c.weight : 0, weight: c.weight
        }));
        const improvementItems = [
            ...groupRows.map(g => ({ label: g.label, score: g.maxScore ? (g.score / g.maxScore) * 100 : 0 })),
            ...routineCriteria.map(c => ({ label: c.label, score: c.score }))
        ];
        const strongest = improvementItems.length
            ? improvementItems.reduce((a, b) => a.score > b.score ? a : b) : null;
        const weakest = improvementItems.length
            ? improvementItems.reduce((a, b) => a.score < b.score ? a : b) : null;
        const changeText = delta == null ? "اولین دوره ثبت‌شده"
            : delta > 0 ? `${delta.toFixed(1)} امتیاز بهبود`
                : delta < 0 ? `${Math.abs(delta).toFixed(1)} امتیاز کاهش` : "بدون تغییر";

        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "inherit", direction: "inherit"
            }}>
                <TopBar onBack={() => { setView(periodDetailOrigin === "history" ? null : "cvi-detail"); setSelectedPeriod(null); }}
                    title={selectedPeriod.label} />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                        {[["CVI", selectedPeriod.cvi.toFixed(1), C.cyan],
                        ["عملکرد فردی", selectedPeriod.individual.toFixed(0), C.navy],
                        ["مشارکت جمعی", selectedPeriod.org.toFixed(1), C.purple]].map(([l, v, c]) => (
                            <div key={l} style={{
                                background: "#fff", border: `1px solid ${C.border}`,
                                borderRadius: 10, padding: "10px", textAlign: "center"
                            }}>
                                <div style={{ fontSize: 10, color: C.hint, marginBottom: 4 }}>{l}</div>
                                <div style={{ fontSize: 18, fontWeight: 500, color: c }}>{v}</div>
                            </div>
                        ))}
                    </div>

                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                <span style={{ fontSize: 12, fontWeight: 500 }}>مقایسه با دوره قبل</span>
                                <span style={{
                                    fontSize: 11, fontWeight: 500,
                                    color: delta == null ? C.muted : delta >= 0 ? C.teal : C.red
                                }}>{changeText}</span>
                            </div>
                            {previousPeriod && (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                                    {[["CVI", selectedPeriod.cvi, previousPeriod.cvi],
                                    ["فردی", selectedPeriod.individual, previousPeriod.individual],
                                    ["سازمانی", selectedPeriod.org, previousPeriod.org]].map(([label, current, previous]) => (
                                        <div key={label} style={{ background: C.bg, borderRadius: 8, padding: 8, textAlign: "center" }}>
                                            <div style={{ fontSize: 9.5, color: C.hint }}>{label}</div>
                                            <div style={{ fontSize: 12, fontWeight: 500, marginTop: 3 }}>
                                                {Number(current).toFixed(1)}
                                                <span style={{ fontSize: 9.5, color: Number(current) >= Number(previous) ? C.teal : C.red, marginRight: 4 }}>
                                                    ({Number(current) >= Number(previous) ? "+" : ""}{(Number(current) - Number(previous)).toFixed(1)})
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>ترکیب عملکرد فردی</div>
                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            {[["وظایف و فعالیت‌ها", nonRoutine, selectedPeriod.weights?.nonRoutine ?? periodRoleWeights?.wNonRoutine, C.cyan],
                            ["فعالیت‌های مستمر", routine, selectedPeriod.weights?.routine ?? periodRoleWeights?.wRoutine, C.purple],
                            ["بازخورد همکاران", peerFeedback, selectedPeriod.weights?.peerFeedback ?? periodRoleWeights?.wPeerFeedback, C.amber]].map(([label, score, weight, color], i) => (
                                <div key={label} style={{ marginBottom: i < 2 ? 14 : 0 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                                        <span style={{ fontSize: 11.5, fontWeight: 500 }}>{label}</span>
                                        <span style={{ fontSize: 11, color }}>
                                            {score == null ? "بدون ارزیابی" : `${Number(score).toFixed(0)}٪`} · وزن {weight}٪
                                        </span>
                                    </div>
                                    <MiniBar value={score ?? 0} color={color} height={6} />
                                </div>
                            ))}
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>فعالیت‌های غیرمستمر به تفکیک حوزه</div>
                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            {groupRows.map((g, i) => {
                                const pct = g.maxScore ? Math.min(100, (g.score / g.maxScore) * 100) : 0;
                                return (
                                    <div key={g.id} style={{ marginBottom: i < groupRows.length - 1 ? 13 : 0 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                                            <span style={{ fontSize: 11.5 }}>{g.icon} {g.label}</span>
                                            <span style={{ fontSize: 10.5, color: C.muted }}>
                                                {g.score} از {g.maxScore} · {g.count} فعالیت
                                            </span>
                                        </div>
                                        <MiniBar value={pct} color={pct >= 70 ? C.teal : pct >= 50 ? C.amber : C.red} height={5} />
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>معیارهای ارزیابی وظایف مستمر</div>
                    <Card style={{ marginBottom: 12 }}>
                        {routineCriteria.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 11.5 }}>
                                جزئیات ارزیابی وظایف مستمر برای این دوره ثبت نشده است
                            </div>
                        ) : routineCriteria.map((c, i) => (
                            <div key={`${c.label}-${i}`} style={{ padding: 14, borderTop: i > 0 ? `1px solid ${C.bg}` : "none" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                    <span style={{ fontSize: 11.5, fontWeight: 500 }}>{c.label}</span>
                                    <span style={{ fontSize: 10.5, color: c.score >= 70 ? C.teal : c.score >= 50 ? C.amber : C.red }}>
                                        {Number(c.score).toFixed(0)}٪ · وزن {c.weight}٪
                                    </span>
                                </div>
                                <MiniBar value={c.score} color={c.score >= 70 ? C.teal : c.score >= 50 ? C.amber : C.red} height={5} />
                            </div>
                        ))}
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>جمع‌بندی این دوره</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                        <div style={{ background: C.tealLt, borderRadius: 10, padding: 12 }}>
                            <div style={{ fontSize: 10, color: C.teal, marginBottom: 4 }}>نقطه قوت</div>
                            <div style={{ fontSize: 11.5, fontWeight: 500 }}>{strongest?.label || "—"}</div>
                        </div>
                        <div style={{ background: "#FFF1F0", borderRadius: 10, padding: 12 }}>
                            <div style={{ fontSize: 10, color: C.red, marginBottom: 4 }}>نیازمند توجه</div>
                            <div style={{ fontSize: 11.5, fontWeight: 500 }}>{weakest?.label || "—"}</div>
                        </div>
                    </div>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>فعالیت‌های ثبت‌شده دوره</div>
                    <Card>
                        {periodActs.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 12 }}>
                                جزئیات فعالیت‌های این دوره در دسترس نیست
                            </div>
                        ) : periodActs.map((a, i) => (
                            <div key={a.id} onClick={() => { setSelectedAct(a); setView("act-detail"); }}
                                style={{
                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                    padding: "11px 14px", borderBottom: i < periodActs.length - 1 ? `1px solid ${C.bg}` : "none", cursor: "pointer"
                                }}>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{a.title}</div>
                                    <div style={{ fontSize: 10, color: C.hint, marginTop: 1 }}>
                                        {groupLabel(activityGroups, a.groupId)} · امتیاز: {a.score || 0}
                                    </div>
                                </div>
                                <StatusBadge status={a.status} />
                            </div>
                        ))}
                    </Card>
                </div>
            </div>
        );
    }

    if (view === "tasks-activities-detail") {
        const options = [
            { id: "routine-detail", title: "فعالیت‌های مستمر", desc: "مشاهده جزئیات ارزیابی فعالیت‌های جاری و مستمر", color: C.purple },
            { id: "nonroutine-detail", title: "فعالیت‌های غیرمستمر", desc: "مشاهده فعالیت‌های موردی و ثبت‌شده دوره", color: C.cyan },
        ];
        return <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "inherit", direction: "inherit" }}>
            <TopBar onBack={() => setView(null)} title="وظایف و فعالیت‌ها" />
            <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>این بخش از دو مؤلفه فعالیت‌های مستمر و غیرمستمر تشکیل شده است.</div>
                <div style={{ display: "grid", gap: 10 }}>
                    {options.map(option => <button key={option.id} type="button" onClick={() => setView(option.id)} style={{ width: "100%", textAlign: "right", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 15, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ width: 10, height: 36, borderRadius: 6, background: option.color, flexShrink: 0 }} />
                        <span style={{ flex: 1 }}><strong style={{ display: "block", fontSize: 13, color: C.text, marginBottom: 4 }}>{option.title}</strong><span style={{ fontSize: 10.5, color: C.muted }}>{option.desc}</span></span>
                        <span style={{ color: C.hint, fontSize: 16 }}>←</span>
                    </button>)}
                </div>
            </div>
        </div>;
    }

    if (view === "cvi-detail") {
        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "inherit", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title="نمودار دوره‌ها" />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <Card style={{ marginBottom: 12 }}>
                        <div style={{ padding: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>روند CVI — دوره‌های اخیر</div>
                            <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                                {[[C.cyan, "CVI"], [C.navy, "عملکرد فردی"], [C.purple, "مشارکت جمعی"]].map(([c, l]) => (
                                    <div key={l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#666" }}>
                                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
                                        {l}
                                    </div>
                                ))}
                            </div>
                            <svg width="100%" viewBox="0 0 300 130" preserveAspectRatio="none" style={{ overflow: "visible" }}>
                                {[20, 55, 90, 125].map(y => (
                                    <line key={y} x1="0" y1={y} x2="300" y2={y} stroke={C.bg} strokeWidth="1" />
                                ))}
                                {allPeriods.map((p, i) => {
                                    const x = 40 + i * 110;
                                    const yi = (ind) => 125 - (ind / 100) * 105;
                                    return <g key={p.periodId}>
                                        {i > 0 && <>
                                            <line x1={40 + (i - 1) * 110} y1={yi(allPeriods[i - 1].individual)} x2={x} y2={yi(p.individual)}
                                                stroke={C.navy} strokeWidth="2" fill="none" />
                                            <line x1={40 + (i - 1) * 110} y1={yi(allPeriods[i - 1].org)} x2={x} y2={yi(p.org)}
                                                stroke={C.purple} strokeWidth="2" strokeDasharray="5 3" fill="none" />
                                            <line x1={40 + (i - 1) * 110} y1={yi(allPeriods[i - 1].cvi)} x2={x} y2={yi(p.cvi)}
                                                stroke={C.cyan} strokeWidth="2.5" fill="none" />
                                        </>}
                                        <circle cx={x} cy={yi(p.cvi)} r={i === allPeriods.length - 1 ? 5 : 4}
                                            fill={C.cyan} stroke="#fff" strokeWidth="2" />
                                        <circle cx={x} cy={yi(p.individual)} r="3.5" fill={C.navy} stroke="#fff" strokeWidth="2" />
                                        <circle cx={x} cy={yi(p.org)} r="3.5" fill={C.purple} stroke="#fff" strokeWidth="2" />
                                        {i === allPeriods.length - 1 && <>
                                            <text x={x + 7} y={yi(p.cvi) + 4} fontSize="9" fill={C.cyan}>{p.cvi.toFixed(1)}</text>
                                            <text x={x + 7} y={yi(p.individual) + 4} fontSize="9" fill={C.navy}>{p.individual.toFixed(0)}</text>
                                            <text x={x + 7} y={yi(p.org) + 4} fontSize="9" fill={C.purple}>{p.org.toFixed(1)}</text>
                                        </>}
                                    </g>;
                                })}
                            </svg>
                            <div style={{
                                display: "flex", justifyContent: "space-around",
                                marginTop: 8, fontSize: 9, color: C.hint
                            }}>
                                {allPeriods.map(p => (
                                    <span key={p.periodId} style={{ textAlign: "center" }}>{p.label}</span>
                                ))}
                            </div>
                        </div>
                    </Card>

                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>جزئیات هر دوره</div>
                    {allPeriods.map((p, i) => (
                        <div key={p.periodId} onClick={() => { setSelectedPeriod(p); setPeriodDetailOrigin?.("cvi-detail"); setView("period-detail"); }}
                            style={{ marginBottom: 8, cursor: "pointer" }}>
                            <Card>
                                <div style={{
                                    background: i === allPeriods.length - 1 ? C.navy : "#1a1a4a",
                                    padding: "10px 14px", display: "flex", justifyContent: "space-between"
                                }}>
                                    <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                                        {p.label}
                                        {i === allPeriods.length - 1 && (
                                            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginRight: 6 }}>جاری</span>
                                        )}
                                    </span>
                                    <span style={{ color: C.cyan, fontSize: 16, fontWeight: 500 }}>{p.cvi.toFixed(1)}</span>
                                </div>
                                <div style={{ padding: "0 14px" }}>
                                    {[["امتیاز فردی", p.individual.toFixed(0)], ["امتیاز سازمانی", p.org.toFixed(1)]].map(([l, v]) => (
                                        <div key={l} style={{
                                            display: "flex", justifyContent: "space-between",
                                            padding: "8px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 12
                                        }}>
                                            <span style={{ color: C.muted }}>{l}</span>
                                            <span style={{ fontWeight: 500 }}>{v}</span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (view === "nonroutine-detail") {
        const approvedActs = myActs.filter(a => a.status === "approved");
        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "inherit", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title="فعالیت‌های غیرمستمر — جزئیات گروه‌ها" />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 14, lineHeight: 1.7 }}>
                        امتیاز فعالیت‌های غیرمستمر از مجموع فعالیت‌های تأییدشده‌ی خودثبتی، به تفکیک گروه، تشکیل می‌شود.
                        روی هر گروه بزنید تا فعالیت‌های آن را ببینید.
                    </div>
                    {activityGroups.map(g => {
                        const groupActs = approvedActs.filter(a => a.groupId === g.id);
                        const earned = groupActs.reduce((s, a) => s + a.score, 0);
                        const capped = Math.min(earned, g.maxScore);
                        const isOpen = expandedGroupId === g.id;
                        return (
                            <Card key={g.id} style={{ marginBottom: 10 }}>
                                <div onClick={() => setExpandedGroupId(isOpen ? null : g.id)}
                                    style={{ padding: 14, cursor: "pointer" }}>
                                    <div style={{
                                        display: "flex", justifyContent: "space-between",
                                        alignItems: "center", marginBottom: 7
                                    }}>
                                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                                            {g.icon} {g.label}
                                        </span>
                                        <span style={{ fontSize: 14, fontWeight: 600, color: C.cyan }}>
                                            {capped} / {g.maxScore}
                                        </span>
                                    </div>
                                    <MiniBar value={capped} max={g.maxScore} color={C.cyan} height={6} />
                                    <div style={{ fontSize: 10.5, color: C.hint, marginTop: 8 }}>
                                        {groupActs.length} فعالیت تأییدشده {isOpen ? "▲" : "▼"}
                                    </div>
                                </div>
                                {isOpen && (
                                    <div style={{ borderTop: `1px solid ${C.bg}` }}>
                                        {groupActs.length === 0 ? (
                                            <div style={{ padding: 14, textAlign: "center", color: C.muted, fontSize: 11.5 }}>
                                                فعالیت تأییدشده‌ای در این گروه ثبت نشده
                                            </div>
                                        ) : groupActs.map((a, i) => (
                                            <div key={a.id} onClick={() => { setSelectedAct(a); setView("act-detail"); }}
                                                style={{
                                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                                    padding: "10px 14px", borderTop: i > 0 ? `1px solid ${C.bg}` : "none", cursor: "pointer"
                                                }}>
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{a.title}</div>
                                                    <div style={{ fontSize: 10, color: C.hint, marginTop: 1 }}>{a.sub}</div>
                                                </div>
                                                <Badge color="cyan" small>{a.score} امتیاز</Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (view === "routine-detail") {
        let scopedQuestions, scopedAnswers, evaluatorLabel;
        if (user.role === "manager") {
            scopedQuestions = routineCtx.deptRoutineQuestions.filter(q => q.deptId === user.deptId);
            scopedAnswers = routineCtx.deptRoutineAnswers;
            evaluatorLabel = "معاون";
        } else if (user.role === "deputy") {
            scopedQuestions = routineCtx.orgRoutineQuestions;
            scopedAnswers = routineCtx.orgRoutineAnswers;
            evaluatorLabel = "مدیرعامل";
        } else {
            scopedQuestions = routineCtx.routineQuestions.filter(q => q.teamId === user.teamId);
            scopedAnswers = routineCtx.routineAnswers;
            evaluatorLabel = "مدیر";
        }
        const routineLevels = routineCtx.routineLevels;
        const byMonth = { 1: [], 2: [], 3: [] };
        scopedQuestions.forEach(q => byMonth[q.month]?.push(q));
        const answerFor = (qId) => scopedAnswers.find(a => a.questionId === qId && a.userId === user.id && a.periodId === activePeriod?.id);
        const maxLevelScore = routineLevels.length ? Math.max(...routineLevels.map(l => l.score)) : 0;

        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "inherit", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title="فعالیت‌های مستمر — جزئیات سؤالات" />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 14, lineHeight: 1.7 }}>
                        این سؤالات مربوط به عملکرد وظایف مستمر و تکراری‌ست که {evaluatorLabel} شما در پایان هر ماه پاسخ می‌دهد.
                    </div>
                    {scopedQuestions.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                            هنوز سؤال وظایف مستمری برای شما تعریف نشده است.
                        </div>
                    ) : [1, 2, 3].map(month => byMonth[month].length > 0 && (
                        <div key={month} style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
                                {month === 1 ? "ماه اول" : month === 2 ? "ماه دوم" : "ماه سوم"}
                            </div>
                            <Card>
                                {byMonth[month].map((q, i) => {
                                    const ans = answerFor(q.id);
                                    const lvl = ans ? routineLevels.find(l => l.id === ans.levelId) : null;
                                    const pct = lvl && maxLevelScore ? Math.min(100, (lvl.score / maxLevelScore) * 100) : 0;
                                    return (
                                        <div key={q.id} style={{
                                            padding: 14,
                                            borderTop: i > 0 ? `1px solid ${C.bg}` : "none"
                                        }}>
                                            <div style={{
                                                display: "flex", justifyContent: "space-between",
                                                alignItems: "flex-start", gap: 8, marginBottom: 8
                                            }}>
                                                <span style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.6 }}>{q.label}</span>
                                                <Badge color="gray" small>وزن {q.weight}٪</Badge>
                                            </div>
                                            {lvl ? (
                                                <>
                                                    <div style={{
                                                        display: "flex", justifyContent: "space-between",
                                                        fontSize: 11, marginBottom: 5
                                                    }}>
                                                        <span style={{ color: C.purple, fontWeight: 500 }}>{lvl.label}</span>
                                                        <span style={{ color: C.muted }}>{pct.toFixed(0)}٪</span>
                                                    </div>
                                                    <MiniBar value={pct} color={C.purple} height={5} />
                                                </>
                                            ) : (
                                                <div style={{ fontSize: 11, color: C.hint }}>هنوز توسط {evaluatorLabel} پاسخ داده نشده</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (view === "org-level-detail") {
        const level = selectedOrgLevel;
        const scopeId = level === "team" ? user.teamId : level === "dept" ? user.deptId : null;
        const defs = indicatorDefs.filter(i => {
            if (i.level !== level) return false;
            if (level === "org") return true;
            if (level === "team") return i.teamId === scopeId;
            if (level === "dept") return i.deptId === scopeId;
            return false;
        });
        const withData = withPeriodData(defs, indicatorData, activePeriod?.id);
        const titleMap = { org: "سازمانی", dept: "معاونت", team: "تیمی" };
        return (
            <div style={{
                display: "flex", flexDirection: "column", height: "100%",
                fontFamily: "inherit", direction: "inherit"
            }}>
                <TopBar onBack={() => setView(null)} title={`شاخص‌های ${titleMap[level] || ""}`} />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    {withData.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                            شاخصی برای این سطح تعریف نشده است.
                        </div>
                    ) : (
                        <Card>
                            {withData.map((ind, i) => {
                                const pct = ind.target ? Math.min(100, (ind.actual / ind.target) * 100) : null;
                                return (
                                    <div key={ind.id} style={{
                                        padding: 14,
                                        borderTop: i > 0 ? `1px solid ${C.bg}` : "none"
                                    }}>
                                        <div style={{
                                            display: "flex", justifyContent: "space-between",
                                            alignItems: "flex-start", gap: 8, marginBottom: 8
                                        }}>
                                            <span style={{ fontSize: 12, fontWeight: 500 }}>{ind.label}</span>
                                            <Badge color="gray" small>وزن {ind.weight}٪</Badge>
                                        </div>
                                        <div style={{
                                            display: "flex", justifyContent: "space-between",
                                            fontSize: 11, marginBottom: 5
                                        }}>
                                            <span style={{ color: C.muted }}>هدف: {ind.target || "—"} · واقعی: {ind.actual || "—"}</span>
                                            <span style={{ color: pct == null ? C.hint : C.teal, fontWeight: 500 }}>
                                                {pct == null ? "بدون داده" : `${pct.toFixed(0)}٪`}
                                            </span>
                                        </div>
                                        <MiniBar value={pct ?? 0} color={C.teal} height={5} />
                                    </div>
                                );
                            })}
                        </Card>
                    )}
                </div>
            </div>
        );
    }

    return null;
}

function PerformanceTrendChart({ periods = [], compact = false, onOpenHistory = null }) {
    const visible = (periods || []).filter(p => Number.isFinite(Number(p?.cvi))).slice(compact ? -4 : -8);
    if (!visible.length) return <Card><div className="cvi-trend-empty">هنوز داده‌ای برای نمایش روند ثبت نشده است.</div></Card>;

    const width = 760, height = compact ? 210 : 250;
    const pad = { left: 30, right: 14, top: 24, bottom: 42 };
    const chartW = width - pad.left - pad.right;
    const chartH = height - pad.top - pad.bottom;
    const x = i => visible.length === 1 ? pad.left + chartW / 2 : pad.left + (i / (visible.length - 1)) * chartW;
    const y = value => pad.top + (1 - Math.max(0, Math.min(100, Number(value) || 0)) / 100) * chartH;
    const points = key => visible.map((p, i) => `${x(i)},${y(p[key])}`).join(' ');
    const latest = visible[visible.length - 1];
    const previous = visible.length > 1 ? visible[visible.length - 2] : null;
    const delta = previous ? Number(latest.cvi) - Number(previous.cvi) : null;
    const best = visible.reduce((a, b) => Number(a.cvi) >= Number(b.cvi) ? a : b);
    const ticks = [100, 75, 50, 25, 0];

    return <Card>
        <div className="cvi-trend-card">
            <div className="cvi-trend-head">
                <div>
                    <div className="cvi-trend-title">روند عملکرد در دوره‌ها</div>
                    <div className="cvi-trend-subtitle">{compact ? 'CVI در چهار دوره اخیر' : 'مقایسه CVI و مؤلفه‌های اصلی در دوره‌های اخیر'}</div>
                </div>
                <div className="cvi-trend-summary">
                    <div><span>فعلی</span><strong>{Number(latest.cvi).toFixed(1)}</strong></div>
                    <div><span>تغییر</span><strong className={delta == null ? '' : delta >= 0 ? 'is-up' : 'is-down'}>{delta == null ? '—' : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}`}</strong></div>
                    {!compact && <div><span>بهترین دوره</span><strong>{best.label}</strong></div>}
                </div>
            </div>
            <div className="cvi-trend-chart-wrap">
                <svg className="cvi-trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="روند امتیاز CVI در دوره‌ها">
                    {ticks.map(t => <g key={t}><line x1={pad.left} x2={width-pad.right} y1={y(t)} y2={y(t)} className="cvi-trend-gridline"/><text x={pad.left-10} y={y(t)+4} textAnchor="end" className="cvi-trend-axis-label">{t}</text></g>)}
                    {!compact && <polyline points={points('individual')} className="cvi-trend-line individual"/>}
                    {!compact && <polyline points={points('org')} className="cvi-trend-line strategic"/>}
                    <polyline points={points('cvi')} className="cvi-trend-line cvi"/>
                    {visible.map((period, i) => <g key={period.periodId || i}>
                        <circle cx={x(i)} cy={y(period.cvi)} r={compact ? 5 : 5.5} className="cvi-trend-dot cvi"><title>{`${period.label}: CVI ${Number(period.cvi).toFixed(1)} · فردی ${Number(period.individual || 0).toFixed(1)} · راهبردی ${Number(period.org || 0).toFixed(1)}`}</title></circle>
                        {!compact && <circle cx={x(i)} cy={y(period.individual)} r="3.5" className="cvi-trend-dot individual"/>}
                        {!compact && <circle cx={x(i)} cy={y(period.org)} r="3.5" className="cvi-trend-dot strategic"/>}
                        <text x={x(i)} y={height-14} textAnchor="middle" className="cvi-trend-period-label">{period.label}</text>
                    </g>)}
                </svg>
            </div>
            <div className="cvi-trend-footer">
                <div className="cvi-trend-legend"><span><i className="cvi"/>CVI</span>{!compact && <><span><i className="individual"/>عملکرد فردی</span><span><i className="strategic"/>مشارکت جمعی</span></>}</div>
                {onOpenHistory && <button className="cvi-dashboard-text-action" onClick={onOpenHistory}>مشاهده جزئیات دوره‌ها ←</button>}
            </div>
        </div>
    </Card>;
}

// ── SPECIALIST TABS (محتوای هر تب — TopBar/BottomNav در پوسته‌ی اصلی‌ان) ──
function PersonalInsightSupportPanels({ cviScore, indivScore, orgScore, previousCvi = null, activities = [], managementSupportCount = 0 }) {
    const openBlockers = activities.filter(a => ["open", "in_progress"].includes(String(a.blockerStatus || "").toLowerCase()));
    const insights = [];
    const cvi = Number(cviScore);
    const ind = Number(indivScore);
    const org = Number(orgScore);
    const prev = Number(previousCvi);

    if (Number.isFinite(cvi) && Number.isFinite(prev)) {
        const delta = cvi - prev;
        if (Math.abs(delta) >= 0.5) insights.push({
            type: delta >= 0 ? "up" : "down",
            text: `CVI شما نسبت به دوره قبل ${Math.abs(delta).toFixed(1)} امتیاز ${delta >= 0 ? "افزایش" : "کاهش"} داشته است.`
        });
    }
    if (Number.isFinite(ind) && Number.isFinite(org) && Math.abs(ind - org) >= 8) {
        insights.push({
            type: ind > org ? "info" : "good",
            text: ind > org
                ? "عملکرد فردی شما از مشارکت جمعی بالاتر است؛ نتایج مشترک می‌تواند نقطه تمرکز بعدی باشد."
                : "مشارکت جمعی شما از عملکرد فردی بالاتر است؛ بررسی اجزای عملکرد فردی می‌تواند فرصت بهبود را روشن کند."
        });
    }
    if (openBlockers.length > 0) insights.push({
        type: "attention",
        text: `${openBlockers.length} مانع یا نیاز حمایتی باز در فعالیت‌های شما ثبت شده است.`
    });
    if (!insights.length) insights.push({
        type: "good",
        text: "در حال حاضر نشانه مهمی که نیازمند توجه فوری باشد در داده‌های ثبت‌شده دیده نمی‌شود."
    });

    const blockerStatusLabel = status => status === "in_progress" ? "در حال پیگیری" : "باز";

    return <div className="cvi-dashboard-insight-grid">
        <section className="cvi-premium-panel cvi-performance-insights-panel">
            <div className="cvi-premium-panel-head">
                <div><span>PERFORMANCE INSIGHTS</span><h3>بینش‌های عملکرد</h3></div>
            </div>
            <div className="cvi-premium-signal-list">
                {insights.slice(0, 4).map((signal, index) => <div key={index} className={`cvi-premium-signal ${signal.type}`}>
                    <i>{signal.type === "up" ? "↑" : signal.type === "down" ? "↓" : signal.type === "good" ? "✓" : "!"}</i>
                    <span>{signal.text}</span>
                </div>)}
            </div>
        </section>

        <section className="cvi-premium-panel cvi-blockers-panel">
            <div className="cvi-premium-panel-head">
                <div><span>BLOCKERS & SUPPORT</span><h3>موانع و حمایت موردنیاز</h3></div>
                <b className="cvi-premium-attention-badge">{openBlockers.length + managementSupportCount}</b>
            </div>
            <div className="cvi-support-list">
                {openBlockers.length === 0 && managementSupportCount === 0 ? (
                    <div className="cvi-support-empty"><UiIcon name="check" size={16} /><span>مانع یا نیاز حمایتی بازی ثبت نشده است.</span></div>
                ) : <>
                    {openBlockers.slice(0, 3).map(item => <div key={item.id} className="cvi-support-item">
                        <span className="cvi-support-status">{blockerStatusLabel(String(item.blockerStatus || "").toLowerCase())}</span>
                        <div><strong>{item.title || "فعالیت"}</strong><small>{item.blockerText || item.supportNeed || "نیاز حمایتی ثبت شده"}</small></div>
                    </div>)}
                    {managementSupportCount > 0 && <div className="cvi-support-item is-management">
                        <span className="cvi-support-status">زیرمجموعه</span>
                        <div><strong>نیازهای حمایتی تیم</strong><small>{managementSupportCount} مورد باز در زیرمجموعه مستقیم شما</small></div>
                    </div>}
                </>}
            </div>
        </section>
    </div>;
}

function PersonalRecentActivityPanel({ activities = [], activityGroups = [], onOpenActivities, onCreateActivity }) {
    const recent = activities.slice(0, 3);
    return <section className="cvi-premium-panel cvi-personal-recent-panel">
        <div className="cvi-premium-panel-head">
            <div><span>RECENT ACTIVITY</span><h3>فعالیت‌های اخیر</h3></div>
            <button type="button" onClick={onOpenActivities}>مشاهده همه</button>
        </div>
        <div className="cvi-recent-activity-list">
            {recent.length === 0 ? (
                <div className="cvi-dashboard-empty">هنوز فعالیتی ثبت نکرده‌اید</div>
            ) : recent.map(a => {
                const group = activityGroups.find(g => g.id === a.groupId);
                return <button type="button" key={a.id} onClick={onOpenActivities}>
                    <span className="cvi-dashboard-row-icon" aria-hidden="true"><UiIcon name="activity" size={16} /></span>
                    <span className="cvi-dashboard-row-main">
                        <strong>{a.title}</strong>
                        <small>{group?.label || "—"} · {a.date || "—"}</small>
                    </span>
                    <StatusBadge status={a.status} />
                </button>;
            })}
        </div>
        <DashboardLinkButton primary onClick={onCreateActivity}>ثبت فعالیت جدید</DashboardLinkButton>
    </section>;
}

function SpecialistHome({ user, myActs, allPeriods, indivScore, nonRoutineScore, routineScore,
    orgScore, orgBreakdown, cviScore, team, pos, activityGroups, activePeriod, setView, setSelectedAct, setNav,
    setSelectedOrgLevel }) {
    const previousCvi = allPeriods.length > 1 ? allPeriods[allPeriods.length - 2]?.cvi : null;
    const personalNeedsAction = myActs.filter(a => ["revision_requested","needs_revision"].includes(String(a.status || "").toLowerCase())).length;
    const personalOpenBlockers = myActs.filter(a => ["open","in_progress"].includes(String(a.blockerStatus || "").toLowerCase())).length;
    const trendRows = (allPeriods || []).filter(p => Number.isFinite(Number(p?.cvi))).map((p,i) => ({
    periodId: p.periodId || String(i),
    label: p.label || `دوره ${i+1}`,
    order: p.start || p.startDate || String(i).padStart(4,"0"),
    cvi: Number(p.cvi),
    individual: Number(p.individual),
    collective: Number(p.org ?? p.collective)
})).filter(r => [r.cvi,r.individual,r.collective].every(Number.isFinite));

    return (
        <div className="cvi-dashboard-page cvi-premium-dashboard">
            <DashboardPageIntro title={user.name}
                subtitle={`${pos?.label || "—"} · ${team?.label || "—"}`}
                period={activePeriod?.label || "دوره‌ای فعال نیست"} periodStart={activePeriod?.start} periodEnd={activePeriod?.end} periodInline subtitleInline
                attentionCount={personalNeedsAction + personalOpenBlockers} onAttentionClick={() => setNav("activities")} />

            <div className="cvi-premium-top-grid">
                <ScoreOverview user={user} cviScore={cviScore} indivScore={indivScore}
                    nonRoutineScore={nonRoutineScore} routineScore={routineScore}
                    orgScore={orgScore} orgBreakdown={orgBreakdown}
                    onOpenNonRoutine={() => setView("nonroutine-detail")}
                    onOpenRoutine={() => setView("routine-detail")}
                    onOpenTasksActivities={() => setView("tasks-activities-detail")}
                    onOpenGoals={() => setNav("goals")}
                    onOpenPeerFeedback={() => setNav("peer-feedback")}
                    onOpenOrgLevel={(level) => { setSelectedOrgLevel(level); setView("org-level-detail"); }} />

                <section className="cvi-premium-trend-section">
                    <Card>
                        <div className="cvi-premium-trend-card">
                            <div className="cvi-dashboard-card-heading">
                                <h2>روند CVI در دوره‌های اخیر</h2>
                                <p>مقایسه عملکرد فردی و مشارکت جمعی در دوره‌های اخیر.</p>
                            </div>
                            <div className="cvi-premium-chart-card">
                                <PremiumPerformanceTrendChart rows={trendRows} />
                            </div>
                        </div>
                    </Card>
                </section>
            </div>

            <div className="cvi-premium-bottom-grid">
                <div className="cvi-premium-bottom-analysis">
                    <PersonalInsightSupportPanels
                        cviScore={cviScore}
                        indivScore={indivScore}
                        orgScore={orgScore}
                        previousCvi={previousCvi}
                        activities={myActs}
                    />
                </div>
                <PersonalRecentActivityPanel
                    activities={myActs}
                    activityGroups={activityGroups}
                    onOpenActivities={() => setNav("activities")}
                    onCreateActivity={() => setNav("new")}
                />
            </div>
        </div>
    );
}

export function TasksActivitiesPage({ nav, setNav, routineContent, activityContent }) {
    const tab = nav === "activities" ? "activities" : "self-assessment";
    return <section className="cvi-tasks-activities-page">
        <ProfessionalTabs items={[
            { id: "self-assessment", label: "فعالیت‌های مستمر" },
            { id: "activities", label: "فعالیت‌های غیرمستمر" },
        ]} value={tab} onChange={setNav} ariaLabel="وظایف و فعالیت‌ها" />
        <div role="tabpanel" aria-label={tab === "activities" ? "فعالیت‌های غیرمستمر" : "فعالیت‌های مستمر"}>
            {tab === "activities" ? activityContent : routineContent}
        </div>
    </section>;
}

function SpecialistActivities({ myActs, activityGroups, setNav, setSelectedAct, setView }) {
    return (
        <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
            <div className="cvi-workflow-header" style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 12
            }}>
                <div><div className="cvi-workflow-title">فعالیت‌های غیرمستمر</div><div className="cvi-workflow-subtitle">فهرست فعالیت‌های غیرمستمر ثبت‌شده</div></div>
                <button className="cvi-primary-button" onClick={() => setNav("new")}
                    style={{
                        background: C.navy, color: "#fff", border: "none",
                        borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer"
                    }}>
                    ثبت فعالیت جدید
                </button>
            </div>
            {myActs.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                    <div>هنوز فعالیتی ثبت نکرده‌اید</div>
                </div>
            ) : myActs.map(a => (
                <div className="cvi-workflow-record" key={a.id} onClick={() => { setSelectedAct(a); setView("act-detail"); }}
                    style={{ marginBottom: 8, cursor: "pointer" }}>
                    <Card>
                        <div style={{ padding: 14 }}>
                            <div style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "flex-start", marginBottom: 6
                            }}>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{a.title}</div>
                                <StatusBadge status={a.status} />
                            </div>
                            <div style={{ fontSize: 11, color: C.muted }}>{groupLabel(activityGroups, a.groupId)} · {a.date}</div>
                            {a.status === ACTIVITY_STATUS.REVISION_REQUESTED && a.managerNote && (
                                <div style={{
                                    background: C.purpleLt, borderRadius: 6,
                                    padding: "6px 10px", fontSize: 11, color: C.purple, marginTop: 8
                                }}>
                                    💬 {a.managerNote}
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            ))}
        </div>
    );
}

function SpecialistHistory({ allPeriods, user, setView, setSelectedPeriod, setPeriodDetailOrigin }) {
    const openSummary = (period) => {
        setSelectedPeriod(period);
        setPeriodDetailOrigin("history");
        setView("period-detail");
    };
    return <HistoryArchivePage allPeriods={allPeriods} user={user} onOpenSummary={openSummary} />;
}

// پروفایل عمومی — برای همه‌ی نقش‌ها (کارشناس/مدیر/معاون/مدیرعامل/ادمین) یکسان است
export function ProfileTabs({ header, personal, organization, account, preferences }) {
    const [tab, setTab] = useState("personal");
    const tabs = [
        { id: "personal", label: "اطلاعات فردی" },
        { id: "organization", label: "اطلاعات سازمانی" },
        { id: "account", label: "حساب کاربری" },
        { id: "preferences", label: "ترجیحات" },
    ];
    const content = { personal, organization, account, preferences };
    return <div id="cvi-profile" className="cvi-profile-page">
        <div className="cvi-profile-content">
            <div className="cvi-profile-intro"><h1>پروفایل و تنظیمات</h1><p>اطلاعات شخصی، حساب کاربری و ترجیحات شما</p></div>
            <div className="cvi-profile-header">{header}</div>
            <ProfessionalTabs items={tabs} value={tab} onChange={setTab} ariaLabel="پروفایل و تنظیمات" />
            <div role="tabpanel" aria-label={tabs.find(item => item.id === tab).label} className="cvi-profile-panel">
                {content[tab]}
            </div>
        </div>
    </div>;
}

function PersonProfile({ user }) {
    const { users, setUsers, teams, positions, departments, orgSettings } = useData();
    const { language, languages, t } = useI18n();
    const profileUser = users.find(u => u.id === user.id) || user;
    const team = teams.find(t => t.id === profileUser.teamId);
    const pos = positions.find(p => p.id === profileUser.posId);
    const dept = departments.find(d => d.id === (team?.deptId ?? profileUser.deptId));
    const orgUnit = profileUser.orgUnit || null;
    const roleLabelMap = { specialist: "کارشناس", manager: "مدیر", deputy: "معاون", ceo: "مدیرعامل", admin: "ادمین" };

    const rows = [
        ["کد پرسنلی", profileUser.employeeCode || "—"],
        ["کد ملی", profileUser.nationalId || "—"],
        ["ایمیل", profileUser.email || "—"],
        ["سطح دسترسی", roleLabelMap[profileUser.role] || profileUser.role || "—"],
        ["سمت شغلی", pos?.label || "—"],
        ["واحد سازمانی", orgUnit?.name || team?.label || dept?.label || "—"],
        ["مدیر مستقیم", users.find(u => u.id === profileUser.managerId)?.name || "—"],
    ];

    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [pwError, setPwError] = useState("");
    const [pwSuccess, setPwSuccess] = useState(false);
    const [photoError, setPhotoError] = useState("");
    const [languageStatus, setLanguageStatus] = useState("");
    const [languageSaving, setLanguageSaving] = useState(false);
    const [draftLanguage, setDraftLanguage] = useState(language);

    useEffect(() => {
        setDraftLanguage(language);
    }, [language]);

    const pwFieldStyle = {
        width: "100%", padding: "9px 12px", border: `1.5px solid ${C.borderMd}`,
        borderRadius: 8, fontSize: 13, outline: "none", background: "#fff",
        fontFamily: "inherit", direction: "inherit", boxSizing: "border-box"
    };


    const savePreferredLanguage = async () => {
        const nextLanguage = draftLanguage;
        if (!SUPPORTED_LANGUAGES[nextLanguage] || languageSaving || nextLanguage === language) return;
        setLanguageStatus("");
        setLanguageSaving(true);
        const applyAndReload = () => {
            setUsers(prev => prev.map(item => item.id === user.id ? { ...item, preferredLanguage: nextLanguage } : item));
            try {
                const storedUser = JSON.parse(sessionStorage.getItem("cvi_user") || "null");
                if (storedUser?.id === user.id) sessionStorage.setItem("cvi_user", JSON.stringify({ ...storedUser, preferredLanguage: nextLanguage }));
                localStorage.setItem("cvi_language", nextLanguage);
            } catch { }
            window.location.reload();
        };
        const token = sessionStorage.getItem("cvi_token");
        if (!token) {
            applyAndReload();
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/me/preferences`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ preferredLanguage: nextLanguage }),
            });
            if (response.status === 401) {
                sessionStorage.removeItem("cvi_user");
                sessionStorage.removeItem("cvi_token");
                window.dispatchEvent(new Event("cvi-session-expired"));
                return;
            }
            if (!response.ok && response.status !== 404 && response.status !== 405) {
                setLanguageStatus(t("language.saveFailed"));
                return;
            }
            applyAndReload();
        } catch {
            setLanguageStatus(t("language.saveFailed"));
        } finally {
            setLanguageSaving(false);
        }
    };

    const submitPasswordChange = () => {
        setPwSuccess(false);
        if (!currentPw.trim()) return setPwError("رمز فعلی را وارد کنید");
        if (currentPw !== profileUser.password) return setPwError("رمز فعلی اشتباه است");
        if (!newPw.trim() || newPw.length < 3) return setPwError("رمز جدید باید حداقل ۳ کاراکتر باشد");
        if (newPw !== confirmPw) return setPwError("تکرار رمز جدید مطابقت ندارد");
        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, password: newPw } : u));
        setPwError("");
        setCurrentPw(""); setNewPw(""); setConfirmPw("");
        setPwSuccess(true);
    };

    const uploadProfilePhoto = (file) => {
        setPhotoError("");
        if (!file) return;
        if (!file.type.startsWith("image/")) return setPhotoError("فقط فایل تصویری قابل بارگذاری است");
        if (file.size > 3 * 1024 * 1024) return setPhotoError("حجم عکس باید کمتر از ۳ مگابایت باشد");
        const reader = new FileReader();
        reader.onload = () => setUsers(prev => prev.map(u => u.id === user.id
            ? { ...u, photoDataUrl: String(reader.result || "") } : u));
        reader.onerror = () => setPhotoError("بارگذاری عکس انجام نشد؛ دوباره تلاش کنید");
        reader.readAsDataURL(file);
    };

    const renderRows = entries => <Card><dl className="cvi-profile-fields">
        {entries.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl></Card>;
    return <ProfileTabs
        header={<>                    <div className="cvi-profile-identity">
                        <div style={{
                            width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
                            background: C.cyanBg, border: `2px solid ${C.cyan}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 22, fontWeight: 600, color: C.navy, flexShrink: 0
                        }}>
                            {profileUser.photoDataUrl
                                ? <img src={profileUser.photoDataUrl} alt={`عکس ${profileUser.name}`}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                : profileUser.name?.[0] || "?"}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 22, fontWeight: 650 }}>{profileUser.name}</div>
                            <div style={{ fontSize: 12, color: C.muted, marginBottom: 7 }}><bdi>@{profileUser.username}</bdi> · {roleLabelMap[profileUser.role] || profileUser.role}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <label style={{
                                    padding: "5px 10px", border: `1px solid ${C.borderMd}`,
                                    borderRadius: 7, fontSize: 10.5, color: C.navy, cursor: "pointer", background: "#fff"
                                }}>
                                    📷 {profileUser.photoDataUrl ? "تغییر عکس" : "بارگذاری عکس"}
                                    <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }}
                                        onChange={e => { uploadProfilePhoto(e.target.files?.[0]); e.target.value = ""; }} />
                                </label>
                                {profileUser.photoDataUrl && <button onClick={() => setUsers(prev => prev.map(u => u.id === user.id
                                    ? { ...u, photoDataUrl: null } : u))} style={{
                                        border: "none", background: "transparent",
                                        color: C.red, fontSize: 10.5, cursor: "pointer", fontFamily: "inherit"
                                    }}>حذف عکس</button>}
                            </div>
                            {photoError && <div style={{ color: C.red, fontSize: 10, marginTop: 5 }}>{photoError}</div>}
                        </div>
                    </div>
</>}
        personal={renderRows(rows.slice(0, 3))}
        organization={renderRows(rows.slice(3))}
        account={<>            <Card>
                <div style={{ padding: 15 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 12, color: "#444" }}>تغییر رمز عبور</div>
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 4 }}>رمز فعلی</label>
                        <input type="password" style={pwFieldStyle} value={currentPw}
                            onChange={e => { setCurrentPw(e.target.value); setPwError(""); setPwSuccess(false); }} />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 4 }}>رمز جدید</label>
                        <input type="password" style={pwFieldStyle} value={newPw}
                            onChange={e => { setNewPw(e.target.value); setPwError(""); setPwSuccess(false); }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 4 }}>تکرار رمز جدید</label>
                        <input type="password" style={pwFieldStyle} value={confirmPw}
                            onChange={e => { setConfirmPw(e.target.value); setPwError(""); setPwSuccess(false); }} />
                    </div>
                    {pwError && <div style={{ color: C.red, fontSize: 11, marginBottom: 10 }}>{pwError}</div>}
                    {pwSuccess && <div style={{ color: C.teal, fontSize: 11, marginBottom: 10 }}>رمز عبور با موفقیت تغییر کرد</div>}
                    <button onClick={submitPasswordChange}
                        style={{
                            padding: "9px 18px", background: C.navy, color: "#fff",
                            border: "none", borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer"
                        }}>
                        ذخیره‌ی رمز جدید
                    </button>
                </div>
            </Card></>}
        preferences={<>      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: 15 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 5, color: "#444" }}>🌐 {t("language.display")}</div>
          <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.7, marginBottom: 12 }}>{t("language.description")}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, color: C.hint, marginBottom: 3 }}>{t("settings.currentLanguage")}</div>
              <div style={{ fontSize: 11.5, fontWeight: 500 }}>{languages[language]?.label || language}</div>
            </div>
            <LanguageSelector value={draftLanguage} onChange={(nextLanguage) => { setDraftLanguage(nextLanguage); setLanguageStatus(""); }} />
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: C.hint }}>
            {profileUser.preferredLanguage ? `User preference: ${profileUser.preferredLanguage}` : `Organization default: ${orgSettings?.defaultLanguage || "en"}`}
          </div>
          <button onClick={savePreferredLanguage} disabled={languageSaving || draftLanguage === language}
            style={{ marginTop: 12, padding: "8px 16px", background: draftLanguage === language ? C.borderMd : C.navy, color: draftLanguage === language ? C.hint : "#fff", border: "none", borderRadius: 8, fontSize: 11.5, fontWeight: 500, cursor: languageSaving || draftLanguage === language ? "default" : "pointer", fontFamily: "inherit" }}>
            {languageSaving ? t("common.loading") : ({ en: "Apply language", fr: "Appliquer la langue", es: "Aplicar idioma", ar: "تطبيق اللغة", fa: "اعمال زبان" }[language] || "Apply language")}
          </button>
          {languageSaving && <div style={{ marginTop: 8, fontSize: 10.5, color: C.muted }}>{t("common.loading")}</div>}
          {!languageSaving && languageStatus && <div style={{ marginTop: 8, fontSize: 10.5, color: C.teal }}>{languageStatus}</div>}
        </div>
      </Card></>}
    />;
}

// ── PEER FEEDBACK ─────────────────────────────────────────────────────────────
function peerFeedbackPendingCount(peerAssignments, periodId) {
    if (!periodId) return 0;
    return (peerAssignments || []).filter(assignment => assignment.periodId === periodId && assignment.status === "pending").length;
}

function PeerFeedbackPage({ user }) {
    const { t } = useI18n();
    const { peerQuestions, peerAssignments, peerResult, peerLoading, peerError,
        submitPeerReviewApi, activePeriod } = useData();
    const [tab, setTab] = useState("tasks");
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
    const [answers, setAnswers] = useState({});
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const tasks = (peerAssignments || []).filter(assignment => assignment.periodId === activePeriod?.id);
    const pending = tasks.filter(assignment => assignment.status === "pending");
    const selectedTask = tasks.find(assignment => assignment.id === selectedAssignmentId);
    const result = peerResult?.subjectUserId === user.id && peerResult?.period?.id === activePeriod?.id ? peerResult : null;
    const responseCount = result?.summary?.responseCount || 0;
    const assignedCount = result?.summary?.assignedCount || 0;
    const minResponses = result?.config?.minResponses || 1;
    const canShow = Boolean(result?.summary?.enoughResponses && result?.summary?.overallScore != null);

    const openTask = (task) => {
        const existing = {};
        (task.review?.answers || []).forEach(answer => { existing[answer.questionId] = Number(answer.score); });
        setSelectedAssignmentId(task.id); setAnswers(existing); setError("");
    };

    const submit = async () => {
        if (!selectedTask || !activePeriod || saving) return;
        if (peerQuestions.some(question => answers[question.id] == null)) return setError(t("peer.allRequired"));
        setSaving(true); setError("");
        try {
            await submitPeerReviewApi(selectedTask.id, answers);
            setSelectedAssignmentId(null); setAnswers({});
        } catch (submitError) {
            console.error("Peer feedback submission failed:", submitError);
            setError(submitError.message || t("peer.submitFailed"));
        } finally {
            setSaving(false);
        }
    };

    if (peerLoading) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, color: C.muted, fontSize: 12 }}>{t("peer.loading")}</div>;

    if (selectedTask) return <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
        <button onClick={() => { setSelectedAssignmentId(null); setAnswers({}); setError(""); }} style={{
            border: "none", background: "transparent", color: C.cyan,
            fontFamily: "inherit", cursor: "pointer", marginBottom: 10
        }}>{`→ ${t("common.back")}`}</button>
        <div style={{ fontSize: 15, fontWeight: 500 }}>{t("peer.feedbackAbout", { name: selectedTask.subjectUser?.name || "—" })}</div>
        <div style={{ fontSize: 11, color: C.muted, margin: "4px 0 14px" }}>{activePeriod?.label || t("common.noActivePeriod")}</div>
        <Card><div style={{ padding: 16 }}>{peerQuestions.map((question, index) => <div key={question.id}
            style={{ padding: "11px 0", borderBottom: index < peerQuestions.length - 1 ? `1px solid ${C.bg}` : "none" }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>{index + 1}. {question.label}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{[5, 4, 3, 2, 1].map(score => {
                const labels = { 5: t("rating.5"), 4: t("rating.4"), 3: t("rating.3"), 2: t("rating.2"), 1: t("rating.1") };
                const selected = Number(answers[question.id]) === score;
                return <button key={score} onClick={() => setAnswers(current => ({ ...current, [question.id]: score }))}
                    style={{
                        padding: "6px 11px", borderRadius: 16, border: `1px solid ${selected ? C.navy : C.border}`,
                        fontFamily: "inherit", cursor: "pointer", background: selected ? C.navy : "#fff", color: selected ? "#fff" : C.muted, fontSize: 10.5
                    }}>
                    {labels[score]}
                </button>;
            })}</div>
        </div>)}
            {error && <div style={{ color: C.red, fontSize: 11, marginTop: 10 }}>{error}</div>}
            <button onClick={submit} disabled={saving} style={{
                width: "100%", padding: 11, marginTop: 14, border: "none", borderRadius: 8,
                background: saving ? "#E4E4EC" : C.navy, color: saving ? C.hint : "#fff", fontFamily: "inherit", cursor: saving ? "wait" : "pointer"
            }}>
                {saving ? t("peer.submitting") : t("peer.submit")}
            </button>
        </div></Card>
    </div>;

    const tabs = [{ id: "tasks", label: t("peer.myReviews"), icon: <UiIcon name="peer" size={16} />, count: tasks.length },
    { id: "results", label: t("peer.myResults"), icon: <UiIcon name="performance" size={16} />, count: responseCount }];

    return <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
        <div className="cvi-workflow-heading-block"><div className="cvi-workflow-title">{t("peer.title")}</div><div className="cvi-workflow-subtitle">{t("peer.subtitle")}</div></div>
        {peerError && <div style={{ background: C.redLt, color: C.red, borderRadius: 8, padding: "8px 10px", fontSize: 11, marginBottom: 10 }}>{peerError}</div>}
        <ProfessionalTabs items={tabs} value={tab} onChange={setTab} ariaLabel="بخش‌های بازخورد همکاران" />

        {tab === "tasks" && (tasks.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: C.muted, fontSize: 12 }}>{t("peer.noAssignments")}</div> :
            tasks.map(task => <Card key={task.id} style={{ marginBottom: 8 }}><div style={{ padding: 14, display: "flex", alignItems: "center", gap: 10 }}>
                <UserAvatar user={task.subjectUser || { id: task.subjectUserId, name: "—" }} size={38} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 12.5, fontWeight: 500 }}>{task.subjectUser?.name || "—"}</div>
                    <div style={{ fontSize: 10, color: C.hint, marginTop: 2 }}>{task.status === "completed"
                        ? `ارسال‌شده · ${task.completedAt ? jalaliDateFromGregorian(new Date(task.completedAt)) : "—"}` : t("peer.waiting")}</div></div>
                {task.status === "completed" ? <Badge color="teal">{t("common.completed")}</Badge> : <button onClick={() => openTask(task)}
                    style={{ padding: "7px 11px", border: "none", borderRadius: 7, background: C.navy, color: "#fff", fontFamily: "inherit", cursor: "pointer" }}>{t("peer.start")}</button>}
            </div></Card>))}

        {tab === "results" && (!result?.config?.enabled ? <div style={{ padding: 36, textAlign: "center", color: C.muted, fontSize: 12 }}>{t("peer.notEnabled")}</div> :
            !canShow ? <Card><div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12, lineHeight: 1.8 }}>
                {t("peer.threshold", { min: minResponses })}<br />
                {t("peer.progress", { done: responseCount, total: assignedCount })}
            </div></Card> :
                <div><Card style={{ marginBottom: 12 }}><div style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div><div style={{ fontSize: 11, color: C.muted }}>{t("peer.finalScore")}</div>
                        <div style={{ fontSize: 10, color: C.hint, marginTop: 4 }}>{t("peer.validResponses", { count: responseCount })}</div></div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: C.purple }}>{((Number(result.summary.overallScore) || 0) / 5 * 100).toFixed(0)}٪</div>
                </div></Card>
                    <Card><div style={{ padding: 14 }}>{result.questions.map((question, index) => {
                        const percentage = question.averageScore == null ? 0 : (Number(question.averageScore) / 5) * 100;
                        return <div key={question.questionId} style={{ padding: "9px 0", borderBottom: index < result.questions.length - 1 ? `1px solid ${C.bg}` : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5 }}><span>{question.label}</span>
                                <b style={{ color: C.purple }}>{percentage.toFixed(0)}٪</b></div>
                            <div style={{ fontSize: 9.5, color: C.hint, marginTop: 3 }}>{question.responseCount} {t("common.responses")} · {t("common.questionWeight")} {question.weight}%</div>
                            <MiniBar value={percentage} color={C.purple} />
                        </div>;
                    })}</div></Card>
                    {result.config.visibility === "anonymous" && <div style={{ fontSize: 10, color: C.hint, marginTop: 8 }}>{t("peer.anonymous")}</div>}
                </div>)}
    </div>;
}

// ── SPECIALIST APP SHELL (ریسپانسیو — دسکتاپ Sidebar / موبایل BottomNav) ──────
function SpecialistApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels,
        positions, indicatorDefs, indicatorData, teams, departments, users, activePeriod,
        routineAnswers, routineQuestions, routineLevels, orgSettings, periodSnapshots,
        scheduleTemplates, scheduleExceptions, peerAssignments } = useData();
    const routineCtx = useRoutineCtx();
    const isMobile = useIsMobile();
    const [nav, setNav] = useState("dashboard");
    const [view, setView] = useState(null);
    const [selectedAct, setSelectedAct] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [periodDetailOrigin, setPeriodDetailOrigin] = useState("cvi-detail");
    const [selectedOrgLevel, setSelectedOrgLevel] = useState(null);

    const myActs = activities.filter(a => a.userId === user.id);
    const nonRoutineScore = calcNonRoutineScore(activities, activityGroups, user.id, activePeriod?.id);
    const routineScore = calcRoutineScore(routineAnswers, routineQuestions, routineLevels,
        user.id, user.teamId, activePeriod?.id);
    const indivScore = calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, user, activePeriod?.id);
    const orgBreakdown = calcOrgScoreBreakdown(user, positions, indicatorDefs, indicatorData, activePeriod?.id);
    const orgScore = orgBreakdown.total;
    const cviScore = calcCVI(indivScore, orgScore);
    const pendingCount = myActs.filter(isReviewableActivity).length;
    const revisionCount = myActs.filter(a => a.status === ACTIVITY_STATUS.REVISION_REQUESTED).length;
    const badgeCount = pendingCount + revisionCount;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);
    const indicatorActionCount = activePeriod ? indicatorDefs.filter(d => d.registrarId === user.id)
        .flatMap(d => indicatorScheduleOccurrences(d, activePeriod, scheduleTemplates, scheduleExceptions).map(schedule => ({ d, schedule })))
        .filter(({ d, schedule }) => {
            const data = indicatorData.find(x => x.indicatorId === d.id && x.periodId === activePeriod.id &&
                (x.occurrenceId === schedule.occurrenceId || (!x.occurrenceId && schedule.occurrenceId.endsWith(":1")))); return !data || ["draft", "revision_requested"].includes(data.status);
        }).length : 0;

    const team = teams.find(t => t.id === user.teamId);
    const pos = positions.find(p => p.id === user.posId);

    const allPeriods = [
        ...latestPeriodSnapshotsForUser(periodSnapshots, user.id),
        ...(activePeriod ? [{
            periodId: activePeriod.id, label: activePeriod.label,
            individual: indivScore, org: orgScore, cvi: cviScore,
            nonRoutine: nonRoutineScore, routine: routineScore,
            peerFeedback: calcEligiblePeerFeedbackScore(routineCtx, user.id, activePeriod.id),
            status: "temporary", locked: false
        }] : [])
    ];

    const { createActivityApi } = useData();

    if (nav === "new" && !activePeriod) {
        return <div style={{ padding: 24, direction: "inherit", fontFamily: "inherit" }}>
            <Card><div style={{ padding: 20, textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>ثبت فعالیت غیرفعال است</div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>دوره فعالی وجود ندارد؛ فعالیت جدید فقط در دوره فعال ثبت می‌شود.</div>
                <button onClick={() => setNav("activities")} style={{
                    padding: "8px 16px", background: C.navy,
                    color: "#fff", border: "none", borderRadius: 8, cursor: "pointer"
                }}>بازگشت</button>
            </div></Card>
        </div>;
    }
    if (nav === "new") {
        return (
            <ActivityForm
                draftKey={`activity-draft-${user.id}`}
                onBack={() => setNav("activities")}
                onSubmit={async (form) => {
                    try {
                        await createActivityApi(form, activePeriod?.id);
                        setNav("activities");
                    } catch (error) {
                        console.error("Activity creation failed:", error);
                        uxNotify(error.message || "ثبت فعالیت انجام نشد");
                    }
                }}
            />
        );
    }

    if (view) {
        return (
            <SpecialistDetailView view={view} setView={setView}
                selectedAct={selectedAct} setSelectedAct={setSelectedAct}
                selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}
                periodDetailOrigin={periodDetailOrigin} setPeriodDetailOrigin={setPeriodDetailOrigin}
                myActs={myActs} allPeriods={allPeriods}
                activityGroups={activityGroups} impactLevels={impactLevels}
                user={user} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={activePeriod}
                routineCtx={routineCtx} selectedOrgLevel={selectedOrgLevel} />
        );
    }

    const dashboardContent = (
        <SpecialistHome user={user} myActs={myActs} indivScore={indivScore}
            nonRoutineScore={nonRoutineScore} routineScore={routineScore}
            orgScore={orgScore} orgBreakdown={orgBreakdown} cviScore={cviScore} team={team} pos={pos}
            activityGroups={activityGroups} allPeriods={allPeriods} activePeriod={activePeriod}
            setView={setView} setSelectedAct={setSelectedAct} setNav={setNav}
            setSelectedOrgLevel={setSelectedOrgLevel} />
    );

    const renderContent = () => {
        if (["tasks-activities", "self-assessment", "activities"].includes(nav)) return (
            <TasksActivitiesPage nav={nav} setNav={setNav}
                routineContent={<RoutineSelfAssessment activePeriod={activePeriod} />}
                activityContent={<SpecialistActivities myActs={myActs} activityGroups={activityGroups} setNav={setNav}
                    setSelectedAct={setSelectedAct} setView={setView} />} />
        );
        if (nav === "my-indicators") return <MyIndicators user={user} />;
        if (nav === "history") return <SpecialistHistory user={user} allPeriods={allPeriods}
            setView={setView} setSelectedPeriod={setSelectedPeriod} setPeriodDetailOrigin={setPeriodDetailOrigin} />;
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "goals") return <IndividualGoalsPage user={user} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "dashboard", label: "داشبورد من", icon: "dashboard" },
        { id: "self-assessment", label: "خوداظهاری", icon: "self" },
        { id: "goals", label: "اهداف فردی", icon: "target" },
        { id: "activities", label: "فعالیت‌های غیرمستمر", icon: "activity", badge: badgeCount },
        { id: "my-indicators", label: "شاخص‌های من", icon: "target", badge: indicatorActionCount },
        { id: "history", label: "تاریخچه", icon: "history" },
        { id: "peer-feedback", label: "بازخورد همکاران", icon: "peer", badge: peerPending },
        { id: "help", label: "راهنما", icon: "help", section: "account" },
        { id: "profile", label: "پروفایل", icon: "profile" },
    ]);

    return (
        <CviAppShell
            isMobile={isMobile}
            header={<TopBar searchItems={sidebarItems} onSearch={setNav} right={<div style={{ display: "flex", alignItems: "center", gap: 10 }}><UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} /></div>} />}
            periodContext={<PeriodContextBar period={activePeriod} daysLeft={daysRemainingInPeriod(activePeriod)} />}
            sidebar={<Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
            bottomNav={<AppBottomNav items={sidebarItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        >
            <div className="cvi-role-content-scroll">
                <div style={{ display: nav === "dashboard" ? "block" : "none" }} aria-hidden={nav !== "dashboard"}>
                    {dashboardContent}
                </div>
                {nav !== "dashboard" && <TabTransitionContent transitionKey={nav}>{renderContent()}</TabTransitionContent>}
            </div>
        </CviAppShell>
    );
}

function orgUnitSubtreeIds(orgUnits, rootId) {
    if (!rootId) return [];
    const ids = new Set([rootId]);
    let changed = true;
    while (changed) {
        changed = false;
        for (const unit of orgUnits || []) {
            if (unit.parentId && ids.has(unit.parentId) && !ids.has(unit.id)) {
                ids.add(unit.id);
                changed = true;
            }
        }
    }
    return [...ids];
}

function orgUnitChildren(orgUnits, parentId) {
    return (orgUnits || []).filter(unit => unit.parentId === parentId && unit.isActive !== false);
}

function reportingSubtreeUsers(users, managerId) {
    if (!managerId) return [];
    const children = new Map();
    (users || []).filter(u => u.isActive !== false).forEach(user => {
        if (!user.managerId) return;
        if (!children.has(user.managerId)) children.set(user.managerId, []);
        children.get(user.managerId).push(user);
    });
    const result = [];
    const seen = new Set();
    const queue = [...(children.get(managerId) || [])];
    while (queue.length) {
        const user = queue.shift();
        if (!user || seen.has(user.id)) continue;
        seen.add(user.id);
        result.push(user);
        queue.push(...(children.get(user.id) || []));
    }
    return result;
}


// ── MANAGER DASHBOARD ──────────────────────────────────────────────────────────
function PremiumPerformanceTrendChart({ rows }) {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    if (!rows?.length) return <div className="cvi-premium-empty">هنوز داده تاریخی کافی برای نمایش روند وجود ندارد.</div>;

    const width = 660, height = 360, padLeft = 52, padRight = 50, padTop = 30, padBottom = 82;
    const chartH = height - padTop - padBottom;
    const chartW = width - padLeft - padRight;
    const clampScore = value => Math.max(0, Math.min(100, Number(value) || 0));
    const x = index => rows.length === 1 ? width / 2 : padLeft + (index * chartW / (rows.length - 1));
    const y = value => padTop + chartH - (clampScore(value) / 100) * chartH;
    const poly = key => rows.map((row,index)=>`${x(index)},${y(row[key])}`).join(" ");
    const formatScore = value => Number.isFinite(Number(value)) ? formatFaDisplay(Number(value).toFixed(1)) : "—";
    const hovered = hoveredIndex == null ? null : rows[hoveredIndex];

    const tooltipWidth = 220;
    const tooltipHeight = 126;
    const hoveredX = hoveredIndex == null ? 0 : x(hoveredIndex);
    const tooltipX = hoveredIndex == null ? 0 :
        hoveredX > width * 0.62
            ? Math.max(10, hoveredX - tooltipWidth - 18)
            : Math.min(width - tooltipWidth - 10, hoveredX + 18);
    const topPoint = hoveredIndex == null ? 0 : Math.min(y(hovered.cvi), y(hovered.individual), y(hovered.collective));
    const tooltipY = hoveredIndex == null ? 0 : Math.max(
        10,
        Math.min(height - tooltipHeight - padBottom + 26, topPoint - tooltipHeight / 2)
    );

    const labelAnchor = index => index === 0 ? "start" : index === rows.length - 1 ? "end" : "middle";
    const labelX = index => index === 0 ? x(index) - 6 : index === rows.length - 1 ? x(index) + 6 : x(index);

    return <div className="cvi-premium-trend-wrap">
        <div className="cvi-premium-trend-legend">
            <span><i className="cvi-trend-dot total" />CVI</span>
            <span><i className="cvi-trend-dot individual" />عملکرد فردی</span>
            <span><i className="cvi-trend-dot collective" />مشارکت جمعی</span>
        </div>

        <svg className="cvi-premium-trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="روند عملکرد"
            onMouseLeave={() => setHoveredIndex(null)}>
            {[0,25,50,75,100].map(v=><g key={v}>
                <line x1={padLeft} x2={width-padRight} y1={y(v)} y2={y(v)} className="cvi-trend-grid" />
                <text x={padLeft-12} y={y(v)+4} textAnchor="end" className="cvi-trend-axis">{v}</text>
            </g>)}

            {rows.map((row,index)=>
                <line key={`guide-${row.periodId || index}`} x1={x(index)} x2={x(index)} y1={padTop} y2={height-padBottom}
                    className={`cvi-trend-hover-guide${hoveredIndex===index ? " is-visible" : ""}`} />
            )}

            <polyline points={poly("collective")} className="cvi-trend-line collective" />
            <polyline points={poly("individual")} className="cvi-trend-line individual" />
            <polyline points={poly("cvi")} className="cvi-trend-line total" />

            {rows.map((row,index)=><g key={row.periodId || index}>
                <circle cx={x(index)} cy={y(row.collective)} r={hoveredIndex===index ? "6.2" : "4.8"} className="cvi-trend-point collective" />
                <circle cx={x(index)} cy={y(row.individual)} r={hoveredIndex===index ? "6.2" : "4.8"} className="cvi-trend-point individual" />
                <circle cx={x(index)} cy={y(row.cvi)} r={hoveredIndex===index ? "7" : "5.4"} className="cvi-trend-point total" />
                <rect x={Math.max(4,x(index)-48)} y={padTop-10} width="96" height={chartH+42}
                    className="cvi-trend-hit-area" onMouseEnter={() => setHoveredIndex(index)} />
                <text x={labelX(index)} y={height-28} textAnchor={labelAnchor(index)} className="cvi-trend-label">{row.label}</text>
            </g>)}

            {hovered && <foreignObject x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} pointerEvents="none">
                <div xmlns="http://www.w3.org/1999/xhtml" className="cvi-trend-html-tooltip" dir="rtl">
                    <div className="cvi-trend-html-tooltip-title">{hovered.label}</div>
                    <div className="cvi-trend-html-tooltip-divider" />
                    <div className="cvi-trend-html-tooltip-row">
                        <span className="cvi-trend-html-tooltip-label"><i className="cvi-trend-dot total" />CVI</span>
                        <strong>{formatScore(hovered.cvi)}</strong>
                    </div>
                    <div className="cvi-trend-html-tooltip-row">
                        <span className="cvi-trend-html-tooltip-label"><i className="cvi-trend-dot individual" />عملکرد فردی</span>
                        <strong>{formatScore(hovered.individual)}</strong>
                    </div>
                    <div className="cvi-trend-html-tooltip-row">
                        <span className="cvi-trend-html-tooltip-label"><i className="cvi-trend-dot collective" />مشارکت جمعی</span>
                        <strong>{formatScore(hovered.collective)}</strong>
                    </div>
                </div>
            </foreignObject>}
        </svg>
    </div>;
}

function ManagerDashboard({ user, activities, activityGroups, positions, indicatorDefs,
    indicatorData, activePeriod, members, directReports = members, teams, onNavigate, setView, setSelectedOrgLevel }) {
    const { orgSettings, orgUnits, periodSnapshots, periods } = useData();
    const routineCtx = useRoutineCtx();
    const [liveCvi, setLiveCvi] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadLiveCvi = async () => {
            if (!user?.id || !activePeriod?.id) { setLiveCvi(null); return; }
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/cvi/periods/${encodeURIComponent(activePeriod.id)}/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "CVI load failed");
                if (!cancelled) setLiveCvi(payload);
            } catch {
                if (!cancelled) setLiveCvi(null);
            }
        };
        loadLiveCvi();
        return () => { cancelled = true; };
    }, [user?.id, activePeriod?.id]);
    const nonRoutineScore = calcNonRoutineScore(activities, activityGroups, user.id, activePeriod?.id);
    const routineScore = calcDeptRoutineScore(routineCtx.deptRoutineAnswers, routineCtx.deptRoutineQuestions,
        routineCtx.routineLevels, user.id, user.deptId, activePeriod?.id);
    const ind = calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, user, activePeriod?.id);
    const orgBreakdown = calcOrgScoreBreakdown(user, positions, indicatorDefs, indicatorData, activePeriod?.id);
    const org = orgBreakdown.total;
    const cvi = liveCvi?.scores?.cvi == null ? null : Number(liveCvi.scores.cvi);
    const serverIndividual = liveCvi?.scores?.individual == null ? null : Number(liveCvi.scores.individual);
    const serverOrg = liveCvi?.scores?.organizational == null ? null : Number(liveCvi.scores.organizational);

    const avgCVI = members.length
        ? members.reduce((sum, member) => sum + calcCVI(
            calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, member, activePeriod?.id),
            calcOrgScore(member, positions, indicatorDefs, indicatorData, activePeriod?.id)), 0) / members.length
        : null;

    const pending = activities.filter(a => directReports.some(m => m.id === a.userId) && isReviewableActivity(a));
    const myActivities = activities.filter(a => a.userId === user.id);
    const supportNeeds = activities.filter(a =>
        directReports.some(m => m.id === a.userId) &&
        ["open","in_progress"].includes(String(a.blockerStatus || "").toLowerCase())
    );
    const unitInds = withPeriodData(indicatorDefs.filter(i =>
        user.orgUnitId ? (i.level === "org_unit" && i.orgUnitId === user.orgUnitId) : (i.level === "team" && i.teamId === user.teamId)
    ), indicatorData, activePeriod?.id);
    const avgGoal = unitInds.length
        ? unitInds.reduce((sum,item)=>sum+(item.target ? Math.min(100,(item.actual/item.target)*100) : 0),0)/unitInds.length
        : null;

    const team = teams.find(t => t.id === user.teamId);
    const orgUnit = orgUnits.find(unit => unit.id === user.orgUnitId);
    const unitLabel = orgUnit?.label || team?.label || "واحد";
    const childUnits = user.orgUnitId ? orgUnits.filter(unit => unit.isActive !== false && unit.parentId === user.orgUnitId) : [];
    const hasManagementScope = directReports.length > 0 || childUnits.length > 0;

    const snapshotRows = latestPeriodSnapshotsForUser(periodSnapshots || [], user.id)
        .map(s => {
            const p = periods?.find(x => x.id === s.periodId);
            return {
                periodId: s.periodId,
                label: p?.label || s.label || s.periodId,
                order: p?.start || p?.startDate || s.createdAt || "",
                cvi: Number(s.cvi ?? s.scores?.cvi),
                individual: Number(s.individual ?? s.scores?.individual),
                collective: Number(s.org ?? s.organizational ?? s.scores?.organizational),
            };
        })
        .filter(row => [row.cvi,row.individual,row.collective].every(Number.isFinite))
        .sort((a,b)=>String(a.order).localeCompare(String(b.order)));

    const currentCvi = cvi;
    const currentIndividual = serverIndividual;
    const currentCollective = serverOrg;
    const hasLiveCurrent = [currentCvi,currentIndividual,currentCollective].every(value => value != null && Number.isFinite(value));

    const activeTrend = activePeriod && hasLiveCurrent
        ? {
            periodId: activePeriod.id,
            label: activePeriod.label,
            order: activePeriod.start || activePeriod.startDate || "9999",
            cvi: currentCvi,
            individual: currentIndividual,
            collective: currentCollective
        }
        : null;
    const trendMap = new Map(snapshotRows.map(row=>[row.periodId,row]));
    if (activeTrend) trendMap.set(activeTrend.periodId, activeTrend);
    const trendRows = [...trendMap.values()].sort((a,b)=>String(a.order).localeCompare(String(b.order))).slice(-6);

    const previous = trendRows.length > 1 ? trendRows[trendRows.length-2] : null;
    const delta = previous && activeTrend ? Number(activeTrend.cvi) - Number(previous.cvi) : null;
    const cycleProgress = activePeriod?.start && activePeriod?.end
        ? Math.max(0,Math.min(100,Math.round((Date.now()-new Date(activePeriod.start).getTime())/(new Date(activePeriod.end).getTime()-new Date(activePeriod.start).getTime())*100)))
        : null;
    const attentionCount = pending.length + supportNeeds.length;
    const myNeedsAction = myActivities.filter(a => ["revision_requested","needs_revision"].includes(String(a.status || "").toLowerCase())).length;
    const myOpenBlockers = myActivities.filter(a => ["open","in_progress"].includes(String(a.blockerStatus || "").toLowerCase()));
    const scoreText=value=>value==null||!Number.isFinite(Number(value))?"—":formatFaDisplay(Number(value).toFixed(0));

    const individualDrivers = [
        { label:"وظایف مستمر", value:routineScore, onClick:()=>setView("routine-detail") },
        { label:"فعالیت‌های غیرمستمر", value:nonRoutineScore, onClick:()=>setView("nonroutine-detail") },
        { label:"اهداف فردی", value:null, onClick:()=>onNavigate("goals") },
        { label:"بازخورد همکاران", value:null, onClick:()=>onNavigate("peer-feedback") },
    ];
    const collectiveDrivers = [
        { label:"تیمی", value:orgBreakdown?.team, weight:orgBreakdown?.wTeam, level:"team" },
        { label:"واحدهای بالادستی", value:orgBreakdown?.upstream ?? orgBreakdown?.dept, weight:orgBreakdown?.wUpstream ?? orgBreakdown?.wDept, level:"dept" },
        { label:"کلان سازمان", value:orgBreakdown?.org, weight:orgBreakdown?.wOrg, level:"org" },
    ];

    const signals = [];
    if (delta != null) signals.push({ type:delta>=0?"up":"down", text:`CVI نسبت به دوره قبل ${Math.abs(delta).toFixed(1)} امتیاز ${delta>=0?"بهبود":"کاهش"} داشته است.` });
    if (serverIndividual != null && serverOrg != null && serverIndividual > serverOrg+8) signals.push({ type:"info", text:"عملکرد فردی بالاتر از مشارکت جمعی است؛ نتایج واحد می‌تواند نقطه تمرکز بعدی باشد." });
    if (serverIndividual != null && serverOrg != null && serverOrg > serverIndividual+8) signals.push({ type:"info", text:"مشارکت جمعی قوی‌تر از عملکرد فردی است؛ بررسی اجزای عملکرد فردی می‌تواند مفید باشد." });
    if (attentionCount===0) signals.push({ type:"good", text:"در حال حاضر مورد فوری نیازمند اقدام ثبت نشده است." });
    if (supportNeeds.length) signals.push({ type:"attention", text:`${supportNeeds.length} نیاز حمایتی باز در زیرمجموعه مستقیم شما وجود دارد.` });
    if (!signals.length) signals.push({ type:"info", text:"با تکمیل داده‌های دوره، تحلیل‌های بیشتری در این بخش نمایش داده می‌شود." });

    return <div className="cvi-dashboard-page cvi-premium-dashboard">
        <DashboardPageIntro title={user.name} subtitle={`مدیر ${unitLabel}`}
            period={activePeriod?.label || "دوره‌ای فعال نیست"} periodStart={activePeriod?.start} periodEnd={activePeriod?.end} periodInline subtitleInline
            attentionCount={myNeedsAction + myOpenBlockers.length} onAttentionClick={() => onNavigate("activities")} />

        <div className="cvi-premium-top-grid">
            <ScoreOverview user={user} cviScore={cvi} indivScore={serverIndividual}
                nonRoutineScore={nonRoutineScore} routineScore={routineScore}
                orgScore={serverOrg} orgBreakdown={orgBreakdown} periodDelta={delta}
                onOpenNonRoutine={() => setView("nonroutine-detail")}
                onOpenRoutine={() => setView("routine-detail")}
                onOpenTasksActivities={() => setView("tasks-activities-detail")}
                onOpenGoals={() => onNavigate("goals")}
                onOpenPeerFeedback={() => onNavigate("peer-feedback")}
                onOpenOrgLevel={(level) => { setSelectedOrgLevel(level); setView("org-level-detail"); }} />

            <section className="cvi-premium-trend-section">
                <Card>
                    <div className="cvi-premium-trend-card">
                        <div className="cvi-dashboard-card-heading">
                            <h2>روند CVI در دوره‌های اخیر</h2>
                            <p>مقایسه عملکرد فردی و مشارکت جمعی در دوره‌های اخیر.</p>
                        </div>
                        <div className="cvi-premium-chart-card">
                            <PremiumPerformanceTrendChart rows={trendRows} />
                        </div>
                    </div>
                </Card>
            </section>
        </div>

        <div className="cvi-premium-bottom-grid">
            <div className="cvi-premium-bottom-analysis">
                <PersonalInsightSupportPanels
                    cviScore={cvi}
                    indivScore={serverIndividual}
                    orgScore={serverOrg}
                    previousCvi={previous?.cvi ?? null}
                    activities={myActivities}
                    managementSupportCount={0}
                />
            </div>
            <PersonalRecentActivityPanel
                activities={myActivities}
                activityGroups={activityGroups}
                onOpenActivities={() => onNavigate("activities")}
                onCreateActivity={() => onNavigate("new")}
            />
        </div>
    </div>;
}

const INDICATOR_STATUS_LABELS = {
    not_started: "ثبت‌نشده", draft: "پیش‌نویس", submitted: "ارسال‌شده",
    revision_requested: "نیازمند اصلاح", approved: "تأییدشده", locked: "قفل‌شده",
    closed_without_data: "بسته‌شده بدون داده"
};
function indicatorStatusLabel(status) { return INDICATOR_STATUS_LABELS[status] || INDICATOR_STATUS_LABELS.not_started; }

function IndicatorAudit({ data }) {
    const rows = data?.history || [];
    return <Card><div style={{ padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>تاریخچه تغییرات</div>
        {!rows.length ? <div style={{ fontSize: 11, color: C.hint }}>هنوز رویدادی ثبت نشده است.</div> :
            rows.slice().reverse().map((e, i) => <div key={`${e.date}_${i}`} style={{ padding: "8px 0", borderBottom: `1px solid ${C.bg}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 500 }}>{indicatorStatusLabel(e.toStatus || e.type)} · {e.by}</div>
                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{e.date}{e.note ? ` — ${e.note}` : ""}</div>
                {(e.oldValue != null || e.newValue != null) && <div style={{ fontSize: 10, color: C.hint, marginTop: 2 }}>
                    مقدار قبلی: {e.oldValue ?? "—"} ← مقدار جدید: {e.newValue ?? "—"}
                </div>}
            </div>)}
    </div></Card>;
}

function MyIndicators({ user }) {
    const { language } = useI18n();
    const { indicatorDefs, indicatorData, activePeriod, users, periods,
        scheduleTemplates, scheduleExceptions, saveIndicatorValueApi, indicatorLoading, indicatorError } = useData();
    const [tab, setTab] = useState("action");
    const [selectedId, setSelectedId] = useState(null);
    const [selectedOccurrence, setSelectedOccurrence] = useState(null);
    const [form, setForm] = useState({ actual: "", note: "", deviationReason: "", correctiveAction: "", doc: "", docDataUrl: "", _file: null });
    const assigned = indicatorDefs.filter(d => [d.ownerId, d.registrarId, d.approverId].includes(user.id));
    const activeRows = activePeriod ? assigned.flatMap(def => indicatorScheduleOccurrences(def, activePeriod, scheduleTemplates, scheduleExceptions)
        .map(schedule => ({ def, schedule }))) : [];
    const getData = (id, occurrenceId) => tab === "history" ? indicatorData.filter(d => d.indicatorId === id && ["approved", "locked", "closed_without_data"].includes(d.status)).slice(-1)[0]
        : activePeriod ? indicatorData.find(d => d.indicatorId === id && d.periodId === activePeriod.id &&
            (d.occurrenceId === occurrenceId || (!d.occurrenceId && occurrenceId?.endsWith(":1")))) : indicatorData.filter(d => d.indicatorId === id).slice(-1)[0];
    const statusOf = row => getData(row.def.id, row.schedule?.occurrenceId)?.status || "not_started";
    const inTab = row => tab === "action" ? ((row.def.registrarId === user.id || row.def.ownerId === user.id) && ["not_started", "draft", "revision_requested"].includes(statusOf(row)))
        : tab === "submitted" ? ["submitted"].includes(statusOf(row))
            : ["approved", "locked", "closed_without_data"].includes(statusOf(row));
    const rows = (tab === "history" ? assigned.map(def => ({ def, schedule: null })) : activeRows).filter(inTab);
    const selected = indicatorDefs.find(d => d.id === selectedId);
    const current = selected ? getData(selected.id, selectedOccurrence?.occurrenceId) : null;
    const today = todayJalaliStr();
    const registrationOpen = !selectedOccurrence || daysBetweenJalali(selectedOccurrence.openDate, today) >= 0;
    const beforeLock = !selectedOccurrence || daysBetweenJalali(today, selectedOccurrence.lockDate) >= 0;
    const beforeSubmitDeadline = !selectedOccurrence || daysBetweenJalali(today, selectedOccurrence.submitDeadline) >= 0 || current?.status === "revision_requested";
    const canEdit = selected?.registrarId === user.id && activePeriod?.status === "active" && registrationOpen && beforeSubmitDeadline && beforeLock && !["approved", "locked"].includes(current?.status);
    const activeStatus = row => indicatorData.find(d => d.indicatorId === row.def.id && d.periodId === activePeriod?.id &&
        (d.occurrenceId === row.schedule?.occurrenceId || (!d.occurrenceId && row.schedule?.occurrenceId?.endsWith(":1"))))?.status || "not_started";
    const tabItems = [
        { id: "action", label: "نیازمند اقدام", icon: "⚡", count: activeRows.filter(r => (r.def.registrarId === user.id || r.def.ownerId === user.id) && ["not_started", "draft", "revision_requested"].includes(activeStatus(r))).length },
        { id: "submitted", label: "ارسال‌شده", icon: "↗", count: activeRows.filter(r => activeStatus(r) === "submitted").length },
        { id: "history", label: "تاریخچه", icon: "◷", count: assigned.filter(def => indicatorData.some(d => d.indicatorId === def.id && ["approved", "locked", "closed_without_data"].includes(d.status))).length },
    ];
    const actualNum = Number(form.actual);
    const achievement = current?.target ? actualNum / current.target * 100 : null;
    const needsDeviation = achievement != null && achievement < 80;
    const open = row => {
        const d = row.def, data = getData(d.id, row.schedule?.occurrenceId); setSelectedId(d.id); setSelectedOccurrence(row.schedule); setForm({
            actual: data?.actual ?? "", note: data?.note || "",
            deviationReason: data?.deviationReason || "", correctiveAction: data?.correctiveAction || "", doc: data?.doc || "", docDataUrl: data?.docDataUrl || "", _file: null
        });
    };
    const save = async status => {
        if (!selected || !canEdit || form.actual === "") return;
        if (status === "submitted" && selected.requiresEvidence && !form.doc) {
            uxNotify("آپلود مستند هنوز به فضای ذخیره‌سازی سرور متصل نشده است. برای تست ارسال، از شاخصی که مستند الزامی ندارد استفاده کنید.");
            return;
        }
        if (status === "submitted" && needsDeviation && (!form.deviationReason.trim() || !form.correctiveAction.trim())) return;
        try {
            await saveIndicatorValueApi(
                selected.id,
                activePeriod.id,
                form,
                status === "submitted",
                selectedOccurrence?.occurrenceId || "period"
            );
            setSelectedId(null);
            setSelectedOccurrence(null);
        } catch (error) {
            console.error("Indicator value save failed:", error);
            uxNotify(error.message || "ثبت عملکرد شاخص انجام نشد");
        }
    };
    if (selected) return <div className="cvi-workflow-page cvi-indicator-detail-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
        <button type="button" className="cvi-indicator-back" onClick={() => { setSelectedId(null); setSelectedOccurrence(null); }}>→ بازگشت به شاخص‌ها</button>
        <Card style={{ marginBottom: 12 }}><div className="cvi-indicator-summary">
            <div className="cvi-indicator-summary-header"><div><div className="cvi-indicator-title">{selected.label}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{activePeriod?.label || periods.find(p => p.id === current?.periodId)?.label || "دوره نامشخص"}{selectedOccurrence ? ` · ${selectedOccurrence.label}` : ""}</div></div>
                <Badge color={current?.status === "approved" ? "teal" : current?.status === "revision_requested" ? "amber" : "gray"}>{indicatorStatusLabel(current?.status)}</Badge></div>
            <div className="cvi-indicator-people">
                <div><span>مالک</span><b>{users.find(u => u.id === selected.ownerId)?.name || "—"}</b></div>
                <div><span>ثبت‌کننده</span><b>{users.find(u => u.id === selected.registrarId)?.name || "—"}</b></div>
                <div><span>تأییدکننده</span><b>{users.find(u => u.id === selected.approverId)?.name || "—"}</b></div>
            </div>
            <div className="cvi-indicator-target"><span>هدف دوره</span><b>{current?.target ?? "هنوز تعیین نشده"} {selected.unit}</b></div>
            {selectedOccurrence && <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginTop: 12, fontSize: 10.5 }}>
                <div>شروع ثبت<br /><b>{selectedOccurrence.openDate}</b></div><div>مهلت ثبت<br /><b>{selectedOccurrence.submitDeadline}</b></div>
                <div>مهلت تأیید<br /><b>{selectedOccurrence.approvalDeadline}</b></div><div>قفل خودکار<br /><b>{selectedOccurrence.lockDate}</b></div>
            </div>}
            {!registrationOpen && <div style={{ marginTop: 10, padding: 8, background: C.cyanBg, borderRadius: 7, fontSize: 11 }}>پنجره ثبت هنوز باز نشده است.</div>}
            {!beforeLock && <div style={{ marginTop: 10, padding: 8, background: C.redLt, color: C.red, borderRadius: 7, fontSize: 11 }}>این نوبت به‌صورت خودکار قفل شده است.</div>}
            {current?.reviewNote && <div style={{ marginTop: 10, padding: 9, background: C.amberLt, color: C.amber, borderRadius: 8, fontSize: 11 }}>درخواست اصلاح: {current.reviewNote}</div>}
        </div></Card>
        <Card style={{ marginBottom: 12 }}><div className="cvi-indicator-form">
            <label htmlFor="cvi-indicator-actual" style={{ fontSize: 11, color: C.muted }}>مقدار واقعی *</label>
            <input id="cvi-indicator-actual" type="text" inputMode="decimal" dir="ltr" disabled={!canEdit} value={language === "fa" ? localizePersianDisplay(form.actual) : form.actual} onChange={e => setForm(f => ({ ...f, actual: e.target.value.replace(/[۰-۹٠-٩]/g, digit => String("۰۱۲۳۴۵۶۷۸۹".includes(digit) ? "۰۱۲۳۴۵۶۷۸۹".indexOf(digit) : "٠١٢٣٤٥٦٧٨٩".indexOf(digit))).replace("٫", ".") }))}
                style={{ width: "100%", boxSizing: "border-box", padding: 10, border: `1px solid ${C.borderMd}`, borderRadius: 8, margin: "5px 0 10px" }} />
            <label htmlFor="cvi-indicator-note" style={{ fontSize: 11, color: C.muted }}>توضیح یا منبع داده</label>
            <textarea id="cvi-indicator-note" disabled={!canEdit} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2}
                style={{ width: "100%", boxSizing: "border-box", padding: 10, border: `1px solid ${C.borderMd}`, borderRadius: 8, margin: "5px 0 10px", fontFamily: "inherit" }} />
            {needsDeviation && <div style={{ background: C.redLt, padding: 10, borderRadius: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: C.red, marginBottom: 7 }}>تحقق کمتر از ۸۰٪ است؛ علت انحراف و اقدام اصلاحی الزامی است.</div>
                <textarea disabled={!canEdit} placeholder="علت انحراف" value={form.deviationReason} onChange={e => setForm(f => ({ ...f, deviationReason: e.target.value }))} rows={2} style={{ width: "100%", boxSizing: "border-box", marginBottom: 6 }} />
                <textarea disabled={!canEdit} placeholder="اقدام اصلاحی" value={form.correctiveAction} onChange={e => setForm(f => ({ ...f, correctiveAction: e.target.value }))} rows={2} style={{ width: "100%", boxSizing: "border-box" }} />
            </div>}
            {canEdit && <label style={{ display: "inline-block", padding: "8px 12px", border: `1px dashed ${C.borderMd}`, borderRadius: 8, cursor: "pointer", fontSize: 11 }}>
                📎 {form.doc || "بارگذاری مستند"}<input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => { const file = e.target.files?.[0]; if (!file || file.size > 5 * 1024 * 1024) return; const reader = new FileReader(); reader.onload = () => setForm(f => ({ ...f, doc: file.name, docDataUrl: String(reader.result || ""), _file: file })); reader.readAsDataURL(file); }} />
            </label>}
            <ProtectedAttachmentList module="indicator" entityId={current?.id}
                attachments={current?.attachments || []} canDelete={canEdit} />
            {selected.requiresEvidence && <span style={{ fontSize: 10, color: C.hint, marginRight: 8 }}>مستند برای ارسال الزامی است.</span>}
            {canEdit && <div style={{ display: "flex", gap: 8, marginTop: 14 }}><button onClick={() => save("draft")} disabled={form.actual === ""} style={{ flex: 1, padding: 10 }}>ذخیره پیش‌نویس</button>
                <button onClick={() => save("submitted")} disabled={form.actual === "" || (selected.requiresEvidence && !form.doc) || (needsDeviation && (!form.deviationReason.trim() || !form.correctiveAction.trim()))}
                    style={{ flex: 2, padding: 10, background: C.navy, color: "#fff", border: "none", borderRadius: 8 }}>ارسال برای تأیید</button></div>}
        </div></Card><IndicatorAudit data={current} />
    </div>;
    return <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
        <div className="cvi-workflow-heading-block"><div className="cvi-workflow-title">شاخص‌های مسئولیت من</div><div className="cvi-workflow-subtitle">مواردی که در آن مالک، ثبت‌کننده یا تأییدکننده هستید.</div></div>
        {indicatorLoading && <div style={{ padding: 10, color: C.muted, fontSize: 11 }}>در حال دریافت شاخص‌ها...</div>}
        {indicatorError && <div style={{ padding: 10, color: C.red, fontSize: 11 }}>{indicatorError}</div>}
        {tab === "action" && rows.some(r => { const days = r.schedule ? daysBetweenJalali(today, r.schedule.submitDeadline) : null; return days != null && days <= r.schedule.reminderDays; }) && <div style={{ background: C.amberLt, color: C.amber, borderRadius: 9, padding: "9px 12px", fontSize: 11, marginBottom: 12 }}>
            🔔 {rows.filter(r => { const days = r.schedule ? daysBetweenJalali(today, r.schedule.submitDeadline) : null; return days != null && days <= r.schedule.reminderDays; }).length} نوبت ثبت به مهلت نزدیک شده یا از مهلت گذشته است.
        </div>}
        <ProfessionalTabs items={tabItems} value={tab} onChange={setTab} ariaLabel="وضعیت شاخص‌های من" />
        {!activePeriod && tab !== "history" ? <div style={{ padding: 24, textAlign: "center", color: C.muted }}>دوره فعالی وجود ندارد.</div> : rows.length === 0 ? <div style={{ padding: 32, textAlign: "center", color: C.muted }}>در این بخش شاخصی برای شما وجود ندارد.</div> : rows.map(row => { const d = row.def, data = getData(d.id, row.schedule?.occurrenceId); const days = row.schedule ? daysBetweenJalali(today, row.schedule.submitDeadline) : null; return <Card key={`${d.id}_${row.schedule?.occurrenceId || "history"}`} style={{ marginBottom: 8, cursor: "pointer" }}><div onClick={() => open(row)} style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontSize: 13, fontWeight: 500 }}>{d.label}{row.schedule ? ` — ${row.schedule.label}` : ""}</div><div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>نقش شما: {d.registrarId === user.id ? "ثبت‌کننده" : d.ownerId === user.id ? "مالک" : "تأییدکننده"}{row.schedule ? ` · مهلت ${row.schedule.submitDeadline}` : ""}</div>{tab === "action" && days != null && <div style={{ fontSize: 10, marginTop: 3, color: days < 0 ? C.red : days <= row.schedule.reminderDays ? C.amber : C.hint }}>{days < 0 ? `${Math.abs(days)} روز تأخیر` : days === 0 ? "امروز آخرین مهلت است" : `${days} روز تا مهلت ثبت`}</div>}</div><Badge color={data?.status === "revision_requested" ? "amber" : ["approved", "locked"].includes(data?.status) ? "teal" : "gray"}>{indicatorStatusLabel(data?.status)}</Badge></div></Card>; })}
    </div>;
}

function IndicatorReviewPanel({ evaluator }) {
    const { indicatorDefs, indicatorData, activePeriod, users, reviewIndicatorValueApi, indicatorError } = useData();
    const [selectedId, setSelectedId] = useState(null); const [note, setNote] = useState("");
    const pending = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" && indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === evaluator.id);
    const selected = indicatorData.find(d => d.id === selectedId); const def = selected && indicatorDefs.find(i => i.id === selected.indicatorId);
    const decide = async decision => {
        if (!selected || !def || def.approverId !== evaluator.id || def.registrarId === evaluator.id) return;
        if (decision === "revision_requested" && !note.trim()) return;
        try {
            await reviewIndicatorValueApi(selected.id, decision, note);
            setSelectedId(null);
            setNote("");
        } catch (error) {
            console.error("Indicator review failed:", error);
            uxNotify(error.message || "ثبت تصمیم شاخص انجام نشد");
        }
    };
    if (selected && def) return <div><button onClick={() => setSelectedId(null)} style={{ border: "none", background: "none", color: C.cyan, marginBottom: 10 }}>بازگشت</button><Card style={{ marginBottom: 12 }}><div style={{ padding: 16 }}><div style={{ fontSize: 15, fontWeight: 500 }}>{def.label}</div><div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>ثبت‌کننده: {users.find(u => u.id === def.registrarId)?.name} · مالک: {users.find(u => u.id === def.ownerId)?.name}</div><div style={{ fontSize: 20, fontWeight: 600, margin: "16px 0" }}>{selected.actual} {def.unit}</div><div style={{ fontSize: 11 }}>هدف: {selected.target ?? "—"} · مهلت تأیید: {selected.scheduleSnapshot?.approvalDeadline || "—"}</div>{selected.doc && <div style={{ marginTop: 10 }}>📎 {selected.docDataUrl ? <a href={selected.docDataUrl} download={selected.doc}>{selected.doc}</a> : selected.doc}</div>}{selected.deviationReason && <div style={{ background: C.redLt, padding: 10, borderRadius: 8, marginTop: 10, fontSize: 11 }}>علت انحراف: {selected.deviationReason}<br />اقدام اصلاحی: {selected.correctiveAction}</div>}</div></Card><textarea value={note} onChange={e => setNote(e.target.value)} placeholder="یادداشت بررسی (برای اصلاح الزامی)" rows={3} style={{ width: "100%", boxSizing: "border-box", marginBottom: 8 }} /><div style={{ display: "flex", gap: 8 }}><button onClick={() => decide("approved")} style={{ flex: 1, padding: 10, background: C.teal, color: "#fff", border: "none", borderRadius: 8 }}>تأیید</button><button disabled={!note.trim()} onClick={() => decide("revision_requested")} style={{ flex: 1, padding: 10, borderRadius: 8 }}>درخواست اصلاح</button></div><div style={{ marginTop: 12 }}><IndicatorAudit data={selected} /></div></div>;
    return <div>{indicatorError && <div style={{ padding: 10, color: C.red, fontSize: 11 }}>{indicatorError}</div>}{pending.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: C.muted }}>هیچ شاخصی در انتظار تأیید شما نیست.</div> : pending.map(d => { const i = indicatorDefs.find(x => x.id === d.indicatorId); return <Card key={d.id} style={{ marginBottom: 8, cursor: "pointer" }}><div onClick={() => setSelectedId(d.id)} style={{ padding: 14, display: "flex", justifyContent: "space-between" }}><div><div style={{ fontSize: 13, fontWeight: 500 }}>{i?.label}</div><div style={{ fontSize: 10.5, color: C.muted }}>{users.find(u => u.id === i?.registrarId)?.name} · {d.submittedAt}</div></div><Badge color="amber">در انتظار تأیید</Badge></div></Card>; })}</div>;
}

// ── INBOX ──────────────────────────────────────────────────────────────────────
function Inbox({ activities, onUpdate, isMobile, members, impactLevels, evaluator, users }) {
    const { indicatorDefs, indicatorData, activePeriod, reviewActivityApi } = useData();
    const [selected, setSelected] = useState(null);
    const [impactLevel, setImpactLevel] = useState("");
    const [note, setNote] = useState("");
    const [tab, setTab] = useState("pending");

    const pending = activities.filter(a => isReviewableActivity(a) && canReviewActivity(evaluator, a, users));
    const indicatorPending = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" &&
        indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === evaluator.id).length;
    const reviewedActivities = activities.filter(a => !isReviewableActivity(a) && canReviewActivity(evaluator, a, users) && ["approved", "rejected", "revision_requested"].includes(a.status));
    const reviewedIndicators = indicatorData.filter(d => indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === evaluator.id && ["approved", "revision_requested", "rejected"].includes(d.status));
    const inboxTabs = [{ id: "pending", label: "در انتظار بررسی", icon: "inbox", count: pending.length + indicatorPending },
        { id: "history", label: "تاریخچه", icon: "history", count: reviewedActivities.length + reviewedIndicators.length }];
    const typeBadge = (label, icon) => <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 8px",borderRadius:999,background:C.bg,fontSize:10.5,color:C.navy}}><UiIcon name={icon} size={13}/>{label}</span>;

    if (tab === "history") return <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>صندوق بررسی</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>همه بررسی‌هایی که قبلاً انجام داده‌اید.</div>
        <ProfessionalTabs items={inboxTabs} value={tab} onChange={setTab} ariaLabel="وضعیت صندوق بررسی" />
        {[...reviewedActivities.map(a=>({id:'a-'+a.id,type:'فعالیت غیرمستمر',icon:'activity',title:a.title,person:users.find(u=>u.id===a.userId)?.name||'—',date:a.reviewedAt||a.updatedAt||a.date||'—',result:a.status})),...reviewedIndicators.map(d=>{const i=indicatorDefs.find(x=>x.id===d.indicatorId);return {id:'i-'+d.id,type:'شاخص راهبردی',icon:'target',title:i?.label||'شاخص راهبردی',person:users.find(u=>u.id===i?.registrarId)?.name||'—',date:d.approvedAt||d.updatedAt||d.submittedAt||'—',result:d.status};})].length===0?<div style={{padding:40,textAlign:'center',color:C.muted}}>هنوز سابقه‌ای از بررسی‌های شما ثبت نشده است.</div>:[...reviewedActivities.map(a=>({id:'a-'+a.id,type:'فعالیت غیرمستمر',icon:'activity',title:a.title,person:users.find(u=>u.id===a.userId)?.name||'—',date:a.reviewedAt||a.updatedAt||a.date||'—',result:a.status})),...reviewedIndicators.map(d=>{const i=indicatorDefs.find(x=>x.id===d.indicatorId);return {id:'i-'+d.id,type:'شاخص راهبردی',icon:'target',title:i?.label||'شاخص راهبردی',person:users.find(u=>u.id===i?.registrarId)?.name||'—',date:d.approvedAt||d.updatedAt||d.submittedAt||'—',result:d.status};})].map(item=><Card key={item.id} style={{marginBottom:8}}><div style={{padding:13,display:'grid',gridTemplateColumns:'minmax(180px,1.5fr) 150px 140px 130px',gap:10,alignItems:'center'}}><div><div style={{fontSize:12.5,fontWeight:600}}>{item.title}</div><div style={{fontSize:10.5,color:C.muted,marginTop:3}}>{item.person}</div></div><div>{typeBadge(item.type,item.icon)}</div><div style={{fontSize:10.5,color:C.muted}}>{item.date}</div><Badge color={item.result==='approved'?'teal':item.result==='rejected'?'red':'amber'}>{item.result==='approved'?'تأیید شده':item.result==='rejected'?'رد شده':'درخواست اصلاح'}</Badge></div></Card>)}
    </div>;

    const decide = async (actId, decision) => {
        if (decision === "approve" && !impactLevel) return;
        if (["revision", "reject"].includes(decision) && !note.trim()) return;

        try {
            await reviewActivityApi(actId, decision, impactLevel, note);
            setSelected(null);
            setImpactLevel("");
            setNote("");
        } catch (error) {
            console.error("Activity review failed:", error);
            uxNotify(error.message || "ثبت تصمیم انجام نشد");
        }
    };

    const member = selected ? members.find(u => u.id === selected.userId) : null;
    const inp = {
        width: "100%", padding: "9px 12px", border: `1.5px solid ${C.borderMd}`,
        borderRadius: 8, fontSize: 13, outline: "none", background: "#fff",
        fontFamily: "inherit", direction: "inherit", boxSizing: "border-box"
    };

    if (selected) return (
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <SubBar title="بررسی فعالیت"
                onBack={() => { setSelected(null); setImpactLevel(""); setNote(""); }} />
            <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>

                <div style={{
                    background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
                    overflow: "hidden", marginBottom: 12
                }}>
                    <div style={{ background: C.navy, padding: "12px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                                <div style={{ color: "#fff", fontSize: 14, fontWeight: 500, marginBottom: 3 }}>
                                    {selected.title}
                                </div>
                                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>
                                    {selected.sub}
                                </div>
                            </div>
                            {selected.doc && (
                                <span style={{
                                    background: "rgba(0,200,230,0.15)", color: C.cyan,
                                    fontSize: 10, padding: "3px 8px", borderRadius: 20
                                }}>📎 {selected.doc}</span>
                            )}
                        </div>
                    </div>
                    <div style={{ padding: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: "50%", background: C.cyanBg,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontWeight: 600, color: C.navy, fontSize: 13
                            }}>{member?.name[0]}</div>
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 500 }}>{member?.name}</div>
                                <div style={{ fontSize: 11, color: C.hint }}>{selected.date}</div>
                            </div>
                        </div>
                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                            <p style={{ fontSize: 13, color: "#444", lineHeight: 1.7 }}>{selected.desc}</p>
                        </div>
                    </div>
                </div>

                <ActivityTimeline activity={selected} />

                <div style={{
                    background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
                    overflow: "hidden"
                }}>
                    <div style={{ background: C.purple, padding: "10px 16px" }}>
                        <div style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>تصمیم مدیر</div>
                    </div>
                    <div style={{ padding: 14 }}>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{
                                display: "block", fontSize: 11, fontWeight: 600, color: "#666",
                                letterSpacing: "0.5px", marginBottom: 6
                            }}>سطح تأثیر *</label>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                {impactLevels.map(l => (
                                    <button key={l.id} onClick={() => setImpactLevel(l.id)}
                                        style={{
                                            padding: "9px 12px",
                                            border: `1.5px solid ${impactLevel === l.id ? C.navy : C.borderMd}`,
                                            borderRadius: 8, background: impactLevel === l.id ? "#F0F4FF" : "#fff",
                                            cursor: "pointer", textAlign: "right", transition: "all 0.15s"
                                        }}>
                                        <div style={{
                                            fontSize: 12, fontWeight: 500,
                                            color: impactLevel === l.id ? C.navy : "#333"
                                        }}>{l.label}</div>
                                        <div style={{ fontSize: 10, color: C.hint, marginTop: 1 }}>امتیاز: {l.score}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{
                                display: "block", fontSize: 11, fontWeight: 600, color: "#666",
                                letterSpacing: "0.5px", marginBottom: 6
                            }}>یادداشت (برای درخواست اصلاح الزامی)</label>
                            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                                placeholder="بازخورد یا توضیح برای کارشناس..."
                                style={{ ...inp, resize: "none", lineHeight: 1.6 }} />
                        </div>
                        {impactLevel && (
                            <div style={{
                                background: C.cyanBg, borderRadius: 8, padding: "8px 12px",
                                fontSize: 12, color: "#007A8C", marginBottom: 14
                            }}>
                                امتیاز این فعالیت:{" "}
                                <strong>{impactLevels.find(l => l.id === impactLevel)?.score}</strong> امتیاز
                            </div>
                        )}
                        <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={() => decide(selected.id, "approve")} disabled={!impactLevel}
                                style={{
                                    flex: 2, padding: "10px 0",
                                    background: impactLevel ? C.teal : "#E4E4EC",
                                    color: impactLevel ? "#fff" : C.hint, border: "none", borderRadius: 8,
                                    fontSize: 13, fontWeight: 500, cursor: impactLevel ? "pointer" : "not-allowed"
                                }}>
                                ✓ تأیید
                            </button>
                            <button onClick={() => decide(selected.id, "revision")} disabled={!note.trim()}
                                style={{
                                    flex: 2, padding: "10px 0", background: note.trim() ? C.amberLt : "#E4E4EC",
                                    color: note.trim() ? C.amber : C.hint,
                                    border: `1px solid ${note.trim() ? C.amber : C.border}`, borderRadius: 8,
                                    fontSize: 13, fontWeight: 500, cursor: note.trim() ? "pointer" : "not-allowed"
                                }}>اصلاح</button>
                            <button onClick={() => decide(selected.id, "reject")} disabled={!note.trim()}
                                style={{
                                    flex: 1, padding: "10px 0", background: note.trim() ? C.redLt : "#E4E4EC",
                                    color: note.trim() ? C.red : C.hint,
                                    border: `1px solid ${note.trim() ? C.red : C.border}`, borderRadius: 8,
                                    fontSize: 13, fontWeight: 500, cursor: note.trim() ? "pointer" : "not-allowed"
                                }}>رد</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>صندوق بررسی</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>مواردی که برای تصمیم‌گیری شما ارسال شده‌اند.</div>
            <ProfessionalTabs items={inboxTabs} value={tab} onChange={setTab} ariaLabel="وضعیت صندوق بررسی" />
            {(pending.length + indicatorPending) > 0 && <Card style={{marginBottom:10}}><div style={{padding:0,overflowX:'auto'}}><div style={{display:'grid',gridTemplateColumns:'minmax(190px,1.5fr) 150px 130px 130px 90px',gap:10,padding:'10px 13px',fontSize:10,color:C.muted,borderBottom:'1px solid '+C.border}}><span>مورد</span><span>نوع</span><span>دوره</span><span>تاریخ ارسال</span><span>اقدام</span></div>{pending.map(a=><button key={'qa-'+a.id} onClick={()=>setSelected(a)} style={{width:'100%',display:'grid',gridTemplateColumns:'minmax(190px,1.5fr) 150px 130px 130px 90px',gap:10,alignItems:'center',padding:'11px 13px',border:'none',borderBottom:'1px solid '+C.border,background:'#fff',fontFamily:'inherit',textAlign:'right',cursor:'pointer'}}><span><strong>{a.title}</strong><small style={{display:'block',color:C.muted,marginTop:3}}>{users.find(u=>u.id===a.userId)?.name||'—'}</small></span><span>{typeBadge('فعالیت غیرمستمر','activity')}</span><span style={{fontSize:10.5}}>{activePeriod?.label||'—'}</span><span style={{fontSize:10.5,color:C.muted}}>{formatIranSubmissionTimestamp(a.submittedAt || a.date)}</span><span style={{color:C.navy,fontSize:11}}>بررسی</span></button>)}{indicatorData.filter(d=>d.periodId===activePeriod?.id&&d.status==='submitted'&&indicatorDefs.find(i=>i.id===d.indicatorId)?.approverId===evaluator.id).map(d=>{const i=indicatorDefs.find(x=>x.id===d.indicatorId);return <div key={'qi-'+d.id} style={{display:'grid',gridTemplateColumns:'minmax(190px,1.5fr) 150px 130px 130px 90px',gap:10,alignItems:'center',padding:'11px 13px',borderBottom:'1px solid '+C.border}}><span><strong>{i?.label||'شاخص راهبردی'}</strong><small style={{display:'block',color:C.muted,marginTop:3}}>{users.find(u=>u.id===i?.registrarId)?.name||'—'}</small></span><span>{typeBadge('شاخص راهبردی','target')}</span><span style={{fontSize:10.5}}>{activePeriod?.label||'—'}</span><span style={{fontSize:10.5,color:C.muted}}>{formatIranSubmissionTimestamp(d.submittedAt)}</span><span><IndicatorReviewPanel evaluator={evaluator} focusId={d.id}/></span></div>})}</div></Card>}
            {(pending.length + indicatorPending) > 0 ? null : pending.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 0", color: C.muted }}>
                    <div style={{ marginBottom: 12, color: C.teal }}><UiIcon name="check" size={34} /></div>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>همه بررسی شدند</div>
                    <div style={{ fontSize: 12 }}>هیچ فعالیتی در انتظار بررسی نیست</div>
                </div>
            ) : pending.map(act => {
                const m = members.find(u => u.id === act.userId);
                return (
                    <div key={act.id}
                        onClick={() => { setSelected(act); setImpactLevel(""); setNote(""); }}
                        style={{
                            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
                            marginBottom: 8, cursor: "pointer", overflow: "hidden"
                        }}>
                        <div style={{ padding: 14 }}>
                            <div style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "flex-start", marginBottom: 8
                            }}>
                                <div style={{ fontSize: 14, fontWeight: 500, flex: 1, marginLeft: 8 }}>
                                    {act.title}
                                </div>
                                <Badge color="amber" small>
                                    {activityTimeline(act).some(e => e.type === "resubmitted") ? "ارسال مجدد" : "جدید"}
                                </Badge>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <div style={{
                                    width: 24, height: 24, borderRadius: "50%", background: C.cyanBg,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 11, fontWeight: 600, color: C.navy
                                }}>{m?.name[0]}</div>
                                <span style={{ fontSize: 12, color: C.muted }}>{m?.name}</span>
                            </div>
                            <p style={{
                                fontSize: 12, color: "#555", lineHeight: 1.6,
                                display: "-webkit-box", WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical", overflow: "hidden"
                            }}>{act.desc}</p>
                            <div style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", marginTop: 8
                            }}>
                                <span style={{ fontSize: 11, color: C.hint }}>{act.date}</span>
                                {act.doc && <span style={{ fontSize: 10, color: C.cyan, display: "inline-flex", alignItems: "center", gap: 4 }}><UiIcon name="attachment" size={12} /> {act.doc}</span>}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function useScopedCvi(periodId) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setRows(null);
        if (!periodId) return () => { cancelled = true; };
        const token = sessionStorage.getItem("cvi_token");
        fetch(`${API_BASE_URL}/api/cvi/periods/${encodeURIComponent(periodId)}/scope`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            if (!res.ok) throw new Error("Scoped CVI unavailable");
            return res.json();
        }).then(data => {
            if (!cancelled) setRows(new Map((data.results || []).map(item => [item.user.id, item.scores])));
        }).catch(() => { if (!cancelled) setRows(new Map()); });
        return () => { cancelled = true; };
    }, [periodId]);
    return rows;
}
function validCviScore(scores, key = "cvi") {
    const raw = scores?.[key];
    return raw == null || !Number.isFinite(Number(raw)) ? null : Number(raw);
}
function scopedAverage(members, rows) {
    if (!rows) return null;
    const values = members.map(person => validCviScore(rows.get(person.id))).filter(value => value != null);
    return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

// ── TEAM DASHBOARD ─────────────────────────────────────────────────────────────
function TeamDashboard({ user, activities, activityGroups, positions, indicatorDefs,
    indicatorData, activePeriod, members, directReports = members, teams, onNavigate }) {
    const { orgSettings, orgUnits } = useData();
    const scopedCvi = useScopedCvi(activePeriod?.id);
    const [showTeamActions, setShowTeamActions] = useState(false);
    const [supportFilter, setSupportFilter] = useState("active");
    const teamInds = withPeriodData(indicatorDefs.filter(i => user.orgUnitId ? (i.level === "org_unit" && i.orgUnitId === user.orgUnitId) : (i.level === "team" && i.teamId === user.teamId)), indicatorData, activePeriod?.id);
    const team = teams.find(t => t.id === user.teamId);
    const orgUnit = orgUnits.find(unit => unit.id === user.orgUnitId);
    const unitLabel = orgUnit?.label || team?.label || "واحد";
    const avgCVI = scopedAverage(members, scopedCvi);
    const memberIds = new Set(members.map(m => m.id));
    const pendingCount = activities.filter(a => memberIds.has(a.userId) && isReviewableActivity(a)).length;
    const supportItems = activities.filter(a => memberIds.has(a.userId) && a.periodId === activePeriod?.id && ["open","in_progress","resolved"].includes(String(a.blockerStatus || "").toLowerCase()));
    const supportCounts = {open:supportItems.filter(a=>a.blockerStatus==="open").length,in_progress:supportItems.filter(a=>a.blockerStatus==="in_progress").length,resolved:supportItems.filter(a=>a.blockerStatus==="resolved").length};
    const activeSupportCount = supportCounts.open + supportCounts.in_progress;
    const attentionCount = pendingCount + activeSupportCount;
    const visibleSupportItems = supportItems.filter(a=>supportFilter==="all" || (supportFilter==="active" ? a.blockerStatus!=="resolved" : a.blockerStatus===supportFilter));
    const measuredIndicators = teamInds.filter(i => i.target != null && Number(i.target) > 0 && i.actual != null);
    const avgGoal = measuredIndicators.length ? measuredIndicators.reduce(
        (sum, i) => sum + Math.max(0, Math.min(100, Number(i.actual) / Number(i.target) * 100)), 0
    ) / measuredIndicators.length : null;

    return (
        <div className="cvi-dashboard-page cvi-team-dashboard-refined">
            <DashboardPageIntro title={`داشبورد ${unitLabel === "واحد" ? "تیم" : unitLabel.startsWith("تیم") ? unitLabel : `تیم ${unitLabel}`}`}
                period={activePeriod?.label || "دوره‌ای فعال نیست"}
                periodStart={activePeriod?.start} periodEnd={activePeriod?.end}
                periodInline subtitleInline
                attentionCount={attentionCount}
                onAttentionClick={() => setShowTeamActions(value => !value)} />

            <div className="cvi-dashboard-kpi-grid" style={{gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,220px),1fr))"}}>
                <DashboardMetricCard label="میانگین CVI اعضا" value={avgCVI == null ? "—" : `${avgCVI.toFixed(1)}٪`}
                    helper="" tone="cyan" icon={<UiIcon name="performance" size={16} />} />
                <DashboardMetricCard label="تحقق شاخص‌های تیم" value={avgGoal == null ? "—" : `${avgGoal.toFixed(0)}٪`}
                    helper={`${measuredIndicators.length} شاخص دارای عملکرد از ${teamInds.length}`}
                    tone={avgGoal == null ? "navy" : avgGoal >= 80 ? "teal" : avgGoal >= 60 ? "amber" : "red"}
                    icon={<UiIcon name="target" size={16} />} />
                <DashboardMetricCard label="اعضای زیرمجموعه" value={members.length}
                    helper={`${directReports.length} نفر مستقیم`} tone="navy"
                    icon={<UiIcon name="members" size={16} />} />
            </div>

            {attentionCount > 0 && showTeamActions && <section className="cvi-premium-panel cvi-manager-attention-panel" style={{padding:"14px 18px"}}>
                <div className="cvi-premium-panel-head" style={{marginBottom:10}}>
                    <div><h3>موارد نیازمند توجه</h3><small style={{color:C.muted}}>اقدامات باز مرتبط با اعضای زیرمجموعه</small></div>
                    <b className="cvi-premium-attention-badge">{attentionCount}</b>
                </div>
                <div className="cvi-premium-action-list">
                    {pendingCount > 0 && <button onClick={()=>onNavigate?.("inbox")}>
                        <span className="cvi-premium-action-icon">!</span>
                        <span><strong>فعالیت‌های در انتظار بررسی</strong><small>{pendingCount} مورد نیازمند تصمیم</small></span><em>←</em>
                    </button>}
                    {activeSupportCount > 0 && <button onClick={()=>document.getElementById("cvi-team-support-panel")?.scrollIntoView({behavior:"smooth",block:"start"})}>
                        <span className="cvi-premium-action-icon support">+</span>
                        <span><strong>موانع و نیازهای حمایتی</strong><small>{activeSupportCount} مورد باز برای پیگیری</small></span><em>←</em>
                    </button>}
                </div>
            </section>}

            <div className="cvi-team-dashboard-two-column">
            <section id="cvi-team-support-panel" className="cvi-premium-panel" style={{padding:18}}>
                <div className="cvi-premium-panel-head"><div><h3>موانع و درخواست‌های حمایت</h3><small>جمع‌بندی موارد ثبت‌شده توسط اعضای تیم در دوره جاری</small></div></div>
                <div className="cvi-team-support-summary">
                    {[
                        ["open","باز",supportCounts.open],["in_progress","در حال بررسی",supportCounts.in_progress],["resolved","بسته‌شده",supportCounts.resolved]
                    ].map(([key,label,count])=><button type="button" className={`cvi-team-support-stat ${key}${supportFilter===key?" is-selected":""}`} key={key} onClick={()=>setSupportFilter(key)}><span>{label}</span><strong>{count}</strong></button>)}
                </div>
                <div className="cvi-team-support-toolbar"><strong>موارد ثبت‌شده</strong><select value={supportFilter} onChange={e=>setSupportFilter(e.target.value)} aria-label="فیلتر وضعیت">
                    <option value="active">باز و در حال بررسی</option><option value="all">همه موارد</option><option value="open">باز</option><option value="in_progress">در حال بررسی</option><option value="resolved">بسته‌شده</option>
                </select></div>
                <div className="cvi-team-support-list">{visibleSupportItems.length===0 ? <div className="cvi-dashboard-empty">موردی با این وضعیت ثبت نشده است.</div>
                    : visibleSupportItems.map(item=><div key={item.id} className="cvi-team-support-row">
                        <div className="cvi-team-support-row-title"><strong>{item.title||"فعالیت"}</strong>
                            <span className={`cvi-team-support-status ${item.blockerStatus}`}>{item.blockerStatus==="open"?"باز":item.blockerStatus==="in_progress"?"در حال بررسی":"بسته‌شده"}</span></div>
                        <small>{members.find(m=>m.id===item.userId)?.name||"عضو تیم"}{item.date?` · ${item.date}`:""}</small>
                        {item.blockerText && <p><b>مانع:</b> {item.blockerText}</p>}
                        {item.supportNeed && <p><b>حمایت موردنیاز:</b> {item.supportNeed}</p>}
                        {!item.blockerText&&!item.supportNeed&&<p>نوع مانع: {item.blockerCategory||"مشخص نشده"}</p>}
                    </div>)}
                </div>
            </section>
            <section className="cvi-team-indicators-panel">
                <div className="cvi-premium-panel-head"><div><h3>تحقق شاخص‌های تیم</h3><small>عملکرد واقعی شاخص‌ها در مقایسه با اهداف دوره</small></div></div>
                    <div className="cvi-dashboard-indicator-list">
                        {teamInds.length === 0 ? <div className="cvi-dashboard-empty">شاخصی برای این تیم تعریف نشده</div>
                            : teamInds.map(tind => {
                                const score = tind.target ? Math.min(100, (tind.actual / tind.target) * 100) : 0;
                                const color = score >= 85 ? C.teal : score >= 65 ? C.amber : C.red;
                                return <div key={tind.id} className="cvi-dashboard-indicator-row">
                                    <div className="cvi-dashboard-indicator-meta"><strong>{tind.label}</strong><span style={{ color }}>{score.toFixed(0)}٪</span></div>
                                    <MiniBar value={score} color={color} height={7} />
                                    <small>واقعی: {tind.actual} {tind.unit} · هدف: {tind.target} {tind.unit}</small>
                                </div>;
                            })}
                    </div>
            </section>

            </div>
            <DashboardSection title="اعضای تیم">
                <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:9}}>
                    <button type="button" className="cvi-button-secondary" onClick={()=>onNavigate?.("employee-evaluation")}>ارزیابی کارکنان ←</button>
                    <button type="button" className="cvi-button-secondary" onClick={()=>onNavigate?.("team")}>جزئیات اعضا ←</button>
                </div>
                <Card>
                    {members.length === 0 ? <div className="cvi-dashboard-empty">هنوز عضوی به این مدیر متصل نشده</div>
                        : members.map((m, i) => {
                            const mcvi = validCviScore(scopedCvi?.get(m.id));
                            const mp = activities.filter(a => a.userId === m.id && isReviewableActivity(a)).length;
                            return <div key={m.id} className="cvi-dashboard-person-row" style={{ borderBottom: i < members.length - 1 ? `1px solid ${C.bg}` : "none" }}>
                                <UserAvatar user={m} size={38} />
                                <div className="cvi-dashboard-row-main"><strong>{m.name}</strong><small>{mp > 0 ? `${mp} در انتظار بررسی` : "همه بررسی شده"}</small></div>
                                <div className="cvi-dashboard-person-score"><span>CVI</span><strong>{mcvi == null ? "—" : mcvi.toFixed(1)}</strong></div>
                            </div>;
                        })}
                </Card>
            </DashboardSection>
        </div>
    );
}

// ── TEAM MEMBERS ───────────────────────────────────────────────────────────────
// نمای فقط‌خواندنی جزئیات سؤالات وظایف مستمر یک فرد — بانک درست را بر اساس نقش او انتخاب می‌کند
function PersonRoutineDetail({ user, routineCtx, activePeriod }) {
    let questions, answers;
    if (user.role === "manager") {
        questions = user.orgUnitId
            ? routineCtx.deptRoutineQuestions.filter(q => q.orgUnitId === user.orgUnitId)
            : routineCtx.deptRoutineQuestions.filter(q => q.deptId === user.deptId);
        answers = routineCtx.deptRoutineAnswers;
    } else if (user.role === "deputy") {
        questions = user.orgUnitId
            ? routineCtx.orgRoutineQuestions.filter(q => q.orgUnitId === user.orgUnitId || q.scope === "organization")
            : routineCtx.orgRoutineQuestions;
        answers = routineCtx.orgRoutineAnswers;
    } else if (user.role === "specialist") {
        questions = user.orgUnitId
            ? routineCtx.routineQuestions.filter(q => q.orgUnitId === user.orgUnitId)
            : routineCtx.routineQuestions.filter(q => q.teamId === user.teamId);
        answers = routineCtx.routineAnswers;
    } else {
        return null;
    }
    if (!questions.length) {
        return <div style={{ fontSize: 11, color: C.hint }}>سؤال وظایف مستمری برای این فرد تعریف نشده است.</div>;
    }
    const byMonth = { 1: [], 2: [], 3: [] };
    questions.forEach(q => byMonth[q.month]?.push(q));
    const answerFor = (qId) => answers.find(a => a.questionId === qId && a.userId === user.id && a.periodId === activePeriod?.id);

    return (
        <div>
            {[1, 2, 3].map(month => byMonth[month].length > 0 && (
                <div key={month} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 500, color: C.muted, marginBottom: 6 }}>
                        {month === 1 ? "ماه اول" : month === 2 ? "ماه دوم" : "ماه سوم"}
                    </div>
                    {byMonth[month].map(q => {
                        const ans = answerFor(q.id);
                        const lvl = ans ? routineCtx.routineLevels.find(l => l.id === ans.levelId) : null;
                        return (
                            <div key={q.id} style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 11.5
                            }}>
                                <span style={{ color: "#444" }}>{q.label}</span>
                                {lvl ? <Badge color="purple" small>{lvl.label}</Badge> : <span style={{ color: C.hint, flexShrink: 0 }}>—</span>}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

function TeamView({ activities, activityGroups, positions, indicatorDefs,
    indicatorData, activePeriod, members, title = "اعضای تیم",
    emptyMessage = "هنوز عضوی به این مدیر متصل نشده", enableOrgFilters = false, fixedDepartmentId = null }) {
    const { orgSettings, teams, departments, orgUnits } = useData();
    const routineCtx = useRoutineCtx();
    const scopedCvi = useScopedCvi(activePeriod?.id);
    const [selected, setSelected] = useState(null);
    const [memberCvi, setMemberCvi] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setMemberCvi(null);
        if (!selected?.id || !activePeriod?.id) return;
        const token = sessionStorage.getItem("cvi_token");
        fetch(`${API_BASE_URL}/api/cvi/periods/${encodeURIComponent(activePeriod.id)}/users/${encodeURIComponent(selected.id)}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(r => { if (!r.ok) throw new Error("CVI unavailable"); return r.json(); })
          .then(payload => { if (!cancelled) setMemberCvi(payload); })
          .catch(() => { if (!cancelled) setMemberCvi(null); });
        return () => { cancelled = true; };
    }, [selected?.id, activePeriod?.id]);
    const [departmentFilter, setDepartmentFilter] = useState(fixedDepartmentId || "all");
    const [teamFilter, setTeamFilter] = useState("all");
    const [orgUnitFilter, setOrgUnitFilter] = useState("all");
    const flexibleOrgActive = orgUnits.some(unit => unit.isActive !== false);
    const availableTeams = departmentFilter === "all"
        ? teams : teams.filter(t => t.deptId === departmentFilter);
    const visibleMembers = enableOrgFilters ? members.filter(m => {
        if (flexibleOrgActive) {
            if (orgUnitFilter === "all") return true;
            return orgUnitSubtreeIds(orgUnits, orgUnitFilter).includes(m.orgUnitId);
        }
        return (departmentFilter === "all" || m.deptId === departmentFilter ||
            teams.find(t => t.id === m.teamId)?.deptId === departmentFilter) &&
            (teamFilter === "all" || m.teamId === teamFilter);
    }) : members;

    if (selected) {
        const mActs = activities.filter(a => a.userId === selected.id);
        const nonRoutine = calcNonRoutineScore(activities, activityGroups, selected.id, activePeriod?.id);
        const routine = calcRoutineScoreForUser(selected, routineCtx, activePeriod?.id);
        const peerFeedback = calcEligiblePeerFeedbackScore(routineCtx, selected.id, activePeriod?.id);
        const selectedWeights = positions.find(p => p.id === selected.posId) || orgSettings;
        const ind = calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, selected, activePeriod?.id);
        const org = calcOrgScore(selected, positions, indicatorDefs, indicatorData, activePeriod?.id);
        const cvi = calcCVI(ind, org);
        return (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <SubBar title={selected.name} onBack={() => setSelected(null)} />
                <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                        <div><h2 style={{margin:0,fontSize:16}}>عملکرد {selected.name}</h2><small style={{color:C.muted}}>نمای دوره جاری · امتیازهای موقت</small></div>
                        {memberCvi?.assessmentCoverage?.overall != null && <small style={{color:C.muted,fontSize:11}}>پوشش ارزیابی: {memberCvi.assessmentCoverage.overall.toFixed(0)}٪</small>}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:10,marginBottom:14}}>
                        {[
                            ["CVI",memberCvi?.scores?.cvi,C.navy],
                            ["عملکرد فردی",memberCvi?.scores?.individual,C.cyan],
                            ["مشارکت جمعی",memberCvi?.scores?.organizational,C.purple]
                        ].map(([label,value,color])=><div key={label} style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:15}}>
                            <div style={{color:C.muted,fontSize:11}}>{label}</div>
                            <div style={{fontSize:22,fontWeight:600,color:color,margin:"8px 0"}}>{value==null?"—":`${Number(value).toFixed(1)}٪`} </div>
                            {value!=null&&<MiniBar value={value} color={color} height={5}/>}
                        </div>)}
                    </div>
                    {memberCvi?.scores && <details style={{background:"#f9fafc",border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 15px",marginBottom:12}}>
                        <summary style={{cursor:"pointer",fontSize:13,fontWeight:600,color:C.navy,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}><span>عملکرد فردی <span style={{fontSize:11,color:C.muted,fontWeight:400}}>· مشاهده جزئیات</span></span><span style={{fontSize:14}}>{memberCvi.scores.individual==null?"—":`${Number(memberCvi.scores.individual).toFixed(1)}٪`}</span></summary>
                        <div style={{fontSize:11,color:C.muted,margin:"9px 0"}}>میانگین وزنی مؤلفه‌های دارای امتیاز؛ مؤلفه‌های ثبت‌نشده موقتاً حذف می‌شوند.</div>
                        {(memberCvi.assessmentCoverage?.individual?.components||[]).map(p=><div key={p.key} style={{borderTop:`1px solid ${C.border}`,padding:"9px 0"}}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,gap:8}}><strong>{p.label}</strong><span><strong style={{fontWeight:600,color:C.navy}}>{p.score==null?"—":`${Number(p.score).toFixed(1)}٪`}</strong> <small style={{fontSize:10.5,fontWeight:400,color:C.muted,marginInlineStart:8}}>وزن {p.effectiveWeight == null ? "—" : `${p.effectiveWeight}٪`}</small></span></div>
                            {p.score!=null&&<div style={{fontSize:10.5,color:C.muted,marginTop:4}}>سهم در عملکرد فردی: {(Number(p.score)*p.effectiveWeight/100).toFixed(2)} امتیاز</div>}
                            {p.note&&<small style={{color:C.muted}}>{p.note}</small>}
                        </div>)}
                        <div style={{paddingTop:9,fontWeight:600,fontSize:12}}>جمع عملکرد فردی: {memberCvi.scores.individual==null?"—":`${Number(memberCvi.scores.individual).toFixed(1)}٪`}</div>
                        <details style={{marginTop:12,borderTop:`1px solid ${C.border}`,paddingTop:11}}>
                            <summary style={{cursor:"pointer",fontSize:12,color:C.navy}}>ریز فعالیت‌های غیرمستمر</summary>
                            {(memberCvi.breakdown?.nonRoutine?.groups||[]).map(g=><div key={g.groupId} style={{fontSize:11,padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>{g.name}: {g.score} از {g.maxScore} · {g.activityCount} فعالیت تأییدشده</div>)}
                            {!memberCvi.breakdown?.nonRoutine?.groups?.length&&<small style={{color:C.muted}}>گروهی ثبت نشده است.</small>}
                        </details>
                        <details style={{marginTop:10,borderTop:`1px solid ${C.border}`,paddingTop:11}}>
                            <summary style={{cursor:"pointer",fontSize:12,color:C.navy}}>ریز ارزیابی وظایف مستمر</summary>
                            {(memberCvi.breakdown?.routine?.questions||[]).map(q=><div key={q.questionId} style={{fontSize:11,padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>{q.text}: {q.normalizedScore==null?"ارزیابی نشده":`${Number(q.normalizedScore).toFixed(1)}٪`} · وزن {q.weight}٪</div>)}
                            {!memberCvi.breakdown?.routine?.questions?.length&&<small style={{color:C.muted}}>ارزیابی ثبت نشده است.</small>}
                        </details>
                        <details style={{marginTop:10,borderTop:`1px solid ${C.border}`,paddingTop:11}}>
                            <summary style={{cursor:"pointer",fontSize:12,color:C.navy}}>ریز اهداف فردی</summary>
                            {(memberCvi.breakdown?.individualGoals?.goals||[]).map(g=><div key={g.id} style={{fontSize:11,padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>{g.title}: {g.managerScore==null?"ارزیابی نشده":`${Number(g.managerScore).toFixed(1)}٪`} · وزن {g.effectiveWeight == null ? "—" : `${Number(g.effectiveWeight).toFixed(1)}٪`}</div>)}
                            {!memberCvi.breakdown?.individualGoals?.goals?.length&&<small style={{color:C.muted}}>هدفی ثبت نشده است.</small>}
                        </details>
                        <details style={{marginTop:10,borderTop:`1px solid ${C.border}`,paddingTop:11}}>
                            <summary style={{cursor:"pointer",fontSize:12,color:C.navy}}>ریز بازخورد همکاران</summary>
                            <div style={{fontSize:11,color:C.muted,margin:"8px 0"}}>پاسخ‌های معتبر: {memberCvi.breakdown?.peerFeedback?.responseCount??0} · حداقل موردنیاز: {memberCvi.breakdown?.peerFeedback?.minResponses??"—"}</div>
                            {(memberCvi.breakdown?.peerFeedback?.questions||[]).map(q=><div key={q.questionId} style={{fontSize:11,padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>{q.text}: {q.normalizedScore==null?"—":`${Number(q.normalizedScore).toFixed(1)}٪`}</div>)}
                        </details>
                    </details>}
                    {memberCvi?.assessmentCoverage?.organizational&&<section style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:15,marginBottom:12}}>
                        <h3 style={{fontSize:13,margin:"0 0 8px"}}>مشارکت جمعی</h3>
                        {memberCvi.assessmentCoverage.organizational.components.map(p=><div key={p.key} style={{padding:"9px 0",borderTop:`1px solid ${C.border}`}}>
                            <div style={{display:"flex",justifyContent:"space-between",gap:8,fontSize:12}}><strong>{p.label}</strong><span><strong style={{fontWeight:600,color:C.navy}}>{p.score==null?"—":`${Number(p.score).toFixed(1)}٪`}</strong> <small style={{fontSize:10.5,fontWeight:400,color:C.muted,marginInlineStart:8}}>وزن {p.effectiveWeight == null ? "—" : `${p.effectiveWeight}٪`}</small></span></div>
                            {p.note&&<div style={{fontSize:10.5,color:C.amber,marginTop:5}}>{p.note}</div>}
                        </div>)}
                    </section>}
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>جزئیات سؤالات وظایف مستمر</div>
                    <div style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, padding: 14, marginBottom: 12
                    }}>
                        <PersonRoutineDetail user={selected} routineCtx={routineCtx} activePeriod={activePeriod} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>امتیاز گروه‌ها</div>
                    <div style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, padding: 14, marginBottom: 12
                    }}>
                        {activityGroups.map((g, i) => {
                            const earned = mActs.filter(a => a.groupId === g.id && a.status === "approved")
                                .reduce((s, a) => s + a.score, 0);
                            const capped = Math.min(earned, g.maxScore);
                            return (
                                <div key={g.id} style={{ marginBottom: i < activityGroups.length - 1 ? 12 : 0 }}>
                                    <div style={{
                                        display: "flex", justifyContent: "space-between",
                                        fontSize: 11, marginBottom: 4
                                    }}>
                                        <span style={{ color: "#444", fontWeight: 500 }}>{g.label}</span>
                                        <span style={{ color: C.muted }}>{capped} / {g.maxScore}</span>
                                    </div>
                                    <MiniBar value={capped} max={g.maxScore} color={C.cyan} height={5} />
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>فعالیت‌های ثبت‌شده</div>
                    <div style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 12, overflow: "hidden"
                    }}>
                        {mActs.length === 0 ? (
                            <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 12 }}>
                                هنوز فعالیتی ثبت نشده
                            </div>
                        ) : mActs.map((a, i) => (
                            <div key={a.id} style={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", padding: "10px 14px",
                                borderBottom: i < mActs.length - 1 ? `1px solid ${C.bg}` : "none"
                            }}>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{a.title}</div>
                                    <div style={{ fontSize: 10, color: C.hint, marginTop: 1 }}>{groupLabel(activityGroups, a.groupId)}</div>
                                </div>
                                <StatusBadge status={a.status} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const avgCVI = scopedAverage(visibleMembers, scopedCvi);

    return (
        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
            <div style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 12
            }}>
                <span style={{ fontSize: 15, fontWeight: 500 }}>{title}</span>
                <span style={{
                    background: C.cyanBg, color: "#007A8C", fontSize: 11,
                    padding: "3px 10px", borderRadius: 20, fontWeight: 500
                }}>
                    میانگین CVI: {avgCVI == null ? "—" : avgCVI.toFixed(1)}
                </span>
            </div>
            {enableOrgFilters && <Card style={{ marginBottom: 12 }}>
                {flexibleOrgActive ? (
                    <div style={{ padding: 12 }}>
                        <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>واحد سازمانی</label>
                        <select value={orgUnitFilter} onChange={e => setOrgUnitFilter(e.target.value)}
                            style={{
                                width: "100%", padding: "9px 10px", border: `1px solid ${C.borderMd}`,
                                borderRadius: 8, background: "#fff", fontFamily: "inherit", fontSize: 11.5
                            }}>
                            <option value="all">همه واحدهای سازمانی</option>
                            {orgUnits.filter(unit => unit.isActive !== false).map(unit =>
                                <option key={unit.id} value={unit.id}>{unit.label}</option>)}
                        </select>
                    </div>
                ) : (
                    <div style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10 }}>
                        {!fixedDepartmentId && <div>
                            <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>معاونت</label>
                            <select value={departmentFilter} onChange={e => {
                                setDepartmentFilter(e.target.value); setTeamFilter("all");
                            }} style={{
                                width: "100%", padding: "9px 10px", border: `1px solid ${C.borderMd}`,
                                borderRadius: 8, background: "#fff", fontFamily: "inherit", fontSize: 11.5
                            }}>
                                <option value="all">همه معاونت‌ها</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                            </select>
                        </div>}
                        <div style={{ gridColumn: fixedDepartmentId ? "1 / -1" : "auto" }}>
                            <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>تیم</label>
                            <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)}
                                style={{
                                    width: "100%", padding: "9px 10px", border: `1px solid ${C.borderMd}`,
                                    borderRadius: 8, background: "#fff", fontFamily: "inherit", fontSize: 11.5
                                }}>
                                <option value="all">همه تیم‌ها</option>
                                {availableTeams.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                            </select>
                        </div>
                    </div>
                )}
            </Card>}
            {visibleMembers.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 12 }}>
                    {members.length === 0 ? emptyMessage : "عضوی با این فیلتر پیدا نشد"}
                </div>
            ) : visibleMembers.map(m => {
                // Use the same authoritative server-side scores as the member detail view.
                const scores = scopedCvi?.get(m.id);
                const ind = validCviScore(scores, "individual");
                const cvi = validCviScore(scores);
                const mp = activities.filter(a => a.userId === m.id && isReviewableActivity(a)).length;
                return (
                    <div key={m.id} onClick={() => setSelected(m)}
                        style={{
                            background: C.card, border: `1px solid ${C.border}`,
                            borderRadius: 12, marginBottom: 8, cursor: "pointer"
                        }}>
                        <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: "50%", background: C.cyanBg,
                                border: `2px solid ${C.cyan}`, display: "flex", alignItems: "center",
                                justifyContent: "center", fontSize: 17, fontWeight: 600,
                                color: C.navy, flexShrink: 0
                            }}>{m.name[0]}</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{m.name}</div>
                                <div style={{ display: "flex", gap: 6 }}>
                                    {mp > 0 && <Badge color="amber" small>{mp} در انتظار</Badge>}
                                    <Badge color="gray" small>عملکرد فردی: {ind == null ? "—" : ind.toFixed(0)}</Badge>
                                </div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                {cvi == null
                                    ? <span style={{display:"inline-flex",width:52,height:52,alignItems:"center",justifyContent:"center",color:C.hint}}>—</span>
                                    : <ScoreRing value={cvi} size={52}
                                        color={cvi >= 70 ? C.teal : cvi >= 50 ? C.amber : C.red} />}
                                <div style={{ fontSize: 9, color: C.hint, marginTop: 2 }}>CVI</div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── داشبورد معاونت — مقایسه‌ی تیم‌های زیرمجموعه + شاخص‌های معاونت + drill-down به افراد ──
function DeptDashboard({ deptId, orgUnitId, activities, activityGroups, positions, indicatorDefs, indicatorData, activePeriod }) {
    const { teams, users, departments, orgUnits, orgSettings } = useData();
    const scopedCvi = useScopedCvi(activePeriod?.id);
    const [selectedChildId, setSelectedChildId] = useState(null);

    const flexible = Boolean(orgUnitId);
    const rootUnit = flexible ? orgUnits.find(unit => unit.id === orgUnitId) : null;
    const scopeIds = flexible ? orgUnitSubtreeIds(orgUnits, orgUnitId) : [];
    const childUnits = flexible ? orgUnitChildren(orgUnits, orgUnitId) : [];
    const dept = departments.find(d => d.id === deptId);
    const deptTeams = flexible ? [] : teams.filter(t => t.deptId === deptId);
    const scopeMembers = users.filter(u => {
        if (!["manager", "specialist"].includes(u.role)) return false;
        return flexible ? scopeIds.includes(u.orgUnitId) : u.deptId === deptId;
    });
    const scopeInds = withPeriodData(indicatorDefs.filter(i =>
        flexible ? (i.level === "org_unit" && i.orgUnitId === orgUnitId) : (i.level === "dept" && i.deptId === deptId)
    ), indicatorData, activePeriod?.id);

    const scoreOf = m => validCviScore(scopedCvi?.get(m.id));
    const childAvg = unitId => {
        const ids = orgUnitSubtreeIds(orgUnits, unitId);
        return scopedAverage(scopeMembers.filter(m => ids.includes(m.orgUnitId)), scopedCvi);
    };
    const teamAvg = teamId => scopedAverage(scopeMembers.filter(m => m.teamId === teamId), scopedCvi);
    const scopeAvg = scopedAverage(scopeMembers, scopedCvi);
    const indicatorAvg = scopeInds.length
        ? scopeInds.reduce((s, ind) => s + (ind.target ? Math.min(100, (ind.actual / ind.target) * 100) : 0), 0) / scopeInds.length
        : 0;

    if (selectedChildId) {
        if (flexible) {
            const child = orgUnits.find(unit => unit.id === selectedChildId);
            const ids = orgUnitSubtreeIds(orgUnits, selectedChildId);
            const members = scopeMembers.filter(member => ids.includes(member.orgUnitId));
            return <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <SubBar title={child?.label || "زیرواحد"} onBack={() => setSelectedChildId(null)} />
                <TeamView activities={activities} activityGroups={activityGroups} positions={positions} indicatorDefs={indicatorDefs}
                    indicatorData={indicatorData} activePeriod={activePeriod} members={members}
                    title={`اعضای ${child?.label || "زیرواحد"}`} />
            </div>;
        }
        const team = teams.find(t => t.id === selectedChildId);
        const members = scopeMembers.filter(m => m.teamId === selectedChildId);
        return <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <SubBar title={team?.label || "تیم"} onBack={() => setSelectedChildId(null)} />
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={activePeriod} members={members} />
        </div>;
    }

    const scopeLabel = flexible ? (rootUnit?.label || "داشبورد واحد") : (dept?.label || "داشبورد معاونت");
    const children = flexible ? childUnits : deptTeams;

    return (
        <div className="cvi-dashboard-page">
            <DashboardPageIntro title={scopeLabel}
                subtitle={`${scopeMembers.length} نفر · ${children.length} زیرواحد`}
                eyebrow="داشبورد واحد" period={activePeriod?.label || "—"} />
            <div className="cvi-dashboard-kpi-grid">
                <DashboardMetricCard label="میانگین CVI تیم" value={scopeAvg == null ? "—" : `${scopeAvg.toFixed(1)}٪`} helper="" tone="cyan" icon={<UiIcon name="performance" size={16} />} />
                <DashboardMetricCard label="تحقق شاخص‌های تیم" value={`${indicatorAvg.toFixed(0)}٪`} helper={`${scopeInds.length} شاخص`} tone={indicatorAvg >= 80 ? "teal" : indicatorAvg >= 60 ? "amber" : "red"} icon={<UiIcon name="performance" size={16} />} />
                <DashboardMetricCard label="زیرواحدها" value={children.length} helper={`${scopeMembers.length} عضو`} tone="navy" icon={<UiIcon name="team" size={16} />} />
            </div>
            <DashboardSection title="شاخص‌های واحد">
                <Card><div className="cvi-dashboard-indicator-list">
                    {scopeInds.length === 0 ? <div className="cvi-dashboard-empty">شاخصی برای این تیم تعریف نشده است</div>
                        : scopeInds.map(ind => {
                            const score = ind.target ? Math.min(100, (ind.actual / ind.target) * 100) : 0;
                            const color = score >= 85 ? C.teal : score >= 65 ? C.amber : C.red;
                            return <div key={ind.id} className="cvi-dashboard-indicator-row">
                                <div className="cvi-dashboard-indicator-meta"><strong>{ind.label}</strong><span style={{ color }}>{score.toFixed(0)}٪</span></div>
                                <MiniBar value={score} color={color} height={6} />
                                <small>واقعی: {ind.actual ?? "—"} {ind.unit} · هدف: {ind.target ?? "—"} {ind.unit}</small>
                            </div>;
                        })}
                </div></Card>
            </DashboardSection>
            <DashboardSection title="مقایسه زیرواحدها">
                {children.length === 0 ? <div className="cvi-dashboard-empty standalone">زیرواحدی برای این بخش تعریف نشده است</div>
                    : <div className="cvi-dashboard-comparison-grid">{children.map(child => {
                        const avg = flexible ? childAvg(child.id) : teamAvg(child.id);
                        const count = flexible
                            ? scopeMembers.filter(m => orgUnitSubtreeIds(orgUnits, child.id).includes(m.orgUnitId)).length
                            : scopeMembers.filter(m => m.teamId === child.id).length;
                        return <button type="button" key={child.id} className="cvi-dashboard-comparison-card" onClick={() => setSelectedChildId(child.id)}>
                            <div><strong>{child.label}</strong><small>{count} نفر</small></div>
                            <div className="cvi-dashboard-comparison-score"><span>CVI</span><strong>{avg == null ? "—" : avg.toFixed(1)}</strong></div>
                        </button>;
                    })}</div>}
            </DashboardSection>
        </div>
    );
}

// ── داشبورد کل شرکت — خلاصه سازمانی + مقایسه‌ی معاونت‌ها و تیم‌ها + drill-down به افراد ──
function CompanyDashboard({ activities, activityGroups, positions, indicatorDefs, indicatorData, activePeriod, onNavigate, user }) {
    const { teams, departments, orgUnits, users, orgSettings } = useData();
    const scopedCvi = useScopedCvi(activePeriod?.id);
    const routineCtx = useRoutineCtx();
    const [selectedUnitId, setSelectedUnitId] = useState(null);
    const [detailUnitId, setDetailUnitId] = useState(null);

    const flexibleOrgActive = orgUnits.some(unit => unit.isActive !== false);
    const rootUnits = flexibleOrgActive
        ? orgUnits.filter(unit => unit.isActive !== false && !unit.parentId)
        : [];
    const allMembers = users.filter(u => ["deputy", "manager", "specialist"].includes(u.role));
    const scoreOf = m => validCviScore(scopedCvi?.get(m.id));
    const allScores = allMembers.map(scoreOf).filter(value => value != null);
    const missingScoreCount = allMembers.length - allScores.length;
    const scoreCoverage = allMembers.length ? (allScores.length / allMembers.length) * 100 : null;
    const companyAvg = scopedAverage(allMembers, scopedCvi);
    const companyMax = allScores.length ? Math.max(...allScores) : null;
    const orgInds = withPeriodData(indicatorDefs.filter(i => i.level === "org"), indicatorData, activePeriod?.id);
    const evaluatedPopulation = users.filter(u => ["deputy", "manager", "specialist"].includes(u.role));
    const evaluatedCount = evaluatedPopulation.filter(u => calcRoutineScoreForUser(u, routineCtx, activePeriod?.id) != null).length;
    const evaluationCompletion = evaluatedPopulation.length ? (evaluatedCount / evaluatedPopulation.length) * 100 : null;
    const ceo = users.find(u => u.role === "ceo");
    const pendingExecutive = ceo ? activities.filter(a => isReviewableActivity(a) && canReviewActivity(ceo, a, users)).length : 0;
    const criticalIndicators = orgInds.filter(ind => ind.actual != null && ind.actual !== "" && Number.isFinite(Number(ind.actual)) && Number(ind.target) > 0 && (Number(ind.actual) / Number(ind.target)) * 100 < 65).length;
    const measuredOrgIndicators = orgInds.filter(ind => ind.actual != null && ind.actual !== "" && Number.isFinite(Number(ind.actual)) && Number(ind.target) > 0).length;
    const distribution = [
        { label: "عملکرد بالا", count: allScores.filter(s => s >= 80).length, color: C.teal },
        { label: "عملکرد قابل قبول", count: allScores.filter(s => s >= 60 && s < 80).length, color: C.amber },
        { label: "نیازمند توجه", count: allScores.filter(s => s < 60).length, color: C.red },
        { label: "فاقد امتیاز دوره", count: missingScoreCount, color: C.hint },
    ];

    const unitMembers = unitId => {
        const ids = orgUnitSubtreeIds(orgUnits, unitId);
        return allMembers.filter(member => ids.includes(member.orgUnitId));
    };
    const unitAvg = unitId => {
        const members = unitMembers(unitId);
        return scopedAverage(members, scopedCvi);
    };

    const legacyTopUnits = departments;
    const topUnitCount = flexibleOrgActive ? rootUnits.length : legacyTopUnits.length;
    const totalUnitCount = flexibleOrgActive ? orgUnits.filter(unit => unit.isActive !== false).length : departments.length + teams.length;


    const activeUnits = orgUnits.filter(unit => unit.isActive !== false);
    const selectedUnit = activeUnits.find(unit => unit.id === selectedUnitId);
    const unitIndicators = selectedUnit ? indicatorDefs.filter(ind => ind.level === "org_unit" && ind.orgUnitId === selectedUnitId).map(ind => {
        const data = indicatorData.find(row => row.indicatorId === ind.id && row.periodId === activePeriod?.id);
        const approved = ["approved", "locked"].includes(data?.status);
        return { ...ind, actual: approved && data.actual != null ? Number(data.actual) : null, target: data?.target == null ? null : Number(data.target) };
    }) : [];

    const buildChart = (unit, visited = new Set()) => {
        if (visited.has(unit.id)) return null;
        const seen = new Set(visited);
        seen.add(unit.id);
        const children = orgUnitChildren(activeUnits, unit.id).filter(child => !seen.has(child.id))
            .map(child => buildChart(child, seen)).filter(Boolean);
        return {
            id: unit.id, name: unit.label, type: unit.type || "واحد",
            directMembers: allMembers.filter(person => person.orgUnitId === unit.id).length,
            members: unitMembers(unit.id).length,
            cvi: unitAvg(unit.id), children
        };
    };
    const chartRoot = {
        id: "organization-root", name: "کل سازمان", type: "سازمان",
        directMembers: allMembers.length, members: allMembers.length,
        unitsCount: totalUnitCount, cvi: companyAvg,
        children: rootUnits.map(unit => buildChart(unit)).filter(Boolean)
    };
    const selectedUnitMemberCount = selectedUnit ? unitMembers(selectedUnit.id).length : 0;
    const selectedUnitDirectCount = selectedUnit ? allMembers.filter(person => person.orgUnitId === selectedUnit.id).length : 0;
    if (detailUnitId) {
        const detail = activeUnits.find(unit => unit.id === detailUnitId);
        if (detail) return <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <SubBar title={detail.label} onBack={() => setDetailUnitId(null)} />
            <DeptDashboard orgUnitId={detailUnitId} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData} activePeriod={activePeriod} />
        </div>;
    }
    return (
        <div className="cvi-dashboard-page">
            <DashboardPageIntro title="داشبورد کل سازمان"
                subtitle="نمای کلی عملکرد و مشارکت کارکنان و واحدهای سازمان در دوره انتخاب‌شده"
                eyebrow="نمای مدیریتی عملکرد و مشارکت سازمان" period={activePeriod?.label || "—"} />
            <DashboardActionCenter apiBaseUrl={API_BASE_URL} activePeriod={activePeriod}
                onAction={task => onNavigate?.(workflowTaskNav(task, user?.role || "ceo"))} />

            <div className="cvi-dashboard-kpi-grid executive">
                <DashboardMetricCard label="میانگین CVI کارکنان دارای امتیاز" value={companyAvg == null ? "—" : `${companyAvg.toFixed(1)}٪`} helper={`${allScores.length} نفر از ${allMembers.length} نفر · پوشش ${scoreCoverage == null ? "—" : `${scoreCoverage.toFixed(0)}٪`}`} tone="cyan" icon={<UiIcon name="performance" size={16} />} />
                <DashboardMetricCard label="حداکثر CVI سازمان" value={companyMax == null ? "—" : `${companyMax.toFixed(1)}٪`} helper="بالاترین امتیاز ثبت‌شده در این دوره" tone="teal" icon={<UiIcon name="performance" size={16} />} />
                <DashboardMetricCard label="تکمیل ارزیابی‌های وظایف مستمر" value={evaluationCompletion == null ? "—" : `${evaluationCompletion.toFixed(0)}٪`} helper={`${evaluatedCount} نفر دارای امتیاز وظایف مستمر از ${evaluatedPopulation.length} نفر`} tone="purple" icon={<UiIcon name="check" size={16} />} />
                <DashboardMetricCard label="منتظر تصمیم مدیرعامل" value={pendingExecutive} helper={pendingExecutive ? "نیازمند اقدام" : "مورد بازی نیست"} tone={pendingExecutive ? "amber" : "teal"} icon={<UiIcon name="warning" size={16} />} />
                <DashboardMetricCard label="شاخص سازمانی بحرانی" value={criticalIndicators} helper={`${measuredOrgIndicators} شاخص دارای مقدار معتبر از ${orgInds.length} · آستانه هشدار ۶۵٪`} tone={criticalIndicators ? "red" : "teal"} icon={<UiIcon name="warning" size={16} />} />
            </div>

            <div className="cvi-dashboard-two-column">
                <DashboardSection title="توزیع CVI کارکنان در دوره انتخاب‌شده">
                    <Card><div className="cvi-ceo-distribution-summary">از {allMembers.length} نفر مشمول، {allScores.length} نفر دارای امتیاز CVI و {missingScoreCount} نفر فاقد امتیاز این دوره هستند. افراد فاقد امتیاز در گروه عملکرد ضعیف قرار نمی‌گیرند.</div><div className="cvi-dashboard-distribution cvi-dashboard-distribution-four">
                        {distribution.map(item => <div key={item.label}><strong style={{ color: item.color }}>{item.count}</strong><span>{item.label}</span></div>)}
                    </div></Card>
                </DashboardSection>

                <DashboardSection title="شاخص‌های کلان سازمان">
                    <Card><div className="cvi-dashboard-indicator-list">
                        {orgInds.length === 0 ? <div className="cvi-dashboard-empty">شاخصی در سطح کلان سازمان تعریف نشده است</div>
                            : orgInds.map(ind => {
                                const measured = ind.actual != null && ind.actual !== "" && Number.isFinite(Number(ind.actual)) && Number(ind.target) > 0;
                                const score = measured ? Math.min(100, (Number(ind.actual) / Number(ind.target)) * 100) : null;
                                const color = score == null ? C.hint : score >= 85 ? C.teal : score >= 65 ? C.amber : C.red;
                                return <div key={ind.id} className="cvi-dashboard-indicator-row">
                                    <div className="cvi-dashboard-indicator-meta"><strong>{ind.label}</strong><span style={{ color }}>{score == null ? "ثبت‌نشده" : `${score.toFixed(0)}٪`}</span></div>
                                    {score != null && <MiniBar value={score} color={color} height={6} />}
                                    <small>واقعی: {ind.actual ?? "—"} {ind.unit} · هدف: {ind.target ?? "—"} {ind.unit}</small>
                                </div>;
                            })}
                    </div></Card>
                </DashboardSection>
            </div>



            <DashboardSection title="ساختار سازمانی">
                <div className="cvi-ceo-summary-strip">
                    <span>افراد مشمول ارزیابی <strong>{allMembers.length}</strong></span>
                    <span>دارای CVI این دوره <strong>{allScores.length}</strong></span>
                    <span>واحدهای سازمانی <strong>{totalUnitCount}</strong></span>
                </div>
                <div className="cvi-ceo-chart-panel">
                    <p className="cvi-ceo-panel-note">نمای ساختار ثبت‌شده در سامانه · برای دیدن خلاصه عملکرد هر واحد، کارت آن را انتخاب کنید.</p>
                    {flexibleOrgActive && rootUnits.length
                        ? <div className="cvi-ceo-chart-scroll" dir="ltr">
                            <OrgChartNode node={chartRoot} selected={selectedUnitId} onSelect={node => {
                                if (node.id !== "organization-root") setSelectedUnitId(node.id);
                            }} />
                        </div>
                        : <div className="cvi-dashboard-empty">ساختار سازمانی منعطف هنوز ثبت نشده است.</div>}
                </div>
            </DashboardSection>
            {selectedUnit && <div className="cvi-ceo-dialog-backdrop" onClick={() => setSelectedUnitId(null)}>
                <section className="cvi-ceo-dialog" role="dialog" aria-modal="true" aria-label={selectedUnit.label} onClick={event => event.stopPropagation()} dir="rtl">
                    <div className="cvi-ceo-dialog-header">
                        <div><strong>{selectedUnit.label}</strong><small>خلاصه واحد · {activePeriod?.label || "دوره جاری"}</small></div>
                        <button type="button" aria-label="بستن پنجره" className="cvi-ceo-dialog-close" onClick={() => setSelectedUnitId(null)}>×</button>
                    </div>
                    <div className="cvi-ceo-dialog-metrics">
                        <div><small>اعضای مستقیم</small><strong>{selectedUnitDirectCount}</strong></div>
                        <div><small>اعضا با زیرواحدها</small><strong>{selectedUnitMemberCount}</strong></div>
                        <div><small>زیرواحدهای مستقیم</small><strong>{orgUnitChildren(activeUnits, selectedUnit.id).length}</strong></div>
                        <div><small>میانگین CVI اعضای دارای امتیاز</small><strong>{unitAvg(selectedUnit.id) == null ? "—" : unitAvg(selectedUnit.id).toFixed(1) + "٪"}</strong></div>
                        <div><small>شاخص اختصاصی</small><strong>{unitIndicators.length}</strong></div>
                    </div>
                    <div className="cvi-ceo-dialog-indicators">
                        <strong>نمای کلی شاخص‌ها</strong>
                        {unitIndicators.length ? unitIndicators.slice(0, 5).map(ind => {
                            const valid = ind.actual != null && Number.isFinite(ind.actual) && Number.isFinite(ind.target) && ind.target > 0;
                            const score = valid ? ind.actual / ind.target * 100 : null;
                            return <div key={ind.id} className="cvi-dashboard-indicator-row">
                                <div className="cvi-dashboard-indicator-meta"><strong>{ind.label}</strong><span>{score == null ? "فاقد عملکرد تأییدشده" : score.toFixed(1) + "٪"}</span></div>
                                {score != null && <MiniBar value={Math.max(0, Math.min(100,score))} color={score >= 85 ? C.teal : score >= 65 ? C.amber : C.red} height={6} />}
                            </div>;
                        }) : <div className="cvi-dashboard-empty">شاخص اختصاصی برای این واحد تعریف نشده است.</div>}
                        {unitIndicators.length > 5 && <small>و {unitIndicators.length - 5} شاخص دیگر در صفحه جزئیات</small>}
                    </div>
                    <div className="cvi-ceo-dialog-actions">
                        <button type="button" className="cvi-dashboard-link-button" onClick={() => setSelectedUnitId(null)}>بستن</button>
                        <button type="button" className="cvi-dashboard-link-button is-primary" onClick={() => {setDetailUnitId(selectedUnit.id);setSelectedUnitId(null)}}>مشاهده جزئیات واحد ←</button>
                    </div>
                </section>
            </div>}

        </div>
    );
}

// ── خوداظهاری فرد — بدون اثر مستقیم بر امتیاز CVI ────────────────────
function RoutineSelfAssessment({ activePeriod }) {
    const { routineLevels } = useData();
    const { t, language } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState("");
    const [error, setError] = useState("");
    const [pendingTasks, setPendingTasks] = useState([]);
    const [month, setMonth] = useState(1);

    const request = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api/routine${path}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || "Self-assessment request failed.");
        return payload;
    };

    const load = async () => {
        if (!activePeriod?.id) { setData(null); setPendingTasks([]); setLoading(false); return; }
        setData(null); setPendingTasks([]); setLoading(true); setError("");
        try { setData(await request(`/self?periodId=${encodeURIComponent(activePeriod.id)}`)); }
        catch (e) { setError(e.message || "دریافت خوداظهاری انجام نشد"); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        if (!activePeriod?.id) { setData(null); setLoading(false); setError(""); return undefined; } load(); }, [activePeriod?.id]);

    const answerFor = questionId => data?.answers?.find(answer => answer.questionId === questionId);
    const saveAnswer = async (questionId, levelId, note, blocker = {}) => {
        const existing = answerFor(questionId);
        if (!activePeriod?.id || existing?.submittedAt) return;
        setSavingKey(questionId); setError("");
        try {
            const saved = await request("/self/answers", {
                method: "POST",
                body: JSON.stringify({
                    questionId, periodId: activePeriod.id, levelId, note: note || null,
                    blockerCategory: blocker.blockerCategory ?? existing?.blockerCategory ?? "",
                    blockerText: blocker.blockerText ?? existing?.blockerText ?? "",
                    supportNeed: blocker.supportNeed ?? existing?.supportNeed ?? "",
                    suggestedAction: blocker.suggestedAction ?? existing?.suggestedAction ?? "",
                    blockerStatus: blocker.blockerStatus ?? existing?.blockerStatus ?? "none",
                }),
            });
            setData(prev => ({ ...prev, answers: [...(prev.answers || []).filter(answer => answer.questionId !== questionId), saved] }));
        } catch (e) { setError(e.message || "ذخیره خوداظهاری انجام نشد"); }
        finally { setSavingKey(""); }
    };

    const submitMonth = async () => {
        if (!activePeriod?.id) return;
        const monthQuestions = (data?.questions || []).filter(question => Number(question.month) === month);
        const allAnswered = monthQuestions.length && monthQuestions.every(question => answerFor(question.id)?.levelId);
        if (!allAnswered) { setError("لطفاً ابتدا به همه سؤالات این ماه پاسخ دهید."); return; }
        if (!await uxConfirm("پس از ارسال نهایی این ماه، پاسخ‌های خوداظهاری آن ماه قفل می‌شوند. ادامه می‌دهید؟")) return;
        setSavingKey("submit"); setError("");
        try {
            setData(await request("/self/submit", { method: "POST", body: JSON.stringify({ periodId: activePeriod.id, month }) }));
        } catch (e) { setError(e.message || "ارسال نهایی خوداظهاری انجام نشد"); }
        finally { setSavingKey(""); }
    };

    useEffect(() => {
        if (!activePeriod?.id) return;
        const token = sessionStorage.getItem("cvi_token");
        fetch(`${API_BASE_URL}/api/routine/tasks?periodId=${encodeURIComponent(activePeriod.id)}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : Promise.reject(new Error("tasks")))
            .then(payload => setPendingTasks(payload.tasks || []))
            .catch(() => setPendingTasks([]));
    }, [activePeriod?.id, data?.submittedMonths?.join(",")]);

    if (loading) return <div style={{ padding: 24, color: C.muted }}>در حال دریافت خوداظهاری...</div>;
    if (!activePeriod) return <div style={{ padding: 24, color: C.muted }}>دوره فعالی وجود ندارد.</div>;

    const questions = data?.questions || [];
    const monthQuestions = questions.filter(question => Number(question.month) === month);
    const monthSubmitted = (data?.submittedMonths || []).includes(month);
    return <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{t("self.title")}</div>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.8, marginBottom: 12 }}>
            {t("self.subtitle")}
        </div>
        {error && <div style={{ background: C.redLt, color: C.red, borderRadius: 8, padding: 10, fontSize: 11, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>{[1, 2, 3].map(m => <button key={m} onClick={() => setMonth(m)}
            style={{ flex: 1, padding: 8, borderRadius: 8, border: `1px solid ${month === m ? C.navy : C.border}`, background: month === m ? C.navy : "#fff", color: month === m ? "#fff" : C.muted, cursor: "pointer" }}>
            {m === 1 ? t("self.month1") : m === 2 ? t("self.month2") : t("self.month3")}{(data?.submittedMonths || []).includes(m) ? " ✓" : ""}
        </button>)}</div>
        {monthSubmitted && <div style={{ background: C.tealLt, color: C.teal, borderRadius: 8, padding: 10, fontSize: 11, marginBottom: 12 }}>{t("self.submitted")}</div>}
        {(() => { const d=(data?.deadlines||[]).find(x=>Number(x.month)===month); const evaluated=(data?.evaluatedMonths||[]).includes(month); return <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
            <Badge color={evaluated?"teal":monthSubmitted?"purple":"amber"}>{evaluated?t("self.evaluated"):monthSubmitted?t("self.submitted"):t("self.draft")}</Badge>
            {d?.dueAt && <Badge color={d.overdue?"red":"gray"}>{t("self.deadline")}: {new Intl.DateTimeFormat(language === "fa" ? "fa-IR-u-ca-persian" : language === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(d.dueAt))} {d.overdue?`· ${t("self.overdue")}`:""}</Badge>}
            {pendingTasks.length>0 && <Badge color="amber">{t("self.pending")}: {pendingTasks.length}</Badge>}
        </div>; })()}
        {monthQuestions.length === 0 ? <div style={{ padding: 28, textAlign: "center", color: C.muted, fontSize: 12 }}>{t("self.noQuestions")}</div> : <Card>{monthQuestions.map((q, i) => {
            const ans = answerFor(q.id);
            const locked = Boolean(ans?.submittedAt);
            return <div key={q.id} style={{ padding: 14, borderBottom: i < monthQuestions.length - 1 ? `1px solid ${C.bg}` : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 9 }}><span style={{ fontSize: 12, fontWeight: 500 }}>{q.label}</span><Badge color="gray" small>وزن {q.weight}٪</Badge></div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>{routineLevels.map(lvl => <button key={lvl.id}
                    disabled={locked || Boolean(savingKey)} onClick={() => saveAnswer(q.id, lvl.id, ans?.note || "", ans || {})}
                    style={{ padding: "6px 11px", borderRadius: 18, border: `1px solid ${ans?.levelId === lvl.id ? C.purple : C.border}`, background: ans?.levelId === lvl.id ? C.purpleLt : "#fff", color: ans?.levelId === lvl.id ? C.purple : C.muted, cursor: locked ? "default" : "pointer", opacity: savingKey && savingKey !== q.id ? 0.55 : 1 }}>{lvl.label}</button>)}</div>
                <textarea disabled={locked} defaultValue={ans?.note || ""} onBlur={e => ans?.levelId && !locked && e.target.value !== (ans?.note || "") && saveAnswer(q.id, ans.levelId, e.target.value)}
                    placeholder="توضیح یا شواهد (اختیاری)" style={{ width: "100%", boxSizing: "border-box", minHeight: 54, border: `1px solid ${C.border}`, borderRadius: 7, padding: 8, fontFamily: "inherit", fontSize: 11, resize: "vertical", background: locked ? C.bg : "#fff" }} />
                {ans?.levelId && <details style={{marginTop:8}} open={Boolean(ans?.blockerText || ans?.supportNeed)}>
                    <summary style={{cursor:locked?"default":"pointer",fontSize:10.8,fontWeight:600,color:C.navy}}>مانع یا نیاز حمایتی (اختیاری)</summary>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:7,marginTop:8}}>
                        <select disabled={locked} defaultValue={ans?.blockerCategory || ""} onChange={e=>!locked&&saveAnswer(q.id,ans.levelId,ans?.note||"",{...ans,blockerCategory:e.target.value,blockerStatus:e.target.value&&(!ans?.blockerStatus||ans.blockerStatus==="none")?"open":ans?.blockerStatus||"none"})} style={{padding:7,border:`1px solid ${C.border}`,borderRadius:7,fontFamily:"inherit",fontSize:10.5}}>
                            <option value="">نوع مانع</option><option value="EXTERNAL_DEPENDENCY">وابستگی بیرونی / بین‌واحدی</option><option value="RESOURCE_LIMITATION">محدودیت منابع</option><option value="PROCESS_ISSUE">مسئله فرایندی</option><option value="SYSTEM_TOOL">سیستم / ابزار</option><option value="MANAGEMENT_DECISION">تصمیم مدیریتی</option><option value="OTHER">سایر</option>
                        </select>
                        <select disabled={locked} defaultValue={ans?.blockerStatus || "none"} onChange={e=>!locked&&saveAnswer(q.id,ans.levelId,ans?.note||"",{...ans,blockerStatus:e.target.value})} style={{padding:7,border:`1px solid ${C.border}`,borderRadius:7,fontFamily:"inherit",fontSize:10.5}}>
                            <option value="none">بدون مانع</option><option value="open">باز</option><option value="in_progress">در حال پیگیری</option><option value="resolved">حل‌شده</option>
                        </select>
                        <textarea disabled={locked} defaultValue={ans?.blockerText || ""} onBlur={e=>!locked&&e.target.value!==(ans?.blockerText||"")&&saveAnswer(q.id,ans.levelId,ans?.note||"",{...ans,blockerText:e.target.value,blockerStatus:ans?.blockerStatus==="none"?"open":ans?.blockerStatus||"open"})} placeholder="شرح مانع" style={{gridColumn:"1/-1",minHeight:48,padding:7,border:`1px solid ${C.border}`,borderRadius:7,fontFamily:"inherit",fontSize:10.5,resize:"vertical"}}/>
                        <textarea disabled={locked} defaultValue={ans?.supportNeed || ""} onBlur={e=>!locked&&e.target.value!==(ans?.supportNeed||"")&&saveAnswer(q.id,ans.levelId,ans?.note||"",{...ans,supportNeed:e.target.value})} placeholder="نیاز حمایتی" style={{minHeight:48,padding:7,border:`1px solid ${C.border}`,borderRadius:7,fontFamily:"inherit",fontSize:10.5,resize:"vertical"}}/>
                        <textarea disabled={locked} defaultValue={ans?.suggestedAction || ""} onBlur={e=>!locked&&e.target.value!==(ans?.suggestedAction||"")&&saveAnswer(q.id,ans.levelId,ans?.note||"",{...ans,suggestedAction:e.target.value})} placeholder="اقدام پیشنهادی" style={{minHeight:48,padding:7,border:`1px solid ${C.border}`,borderRadius:7,fontFamily:"inherit",fontSize:10.5,resize:"vertical"}}/>
                    </div>
                </details>}
            </div>;
        })}</Card>}
        {!monthSubmitted && monthQuestions.length > 0 && <button onClick={submitMonth} disabled={savingKey === "submit"}
            style={{ width: "100%", marginTop: 14, padding: 11, border: "none", borderRadius: 8, background: C.navy, color: "#fff", fontFamily: "inherit", cursor: savingKey === "submit" ? "wait" : "pointer" }}>
            {savingKey === "submit" ? "در حال ارسال..." : `ارسال نهایی خوداظهاری ${month === 1 ? "ماه اول" : month === 2 ? "ماه دوم" : "ماه سوم"}`}
        </button>}
    
        {(data?.history || []).length > 0 && <Card style={{ marginTop: 14 }}><div style={{ padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t("self.history")}</div>
            {(data.history || []).slice(0, 10).map(item => <div key={item.id} style={{ padding: "7px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 10.5, color: C.muted }}>
                <b style={{ color: C.text }}>{item.eventType === "SELF_ASSESSMENT_SUBMITTED" ? t("self.submitted") : item.eventType.includes("MANAGER_EVALUATION") ? t("self.evaluated") : item.eventType}</b>
                {item.month ? ` · ${item.month}` : ""} · {formatIranSubmissionTimestamp(item.createdAt)}
            </div>)}
        </div></Card>}
</div>;
}

// ── MANAGER APP SHELL (ریسپانسیو — دسکتاپ Sidebar / موبایل BottomNav) ─────────
// ── ارزیابی وظایف مستمر (پاسخ‌دهی ماهانه‌ی مدیر به سؤالات تیم برای هر عضو) ─────────
// ── ارزیابی وظایف مستمر عمومی — با هر بانک سؤالی (تیم/معاونت/سازمان) و حالت فقط‌خواندنی قابل استفاده است ──
function GenericRoutineEvaluation({ title, members, questions, answers, setAnswers, routineLevels,
    activePeriod, readOnly, noMembersMessage, noQuestionsMessage, subject = null, embedded = false }) {
    const { routineLoading, routineError, saveRoutineAnswerApi } = useData();
    const [selectedMember, setSelectedMember] = useState(subject);
    useEffect(() => { if (subject?.id) setSelectedMember(subject); }, [subject?.id]);
    const [month, setMonth] = useState(1);
    const [savingKey, setSavingKey] = useState("");
    const [saveError, setSaveError] = useState("");
    const [selfReport, setSelfReport] = useState(null);
    const [selfLoading, setSelfLoading] = useState(false);
    const weightSum = questions.reduce((s, q) => s + (Number(q.weight) || 0), 0);

    useEffect(() => {
        if (!selectedMember?.id || !activePeriod?.id) { setSelfReport(null); return; }
        let cancelled = false;
        (async () => {
            setSelfReport(null);
            setSaveError("");
            setSelfLoading(true);
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/routine/self/${selectedMember.id}?periodId=${encodeURIComponent(activePeriod.id)}`, { headers: { Authorization: `Bearer ${token}` } });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "Self-assessment status failed.");
                if (!cancelled) setSelfReport(payload);
            } catch (error) {
                if (!cancelled) { setSelfReport({ status: "SELF_ASSESSMENT_PENDING", submittedMonths: [], answers: [] }); setSaveError(error.message || "دریافت خوداظهاری انجام نشد"); }
            } finally { if (!cancelled) setSelfLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [selectedMember?.id, activePeriod?.id]);
    const effectiveReadOnly = readOnly || !activePeriod || activePeriod.status !== "active";

    if (routineLoading) {
        return (
            <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>{title}</div>
                <Card><div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12 }}>
                    در حال بارگذاری...
                </div></Card>
            </div>
        );
    }

    if (!questions.length) {
        return (
            <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>{title}</div>
                <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                    {noQuestionsMessage || "هنوز سؤال وظایف مستمری توسط ادمین تعریف نشده است."}
                </div>
            </div>
        );
    }

    const answerFor = (questionId, userId) =>
        answers.find(a => a.questionId === questionId && a.userId === userId && a.periodId === activePeriod?.id);

    const setAnswer = async (questionId, userId, levelId) => {
        if (effectiveReadOnly || !saveRoutineAnswerApi) return;
        const submittedSelfAnswer = selfReport?.answers?.find(answer => answer.questionId === questionId && answer.submittedAt);
        if (!submittedSelfAnswer) { setSaveError("خوداظهاری این معیار هنوز توسط فرد ارسال نشده است."); return; }
        const key = `${questionId}:${userId}`;
        setSavingKey(key);
        setSaveError("");
        try {
            await saveRoutineAnswerApi(questionId, userId, levelId);
        } catch (error) {
            console.error("Routine answer save failed:", error);
            setSaveError(error.message || "ذخیره ارزیابی وظایف مستمر انجام نشد");
        } finally {
            setSavingKey("");
        }
    };

    const scoreFor = (userId) => calcScopedRoutineScore(answers, questions, routineLevels, userId, null, null, activePeriod?.id);

    if (selectedMember) {
        const monthQuestions = questions.filter(q => q.month === month);
        const routineScore = scoreFor(selectedMember.id);
        const answeredInQuarter = questions.filter(q => answerFor(q.id, selectedMember.id)).length;

        return (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {embedded ? <div style={{ padding: "12px 16px", background: C.bg, fontSize: 13, fontWeight: 600 }}>{selectedMember.name}</div> : <SubBar title={`${title} — ${selectedMember.name}`} onBack={() => setSelectedMember(null)} />}
                <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>

                    {(routineError || saveError) && <div style={{
                        background: C.redLt, color: C.red,
                        borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12
                    }}>
                        {saveError || routineError}
                    </div>}

                    {effectiveReadOnly && <div style={{
                        background: C.amberLt, color: C.amber,
                        borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12
                    }}>
                        دوره فعالی وجود ندارد؛ ارزیابی‌های دوره‌های بسته فقط قابل مشاهده‌اند.
                    </div>}
                    {!effectiveReadOnly && (selfLoading ? <div style={{ background: C.bg, color: C.muted, borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12 }}>در حال بررسی وضعیت خوداظهاری...</div> :
                        (selfReport?.submittedMonths || []).length === 0 ? <div style={{ background: C.amberLt, color: C.amber, borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12 }}>این فرد هنوز هیچ خوداظهاری ماهانه‌ای را ارسال نکرده است. ارزیابی رسمی هر معیار پس از ارسال خوداظهاری همان ماه فعال می‌شود.</div> :
                            <div style={{ background: C.tealLt, color: C.teal, borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12 }}>خوداظهاری ماه‌های {(selfReport.submittedMonths || []).join("، ")} ارسال شده است. این امتیازها فقط جهت مقایسه نمایش داده می‌شوند و در CVI وزن ندارند.</div>)}

                    <Card style={{ marginBottom: 14 }}>
                        <div style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <div style={{ fontSize: 11, color: C.hint, marginBottom: 3 }}>امتیاز وظایف مستمر فصل جاری</div>
                                <div style={{ fontSize: 20, fontWeight: 500, color: C.purple }}>
                                    {routineScore == null ? "—" : routineScore.toFixed(0)}
                                </div>
                            </div>
                            <div style={{ fontSize: 11, color: C.muted, textAlign: "left" }}>
                                {answeredInQuarter} از {questions.length} سؤال فصل پاسخ داده شده
                            </div>
                        </div>
                    </Card>

                    <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                        {[1, 2, 3].map(m => (
                            <button key={m} onClick={() => setMonth(m)}
                                style={{
                                    flex: 1, padding: "8px 0", borderRadius: 8,
                                    border: `1.5px solid ${month === m ? C.navy : C.border}`,
                                    background: month === m ? C.navy : "#fff", color: month === m ? "#fff" : C.muted,
                                    fontSize: 12, cursor: "pointer"
                                }}>
                                {m === 1 ? "ماه اول" : m === 2 ? "ماه دوم" : "ماه سوم"}
                            </button>
                        ))}
                    </div>

                    {monthQuestions.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 24, color: C.muted, fontSize: 12 }}>
                            سؤالی برای این ماه تعریف نشده است.
                        </div>
                    ) : (
                        <Card>
                            {monthQuestions.map((q, i) => {
                                const ans = answerFor(q.id, selectedMember.id);
                                const selfAns = selfReport?.answers?.find(answer => answer.questionId === q.id && answer.submittedAt);
                                return (
                                    <div key={q.id} style={{
                                        padding: 14,
                                        borderBottom: i < monthQuestions.length - 1 ? `1px solid ${C.bg}` : "none"
                                    }}>
                                        <div style={{
                                            display: "flex", justifyContent: "space-between",
                                            alignItems: "flex-start", marginBottom: 10, gap: 8
                                        }}>
                                            <span style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.6 }}>{q.label}</span>
                                            <Badge color="gray" small>وزن {q.weight}٪</Badge>
                                        </div>
                                        {selfAns ? <>
                                            <div style={{ marginBottom: 9, padding: "7px 9px", borderRadius: 7, background: C.cyanLt, fontSize: 10.5, color: C.muted }}>
                                                <b style={{ color: C.navy }}>خوداظهاری فرد:</b> {routineLevels.find(level => level.id === selfAns.levelId)?.label || "—"}{selfAns.note ? ` — ${selfAns.note}` : ""}
                                            </div>
                                            {selfAns.blockerStatus && selfAns.blockerStatus !== "none" && <div style={{marginBottom:9,padding:"7px 9px",borderRadius:7,background:C.amberLt,fontSize:10.5,color:C.muted}}>
                                                <b style={{color:C.amber}}>مانع / نیاز حمایتی:</b> {selfAns.blockerText || "ثبت شده"}
                                                {selfAns.supportNeed && <div style={{marginTop:3}}>نیاز حمایتی: {selfAns.supportNeed}</div>}
                                                {selfAns.suggestedAction && <div style={{marginTop:3}}>اقدام پیشنهادی: {selfAns.suggestedAction}</div>}
                                            </div>}
                                        </> : !effectiveReadOnly && <div style={{ marginBottom: 9, fontSize: 10.5, color: C.amber }}>در انتظار ارسال خوداظهاری این معیار</div>}
                                        {effectiveReadOnly ? (
                                            ans ? (
                                                <Badge color="purple">{routineLevels.find(l => l.id === ans.levelId)?.label || "—"}</Badge>
                                            ) : (
                                                <span style={{ fontSize: 11, color: C.hint }}>هنوز پاسخ داده نشده</span>
                                            )
                                        ) : (
                                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                {routineLevels.map(lvl => (
                                                    <button key={lvl.id} onClick={() => setAnswer(q.id, selectedMember.id, lvl.id)}
                                                        disabled={Boolean(savingKey) || !selfAns}
                                                        style={{
                                                            padding: "6px 12px", borderRadius: 20,
                                                            border: `1.5px solid ${ans?.levelId === lvl.id ? C.purple : C.border}`,
                                                            background: ans?.levelId === lvl.id ? C.purpleLt : "#fff",
                                                            color: ans?.levelId === lvl.id ? C.purple : C.muted,
                                                            opacity: savingKey && savingKey !== `${q.id}:${selectedMember.id}` ? 0.55 : 1,
                                                            fontSize: 11, fontWeight: ans?.levelId === lvl.id ? 500 : 400,
                                                            cursor: savingKey ? "wait" : "pointer"
                                                        }}>
                                                        {savingKey === `${q.id}:${selectedMember.id}` && ans?.levelId !== lvl.id ? "..." : lvl.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </Card>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
            <div className="cvi-workflow-heading-block"><div className="cvi-workflow-title">{title}</div><div className="cvi-workflow-subtitle">ارزیابی رسمی وظایف مستمر پس از ارسال خوداظهاری فرد</div></div>
            {(routineError || saveError) && <div style={{
                background: C.redLt, color: C.red,
                borderRadius: 8, padding: "8px 12px", fontSize: 11, marginBottom: 12
            }}>
                {saveError || routineError}
            </div>}
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, lineHeight: 1.7 }}>
                {readOnly ? (
                    <>این بخش فقط جهت مشاهده است. جمع وزن {questions.length} سؤال: {weightSum}٪</>
                ) : (
                    <>در پایان هر ماه، برای هر نفر به سؤالات همان ماه پاسخ دهید. جمع وزن {questions.length} سؤال فصل: {weightSum}٪
                        {weightSum !== 100 && <span style={{ color: C.red }}> (باید توسط ادمین به ۱۰۰٪ برسد)</span>}
                    </>
                )}
            </div>
            {members.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 12 }}>
                    {noMembersMessage || "عضوی یافت نشد"}
                </div>
            ) : members.map(m => {
                const routineScore = scoreFor(m.id);
                const answeredCount = questions.filter(q => answerFor(q.id, m.id)).length;
                return (
                    <div key={m.id} onClick={() => setSelectedMember(m)}
                        style={{
                            background: C.card, border: `1px solid ${C.border}`,
                            borderRadius: 12, marginBottom: 8, cursor: "pointer"
                        }}>
                        <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: "50%", background: C.purpleLt,
                                border: `2px solid ${C.purple}`, display: "flex", alignItems: "center",
                                justifyContent: "center", fontSize: 17, fontWeight: 600,
                                color: C.purple, flexShrink: 0
                            }}>{m.name[0]}</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{m.name}</div>
                                <Badge color="gray" small>{answeredCount} از {questions.length} سؤال پاسخ داده شده</Badge>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontSize: 18, fontWeight: 500, color: C.purple }}>
                                    {routineScore == null ? "—" : routineScore.toFixed(0)}
                                </div>
                                <div style={{ fontSize: 9, color: C.hint, marginTop: 2 }}>روتین</div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ارزیابی وظایف مستمر مدیر برای کارشناس‌های تیمش (سطح تیم)
function RoutineEvaluation({ user, members, activePeriod, subject = null }) {
    const { routineQuestions, routineAnswers, routineLevels, routineTargets } = useData();
    const unitQuestions = user.orgUnitId
        ? routineQuestions.filter(q => q.scope === "org_unit" && q.orgUnitId === (subject?.orgUnitId || user.orgUnitId))
        : routineQuestions.filter(q => q.teamId === user.teamId);
    const apiMembers = routineTargets;
    return (
        <GenericRoutineEvaluation title="ارزیابی وظایف مستمر" members={apiMembers}
            questions={unitQuestions} answers={routineAnswers}
            routineLevels={routineLevels} activePeriod={activePeriod} subject={subject} embedded={Boolean(subject)}
            noMembersMessage="هنوز عضوی به این مدیر متصل نشده"
            noQuestionsMessage="هنوز سؤال وظایف مستمری برای این واحد توسط ادمین تعریف نشده است." />
    );
}

function EmployeeEvaluationWorkspace({ user, members, activePeriod }) {
    const [selectedMember, setSelectedMember] = useState(null);
    const [tab, setTab] = useState("continuous");
    if (!selectedMember) return <div className="cvi-workflow-page" style={{ flex: 1, overflowY: "auto", padding: 16, background: C.bg }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>ارزیابی کارکنان</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>یک کارمند را انتخاب کنید تا فعالیت‌های مستمر و اهداف فردی او را ارزیابی کنید.</div>
        {members.length === 0 ? <EmptyState title="کارمندی برای ارزیابی وجود ندارد" description="کارکنان زیرمجموعه مستقیم شما اینجا نمایش داده می‌شوند." icon="members" /> : members.map(member => <button type="button" key={member.id} onClick={() => { setSelectedMember(member); setTab("continuous"); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 8, cursor: "pointer", fontFamily: "inherit", textAlign: "right" }}>
            <UserAvatar user={member} size={42} /><span style={{ flex: 1 }}><strong style={{ display: "block", fontSize: 13 }}>{member.name}</strong><small style={{ color: C.muted }}>{positionsLabelForEmployee(member)}</small></span><UiIcon name="chevronLeft" size={16} />
        </button>)}
    </div>;
    return <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: C.bg }}>
        <div style={{ padding: "14px 16px 0" }}>
            <button type="button" onClick={() => setSelectedMember(null)} style={{ border: 0, background: "transparent", color: C.navy, cursor: "pointer", fontFamily: "inherit", padding: 0, marginBottom: 10 }}>بازگشت به کارکنان</button>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}><UserAvatar user={selectedMember} size={42} /><div><div style={{ fontSize: 15, fontWeight: 700 }}>{selectedMember.name}</div><div style={{ fontSize: 10.5, color: C.muted }}>ارزیابی دوره {activePeriod?.label || "جاری"}</div></div></div>
            <div style={{ display: "flex", gap: 6, marginTop: 14, borderBottom: `1px solid ${C.border}` }}>
                {[{ id: "continuous", label: "فعالیت‌های مستمر" }, { id: "goals", label: "اهداف فردی" }].map(item => <button key={item.id} onClick={() => setTab(item.id)} style={{ border: 0, borderBottom: tab === item.id ? `2px solid ${C.navy}` : "2px solid transparent", background: "transparent", color: tab === item.id ? C.navy : C.muted, padding: "9px 13px", fontFamily: "inherit", fontWeight: tab === item.id ? 700 : 500, cursor: "pointer" }}>{item.label}</button>)}
            </div>
        </div>
        {tab === "continuous" ? <RoutineEvaluation user={user} members={members} activePeriod={activePeriod} subject={selectedMember} /> : <IndividualGoalsPage user={user} managerOnly selectedSubjectId={selectedMember.id} selectedSubjectName={selectedMember.name} />}
    </div>;
}

function positionsLabelForEmployee(member) {
    return member?.role === "specialist" ? "کارشناس" : member?.role === "manager" ? "مدیر" : member?.role === "deputy" ? "معاون" : "کارمند";
}

function ManagerApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels, positions,
        indicatorDefs, indicatorData, activePeriod, teams, users, orgSettings, periodSnapshots,
        peerAssignments, createActivityApi } = useData();
    const routineCtx = useRoutineCtx();
    const isMobile = useIsMobile();
    const [nav, setNav] = useState("dashboard");
    const [view, setView] = useState(null);
    const [selectedAct, setSelectedAct] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [periodDetailOrigin, setPeriodDetailOrigin] = useState("cvi-detail");
    const [selectedOrgLevel, setSelectedOrgLevel] = useState(null);

    const directReports = users.filter(u => u.managerId === user.id && u.isActive !== false);
    const members = reportingSubtreeUsers(users, user.id);
    const pendingCount = activities.filter(a =>
        directReports.some(m => m.id === a.userId) && isReviewableActivity(a)).length;
    const indicatorReviewCount = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" &&
        indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === user.id).length;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);
    const myActs = activities.filter(a => a.userId === user.id);
    const nonRoutineScore = calcNonRoutineScore(activities, activityGroups, user.id, activePeriod?.id);
    const routineScore = calcRoutineScoreForUser(user, routineCtx, activePeriod?.id);
    const indivScore = calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, user, activePeriod?.id);
    const orgScore = calcOrgScore(user, positions, indicatorDefs, indicatorData, activePeriod?.id);
    const allPeriods = [...latestPeriodSnapshotsForUser(periodSnapshots, user.id),
    ...(activePeriod ? [{
        periodId: activePeriod.id, label: activePeriod.label,
        individual: indivScore, org: orgScore, cvi: calcCVI(indivScore, orgScore),
        nonRoutine: nonRoutineScore, routine: routineScore,
        peerFeedback: calcEligiblePeerFeedbackScore(routineCtx, user.id, activePeriod.id),
        status: "temporary", locked: false
    }] : [])];

    if (nav === "new" && !activePeriod) {
        return <div style={{ padding: 24, direction: "inherit", fontFamily: "inherit" }}>
            <Card><div style={{ padding: 20, textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>ثبت فعالیت غیرفعال است</div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>دوره فعالی وجود ندارد؛ فعالیت جدید فقط در دوره فعال ثبت می‌شود.</div>
                <button onClick={() => setNav("activities")} style={{
                    padding: "8px 16px", background: C.navy,
                    color: "#fff", border: "none", borderRadius: 8, cursor: "pointer"
                }}>بازگشت</button>
            </div></Card>
        </div>;
    }
    if (nav === "new") {
        return <ActivityForm draftKey={`activity-draft-${user.id}`} onBack={() => setNav("activities")} onSubmit={async (form) => {
            try {
                await createActivityApi(form, activePeriod?.id);
                setNav("activities");
            } catch (error) {
                console.error("Activity creation failed:", error);
                uxNotify(error.message || "ثبت فعالیت انجام نشد");
            }
        }} />;
    }

    if (view) {
        return (
            <SpecialistDetailView view={view} setView={setView}
                selectedAct={selectedAct} setSelectedAct={setSelectedAct}
                selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}
                periodDetailOrigin={periodDetailOrigin} setPeriodDetailOrigin={setPeriodDetailOrigin}
                myActs={myActs} allPeriods={allPeriods}
                activityGroups={activityGroups} impactLevels={impactLevels}
                user={user} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={activePeriod}
                routineCtx={routineCtx} selectedOrgLevel={selectedOrgLevel} />
        );
    }

    const dashboardContent = (
        <ManagerDashboard user={user} activities={activities} activityGroups={activityGroups}
            positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
            activePeriod={activePeriod} members={members} directReports={directReports} teams={teams} onNavigate={setNav}
            setView={setView} setSelectedOrgLevel={setSelectedOrgLevel} />
    );

    const renderContent = () => {
        if (nav === "inbox") return (
            <Inbox activities={activities} onUpdate={setActivities}
                isMobile={isMobile} members={directReports} impactLevels={impactLevels} evaluator={user} users={users} />
        );

        if (nav === "teamdash") return (
            <TeamDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={members} directReports={directReports} teams={teams} onNavigate={setNav} />
        );
        if (nav === "team") return (
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions}
                indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} members={members} />
        );
        if (nav === "employee-evaluation") return (
            <EmployeeEvaluationWorkspace user={user} members={directReports} activePeriod={activePeriod} />
        );
        if (["tasks-activities", "self-assessment", "activities"].includes(nav)) return (
            <TasksActivitiesPage nav={nav} setNav={setNav}
                routineContent={<RoutineSelfAssessment activePeriod={activePeriod} />}
                activityContent={<SpecialistActivities myActs={myActs} activityGroups={activityGroups} setNav={setNav}
                    setSelectedAct={setSelectedAct} setView={setView} />} />
        );
        if (nav === "my-indicators") return <MyIndicators user={user} />;
        if (nav === "history") return <SpecialistHistory user={user} allPeriods={allPeriods}
            setView={setView} setSelectedPeriod={setSelectedPeriod} setPeriodDetailOrigin={setPeriodDetailOrigin} />;
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "goals") return <IndividualGoalsPage user={user} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "dashboard", label: "داشبورد من", icon: "dashboard" },
        ...(directReports.length ? [{ id: "teamdash", label: "داشبورد تیم", icon: "team" }] : []),
        { id: "self-assessment", label: "خوداظهاری", icon: "self" },
        { id: "goals", label: "اهداف فردی", icon: "target" },
        { id: "inbox", label: "صندوق بررسی", icon: "inbox", badge: pendingCount + indicatorReviewCount },
        { id: "employee-evaluation", label: "ارزیابی کارکنان", icon: "routine" },
        { id: "activities", label: "فعالیت‌های غیرمستمر", icon: "activity" },
        { id: "my-indicators", label: "شاخص‌های من", icon: "target" },
        { id: "history", label: "تاریخچه", icon: "history" },
        { id: "peer-feedback", label: "بازخورد همکاران", icon: "peer", badge: peerPending },
        { id: "team", label: "اعضای تیم", icon: "members" },
        { id: "help", label: "راهنما", icon: "help", section: "account" },
        { id: "profile", label: "پروفایل", icon: "profile" },
    ]);
    const bottomItems = [
        ...sidebarItems,
    ];

    return (
        <CviAppShell
            isMobile={isMobile}
            header={<TopBar searchItems={sidebarItems} onSearch={setNav} right={<div style={{ display: "flex", alignItems: "center", gap: 10 }}><UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} /></div>} />}
            periodContext={<PeriodContextBar period={activePeriod} daysLeft={daysRemainingInPeriod(activePeriod)} />}
            sidebar={<Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
            bottomNav={<AppBottomNav items={bottomItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        >
            <div className={nav === "dashboard" ? "cvi-role-content-scroll" : "cvi-role-content"}>
                <div style={{ display: nav === "dashboard" ? "block" : "none", minHeight: 0 }} aria-hidden={nav !== "dashboard"}>
                    {dashboardContent}
                </div>
                {nav !== "dashboard" && <TabTransitionContent transitionKey={nav}>{renderContent()}</TabTransitionContent>}
            </div>
        </CviAppShell>
    );
}

/* ════════════════════════════════════════════════════════════════════════
   پنل معاون
   ════════════════════════════════════════════════════════════════════════ */

function DeputyOwnDashboard({ user, activities, activityGroups, positions, indicatorDefs, indicatorData,
    activePeriod, allPeriods, setView, setSelectedOrgLevel, onNavigate }) {
    const { orgSettings, departments, orgUnits } = useData();
    const routineCtx = useRoutineCtx();
    const nonRoutineScore = calcNonRoutineScore(activities, activityGroups, user.id, activePeriod?.id);
    const routineScore = calcRoutineScoreForUser(user, routineCtx, activePeriod?.id);
    const ind = calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, user, activePeriod?.id);
    const orgBreakdown = calcOrgScoreBreakdown(user, positions, indicatorDefs, indicatorData, activePeriod?.id);
    const org = orgBreakdown.total;
    const cvi = calcCVI(ind, org);
    const dept = departments.find(d => d.id === user.deptId);
    const orgUnit = orgUnits.find(unit => unit.id === user.orgUnitId);
    const scopeLabel = orgUnit?.label || dept?.label || "—";
    const myActivities = activities.filter(a => a.userId === user.id);
    const personalNeedsAction = myActivities.filter(a => ["revision_requested","needs_revision"].includes(String(a.status || "").toLowerCase())).length;
    const personalOpenBlockers = myActivities.filter(a => ["open","in_progress"].includes(String(a.blockerStatus || "").toLowerCase())).length;
    const previousCvi = allPeriods?.length > 1 ? allPeriods[allPeriods.length - 2]?.cvi : null;
    const trendRows = (allPeriods || []).filter(p => Number.isFinite(Number(p?.cvi))).map((p,i) => ({
    periodId: p.periodId || String(i),
    label: p.label || `دوره ${i+1}`,
    order: p.start || p.startDate || String(i).padStart(4,"0"),
    cvi: Number(p.cvi),
    individual: Number(p.individual),
    collective: Number(p.org ?? p.collective)
})).filter(r => [r.cvi,r.individual,r.collective].every(Number.isFinite));

    return (
        <div className="cvi-dashboard-page cvi-premium-dashboard">
            <DashboardPageIntro title={user.name}
                subtitle={`معاون · ${scopeLabel}`}
                period={activePeriod?.label || "دوره‌ای فعال نیست"} periodStart={activePeriod?.start} periodEnd={activePeriod?.end} periodInline subtitleInline
                attentionCount={personalNeedsAction + personalOpenBlockers} onAttentionClick={() => onNavigate("activities")} />

            <div className="cvi-premium-top-grid">
                <ScoreOverview user={user} cviScore={cvi} indivScore={ind}
                    nonRoutineScore={nonRoutineScore} routineScore={routineScore}
                    orgScore={org} orgBreakdown={orgBreakdown}
                    onOpenNonRoutine={() => setView("nonroutine-detail")}
                    onOpenRoutine={() => setView("routine-detail")}
                    onOpenTasksActivities={() => setView("tasks-activities-detail")}
                    onOpenGoals={() => onNavigate("goals")}
                    onOpenPeerFeedback={() => onNavigate("peer-feedback")}
                    onOpenOrgLevel={(level) => { setSelectedOrgLevel(level); setView("org-level-detail"); }} />

                <section className="cvi-premium-trend-section">
                    <Card>
                        <div className="cvi-premium-trend-card">
                            <div className="cvi-dashboard-card-heading">
                                <h2>روند CVI در دوره‌های اخیر</h2>
                                <p>مقایسه عملکرد فردی و مشارکت جمعی در دوره‌های اخیر.</p>
                            </div>
                            <div className="cvi-premium-chart-card">
                                <PremiumPerformanceTrendChart rows={trendRows} />
                            </div>
                        </div>
                    </Card>
                </section>
            </div>

            <div className="cvi-premium-bottom-grid">
                <div className="cvi-premium-bottom-analysis">
                    <PersonalInsightSupportPanels
                        cviScore={cvi}
                        indivScore={ind}
                        orgScore={org}
                        previousCvi={previousCvi}
                        activities={myActivities}
                        managementSupportCount={0}
                    />
                </div>
                <PersonalRecentActivityPanel
                    activities={myActivities}
                    activityGroups={activityGroups}
                    onOpenActivities={() => onNavigate("activities")}
                    onCreateActivity={() => onNavigate("new")}
                />
            </div>
        </div>
    );
}

function DeputyApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels, positions, indicatorDefs, indicatorData,
        activePeriod, users, teams, orgUnits, deptRoutineQuestions, deptRoutineAnswers, routineTargets,
        routineLevels, orgSettings, periodSnapshots, peerAssignments, createActivityApi } = useData();
    const routineCtx = useRoutineCtx();
    const isMobile = useIsMobile();
    const [nav, setNav] = useState("dashboard");
    const [view, setView] = useState(null);
    const [selectedAct, setSelectedAct] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [periodDetailOrigin, setPeriodDetailOrigin] = useState("cvi-detail");
    const [selectedOrgLevel, setSelectedOrgLevel] = useState(null);

    const scopeUnitIds = user.orgUnitId ? orgUnitSubtreeIds(orgUnits, user.orgUnitId) : [];
    const visibleManagers = users.filter(u => u.role === "manager" && (
        user.orgUnitId ? (u.managerId === user.id || scopeUnitIds.includes(u.orgUnitId)) : u.deptId === user.deptId
    ));
    const directManagers = users.filter(u => u.role === "manager" && u.managerId === user.id && u.isActive !== false);
    const hasDirectReports = users.some(u => u.managerId === user.id && u.isActive !== false);
    const managers = visibleManagers;
    const departmentMembers = users.filter(u => {
        if (["admin", "ceo"].includes(u.role) || u.id === user.id) return false;
        if (user.orgUnitId) return scopeUnitIds.includes(u.orgUnitId);
        const memberTeam = teams.find(t => t.id === u.teamId);
        return u.deptId === user.deptId || memberTeam?.deptId === user.deptId;
    });
    const deptQuestions = user.orgUnitId
        ? deptRoutineQuestions.filter(q => q.scope === "org_unit" && scopeUnitIds.includes(q.orgUnitId))
        : deptRoutineQuestions.filter(q => q.deptId === user.deptId);
    const pendingCount = activities.filter(a =>
        directManagers.some(m => m.id === a.userId) && isReviewableActivity(a)).length;
    const indicatorReviewCount = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" &&
        indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === user.id).length;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);
    const myActs = activities.filter(a => a.userId === user.id);
    const nonRoutineScore = calcNonRoutineScore(activities, activityGroups, user.id, activePeriod?.id);
    const routineScore = calcRoutineScoreForUser(user, routineCtx, activePeriod?.id);
    const indivScore = calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, user, activePeriod?.id);
    const orgScore = calcOrgScore(user, positions, indicatorDefs, indicatorData, activePeriod?.id);
    const allPeriods = [...latestPeriodSnapshotsForUser(periodSnapshots, user.id),
    ...(activePeriod ? [{
        periodId: activePeriod.id, label: activePeriod.label,
        individual: indivScore, org: orgScore, cvi: calcCVI(indivScore, orgScore),
        nonRoutine: nonRoutineScore, routine: routineScore,
        peerFeedback: calcEligiblePeerFeedbackScore(routineCtx, user.id, activePeriod.id),
        status: "temporary", locked: false
    }] : [])];

    if (nav === "new" && !activePeriod) {
        return <div style={{ padding: 24, direction: "inherit", fontFamily: "inherit" }}>
            <Card><div style={{ padding: 20, textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>ثبت فعالیت غیرفعال است</div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>دوره فعالی وجود ندارد؛ فعالیت جدید فقط در دوره فعال ثبت می‌شود.</div>
                <button onClick={() => setNav("activities")} style={{
                    padding: "8px 16px", background: C.navy,
                    color: "#fff", border: "none", borderRadius: 8, cursor: "pointer"
                }}>بازگشت</button>
            </div></Card>
        </div>;
    }
    if (nav === "new") {
        return <ActivityForm draftKey={`activity-draft-${user.id}`} onBack={() => setNav("activities")} onSubmit={async (form) => {
            try {
                await createActivityApi(form, activePeriod?.id);
                setNav("activities");
            } catch (error) {
                console.error("Activity creation failed:", error);
                uxNotify(error.message || "ثبت فعالیت انجام نشد");
            }
        }} />;
    }

    if (view) {
        return (
            <SpecialistDetailView view={view} setView={setView}
                selectedAct={selectedAct} setSelectedAct={setSelectedAct}
                selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}
                periodDetailOrigin={periodDetailOrigin} setPeriodDetailOrigin={setPeriodDetailOrigin}
                myActs={myActs} allPeriods={allPeriods}
                activityGroups={activityGroups} impactLevels={impactLevels}
                user={user} positions={positions} indicatorDefs={indicatorDefs}
                indicatorData={indicatorData} activePeriod={activePeriod}
                routineCtx={routineCtx} selectedOrgLevel={selectedOrgLevel} />
        );
    }

    const renderContent = () => {
        if (nav === "dashboard") return (
            <DeputyOwnDashboard user={user} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} allPeriods={allPeriods} setView={setView} setSelectedOrgLevel={setSelectedOrgLevel}
                onNavigate={setNav} />
        );

        if (nav === "dept") return (
            <DeptDashboard deptId={user.deptId} orgUnitId={user.orgUnitId} activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} />
        );
        if (nav === "inbox") return (
            <Inbox activities={activities} onUpdate={setActivities}
                isMobile={isMobile} members={directManagers} impactLevels={impactLevels} evaluator={user} users={users} />
        );
        if (nav === "routine") return (
            <GenericRoutineEvaluation title="ارزیابی وظایف مستمر مدیران" members={routineTargets}
                questions={deptQuestions} answers={deptRoutineAnswers}
                routineLevels={routineLevels} activePeriod={activePeriod}
                noMembersMessage="هنوز مدیری در این واحد تعریف نشده"
                noQuestionsMessage="هنوز سؤال وظایف مستمری برای این واحد توسط ادمین تعریف نشده است." />
        );
        if (["tasks-activities", "self-assessment", "activities"].includes(nav)) return (
            <TasksActivitiesPage nav={nav} setNav={setNav}
                routineContent={<RoutineSelfAssessment activePeriod={activePeriod} />}
                activityContent={<SpecialistActivities myActs={myActs} activityGroups={activityGroups} setNav={setNav}
                    setSelectedAct={setSelectedAct} setView={setView} />} />
        );
        if (nav === "my-indicators") return <MyIndicators user={user} />;
        if (nav === "history") return <SpecialistHistory user={user} allPeriods={allPeriods}
            setView={setView} setSelectedPeriod={setSelectedPeriod} setPeriodDetailOrigin={setPeriodDetailOrigin} />;
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "my-indicators") return <MyIndicators user={user} />;
        if (nav === "members") return (
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions}
                indicatorDefs={indicatorDefs} indicatorData={indicatorData} activePeriod={activePeriod}
                members={departmentMembers} title="اعضای تیم" emptyMessage="هنوز عضوی در این واحد تعریف نشده"
                 />
        );
        if (nav === "goals") return <IndividualGoalsPage user={user} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "dashboard", label: "داشبورد من", icon: "dashboard" },
        ...(hasDirectReports ? [{ id: "dept", label: "داشبورد تیم", icon: "team" }] : []),
        { id: "self-assessment", label: "خوداظهاری", icon: "self" },
        { id: "goals", label: "اهداف فردی", icon: "target" },
        { id: "inbox", label: "صندوق بررسی", icon: "inbox", badge: pendingCount + indicatorReviewCount },
        { id: "routine", label: "ارزیابی وظایف مستمر", icon: "routine" },
        { id: "activities", label: "فعالیت‌های غیرمستمر", icon: "activity" },
        { id: "my-indicators", label: "شاخص‌های من", icon: "target" },
        { id: "history", label: "تاریخچه", icon: "history" },
        { id: "peer-feedback", label: "بازخورد همکاران", icon: "peer", badge: peerPending },
        { id: "members", label: "اعضای تیم", icon: "members" },
        { id: "help", label: "راهنما", icon: "help", section: "account" },
        { id: "profile", label: "پروفایل", icon: "profile" },
    ]);
    const bottomItems = [
        ...sidebarItems,
    ];

    return (
        <CviAppShell
            isMobile={isMobile}
            header={<TopBar searchItems={sidebarItems} onSearch={setNav} right={<UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />} />}
            periodContext={<PeriodContextBar period={activePeriod} daysLeft={daysRemainingInPeriod(activePeriod)} />}
            sidebar={<Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
            bottomNav={<AppBottomNav items={bottomItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        >
            <div className="cvi-role-content">
                <TabTransitionContent transitionKey={nav}>{renderContent()}</TabTransitionContent>
            </div>
        </CviAppShell>
    );
}

/* ════════════════════════════════════════════════════════════════════════
   پنل مدیرعامل
   ════════════════════════════════════════════════════════════════════════ */

function CompanyHistory({ activities, activityGroups, positions, indicatorDefs, indicatorData, activePeriod, user }) {
    const { users, orgSettings, periodSnapshots } = useData();
    const routineCtx = useRoutineCtx();
    // CEO history must use the same complete archive used by the other roles.
    // Include every employee level that can have a CVI snapshot; CEO/admin are intentionally excluded.
    const members = users.filter(u => !["ceo", "admin"].includes(u.role));
    const currentScores = members.map(m => calcCVI(
        calcIndividualScore(activities, activityGroups, routineCtx, orgSettings, m, activePeriod?.id),
        calcOrgScore(m, positions, indicatorDefs, indicatorData, activePeriod?.id)));
    const currentCvi = currentScores.length
        ? currentScores.reduce((sum, score) => sum + score, 0) / currentScores.length : 0;
    const historicalPeriods = [...new Set(periodSnapshots.map(s => s.periodId))].map(periodId => {
        const rows = members.map(m => latestPeriodSnapshotsForUser(periodSnapshots, m.id)
            .find(s => s.periodId === periodId)).filter(Boolean);
        return {
            periodId, label: rows[0]?.label || periodId, locked: true,
            cvi: rows.length ? rows.reduce((sum, s) => sum + (Number(s.cvi) || 0), 0) / rows.length : 0
        };
    });
    const allPeriods = [...historicalPeriods,
        ...(activePeriod && !historicalPeriods.some(p => String(p.periodId) === String(activePeriod.id)) ? [{
            periodId: activePeriod.id,
            label: activePeriod.label,
            cvi: currentCvi,
            locked: false,
            individual: null,
            org: null,
        }] : [])];

    return <HistoryArchivePage allPeriods={allPeriods} user={user} />;
}

function CeoApp({ user, onLogout }) {
    const { activities, setActivities, activityGroups, impactLevels, positions,
        indicatorDefs, indicatorData, activePeriod, users, orgRoutineQuestions,
        orgRoutineAnswers, routineLevels, routineTargets, peerAssignments } = useData();
    const isMobile = useIsMobile();
    const [nav, setNav] = useState("company");
    const deputies = users.filter(u => u.role === "deputy");
    const companyMembers = users.filter(u => !["ceo", "admin"].includes(u.role));
    const pendingCount = activities.filter(a =>
        deputies.some(d => d.id === a.userId) && isReviewableActivity(a)).length;
    const indicatorReviewCount = indicatorData.filter(d => d.periodId === activePeriod?.id && d.status === "submitted" &&
        indicatorDefs.find(i => i.id === d.indicatorId)?.approverId === user.id).length;
    const peerPending = peerFeedbackPendingCount(peerAssignments, activePeriod?.id);

    const renderContent = () => {
        if (nav === "company") return (
            <CompanyDashboard activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} onNavigate={setNav} user={user} />
        );
        if (nav === "inbox") return (
            <Inbox activities={activities} onUpdate={setActivities}
                isMobile={isMobile} members={deputies} impactLevels={impactLevels} evaluator={user} users={users} />
        );
        if (nav === "routine") return (
            <GenericRoutineEvaluation title="ارزیابی وظایف مستمر معاون‌ها" members={routineTargets}
                questions={orgRoutineQuestions} answers={orgRoutineAnswers}
                routineLevels={routineLevels} activePeriod={activePeriod}
                noMembersMessage="هنوز معاونی تعریف نشده"
                noQuestionsMessage="هنوز سؤالی برای وظایف مستمر در سطح سازمان توسط ادمین تعریف نشده است." />
        );
        if (nav === "history") return (
            <CompanyHistory activities={activities} activityGroups={activityGroups}
                positions={positions} indicatorDefs={indicatorDefs} indicatorData={indicatorData}
                activePeriod={activePeriod} user={user} />
        );
        if (nav === "peer-feedback") return <PeerFeedbackPage user={user} />;
        if (nav === "members") return (
            <TeamView activities={activities} activityGroups={activityGroups} positions={positions}
                indicatorDefs={indicatorDefs} indicatorData={indicatorData} activePeriod={activePeriod}
                members={companyMembers} title="اعضای شرکت" emptyMessage="هنوز عضوی در شرکت تعریف نشده"
                enableOrgFilters />
        );
        if (nav === "goals") return <IndividualGoalsPage user={user} />;
        if (nav === "help") return <CviHelpCenter />;

        if (nav === "profile") return <PersonProfile user={user} />;
    };

    const sidebarItems = organizePerformanceNavigation([
        { id: "company", label: "داشبورد کل شرکت", icon: "company" },
        { id: "goals", label: "اهداف فردی", icon: "target" },
        { id: "inbox", label: "صندوق بررسی", icon: "inbox", badge: pendingCount + indicatorReviewCount },
        { id: "routine", label: "ارزیابی وظایف مستمر", icon: "routine" },
        { id: "my-indicators", label: "شاخص‌های من", icon: "target" },
        { id: "history", label: "تاریخچه", icon: "history" },
        { id: "peer-feedback", label: "بازخورد همکاران", icon: "peer", badge: peerPending },
        { id: "members", label: "اعضای شرکت", icon: "members" },
        { id: "help", label: "راهنما", icon: "help", section: "account" },
        { id: "profile", label: "پروفایل", icon: "profile" },
    ]);
    const bottomItems = [
        ...sidebarItems,
    ];

    return (
        <CviAppShell
            isMobile={isMobile}
            header={<TopBar searchItems={sidebarItems} onSearch={setNav} right={<UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />} />}
            periodContext={<PeriodContextBar period={activePeriod} daysLeft={daysRemainingInPeriod(activePeriod)} />}
            sidebar={<Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
            bottomNav={<AppBottomNav items={bottomItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        >
            <div className="cvi-role-content">
                <TabTransitionContent transitionKey={nav}>{renderContent()}</TabTransitionContent>
            </div>
        </CviAppShell>
    );
}

/* ════════════════════════════════════════════════════════════════════════
   پنل ادمین
   ════════════════════════════════════════════════════════════════════════ */

// کامپوننت عمومی برای CRUD روی یک لیست ساده (افزودن فرم بالا + ویرایش/حذف درون‌خطی)
function EditableList({ title, items, fields, onAdd, onUpdate, onDelete, validate, onView, compactTable = false }) {
    const { confirmAction } = useUx();
    const emptyForm = () => {
        const f = {};
        fields.forEach(fd => { f[fd.key] = ""; });
        return f;
    };
    const [newForm, setNewForm] = useState(emptyForm());
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [addError, setAddError] = useState("");
    const [editError, setEditError] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);

    const fieldOptions = (fd, form) => typeof fd.options === "function" ? fd.options(form) : (fd.options || []);

    const commonStyle = {
        width: "100%", padding: "6px 8px", border: `1.5px solid ${C.borderMd}`,
        borderRadius: 6, fontSize: 12, outline: "none", background: "#fff",
        fontFamily: "inherit", direction: "inherit", boxSizing: "border-box"
    };

    const renderField = (fd, form, setForm) => {
        if (fd.type === "icon") {
            const opts = fieldOptions(fd, form);
            return <select value={form[fd.key] || "activity"} onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))} style={commonStyle}>{opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
        }
        if (fd.type === "select") {
            const opts = fieldOptions(fd, form);
            return (
                <select value={form[fd.key] || ""} onChange={e => setForm(f => ({
                    ...f, [fd.key]: e.target.value,
                    ...(fd.clearsFields ? Object.fromEntries(fd.clearsFields.map(k => [k, ""])) : {})
                }))}
                    style={commonStyle}>
                    <option value="">{fd.placeholder || "انتخاب کنید"}</option>
                    {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            );
        }
        if (fd.type === "number") {
            return (
                <input type="number" value={form[fd.key] ?? ""}
                    onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))}
                    placeholder={fd.placeholder} style={commonStyle} />
            );
        }
        if (fd.type === "password") {
            return (
                <input type="password" value={form[fd.key] ?? ""}
                    onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))}
                    placeholder={fd.placeholder} style={commonStyle} />
            );
        }
        return (
            <input type="text" value={form[fd.key] || ""}
                onChange={e => setForm(f => ({ ...f, [fd.key]: e.target.value }))}
                placeholder={fd.placeholder} style={commonStyle} />
        );
    };

    const handleAdd = async () => {
        const err = validate ? validate(newForm) : null;
        if (err) { setAddError(err); return; }
        try {
            await onAdd(newForm);
            setNewForm(emptyForm());
            setAddError("");
            setShowAddForm(false);
        } catch (error) {
            setAddError(error.message || "ثبت مورد انجام نشد");
        }
    };
    const startEdit = (item) => { setEditingId(item.id); setEditForm({ ...item }); setEditError(""); setShowEditForm(true); };
    const cancelEdit = () => { setEditingId(null); setEditError(""); setShowEditForm(false); };
    const saveEdit = async () => {
        const err = validate ? validate(editForm) : null;
        if (err) { setEditError(err); return; }
        try {
            await onUpdate(editingId, editForm);
            setEditingId(null);
            setEditError("");
            setShowEditForm(false);
        } catch (error) {
            setEditError(error.message || "ویرایش مورد انجام نشد");
        }
    };

    const displayValue = (fd, item) => {
        let display = item[fd.key];
        if (fd.type === "icon") return <span style={{ display: "inline-flex", color: C.navy }}><UiIcon name={({ "📚":"activity", "📖":"activity", "🎯":"target", "💡":"performance", "🚀":"performance", "⭐":"performance", "🤝":"team", "👥":"team" }[String(item[fd.key] || "")] || String(item[fd.key] || "activity"))} size={17} /></span>;
        if (fd.type === "select") {
            const opts = fieldOptions(fd, item);
            const matched = opts.find(o => String(o.value) === String(item[fd.key]));
            if (matched) display = matched.label;
            else if (fd.key === "weight") {
                const fallbackImportance = { "5": "بسیار زیاد", "4": "زیاد", "3": "متوسط", "2": "کم", "1": "بسیار کم" };
                display = fallbackImportance[String(item[fd.key])] || "—";
            } else display = display || "—";
        }
        if (fd.suffix && display !== "" && display != null) return <span dir="ltr" style={{unicodeBidi:"isolate"}}>{display}{fd.suffix}</span>;
        return display === "" || display == null ? "—" : display;
    };

    const visibleCols = fields.filter(fd => fd.type !== "password" && fd.tableVisible !== false);
    const thStyle = {
        padding: "10px 12px", fontSize: 11, fontWeight: 600, color: C.muted,
        textAlign: "right", whiteSpace: "nowrap", borderBottom: `1.5px solid ${C.border}`, background: "#FAFAFB"
    };
    const tdStyle = { padding: "9px 12px", fontSize: 12, verticalAlign: "middle" };

    return (
        <div>
            {title && <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>{title}</div>}

            <Card style={{ overflowX: compactTable ? "hidden" : "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: compactTable ? 0 : 720 }}>
                    <colgroup>
                        <col style={{ width: compactTable ? 38 : 46 }} />
                        {visibleCols.map((fd, index) => <col key={fd.key} style={{
                            width: compactTable
                                ? (index === 0 ? "32%" : (fd.key === "role" ? "15%" : (fd.key === "posId" ? "20%" : (fd.key === "orgUnitId" ? "21%" : "auto"))))
                                : (index === 0 ? "auto" : (fd.computed ? 150 : (fd.type === "select" ? 180 : 150)))
                        }} />)}
                        <col style={{ width: compactTable ? 132 : 120 }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, textAlign: "center" }}>#</th>
                            {visibleCols.map((fd, index) => <th key={fd.key} style={{ ...thStyle, textAlign: index === 0 ? "right" : "center" }}>{fd.label}</th>)}
                            <th style={{ ...thStyle, textAlign: "center" }}>عملیات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={visibleCols.length + 2}
                                style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12 }}>
                                <EmptyState title="هنوز موردی ثبت نشده" description="برای شروع، اولین مورد را ایجاد کنید." icon="activity" />
                            </td></tr>
                        )}
                        {items.map((item, i) => (
                            <tr key={item.id} style={{ borderBottom: i < items.length - 1 ? `1px solid ${C.bg}` : "none" }}>
                                <td style={{ ...tdStyle, textAlign: "center", color: C.hint }}>{i + 1}</td>
                                {visibleCols.map((fd, ci) => (
                                    <td key={fd.key} style={{
                                        ...tdStyle,
                                        fontWeight: ci === 0 ? 500 : (fd.type === "select" ? 500 : 400),
                                        textAlign: fd.computed || fd.type === "select" ? "center" : "right",
                                        color: fd.computed ? C.hint : (fd.type === "select" ? C.muted : "inherit"),
                                        fontSize: fd.computed ? 10.5 : 12
                                    }}>
                                        {displayValue(fd, item)}
                                    </td>
                                ))}
                                <td style={{ ...tdStyle, textAlign: "center", whiteSpace: "nowrap" }}>
                                    {onView && <button onClick={() => onView(item)} title="مشاهده" style={{padding:"5px 9px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,cursor:"pointer",marginLeft:4}}><UiIcon name="view" size={15} /></button>}
                                    <button onClick={() => startEdit(item)} title="ویرایش" style={{padding:"5px 9px",background:"#fff",color:C.navy,border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,cursor:"pointer",marginLeft:4}}><UiIcon name="edit" size={15} /></button>
                                    <button onClick={() => confirmAction({title:"غیرفعال‌سازی مورد",message:"این مورد از فهرست فعال خارج می‌شود. سوابق قبلی حفظ خواهند شد.",confirmLabel:"غیرفعال‌سازی",tone:"danger",onConfirm:()=>onDelete(item.id)})} title="غیرفعال‌سازی" style={{padding:"5px 9px",background:C.redLt,color:C.red,border:`1px solid ${C.red}`,borderRadius:6,fontSize:11,cursor:"pointer"}}><UiIcon name="archive" size={15} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>
            {editError && <div style={{ color: C.red, fontSize: 11, marginTop: 8 }}>{editError}</div>}

            <div className="cvi-admin-list-actions"><button type="button" className="cvi-primary-button" onClick={() => { setAddError(""); setShowAddForm(true); }}>افزودن مورد جدید</button></div>
            {showEditForm && <div className="cvi-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) cancelEdit(); }}>
                <div className="cvi-dialog cvi-admin-form-dialog" role="dialog" aria-modal="true">
                    <div className="cvi-dialog-head"><div><h3>ویرایش {title || "مورد"}</h3><p>اطلاعات را اصلاح و ذخیره کنید.</p></div><button type="button" className="cvi-dialog-close" onClick={cancelEdit} aria-label="بستن">×</button></div>
                    <div className="cvi-admin-form-grid">{fields.map(fd => !fd.computed && (!fd.visible || fd.visible(editForm)) && <div key={fd.key} className="cvi-admin-form-field"><label>{fd.label}</label>{renderField(fd, editForm, setEditForm)}</div>)}</div>
                    {editError && <div className="cvi-form-error">{editError}</div>}
                    <div className="cvi-dialog-actions"><button type="button" className="cvi-button-secondary" onClick={cancelEdit}>انصراف</button><button type="button" className="cvi-button-primary" onClick={saveEdit}>ذخیره تغییرات</button></div>
                </div>
            </div>}
            {showAddForm && <div className="cvi-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setShowAddForm(false); }}>
                <div className="cvi-dialog cvi-admin-form-dialog" role="dialog" aria-modal="true">
                    <div className="cvi-dialog-head"><div><h3>{title || "افزودن مورد جدید"}</h3><p>اطلاعات مورد جدید را تکمیل کنید.</p></div><button type="button" className="cvi-dialog-close" onClick={() => setShowAddForm(false)} aria-label="بستن">×</button></div>
                    <div className="cvi-admin-form-grid">{fields.map(fd => !fd.computed && (!fd.visible || fd.visible(newForm)) && <div key={fd.key} className="cvi-admin-form-field"><label>{fd.label}</label>{renderField(fd, newForm, setNewForm)}</div>)}</div>
                    {addError && <div className="cvi-form-error">{addError}</div>}
                    <div className="cvi-dialog-actions"><button type="button" className="cvi-button-secondary" onClick={() => setShowAddForm(false)}>انصراف</button><button type="button" className="cvi-button-primary" onClick={handleAdd}>افزودن</button></div>
                </div>
            </div>}
        </div>
    );
}

function AdminOverview({ setNav }) {
    const { departments, teams, orgUnits, users, positions, indicatorDefs, indicatorData, activePeriod,
        routineQuestions, deptRoutineQuestions, orgRoutineQuestions, orgSettings } = useData();

    const posWeightOk = positions.length > 0 && positions.every(p => (Number(p.wTeam) + Number(p.wDept) + Number(p.wOrg)) === 100 &&
        (Number(p.wTasksActivities ?? 50) + Number(p.wGoals ?? 30) + Number(p.wPeerFeedback ?? 20)) === 100 &&
        (Number(p.wRoutine ?? 60) + Number(p.wNonRoutine ?? 40)) === 100);
    const targetsSet = activePeriod ? indicatorDefs.filter(def => {
        const d = indicatorData.find(x => x.periodId === activePeriod.id && x.indicatorId === def.id);
        return d && d.target != null;
    }).length : 0;
    const actualsSet = activePeriod ? indicatorDefs.filter(def => {
        const d = indicatorData.find(x => x.periodId === activePeriod.id && x.indicatorId === def.id);
        return d && d.actual != null && ["approved", "locked"].includes(d.status);
    }).length : 0;
    const activeOrgUnits = orgUnits.filter(unit => unit.isActive !== false);
    const unitsWithRoutineOk = activeOrgUnits.filter(unit => {
        const sum = routineQuestions.filter(q => q.scope === "org_unit" && q.orgUnitId === unit.id)
            .reduce((total, q) => total + (Number(q.weight) || 0), 0);
        return sum === 100;
    }).length;
    const rootOrgUnits = activeOrgUnits.filter(unit => !unit.parentId);
    const checks = [
        { ok: activeOrgUnits.length > 0, label: "ساختار سازمانی", detail: `${activeOrgUnits.length} واحد · ${rootOrgUnits.length} واحد سطح اول`, go: "org" },
        { ok: users.length > 0, label: "افراد و دسترسی‌ها", detail: `${users.length} نفر ثبت‌شده`, go: "people" },
        { ok: posWeightOk, label: "سمت‌های شغلی", detail: posWeightOk ? `${positions.length} سمت شغلی آماده` : "تنظیمات یک یا چند سمت شغلی نیازمند اصلاح است", go: "org" },
        { ok: activeOrgUnits.length > 0 && unitsWithRoutineOk === activeOrgUnits.length, label: "وظایف مستمر واحدها", detail: `${unitsWithRoutineOk} از ${activeOrgUnits.length} واحد آماده`, go: "individual-performance" },
        { ok: indicatorDefs.length > 0, label: "شاخص‌های مشارکت جمعی", detail: `${indicatorDefs.length} شاخص فعال`, go: "collective-contribution" },
        { ok: !!activePeriod && indicatorDefs.length > 0 && targetsSet === indicatorDefs.length, label: "هدف‌گذاری دوره", detail: !activePeriod ? "دوره‌ای فعال نیست" : `${targetsSet} از ${indicatorDefs.length} شاخص هدف‌گذاری شده`, go: "cycle-management" },
        { ok: !!activePeriod && indicatorDefs.length > 0 && actualsSet === indicatorDefs.length, label: "ثبت عملکرد دوره", detail: !activePeriod ? "دوره‌ای فعال نیست" : `${actualsSet} از ${indicatorDefs.length} شاخص تأیید شده`, go: "cycle-management" },
    ];
    const readyCount = checks.filter(item => item.ok).length;
    const readiness = Math.round((readyCount / checks.length) * 100);
    const attention = checks.filter(item => !item.ok);
    const activeMembers = users.filter(user => user.isActive !== false).length;
    const submittedIndicators = activePeriod ? indicatorData.filter(item => item.periodId === activePeriod.id && item.status === "submitted").length : 0;
    const daysLeft = daysRemainingInPeriod(activePeriod);

    return <div className="cvi-admin-overview">
        <DashboardPageIntro title="مدیریت CVI" subtitle="وضعیت کلی سیستم، آمادگی دوره و موارد نیازمند اقدام" period={activePeriod?.label || "بدون دوره فعال"} attentionCount={attention.length} onAttentionClick={() => { const item = attention[0]; if (item) setNav(item.go); }} />
        <div className="cvi-dashboard-kpi-grid executive">
            <DashboardMetricCard label="آمادگی سیستم" value={`${readiness}٪`} helper={`${readyCount} از ${checks.length} بخش آماده`} tone={readiness === 100 ? "teal" : "amber"} icon={<UiIcon name="check" size={16} />} />
            <DashboardMetricCard label="اعضای فعال" value={activeMembers} helper={`${activeOrgUnits.length} واحد سازمانی`} tone="navy" icon={<UiIcon name="people" size={16} />} onClick={() => setNav("people")} />
            <DashboardMetricCard label="وضعیت دوره جاری" value={activePeriod ? (daysLeft > 0 ? `${daysLeft} روز` : "پایان") : "—"} helper={activePeriod ? `${actualsSet} از ${indicatorDefs.length} شاخص تأیید شده` : "یک دوره را فعال کنید"} tone="purple" icon={<UiIcon name="planning" size={16} />} onClick={() => setNav("cycle-management")} />
            <DashboardMetricCard label="نیازمند اقدام" value={attention.length + submittedIndicators} helper={submittedIndicators ? `${submittedIndicators} شاخص در انتظار بررسی` : "بر اساس تنظیمات و دوره جاری"} tone={(attention.length + submittedIndicators) ? "red" : "teal"} icon={<UiIcon name={(attention.length + submittedIndicators) ? "warning" : "check"} size={16} />} />
        </div>

        <div className="cvi-admin-overview-grid">
            <DashboardSection title="نیازمند توجه شما">
                <div className="cvi-admin-action-list">
                    {attention.length === 0 && submittedIndicators === 0 ? <EmptyState title="همه چیز آماده است" description="در حال حاضر مورد پیکربندی ناقصی وجود ندارد." icon="check" /> : <>
                        {attention.slice(0, 5).map(item => <button type="button" key={item.label} className="cvi-admin-action-row" onClick={() => setNav(item.go)}>
                            <span className="cvi-admin-action-icon"><UiIcon name="warning" size={16} /></span><span><strong>{item.label}</strong><small>{item.detail}</small></span><span className="cvi-admin-action-go">بررسی</span>
                        </button>)}
                        {submittedIndicators > 0 && <button type="button" className="cvi-admin-action-row" onClick={() => setNav("cycle-management")}><span className="cvi-admin-action-icon"><UiIcon name="target" size={16} /></span><span><strong>شاخص‌های در انتظار اقدام</strong><small>{submittedIndicators} مقدار برای پیگیری دوره جاری</small></span><span className="cvi-admin-action-go">مشاهده</span></button>}
                    </>}
                </div>
            </DashboardSection>

            <DashboardSection title="وضعیت پیکربندی سیستم">
                <div className="cvi-readiness-head"><span>آمادگی برای اجرای کامل دوره</span><strong>{readiness}٪</strong></div>
                <div className="cvi-readiness-track"><span style={{ width: `${readiness}%` }} /></div>
                <div className="cvi-admin-check-grid">
                    {checks.map(item => <button key={item.label} type="button" onClick={() => setNav(item.go)} className={`cvi-admin-check${item.ok ? " is-ready" : " is-pending"}`}>
                        <UiIcon name={item.ok ? "check" : "warning"} size={15} /><span><strong>{item.label}</strong><small>{item.detail}</small></span>
                    </button>)}
                </div>
            </DashboardSection>
        </div>
    </div>;
}

function AdminPeople() {
    const { users, positions, orgUnits, createUserApi, updateUserApi, archiveUserApi } = useData();

    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [unitFilter, setUnitFilter] = useState("");
    const [viewUser, setViewUser] = useState(null);

    const positionOptions = positions.map(p => ({ value: p.id, label: p.label }));
    const orgUnitOptions = orgUnits
        .filter(unit => unit.isActive !== false)
        .map(unit => ({ value: unit.id, label: unit.label }));
    const roleOptions = [
        { value: "specialist", label: "کارشناس" }, { value: "manager", label: "مدیر" },
        { value: "deputy", label: "معاون" }, { value: "ceo", label: "مدیرعامل" }, { value: "admin", label: "ادمین" },
    ];

    const needsOrgUnit = (f) => ["specialist", "manager", "deputy"].includes(f.role);
    const needsManager = (f) => ["specialist", "manager", "deputy"].includes(f.role);
    const managerOptionsFor = (f) => {
        if (f.role === "specialist") {
            return users
                .filter(u => u.role === "manager" && (!f.orgUnitId || u.orgUnitId === f.orgUnitId) && u.id !== f.id)
                .map(u => ({ value: u.id, label: `${u.name}${u.orgUnit?.name ? ` · ${u.orgUnit.name}` : ""}` }));
        }
        if (f.role === "manager") {
            return users.filter(u => u.role === "deputy" && u.id !== f.id)
                .map(u => ({ value: u.id, label: `${u.name}${u.orgUnit?.name ? ` · ${u.orgUnit.name}` : ""}` }));
        }
        if (f.role === "deputy") {
            return users.filter(u => u.role === "ceo" && u.id !== f.id)
                .map(u => ({ value: u.id, label: u.name }));
        }
        return [];
    };

    const fields = [
        { key: "name", label: "نام و نام‌خانوادگی", placeholder: "مثال: علی رضایی" },
        { key: "employeeCode", label: "کد پرسنلی", placeholder: "1001", tableVisible: false },
        { key: "nationalId", label: "کد ملی", placeholder: "0011223344", tableVisible: false },
        { key: "email", label: "ایمیل", placeholder: "name@company.com", tableVisible: false },
        { key: "username", label: "نام کاربری", placeholder: "ali.r", tableVisible: false },
        { key: "password", label: "رمز عبور", type: "password", placeholder: "حداقل ۶ کاراکتر" },
        {
            key: "role", label: "سطح دسترسی", type: "select", options: roleOptions, placeholder: "انتخاب سطح دسترسی",
            clearsFields: ["orgUnitId", "managerId"]
        },
        { key: "posId", label: "سمت شغلی", type: "select", options: positionOptions, placeholder: "انتخاب سمت شغلی" },
        {
            key: "orgUnitId", label: "واحد سازمانی", type: "select", options: orgUnitOptions,
            placeholder: "انتخاب واحد سازمانی", visible: needsOrgUnit, clearsFields: ["managerId"]
        },
        {
            key: "managerId", label: "مدیر مستقیم", type: "select", tableVisible: false,
            options: managerOptionsFor, placeholder: "انتخاب مدیر مستقیم", visible: needsManager
        },
    ];

    const roleLabel = role => roleOptions.find(option => option.value === role)?.label || "—";
    const positionLabel = id => positionOptions.find(option => option.value === id)?.label || "—";
    const unitLabel = id => orgUnitOptions.find(option => option.value === id)?.label || "—";
    const managerLabel = id => users.find(user => user.id === id)?.name || "—";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredUsers = users.filter(user => {
        const matchesSearch = !normalizedSearch || [user.name, user.email, user.username, user.employeeCode, user.nationalId]
            .some(value => String(value || "").toLowerCase().includes(normalizedSearch));
        return matchesSearch && (!roleFilter || user.role === roleFilter) && (!unitFilter || user.orgUnitId === unitFilter);
    });

    const normalizeUserForm = (f) => {
        const role = String(f.role || "").toLowerCase();
        if (["admin", "ceo"].includes(role)) {
            return { ...f, orgUnitId: null, deptId: null, teamId: null, managerId: null };
        }
        return {
            ...f,
            orgUnitId: f.orgUnitId || null,
            deptId: null,
            teamId: null,
            managerId: f.managerId || null,
        };
    };

    return (
        <div>
            <DashboardPageIntro
                title="افراد و دسترسی‌ها"
                subtitle="کاربران را به سمت شغلی، واحد سازمانی و مدیر مستقیم متصل کنید."
            />
            <div style={{ marginBottom: 12, padding: "0 2px", fontSize: 10.5, color: C.muted, lineHeight: 1.8 }}>
                نمای اصلی خلاصه است؛ جست‌وجو روی نام، ایمیل، نام کاربری، کد پرسنلی و کد ملی انجام می‌شود.
            </div>
            <Card style={{marginBottom:12}}><div style={{padding:12,display:"grid",gridTemplateColumns:"minmax(260px,1fr) 180px 220px auto",gap:8,alignItems:"center"}}>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="جست‌وجوی نام، ایمیل، نام کاربری، کد پرسنلی یا کد ملی…" style={{width:"100%",padding:"9px 11px",border:`1px solid ${C.borderMd}`,borderRadius:8,fontFamily:"inherit",boxSizing:"border-box"}} />
                <select value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} style={{padding:9,border:`1px solid ${C.borderMd}`,borderRadius:8,background:"#fff",fontFamily:"inherit"}}><option value="">همه سطوح دسترسی</option>{roleOptions.map(option=><option key={option.value} value={option.value}>{option.label}</option>)}</select>
                <select value={unitFilter} onChange={e=>setUnitFilter(e.target.value)} style={{padding:9,border:`1px solid ${C.borderMd}`,borderRadius:8,background:"#fff",fontFamily:"inherit"}}><option value="">همه واحدهای سازمانی</option>{orgUnitOptions.map(option=><option key={option.value} value={option.value}>{option.label}</option>)}</select>
                {(search||roleFilter||unitFilter) && <button type="button" className="cvi-button-secondary" onClick={()=>{setSearch("");setRoleFilter("");setUnitFilter("");}}>پاک کردن</button>}
            </div></Card>
            <div style={{fontSize:10.5,color:C.hint,margin:"0 2px 8px"}}>{filteredUsers.length} نفر نمایش داده می‌شود</div>
            <EditableList items={filteredUsers} fields={fields} onView={setViewUser} compactTable
                onAdd={async f => {
                    try { await createUserApi(normalizeUserForm(f)); uxNotify("کاربر با موفقیت ثبت شد", "success"); }
                    catch (error) { uxNotify(error.message || "ثبت کاربر انجام نشد"); }
                }}
                onUpdate={async (id, f) => {
                    try { await updateUserApi(id, normalizeUserForm(f)); uxNotify("اطلاعات کاربر ذخیره شد", "success"); }
                    catch (error) { uxNotify(error.message || "ویرایش کاربر انجام نشد"); }
                }}
                onDelete={async id => {
                    try { await archiveUserApi(id); uxNotify("کاربر غیرفعال شد", "success"); }
                    catch (error) { uxNotify(error.message || "حذف کاربر انجام نشد"); }
                }}
                validate={f => {
                    if (!f.name?.trim()) return "نام الزامی است";
                    if (!f.employeeCode?.trim()) return "کد پرسنلی الزامی است";
                    if (!f.nationalId?.trim()) return "کد ملی الزامی است";
                    if (!/^\d{10}$/.test(f.nationalId.trim())) return "کد ملی باید ۱۰ رقم باشد";
                    if (!f.email?.trim()) return "ایمیل الزامی است";
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return "ایمیل معتبر نیست";
                    if (!f.username?.trim()) return "نام کاربری الزامی است";
                    if (!f.id && (!f.password?.trim() || f.password.trim().length < 6)) return "رمز عبور باید حداقل ۶ کاراکتر باشد";
                    if (f.password?.trim() && f.password.trim().length < 6) return "رمز عبور باید حداقل ۶ کاراکتر باشد";
                    if (!f.role) return "سطح دسترسی الزامی است";
                    if (!f.posId && !["admin"].includes(f.role)) return "انتخاب سمت شغلی الزامی است";
                    if (needsOrgUnit(f) && !f.orgUnitId) return "انتخاب واحد سازمانی الزامی است";
                    if (needsManager(f) && !f.managerId) return "انتخاب مدیر مستقیم الزامی است";
                    return null;
                }} />
            <div style={{marginTop:8,fontSize:10.5,color:C.muted}}>برای مشاهده اطلاعات کامل، روی نام فرد کلیک کنید یا از گزینه مشاهده استفاده کنید.</div>
            {viewUser && <div className="cvi-dialog-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setViewUser(null);}}>
                <div className="cvi-dialog cvi-admin-form-dialog" role="dialog" aria-modal="true">
                    <div className="cvi-dialog-head"><div><h3>{viewUser.name}</h3><p>اطلاعات فرد و دسترسی سازمانی</p></div><button type="button" className="cvi-dialog-close" onClick={()=>setViewUser(null)} aria-label="بستن">×</button></div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10,padding:"4px 0"}}>
                        {[
                            ["ایمیل",viewUser.email||"—"],["نام کاربری",viewUser.username||"—"],["کد پرسنلی",viewUser.employeeCode||"—"],["کد ملی",viewUser.nationalId||"—"],
                            ["سطح دسترسی",roleLabel(viewUser.role)],["سمت شغلی",positionLabel(viewUser.posId)],["واحد سازمانی",unitLabel(viewUser.orgUnitId)],["مدیر مستقیم",managerLabel(viewUser.managerId)]
                        ].map(([label,value])=><div key={label} style={{padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8}}><div style={{fontSize:9.5,color:C.hint,marginBottom:4}}>{label}</div><div style={{fontSize:11.5,fontWeight:500,overflowWrap:"anywhere"}}>{value}</div></div>)}
                    </div>
                    <div className="cvi-dialog-actions"><button type="button" className="cvi-button-secondary" onClick={()=>setViewUser(null)}>بستن</button></div>
                </div>
            </div>}
        </div>
    );
}

// Period configuration policy: reusable organizational defaults are carried forward into a DRAFT period,
// reviewed before activation, and never rewrite locked historical periods.
function PeriodEvaluationConfigurationInfo() {
    return <Card style={{ marginBottom: 16 }}><div style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <UiIcon name="settings" size={17} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>تنظیمات ارزیابی دوره</div>
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.9 }}>
            سؤالات وظایف مستمر، وزن‌های ارزیابی و تنظیمات پایه لازم نیست در ابتدای هر فصل از صفر تعریف شوند.
            برای هر دوره جدید، تنظیمات دوره قبل به‌عنوان مبنای پیشنهادی استفاده می‌شود و ادمین می‌تواند قبل از فعال‌سازی آن‌ها را بازبینی کند.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, marginTop: 12 }}>
            {[['تنظیمات پایدار','سؤال‌ها، وزن‌ها و ساختار ارزیابی'],['بازبینی هر دوره','امکان اصلاح نسخه دوره پیش از فعال‌سازی'],['داده‌های دوره‌ای','Target، مهلت‌ها و نتایج برای هر دوره جداگانه']].map(([title,text]) =>
                <div key={title} style={{ border: `1px solid ${C.border}`, borderRadius: 9, padding: 10, background: C.bg }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600 }}>{title}</div><div style={{ fontSize: 10.5, color: C.muted, marginTop: 3, lineHeight: 1.7 }}>{text}</div>
                </div>)}
        </div>
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: C.cyanBg, fontSize: 10.5, color: '#007A8C', lineHeight: 1.8 }}>
            دوره‌های قفل‌شده مستقل باقی می‌مانند و تغییر تنظیمات پایه، تاریخچه یا CVI نهایی آن‌ها را تغییر نمی‌دهد.
        </div>
    </div></Card>;
}

// ##PERIOD_CONFIG_UX_PLACEHOLDER##
function AdminKpiCalculationTemplates() {
    const [items,setItems]=useState([]);
    const [name,setName]=useState("");
    const [showEditor,setShowEditor]=useState(false);
    const [previewActual,setPreviewActual]=useState("90");
    const [previewTarget,setPreviewTarget]=useState("100");
    const [tolerance,setTolerance]=useState("50");
    const [rangeLower,setRangeLower]=useState("90");
    const [rangeUpper,setRangeUpper]=useState("110");
    const [rangeTolerance,setRangeTolerance]=useState("30");
    const [method,setMethod]=useState("HIGHER");
    const [points,setPoints]=useState("50:0, 70:50, 100:100, 120:120");
    const [expression,setExpression]=useState("100 * Actual / Target");
    const [busy,setBusy]=useState(false);
    const typeDetails={
        HIGHER:{label:"هرچه بیشتر، بهتر",formula:"(Actual / Target) × 100",detail:"مناسب فروش، درآمد و شاخص‌های افزایشی."},
        LOWER:{label:"هرچه کمتر، بهتر",formula:"(Target / Actual) × 100",detail:"مناسب هزینه، خطا و زمان تأخیر."},
        TARGET:{label:"نزدیکی به هدف",formula:"100 − |(Actual / Target × 100) − 100| × (100 / Tolerance)",detail:"مقدار هدف بیشترین امتیاز را می‌گیرد."},
        RANGE:{label:"بازه مطلوب",formula:"100٪ در بازه مطلوب؛ کاهش خطی در خارج بازه",detail:"برای شاخص‌هایی که محدوده مطلوب دارند."},
        THRESHOLD:{label:"سطوح عملکرد (آستانه‌ای)",formula:"درون‌یابی خطی بین نقاط عملکرد و امتیاز",detail:"حداقل، هدف و عملکرد فراتر از انتظار را خودتان تعریف کنید."},
        CUSTOM:{label:"فرمول اختصاصی",formula:"فرمول دلخواه با Actual و Target",detail:"از متغیرها و عملگرهای ریاضی مجاز استفاده کنید."}
    };
    const currentType=typeDetails[method];
    const inputStyle={width:"100%",boxSizing:"border-box",padding:"10px 12px",border:"1px solid #E4E4EC",borderRadius:8,fontFamily:"inherit",fontSize:12};
    const formLabel={display:"block",marginBottom:5,fontSize:11,fontWeight:500};
    const parsedPoints=()=>{
        const result=points.split(",").map(pair=>{
            const parts=pair.trim().split(":");
            if(parts.length!==2||parts.some(v=>!v.trim()))throw Error("نقاط عملکرد و امتیاز را کامل وارد کنید.");
            const values=parts.map(Number);
            if(!values.every(Number.isFinite))throw Error("همه مقادیر باید عدد معتبر باشند.");
            return {x:values[0],y:values[1]};
        });
        if(result.length<2||result.length>12||result.some((p,i)=>p.y<0||p.y>200||(i>0&&p.x<=result[i-1].x)))throw Error("حداقل دو نقطه با مقدار عملکرد صعودی و امتیاز ۰ تا ۲۰۰ وارد کنید.");
        return result;
    };
    const preview=(()=>{
        const a=Number(previewActual),t=Number(previewTarget);
        if(!previewActual.trim()||!previewTarget.trim()||!Number.isFinite(a)||!Number.isFinite(t)||a<0||t<=0)return null;
        const ratio=a/t*100;let score=null;
        if(method==="HIGHER")score=ratio;
        if(method==="LOWER")score=a===0?120:t/a*100;
        if(method==="TARGET"&&Number(tolerance)>0)score=100-Math.abs(ratio-100)*100/Number(tolerance);
        if(method==="RANGE"&&Number(rangeTolerance)>0&&Number(rangeLower)<=Number(rangeUpper))score=ratio<Number(rangeLower)?100-(Number(rangeLower)-ratio)*100/Number(rangeTolerance):ratio>Number(rangeUpper)?100-(ratio-Number(rangeUpper))*100/Number(rangeTolerance):100;
        if(method==="THRESHOLD"){try{const ps=parsedPoints();if(ratio<=ps[0].x)score=ps[0].y;else if(ratio>=ps[ps.length-1].x)score=ps[ps.length-1].y;else{for(let i=1;i<ps.length;i++)if(ratio<=ps[i].x){score=ps[i-1].y+(ratio-ps[i-1].x)*(ps[i].y-ps[i-1].y)/(ps[i].x-ps[i-1].x);break;}}}catch{return null;}}
        if(method==="CUSTOM")return null;
        return Number.isFinite(score)?Math.max(0,Math.min(120,score)):null;
    })();
    const request=async (path,options={})=>{
        const response=await fetch(`${API_BASE_URL}/api/indicators/calculation-templates${path}`,{
            ...options,headers:{"Content-Type":"application/json",Authorization:`Bearer ${sessionStorage.getItem("cvi_token")}`}
        });
        const data=await response.json().catch(()=>({}));
        if(!response.ok)throw new Error(data.message||"خطا در مدیریت الگوها");
        return data;
    };
    const reload=()=>request("").then(setItems).catch(e=>uxNotify(e.message,"error"));
    useEffect(()=>{reload();},[]);
    const create=async()=>{
        const effectiveName=name.trim()||currentType.label;
        let parameters={};
        try{
            if(method==="THRESHOLD"){
                parameters.points=parsedPoints();
            }
            if(method==="CUSTOM")parameters={expression};
            if(method==="TARGET")parameters={tolerance:Number(tolerance)};
            if(method==="RANGE")parameters={lower:Number(rangeLower),upper:Number(rangeUpper),tolerance:Number(rangeTolerance)};
            setBusy(true);
            await request("",{method:"POST",body:JSON.stringify({name:effectiveName,method,parameters})});
            setName("");setShowEditor(false);await reload();uxNotify("الگوی محاسبه ثبت شد","success");
        }catch(e){uxNotify(e.message,"error");}finally{setBusy(false);}
    };
    const toggle=async(item)=>{
        try{await request(`/${item.id}`,{method:"PATCH",body:JSON.stringify({isActive:!item.isActive})});await reload();}
        catch(e){uxNotify(e.message,"error");}
    };
    const clone=item=>{
        setName(item.name+" - نسخه جدید");setMethod(item.method);setShowEditor(true);
        if(item.method==="THRESHOLD")setPoints((item.parameters?.points||[]).map(p=>`${p.x}:${p.y}`).join(", "));
        if(item.method==="CUSTOM")setExpression(item.parameters?.expression||"100 * Actual / Target");
        if(item.method==="TARGET")setTolerance(String(item.parameters?.tolerance??50));
        if(item.method==="RANGE"){setRangeLower(String(item.parameters?.lower??90));setRangeUpper(String(item.parameters?.upper??110));setRangeTolerance(String(item.parameters?.tolerance??30));}
    };
    return <Card><div style={{padding:16}}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:6}}>الگوهای محاسبه درصد تحقق شاخص‌ها</div>
        <div style={{fontSize:11,color:C.muted,lineHeight:1.9,marginBottom:12}}>فهرست الگوهای محاسبه از قبل آماده است. مدیر می‌تواند الگوی جدید اضافه کند، الگوها را فعال یا غیرفعال کند و از هر الگو نسخه مشابه بسازد. هنگام تعریف شاخص فقط یکی از الگوهای فعال انتخاب می‌شود.</div>
        <div style={{border:"1px solid #E8E8EE",borderRadius:10,overflow:"hidden"}}>
            {items.map(item=><div key={item.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10,padding:"11px 12px",borderBottom:"1px solid #E8E8EE"}}>
                <div style={{minWidth:180,flex:1}}><div style={{fontSize:12,fontWeight:600}}>{item.name}</div><div style={{fontSize:10,color:C.muted,marginTop:4}}>{typeDetails[item.method]?.formula||item.method} · {item.isActive?"فعال":"غیرفعال"}</div></div>
                <div style={{display:"flex",gap:6}}><button type="button" className="cvi-button-secondary" onClick={()=>clone(item)}>ساخت نسخه مشابه</button><button type="button" className="cvi-button-secondary" onClick={()=>toggle(item)}>{item.isActive?"غیرفعال کردن":"فعال کردن"}</button></div>
            </div>)}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:18,marginBottom:12}}>
            <div style={{fontWeight:600,fontSize:13}}>تعریف الگوی جدید</div>
            <button type="button" className="cvi-button-secondary" onClick={()=>setShowEditor(v=>!v)}>{showEditor?"بستن فرم":"＋ الگوی جدید"}</button>
        </div>
        {showEditor&&<div style={{border:"1px solid #E8E8EE",borderRadius:12,padding:14}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
                <label><span style={formLabel}>نام الگو</span><input value={name} onChange={e=>setName(e.target.value)} placeholder={currentType.label} style={inputStyle}/><span style={{fontSize:10,color:C.muted}}>در صورت خالی‌بودن، عنوان روش محاسبه انتخاب می‌شود.</span></label>
                <label><span style={formLabel}>روش محاسبه</span><select value={method} onChange={e=>{setMethod(e.target.value);setName("");}} style={inputStyle}>{Object.entries(typeDetails).map(([id,t])=><option key={id} value={id}>{t.label}</option>)}</select></label>
            </div>
            <div style={{padding:13,marginTop:14,background:"#F8F9FC",border:"1px solid #E8E8EE",borderRadius:10}}>
                <div style={{fontSize:12,fontWeight:600,marginBottom:7}}>فرمول محاسبه</div>
                <div dir="ltr" style={{textAlign:"center",fontFamily:"Consolas,monospace",fontWeight:600,fontSize:15,background:"#fff",borderRadius:8,padding:12,border:"1px solid #E8E8EE",overflowX:"auto"}}>{currentType.formula}</div>
                <div style={{marginTop:8,color:C.muted,fontSize:11,lineHeight:1.9}}>{currentType.detail}</div>
                {method==="CUSTOM"&&<label style={{display:"block",marginTop:12}}><span style={formLabel}>فرمول اختصاصی (خروجی درصد تحقق)</span><textarea dir="ltr" rows={2} value={expression} onChange={e=>setExpression(e.target.value)} spellCheck={false} style={{...inputStyle,fontFamily:"Consolas,monospace",resize:"vertical",fontSize:14}}/><span style={{fontSize:10,color:C.muted}}>برای نمونه: 100 * Actual / Target</span></label>}
                {method==="THRESHOLD"&&<div style={{marginTop:12}}><div style={formLabel}>نقاط عملکرد (درصد نسبت به هدف ← امتیاز تحقق)</div>
                    <div style={{fontSize:10.5,color:C.muted,marginBottom:8}}>هر نقطه را به صورت «عملکرد:امتیاز» وارد کنید. برای نمونه 70:50 یعنی عملکرد ۷۰٪ امتیاز ۵۰٪ می‌گیرد.</div>
                    <input dir="ltr" value={points} onChange={e=>setPoints(e.target.value)} style={inputStyle} aria-label="نقاط عملکرد و امتیاز"/>
                    <div style={{fontSize:10,color:C.muted,marginTop:5}}>50:0, 70:50, 100:100, 120:120 — نقاط را به ترتیب عملکرد صعودی مرتب کنید؛ مقادیر میان نقاط خطی محاسبه می‌شوند.</div>
                </div>}
                {method==="TARGET"&&<label style={{display:"block",marginTop:12}}><span style={formLabel}>تلورانس (%)</span><input type="number" min="1" value={tolerance} onChange={e=>setTolerance(e.target.value)} style={{...inputStyle,maxWidth:200}}/></label>}
                {method==="RANGE"&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,marginTop:12}}>{[["کران پایین (%)",rangeLower,setRangeLower],["کران بالا (%)",rangeUpper,setRangeUpper],["تلورانس (%)",rangeTolerance,setRangeTolerance]].map(([label,value,set])=><label key={label}><span style={formLabel}>{label}</span><input type="number" value={value} onChange={e=>set(e.target.value)} style={inputStyle}/></label>)}</div>}
            </div>
            <div style={{padding:13,marginTop:14,border:"1px solid #E8E8EE",borderRadius:10}}>
                <div style={{fontSize:12,fontWeight:600,marginBottom:7}}>راهنما و مثال محاسبه</div>
                <div style={{fontSize:11,color:C.muted,lineHeight:2}}><b dir="ltr">Actual</b>: عملکرد واقعی ثبت‌شده. <b dir="ltr">Target</b>: مقدار هدف. در فرمول اختصاصی می‌توانید از اعداد، پرانتز و عملگرهای +، −، * و / استفاده کنید؛ اجرای کد و توابع دیگر مجاز نیست. امتیاز نهایی در محدوده ۰ تا ۱۲۰٪ قرار می‌گیرد.</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,marginTop:10}}>
                    <label><span style={formLabel}>Actual نمونه</span><input type="number" dir="ltr" value={previewActual} onChange={e=>setPreviewActual(e.target.value)} style={inputStyle}/></label>
                    <label><span style={formLabel}>Target نمونه</span><input type="number" dir="ltr" value={previewTarget} onChange={e=>setPreviewTarget(e.target.value)} style={inputStyle}/></label>
                    <div style={{background:C.cyanLt,borderRadius:9,padding:10,alignSelf:"end"}}><div style={{fontSize:10,color:C.muted}}>امتیاز نمونه</div><div dir="ltr" style={{fontSize:18,fontWeight:700,color:C.navy}}>{method==="CUSTOM"?"تأیید هنگام ذخیره":preview===null?"ورودی نامعتبر":preview.toFixed(1)+"%"}</div></div>
                </div>
                <div style={{fontSize:10.5,color:C.muted,lineHeight:2,marginTop:8}}>مثال فروش: Actual=90 و Target=100 در مدل افزایشی ← ۹۰٪. مثال هزینه با همان مقادیر در مدل کاهشی ← ۱۱۱٫۱٪. برای مدل آستانه‌ای می‌توانید امتیاز ۷۰٪ عملکرد را برابر ۵۰٪ تعیین کنید.</div>
                {method==="CUSTOM"&&<div style={{fontSize:10.5,color:C.amber,marginTop:6}}>پیش‌نمایش فرمول اختصاصی در مرورگر اجرا نمی‌شود؛ اعتبارسنجی هنگام ذخیره در سرور انجام می‌شود.</div>}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:12}}><button className="cvi-button-primary" disabled={busy} onClick={create}>{busy?"در حال ذخیره...":"ذخیره الگوی جدید"}</button></div>
        </div>}
    </div></Card>;
}
function AdminSettings() {
    const { activityGroups, impactLevels, routineLevels,
        createActivityGroupApi, updateActivityGroupApi, archiveActivityGroupApi,
        createImpactLevelApi, updateImpactLevelApi, archiveImpactLevelApi,
        createRoutineLevelApi, updateRoutineLevelApi, archiveRoutineLevelApi } = useData();
    const [tab, setTab] = useState("advanced-model");
    const [importanceLevels, setImportanceLevels] = useState([]);
    const settingsApiJson = async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}/api${path}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || "انجام عملیات ممکن نشد");
        return payload;
    };
    const loadImportanceLevels = async () => { try { setImportanceLevels(await settingsApiJson("/admin/templates/importance-scale/list")); } catch (error) { uxNotify(error.message || "دریافت مقیاس اهمیت انجام نشد"); } };
    useEffect(() => { loadImportanceLevels(); }, []);
    const importanceFields = [{ key: "label", label: "عنوان سطح" }, { key: "score", label: "ضریب", type: "number" }];
    const groupFields = [
        { key: "label", label: "نام گروه فعالیت" },
        { key: "maxScore", label: "سقف امتیاز", type: "number" },
        { key: "icon", label: "آیکون", type: "icon", options: [{ value: "activity", label: "فعالیت" }, { value: "target", label: "هدف" }, { value: "performance", label: "بهبود / عملکرد" }, { value: "team", label: "همکاری / تیم" }] },
        { key: "subsText", label: "زیرگروه‌ها (با کاما جدا کنید)", placeholder: "دوره تخصصی, دوره عمومی" },
    ];
    const impactFields = [{ key: "label", label: "عنوان سطح تأثیر" }, { key: "score", label: "امتیاز", type: "number" }];
    const routineLevelFields = [{ key: "label", label: "عنوان سطح" }, { key: "score", label: "امتیاز", type: "number" }];

    return <div>
        <DashboardPageIntro title="تنظیمات پیشرفته" />
        <ProfessionalTabs items={[
            { id: "advanced-model", label: "الگوهای ارزیابی", icon: <UiIcon name="settings" size={15} /> },
            { id: "kpi-formulas", label: "فرمول‌های تحقق شاخص", icon: <UiIcon name="target" size={15} /> },
            { id: "importance", label: "مقیاس اهمیت", icon: <UiIcon name="target" size={15} />, count: importanceLevels.length },
            { id: "groups", label: "دسته‌های فعالیت", icon: <UiIcon name="activity" size={15} />, count: activityGroups.length },
            { id: "impact", label: "سطوح تأثیر", icon: <UiIcon name="performance" size={15} />, count: impactLevels.length },
            { id: "routine", label: "سطوح وظایف مستمر", icon: <UiIcon name="routine" size={15} />, count: routineLevels.length },
        ]} value={tab} onChange={setTab} ariaLabel="بخش‌های تنظیمات پیشرفته" />
        <div style={{ display: tab === "advanced-model" ? "block" : "none" }} aria-hidden={tab !== "advanced-model"}>
            <FlexibleOrgPrototype initialTab="weights" visibleTabs={["weights"]} embedded />
        </div>
        {tab === "kpi-formulas" && <AdminKpiCalculationTemplates />}
        {tab === "importance" && <div><div style={{fontSize:11,color:C.muted,marginBottom:12}}>این مقیاس در وزن‌دهی کیفی CVI استفاده می‌شود. کاربر سطح اهمیت را انتخاب می‌کند و سیستم وزن عددی را محاسبه می‌کند.</div><EditableList items={importanceLevels} fields={importanceFields} onAdd={async f=>{const next=[...importanceLevels,{label:f.label,score:Number(f.score)}];try{setImportanceLevels(await settingsApiJson("/admin/templates/importance-scale",{method:"PUT",body:JSON.stringify({levels:next})}));}catch(error){uxNotify(error.message||"ذخیره مقیاس انجام نشد");}}} onUpdate={async(id,f)=>{const next=importanceLevels.map(x=>x.id===id?{...x,label:f.label,score:Number(f.score)}:x);try{setImportanceLevels(await settingsApiJson("/admin/templates/importance-scale",{method:"PUT",body:JSON.stringify({levels:next})}));}catch(error){uxNotify(error.message||"ذخیره مقیاس انجام نشد");}}} onDelete={async id=>{const next=importanceLevels.filter(x=>x.id!==id);try{setImportanceLevels(await settingsApiJson("/admin/templates/importance-scale",{method:"PUT",body:JSON.stringify({levels:next})}));}catch(error){uxNotify(error.message||"حذف سطح انجام نشد");}}} validate={f=>!f.label?.trim()?"عنوان الزامی است":!(Number(f.score)>0)?"ضریب باید بیشتر از صفر باشد":null}/></div>}
        {tab === "groups" && <EditableList
            items={activityGroups.map(g => ({ ...g, subsText: (g.subs || []).join(", ") }))}
            fields={groupFields}
            onAdd={async f => { try { await createActivityGroupApi(f); } catch (error) { uxNotify(error.message || "ثبت گروه فعالیت انجام نشد"); } }}
            onUpdate={async (id, f) => { try { await updateActivityGroupApi(id, f); } catch (error) { uxNotify(error.message || "ویرایش گروه فعالیت انجام نشد"); } }}
            onDelete={async id => { try { await archiveActivityGroupApi(id); } catch (error) { uxNotify(error.message || "حذف گروه فعالیت انجام نشد"); } }}
            validate={f => !f.label?.trim() ? "نام گروه الزامی است" : null} />}
        {tab === "impact" && <EditableList items={impactLevels} fields={impactFields}
            onAdd={async f => { try { await createImpactLevelApi(f); } catch (error) { uxNotify(error.message || "ثبت سطح تأثیر انجام نشد"); } }}
            onUpdate={async (id, f) => { try { await updateImpactLevelApi(id, f); } catch (error) { uxNotify(error.message || "ویرایش سطح تأثیر انجام نشد"); } }}
            onDelete={async id => { try { await archiveImpactLevelApi(id); } catch (error) { uxNotify(error.message || "حذف سطح تأثیر انجام نشد"); } }}
            validate={f => !f.label?.trim() ? "عنوان الزامی است" : null} />}
        {tab === "routine" && <div>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>سطوح کیفی ارزیابی وظایف مستمر</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.7 }}>برای پاسخ به هر سؤال وظایف مستمر یکی از این سطوح انتخاب می‌شود. حداکثر پنج سطح توصیه می‌شود.</div>
            <EditableList items={routineLevels} fields={routineLevelFields}
                onAdd={async f => { try { await createRoutineLevelApi(f); } catch (error) { uxNotify(error.message || "ثبت سطح وظایف مستمر انجام نشد"); } }}
                onUpdate={async (id, f) => { try { await updateRoutineLevelApi(id, f); } catch (error) { uxNotify(error.message || "ویرایش سطح وظایف مستمر انجام نشد"); } }}
                onDelete={async id => { try { await archiveRoutineLevelApi(id); } catch (error) { uxNotify(error.message || "حذف سطح وظایف مستمر انجام نشد"); } }}
                validate={f => !f.label?.trim() ? "عنوان الزامی است" : null} />
        </div>}
    </div>;
}

function AdminIndividualPerformance() {
    const [tab, setTab] = useState("routine");
    return <div>
        <DashboardPageIntro title="عملکرد فردی" subtitle="اجزای ارزیابی عملکرد فردی را از یک بخش مدیریت کنید." />
        <ProfessionalTabs items={[
            { id: "routine", label: "وظایف مستمر", icon: "routine" },
            { id: "peer", label: "بازخورد همکاران", icon: "peer" },
        ]} active={tab} value={tab} onChange={setTab} ariaLabel="بخش‌های عملکرد فردی" />
        <div style={{ display: tab === "routine" ? "block" : "none" }} aria-hidden={tab !== "routine"}><AdminRoutineQuestions /></div>
        <div style={{ display: tab === "peer" ? "block" : "none" }} aria-hidden={tab !== "peer"}><AdminPeerFeedback /></div>
    </div>;
}

function AdminCollectiveContribution() {
    const { indicatorDefs, orgUnits, users, scheduleTemplates, createIndicatorApi, updateIndicatorApi, archiveIndicatorApi } = useData();
    const [assessmentTemplates, setAssessmentTemplates] = useState([]);
    const [organizationAssessment, setOrganizationAssessment] = useState(null);
    const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [qualitativeQuestions, setQualitativeQuestions] = useState([]);
    const [qualitativeDraft, setQualitativeDraft] = useState({ text: "", importanceId: "" });
    const [qualitativeFormOpen, setQualitativeFormOpen] = useState(false);
    const [tab, setTab] = useState("indicators");
    const [scope, setScope] = useState("org");
    const [orgUnitId, setOrgUnitId] = useState("");
    const [editingId, setEditingId] = useState("");
    const [indicatorFormOpen, setIndicatorFormOpen] = useState(false);
    const [kpiCalculationTemplates,setKpiCalculationTemplates]=useState([]);
    const [importanceLevels, setImportanceLevels] = useState([]);
    const [importanceId, setImportanceId] = useState("");
    const activeUnits = orgUnits.filter(unit => unit.isActive !== false);
    const apiJson = async (url, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        if (!token) throw new Error("Authentication required.");
        const response = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        const data = await response.json().catch(() => ({}));
        if (response.status === 401) {
            sessionStorage.removeItem("cvi_user");
            sessionStorage.removeItem("cvi_token");
            window.dispatchEvent(new Event("cvi-session-expired"));
            throw new Error("Session expired.");
        }
        if (!response.ok) throw new Error(data.message || "انجام عملیات ممکن نشد.");
        return data;
    };
    useEffect(() => {
        Promise.all([apiJson("/api/admin/templates"), apiJson("/api/organization"), apiJson("/api/admin/templates/importance-scale/list")]).then(([templates, organization, importance]) => {
            setAssessmentTemplates(templates.assessment || []);
            setOrganizationAssessment(organization.participationIndicatorProfile || null);
            setImportanceLevels(importance || []);
            if (importance?.length) { const defaultImportance = importance[Math.floor(importance.length / 2)]?.id || importance[0].id; setImportanceId(defaultImportance); setQualitativeDraft(d => ({ ...d, importanceId: d.importanceId || defaultImportance })); }
        }).catch(error => uxNotify(error.message || "دریافت الگوهای ارزیابی انجام نشد.", "error"));
    }, []);
    useEffect(()=>{apiJson("/api/indicators/calculation-templates").then(setKpiCalculationTemplates).catch(()=>setKpiCalculationTemplates([]));},[indicatorFormOpen]);
    const selectedUnit = activeUnits.find(unit => unit.id === orgUnitId);
    const assignedTemplate = scope === "org"
        ? organizationAssessment
        : (selectedUnit?.participationIndicatorProfile || assessmentTemplates.find(t => t.id === selectedUnit?.participationIndicatorProfileId) || null);
    const defaultAssessmentTemplate = assessmentTemplates.find(t => t.isDefault === true || String(t.isDefault).toLowerCase() === "true") || null;
    const effectiveTemplate = assignedTemplate || defaultAssessmentTemplate || null;
    const hasKpi = !effectiveTemplate || Number(effectiveTemplate.kpiWeight) > 0;
    const hasQualitative = Boolean(effectiveTemplate && Number(effectiveTemplate.qualitativeWeight) > 0);
    useEffect(() => {
        if (scope !== "org" && !orgUnitId) { setQualitativeQuestions([]); return; }
        const params = new URLSearchParams(scope === "org" ? { level: "ORGANIZATION" } : { level: "ORG_UNIT", orgUnitId });
        apiJson(`/api/admin/templates/qualitative/questions/list?${params.toString()}`).then(setQualitativeQuestions).catch(() => setQualitativeQuestions([]));
    }, [scope, orgUnitId]);
    const changeAssessmentTemplate = async () => {
        if (!selectedTemplateId) return;
        try {
            const targetId = scope === "org" ? (organizationAssessment?.organizationId || null) : orgUnitId;
            let organizationId = targetId;
            if (scope === "org") {
                const organization = await apiJson("/api/organization");
                organizationId = organization.id;
            }
            const updated = await apiJson(`/api/admin/templates/units/${scope === "org" ? "organization" : "org-unit"}/${organizationId}/assessment-template`, { method: "PATCH", body: JSON.stringify({ participationIndicatorProfileId: selectedTemplateId }) });
            if (scope === "org") setOrganizationAssessment(updated.participationIndicatorProfile || assessmentTemplates.find(t => t.id === selectedTemplateId));
            else {
                const chosen = assessmentTemplates.find(t => t.id === selectedTemplateId);
                if (selectedUnit) { selectedUnit.participationIndicatorProfileId = selectedTemplateId; selectedUnit.participationIndicatorProfile = chosen; }
            }
            setTemplateEditorOpen(false); uxNotify("الگوی ارزیابی تغییر کرد.", "success");
        } catch (error) { uxNotify(error.message || "تغییر الگو انجام نشد.", "error"); }
    };
    const addQualitativeQuestion = async () => {
        const selectedImportance = importanceLevels.find(level => level.id === qualitativeDraft.importanceId);
        const score = Number(selectedImportance?.score || 0);
        if (!qualitativeDraft.text.trim() || score <= 0) { uxNotify("متن سؤال و سطح اهمیت را انتخاب کنید.", "error"); return; }
        try {
            const saved = await apiJson("/api/admin/templates/qualitative/questions", { method: "POST", body: JSON.stringify({ text: qualitativeDraft.text, weight: score, level: scope === "org" ? "ORGANIZATION" : "ORG_UNIT", orgUnitId: scope === "org_unit" ? orgUnitId : null, sortOrder: qualitativeQuestions.length + 1 }) });
            setQualitativeQuestions(v => [...v, saved]); setQualitativeDraft({ text: "", importanceId: qualitativeDraft.importanceId }); setQualitativeFormOpen(false); uxNotify("سؤال کیفی ثبت شد.", "success");
        } catch (error) { uxNotify(error.message || "ثبت سؤال انجام نشد.", "error"); }
    };
    const people = users.filter(user => user.isActive !== false && String(user.role).toLowerCase() !== "admin");
    const defaultPeople = {
        ownerId: people.find(user => String(user.role).toLowerCase() === "ceo")?.id || people[0]?.id || "",
        registrarId: people[0]?.id || "",
        approverId: people.find(user => String(user.role).toLowerCase() === "ceo")?.id || people[1]?.id || people[0]?.id || "",
    };
    const emptyForm = () => ({
        label: "", level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null, unit: "%", weight: 0,
        aggregationMethod: "latest", formulaTemplateId:"standard:higher", scheduleTemplateId: scheduleTemplates[0]?.id || "", requiresEvidence: false, ...defaultPeople,
    });
    const [form, setForm] = useState(emptyForm);
    useEffect(() => setForm(current => ({ ...current, level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null, scheduleTemplateId: current.scheduleTemplateId || scheduleTemplates[0]?.id || "" })), [scope, orgUnitId, scheduleTemplates.length]);
    const visibleIndicators = indicatorDefs.filter(item => scope === "org"
        ? item.level === "org"
        : item.level === "org_unit" && item.orgUnitId === orgUnitId);
    const weightSum = visibleIndicators.reduce((sum, item) => sum + Number(item.weight || 0), 0);
    const resetForm = () => { setEditingId(""); setIndicatorFormOpen(false); setForm(emptyForm()); };
    const startEdit = item => {
        setEditingId(item.id);
        setIndicatorFormOpen(true);
        setForm({ ...item, label: item.label || item.name || "", level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null });
        const matchingLevel = importanceLevels.find(level => Math.abs(Number(level.score) - Number(item.weight)) < 0.0001);
        setImportanceId(matchingLevel?.id || "");
    };
    const saveIndicator = async () => {
        const chosenImportance = importanceLevels.find(level => level.id === importanceId);
        const selectedScore = Number(chosenImportance?.score || 0);
        const payload = { ...form, weight: selectedScore, level: scope, orgUnitId: scope === "org_unit" ? orgUnitId : null };
        if (!payload.label.trim() || !payload.unit.trim() || !importanceId || !payload.ownerId || !payload.registrarId || !payload.approverId || !payload.scheduleTemplateId) {
            uxNotify("عنوان، واحد، وزن، مسئولان شاخص و زمان‌بندی را کامل کنید.", "error"); return;
        }
        if (payload.registrarId === payload.approverId) { uxNotify("ثبت‌کننده و تأییدکننده باید دو نفر متفاوت باشند.", "error"); return; }
        try {
            if (editingId) await updateIndicatorApi(editingId, payload); else await createIndicatorApi(payload);
            uxNotify(editingId ? "شاخص ویرایش شد." : "شاخص جدید ثبت شد.", "success"); resetForm();
        } catch (error) { uxNotify(error.message || "ذخیره شاخص انجام نشد.", "error"); }
    };
    const scopeName = scope === "org" ? "کل سازمان" : (activeUnits.find(unit => unit.id === orgUnitId)?.label || "واحد سازمانی");
    return <div>
        <DashboardPageIntro title="مشارکت جمعی" subtitle="شاخص‌های کلان سازمان و شاخص‌های واحدهای سازمانی را تعریف و زمان‌بندی کنید." />
        <ProfessionalTabs items={[
            { id: "indicators", label: "شاخص‌ها", icon: <UiIcon name="target" size={15} /> },
            { id: "schedules", label: "زمان‌بندی", icon: <UiIcon name="schedule" size={15} /> },
        ]} active={tab} value={tab} onChange={setTab} ariaLabel="بخش‌های مشارکت جمعی" />
        {tab === "indicators" && <div>
            <Card style={{marginBottom:12}}><div style={{padding:14}}>
                <div style={{fontSize:12.5,fontWeight:600,marginBottom:4}}>سطح ارزیابی</div>
                <div style={{fontSize:10.5,color:C.muted,marginBottom:10}}>برای ارزیابی کلان «کل سازمان» و برای ارزیابی محلی «واحد سازمانی» را انتخاب کنید.</div>
                <div style={{display:"grid",gridTemplateColumns:"minmax(160px,.7fr) minmax(220px,1.3fr)",gap:10}}>
                    <select value={scope} onChange={e=>{setScope(e.target.value);setEditingId("");setIndicatorFormOpen(false);}} style={{padding:9,border:`1px solid ${C.borderMd}`,borderRadius:8,fontFamily:"inherit",background:"#fff"}}>
                        <option value="org">کل سازمان</option><option value="org_unit">واحد سازمانی</option>
                    </select>
                    {scope === "org_unit" ? <select value={orgUnitId} onChange={e=>{setOrgUnitId(e.target.value);setEditingId("");setIndicatorFormOpen(false);}} style={{padding:9,border:`1px solid ${C.borderMd}`,borderRadius:8,fontFamily:"inherit",background:"#fff"}}>
                        <option value="">انتخاب واحد سازمانی</option>{activeUnits.map(unit=><option key={unit.id} value={unit.id}>{unit.label}</option>)}
                    </select> : <div style={{padding:"9px 11px",borderRadius:8,background:C.bg,color:C.muted,fontSize:11}}>شاخص در سطح کل سازمان تعریف می‌شود.</div>}
                </div>
            </div></Card>

            {(scope === "org" || orgUnitId) && <Card style={{marginBottom:12}}><div style={{padding:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                    <div><div style={{fontSize:10,color:C.hint,marginBottom:3}}>الگوی ارزیابی فعال</div><div style={{fontSize:13,fontWeight:700}}>{effectiveTemplate?.name || "هنوز الگوی ارزیابی تعریف نشده"}</div>{effectiveTemplate && <div style={{fontSize:10,color:C.muted,marginTop:3}}>{effectiveTemplate.kpiWeight}٪ شاخص · {effectiveTemplate.qualitativeWeight}٪ ارزیابی کیفی</div>}</div>
                    <button className="cvi-button-secondary" onClick={()=>{setSelectedTemplateId(effectiveTemplate?.id||"");setTemplateEditorOpen(v=>!v)}}>تغییر الگو</button>
                </div>
                {templateEditorOpen && <div style={{display:"flex",gap:8,marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}><select value={selectedTemplateId} onChange={e=>setSelectedTemplateId(e.target.value)} style={{flex:1,padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}><option value="">انتخاب الگو</option>{assessmentTemplates.map(t=><option key={t.id} value={t.id}>{t.name} — {t.kpiWeight}٪ شاخص / {t.qualitativeWeight}٪ کیفی</option>)}</select><button className="cvi-button-primary" onClick={changeAssessmentTemplate}>ذخیره</button></div>}
            </div></Card>}

            {hasKpi && (scope === "org" || orgUnitId) && <Card style={{marginBottom:12}}><div style={{padding:14}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",marginBottom:10}}><div><div style={{fontSize:13,fontWeight:600}}>شاخص‌های {scopeName}</div><div style={{fontSize:10,color:C.hint,marginTop:2}}>{visibleIndicators.length} شاخص · وزن‌ها به‌صورت خودکار نرمال می‌شوند</div></div></div>
                {!visibleIndicators.length ? <div style={{padding:"18px 8px",textAlign:"center",color:C.hint,fontSize:11}}>هنوز شاخصی برای این سطح تعریف نشده است.</div> :
                <div style={{border:`1px solid ${C.border}`,borderRadius:10,overflowX:"auto"}}>
                    <div style={{display:"grid",gridTemplateColumns:"minmax(240px,1fr) 150px 150px 120px",gap:8,padding:"10px 12px",background:"#FAFAFB",color:C.muted,fontSize:10,fontWeight:600,minWidth:700}}><span>عنوان شاخص</span><span style={{textAlign:"center"}}>سطح اهمیت</span><span style={{textAlign:"center"}}>وزن محاسباتی</span><span style={{textAlign:"center"}}>عملیات</span></div>
                    {visibleIndicators.map(item=>{const info=importancePresentation(item.weight,importanceLevels,visibleIndicators);return <div key={item.id} style={{display:"grid",gridTemplateColumns:"minmax(240px,1fr) 150px 150px 120px",gap:8,alignItems:"center",padding:"10px 12px",borderTop:`1px solid ${C.border}`,minWidth:700}}>
                        <div><b style={{fontSize:11.5}}>{item.label}</b><div style={{fontSize:9.5,color:C.hint,marginTop:2}}>{item.unit} · {item.aggregationMethod==="average"?"میانگین":item.aggregationMethod==="sum"?"مجموع":"آخرین مقدار"}</div></div>
                        <span style={{textAlign:"center",fontSize:12,fontWeight:500,color:C.muted}}>{info.label}</span>
                        <span dir="ltr" style={{textAlign:"center",fontSize:10.5,fontWeight:400,color:C.hint,unicodeBidi:"isolate"}}>{info.effective.toFixed(1)}٪</span>
                        <div style={{display:"flex",justifyContent:"center",gap:5}}><button onClick={()=>startEdit(item)} title="ویرایش" className="cvi-button-secondary" style={{padding:"5px 8px"}}><UiIcon name="edit" size={15}/></button><button onClick={async()=>{if(!await uxConfirm(`شاخص «${item.label}» حذف شود؟`))return;try{await archiveIndicatorApi(item.id);}catch(error){uxNotify(error.message||"حذف شاخص انجام نشد","error");}}} title="حذف" style={{padding:"5px 8px",background:C.redLt,color:C.red,border:`1px solid ${C.red}`,borderRadius:6,cursor:"pointer"}}><UiIcon name="archive" size={15}/></button></div>
                    </div>})}
                </div>}
                {!indicatorFormOpen && <div className="cvi-admin-list-actions" style={{justifyContent:"flex-end"}}><button type="button" className="cvi-primary-button" onClick={()=>{setEditingId("");setForm(emptyForm());setIndicatorFormOpen(true);}}>+ افزودن شاخص</button></div>}
            </div></Card>}

            {hasKpi && indicatorFormOpen && (scope === "org" || orgUnitId) && <div className="cvi-dialog-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget) resetForm();}}>
                <div className="cvi-dialog cvi-admin-form-dialog" role="dialog" aria-modal="true" aria-labelledby="indicator-dialog-title" style={{maxWidth:760,width:"min(760px,calc(100vw - 32px))",maxHeight:"88vh",overflowY:"auto"}}>
                <div className="cvi-dialog-head"><div><h3 id="indicator-dialog-title">{editingId ? "ویرایش شاخص" : `افزودن شاخص برای ${scopeName}`}</h3><p>{editingId ? "اطلاعات شاخص را ویرایش و ذخیره کنید." : "اطلاعات شاخص جدید را تکمیل کنید."}</p></div><button type="button" className="cvi-dialog-close" onClick={resetForm} aria-label="بستن">×</button></div>
                <div style={{padding:"0 18px 18px"}}>
                
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:9}}>
                    <label><span style={{display:"block",fontSize:9.5,color:C.hint,marginBottom:4}}>عنوان شاخص</span><input value={form.label||""} onChange={e=>setForm(x=>({...x,label:e.target.value}))} placeholder="مثلاً رضایت مشتری" style={{width:"100%",boxSizing:"border-box",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}/></label>
                    <label><span style={{display:"block",fontSize:9.5,color:C.hint,marginBottom:4}}>واحد اندازه‌گیری</span><input value={form.unit||""} onChange={e=>setForm(x=>({...x,unit:e.target.value}))} placeholder="٪، عدد، روز..." style={{width:"100%",boxSizing:"border-box",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}/></label>
                    <label><span style={{display:"block",fontSize:9.5,color:C.hint,marginBottom:4}}>اهمیت</span><select value={importanceId} onChange={e=>setImportanceId(e.target.value)} style={{width:"100%",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}><option value="">انتخاب سطح اهمیت</option>{importanceLevels.map(level=><option key={level.id} value={level.id}>{level.label}</option>)}</select>{importanceId && <span style={{display:"block",fontSize:9,color:C.hint,marginTop:4}}>وزن محاسباتی فعلی: {(()=>{const selected=importanceLevels.find(level=>level.id===importanceId);const others=visibleIndicators.filter(item=>item.id!==editingId);const total=others.reduce((sum,item)=>sum+Number(item.weight||0),0)+Number(selected?.score||0);return total>0?((Number(selected?.score||0)/total)*100).toFixed(1):"0.0";})()}٪</span>}</label>
                    <label><span style={{display:"block",fontSize:9.5,color:C.hint,marginBottom:4}}>فرمول محاسبه تحقق</span><select value={form.formulaTemplateId||"standard:higher"} onChange={e=>setForm(x=>({...x,formulaTemplateId:e.target.value}))} style={{width:"100%",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}>{kpiCalculationTemplates.filter(t=>t.isActive||t.id===form.formulaTemplateId).map(t=><option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
                    <label><span style={{display:"block",fontSize:9.5,color:C.hint,marginBottom:4}}>روش تجمیع</span><select value={form.aggregationMethod||"latest"} onChange={e=>setForm(x=>({...x,aggregationMethod:e.target.value}))} style={{width:"100%",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}><option value="latest">آخرین مقدار</option><option value="average">میانگین</option><option value="sum">مجموع</option></select></label>
                    {[["ownerId","مالک شاخص"],["registrarId","ثبت‌کننده"],["approverId","تأییدکننده"]].map(([key,label])=><label key={key}><span style={{display:"block",fontSize:9.5,color:C.hint,marginBottom:4}}>{label}</span><select value={form[key]||""} onChange={e=>setForm(x=>({...x,[key]:e.target.value}))} style={{width:"100%",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}><option value="">انتخاب فرد</option>{people.map(person=><option key={person.id} value={person.id}>{person.name}</option>)}</select></label>)}
                    <label><span style={{display:"block",fontSize:9.5,color:C.hint,marginBottom:4}}>الگوی زمان‌بندی</span><select value={form.scheduleTemplateId||""} onChange={e=>setForm(x=>({...x,scheduleTemplateId:e.target.value}))} style={{width:"100%",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7}}><option value="">انتخاب الگو</option>{scheduleTemplates.map(t=><option key={t.id} value={t.id}>{t.label||t.name}</option>)}</select></label>
                </div>
                <label style={{display:"flex",alignItems:"center",gap:7,marginTop:10,fontSize:10.5}}><input type="checkbox" checked={Boolean(form.requiresEvidence)} onChange={e=>setForm(x=>({...x,requiresEvidence:e.target.checked}))}/>برای ثبت عملکرد، مستند الزامی باشد</label>
                <div className="cvi-dialog-actions" style={{padding:"14px 0 0",marginTop:14,borderTop:`1px solid ${C.border}`}}><button onClick={resetForm} className="cvi-button-secondary">انصراف</button><button onClick={saveIndicator} className="cvi-button-primary">{editingId?"ذخیره تغییرات":"ثبت شاخص"}</button></div>
                </div></div></div>}
            {hasQualitative && (scope === "org" || orgUnitId) && <Card style={{marginTop:12}}><div style={{padding:14}}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:3}}>ارزیابی کیفی {scopeName}</div>
                <div style={{fontSize:10,color:C.muted,marginBottom:10}}>این بخش طبق الگوی «{effectiveTemplate?.name}» فعال است. مجموع وزن سؤالات بهتر است ۱۰۰٪ باشد.</div>
                <div style={{border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden",marginBottom:10}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 150px 150px",gap:8,padding:"9px 12px",background:"#FAFAFB",color:C.muted,fontSize:10,fontWeight:600}}><span>متن سؤال</span><span style={{textAlign:"center"}}>سطح اهمیت</span><span style={{textAlign:"center"}}>وزن محاسباتی</span></div>
                    {qualitativeQuestions.map(q=>{const info=importancePresentation(q.weight,importanceLevels,qualitativeQuestions);return <div key={q.id} style={{display:"grid",gridTemplateColumns:"1fr 150px 150px",gap:8,alignItems:"center",padding:"10px 12px",borderTop:`1px solid ${C.border}`}}><span style={{fontSize:11.5}}>{q.text}</span><span style={{textAlign:"center",fontSize:12,fontWeight:500,color:C.muted}}>{info.label}</span><span dir="ltr" style={{fontSize:10.5,fontWeight:400,color:C.hint,textAlign:"center",unicodeBidi:"isolate"}}>{info.effective.toFixed(1)}٪</span></div>})}
                    {!qualitativeQuestions.length && <div style={{padding:18,textAlign:"center",color:C.hint,fontSize:11}}>هنوز سؤال کیفی تعریف نشده است.</div>}
                </div>
                <div className="cvi-admin-list-actions" style={{justifyContent:"flex-end"}}><button type="button" className="cvi-primary-button" onClick={()=>setQualitativeFormOpen(true)}>+ افزودن سؤال</button></div>
                {qualitativeFormOpen && <div className="cvi-dialog-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setQualitativeFormOpen(false);}}>
                    <div className="cvi-dialog cvi-admin-form-dialog" role="dialog" aria-modal="true">
                        <div className="cvi-dialog-head"><div><h3>افزودن سؤال کیفی</h3><p>متن سؤال و سطح اهمیت را مشخص کنید. وزن محاسباتی به‌صورت خودکار تعیین می‌شود.</p></div><button type="button" className="cvi-dialog-close" onClick={()=>setQualitativeFormOpen(false)} aria-label="بستن">×</button></div>
                        <div className="cvi-admin-form-grid">
                            <div className="cvi-admin-form-field"><label>متن سؤال</label><input value={qualitativeDraft.text} onChange={e=>setQualitativeDraft(x=>({...x,text:e.target.value}))} placeholder="مثال: همسویی با اهداف استراتژیک سازمان" style={{width:"100%",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7,fontFamily:"inherit",boxSizing:"border-box"}}/></div>
                            <div className="cvi-admin-form-field"><label>سطح اهمیت</label><select value={qualitativeDraft.importanceId} onChange={e=>setQualitativeDraft(x=>({...x,importanceId:e.target.value}))} style={{width:"100%",padding:9,border:`1px solid ${C.borderMd}`,borderRadius:7,background:"#fff",fontFamily:"inherit"}}><option value="">انتخاب سطح اهمیت</option>{importanceLevels.map(level=><option key={level.id} value={level.id}>{level.label}</option>)}</select></div>
                        </div>
                        <div className="cvi-dialog-actions"><button type="button" className="cvi-button-secondary" onClick={()=>setQualitativeFormOpen(false)}>انصراف</button><button type="button" className="cvi-button-primary" onClick={addQualitativeQuestion}>ثبت سؤال</button></div>
                    </div>
                </div>}
            </div></Card>}
        </div>}
        {tab === "schedules" && <AdminIndicatorSchedules />}
    </div>;
}

function AdminIndicatorSchedules() {
    const { scheduleTemplates, setScheduleTemplates, scheduleExceptions, setScheduleExceptions,
        indicatorDefs, periods } = useData();
    const [indicatorId, setIndicatorId] = useState(indicatorDefs[0]?.id || "");
    const [periodId, setPeriodId] = useState(periods.find(p => p.active)?.id || periods[0]?.id || "");
    useEffect(() => {
        if (!indicatorDefs.some(item => item.id === indicatorId)) setIndicatorId(indicatorDefs[0]?.id || "");
    }, [indicatorDefs, indicatorId]);
    useEffect(() => {
        if (!periods.some(item => item.id === periodId)) setPeriodId(periods.find(p => p.active)?.id || periods[0]?.id || "");
    }, [periods, periodId]);
    const [exception, setException] = useState({ openDate: "", submitDeadline: "", approvalDeadline: "", lockDate: "", reason: "" });
    const fields = [
        { key: "label", label: "نام الگو", placeholder: "گزارش فصلی استاندارد" },
        { key: "frequency", label: "تناوب", type: "select", options: [{ value: "monthly", label: "ماهانه" }, { value: "quarterly", label: "فصلی" }, { value: "annual", label: "سالانه" }] },
        { key: "openOffsetDays", label: "شروع ثبت؛ روز بعد از پایان", type: "number" },
        { key: "submitAfterDays", label: "مهلت ثبت؛ روز بعد از پایان", type: "number" },
        { key: "approveAfterDays", label: "مهلت تأیید؛ روز پس از ثبت", type: "number" },
        { key: "lockAfterDays", label: "قفل؛ روز بعد از پایان", type: "number" },
        { key: "reminderDays", label: "یادآوری؛ چند روز قبل", type: "number" },
    ];
    const normalize = f => ({
        ...f, openOffsetDays: Number(f.openOffsetDays) || 0, submitAfterDays: Number(f.submitAfterDays) || 0,
        approveAfterDays: Number(f.approveAfterDays) || 0, lockAfterDays: Number(f.lockAfterDays) || 0, reminderDays: Number(f.reminderDays) || 0
    });
    const saveException = () => {
        if (!indicatorId || !periodId || !exception.reason.trim()) return;
        const id = `exception_${indicatorId}_${periodId}`;
        setScheduleExceptions(prev => [...prev.filter(e => !(e.indicatorId === indicatorId && e.periodId === periodId)),
        { id, indicatorId, periodId, ...exception }]);
        setException({ openDate: "", submitDeadline: "", approvalDeadline: "", lockDate: "", reason: "" });
    };
    return <div><div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>تقویم و الگوهای زمان‌بندی شاخص‌ها</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 14, lineHeight: 1.7 }}>تاریخ‌های هر نوبت از پایان بازه به‌صورت خودکار محاسبه می‌شوند. تغییر یک الگو روی دوره‌های آینده اثر دارد؛ تاریخ‌های ذخیره‌شده در رکوردهای قبلی ثابت می‌مانند.</div>
        <EditableList items={scheduleTemplates} fields={fields}
            onAdd={f => setScheduleTemplates(prev => [...prev, { id: `schedule_${Date.now()}`, ...normalize(f) }])}
            onUpdate={(id, f) => setScheduleTemplates(prev => prev.map(t => t.id === id ? { ...t, ...normalize(f) } : t))}
            onDelete={id => { if (indicatorDefs.some(i => i.scheduleTemplateId === id)) { uxNotify("این الگو به یک یا چند شاخص متصل است و قابل حذف نیست."); return; } setScheduleTemplates(prev => prev.filter(t => t.id !== id)); }}
            validate={f => !f.label?.trim() || !f.frequency ? "نام و تناوب الگو الزامی است" : Number(f.lockAfterDays) < Number(f.submitAfterDays) + Number(f.approveAfterDays) ? "تاریخ قفل باید بعد از پایان مهلت تأیید باشد" : null} />
        <div style={{ fontSize: 13, fontWeight: 500, margin: "20px 0 8px" }}>استثنای یک شاخص در یک دوره</div>
        <Card><div style={{ padding: 14 }}><div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
            <select value={indicatorId} onChange={e => setIndicatorId(e.target.value)}><option value="">انتخاب شاخص</option>{indicatorDefs.map(i => <option key={i.id} value={i.id}>{i.label}</option>)}</select>
            <select value={periodId} onChange={e => setPeriodId(e.target.value)}><option value="">انتخاب دوره</option>{periods.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}</select>
            {[['openDate', 'شروع ثبت'], ['submitDeadline', 'مهلت ثبت'], ['approvalDeadline', 'مهلت تأیید'], ['lockDate', 'تاریخ قفل']].map(([key, label]) => <input key={key} value={exception[key]} onChange={e => setException(x => ({ ...x, [key]: e.target.value }))} placeholder={`${label}؛ مثل 1404/10/05`} />)}
        </div><textarea value={exception.reason} onChange={e => setException(x => ({ ...x, reason: e.target.value }))} placeholder="دلیل استثنا (الزامی)" rows={2} style={{ width: "100%", boxSizing: "border-box", marginTop: 8 }} />
            <button onClick={saveException} disabled={!indicatorId || !periodId || !exception.reason.trim()} style={{ marginTop: 8, padding: "8px 14px" }}>ثبت استثنا</button>
            {scheduleExceptions.map(e => <div key={e.id} style={{ fontSize: 11, padding: "8px 0", borderTop: `1px solid ${C.bg}`, marginTop: 8 }}>{indicatorDefs.find(i => i.id === e.indicatorId)?.label} · {periods.find(p => p.id === e.periodId)?.label} — {e.reason}<button onClick={() => setScheduleExceptions(prev => prev.filter(x => x.id !== e.id))} style={{ marginRight: 8, color: C.red }}>حذف</button></div>)}
        </div></Card>
    </div>;
}

// سؤالات وظایف مستمر — به ازای هر تیم، ۳ سؤال برای هر ماه از فصل (جمعاً ۹ سؤال با وزن جمعاً ۱۰۰٪)
// ویرایشگر عمومی بانک سؤالات وظایف مستمر برای یک سطح دارای دامنه (تیم یا معاونت)
function importancePresentation(rawWeight, levels, items = []) {
    const score = Number(rawWeight) || 0;
    const level = levels.find(row => Math.abs(Number(row.score) - score) < 0.0001);
    const total = items.reduce((sum, item) => sum + Math.max(0, Number(item.weight) || 0), 0);
    const effective = total > 0 ? (score / total) * 100 : 0;
    return { label: level?.label || "—", effective };
}

function useImportanceScaleOptions() {
    const [levels, setLevels] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = sessionStorage.getItem("cvi_token");
                const response = await fetch(`${API_BASE_URL}/api/admin/templates/importance-scale/list`, { headers: { Authorization: `Bearer ${token}` } });
                const payload = await response.json().catch(() => []);
                if (!response.ok) throw new Error(payload.message || "Importance scale load failed");
                if (!cancelled) setLevels(Array.isArray(payload) ? payload : []);
            } catch (error) { console.error("Importance scale load failed:", error); }
        })();
        return () => { cancelled = true; };
    }, []);
    return levels.map(level => ({ value: String(level.score), label: level.label }));
}

function ScopedRoutineQuestionsEditor({ scopeItems, scopeKey, questions, scopeLabel, questionBank = [] }) {
    const { createRoutineQuestionApi, updateRoutineQuestionApi, archiveRoutineQuestionApi } = useData();
    const importanceOptions = useImportanceScaleOptions();
    const [scopeId, setScopeId] = useState(scopeItems[0]?.id || null);
    const [bankOpen, setBankOpen] = useState(false);
    const [bankSelection, setBankSelection] = useState([]);
    const fields = [
        { key: "label", label: "متن سؤال", placeholder: "مثال: رعایت مهلت‌های تعیین‌شده" },
        { key: "weight", label: "سطح اهمیت", type: "select", placeholder: "انتخاب سطح اهمیت", options: importanceOptions },
        { key: "computationalWeight", label: "وزن محاسباتی", computed: true, suffix: "٪" },
    ];
    const scope = scopeKey === "orgUnitId" ? "ORG_UNIT" : scopeKey === "teamId" ? "TEAM" : "DEPARTMENT";
    const scopePayload = () => scope === "ORG_UNIT" ? { orgUnitId: scopeId } : scope === "TEAM" ? { teamId: scopeId } : { departmentId: scopeId };

    useEffect(() => {
        if (scopeItems.length && !scopeItems.some(item => item.id === scopeId)) setScopeId(scopeItems[0].id);
    }, [scopeItems, scopeId]);

    if (!scopeItems.length) return <div style={{ padding: 16, color: C.hint, fontSize: 11 }}>{`ابتدا ${scopeLabel}ی تعریف کنید`}</div>;

    const addFromBank = async () => {
        if (!bankSelection.length) return;
        try {
            for (const id of bankSelection) {
                const source = questionBank.find(item => item.id === id);
                if (!source) continue;
                await createRoutineQuestionApi({ scope, ...scopePayload(), month: Number(source.month) || 1, text: source.label || source.text, weight: Number(source.weight) || 0 });
            }
            setBankSelection([]); setBankOpen(false);
        } catch (error) { uxNotify(error.message || "افزودن سؤال از بانک انجام نشد"); }
    };

    return <div>
        <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{scopeLabel}</label>
            <select value={scopeId || ""} onChange={e => setScopeId(e.target.value)} style={{ padding: 8, border: `1px solid ${C.borderMd}`, borderRadius: 7, fontFamily: "inherit" }}>
                {scopeItems.map(item => <option key={item.id} value={item.id}>{item.label || item.name}</option>)}
            </select>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}><button type="button" className="cvi-button-secondary" onClick={()=>setBankOpen(true)}>انتخاب از بانک سؤالات</button></div>
        {bankOpen && <div className="cvi-dialog-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setBankOpen(false);}}><div className="cvi-dialog" role="dialog" aria-modal="true" style={{maxWidth:620,width:"min(620px,calc(100vw - 32px))"}}><div className="cvi-dialog-head"><div><h3>انتخاب از بانک سؤالات</h3><p>سؤال‌های انتخاب‌شده به واحد انتخابی اضافه می‌شوند و بعداً مستقل قابل ویرایش هستند.</p></div><button type="button" className="cvi-dialog-close" onClick={()=>setBankOpen(false)}>×</button></div><div style={{padding:"0 18px 18px",maxHeight:"55vh",overflowY:"auto"}}>{questionBank.length===0?<div style={{padding:24,textAlign:"center",color:C.muted,fontSize:11}}>هنوز سؤالی در بانک تعریف نشده است.</div>:questionBank.map(item=><label key={item.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 4px",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}><input type="checkbox" checked={bankSelection.includes(item.id)} onChange={e=>setBankSelection(current=>e.target.checked?[...current,item.id]:current.filter(id=>id!==item.id))}/><span style={{flex:1,fontSize:11.5}}>{item.label || item.text}</span><span style={{fontSize:9.5,color:C.muted}}>{item.month===1?"ماه اول":item.month===2?"ماه دوم":"ماه سوم"}</span></label>)}<div className="cvi-dialog-actions" style={{padding:"14px 0 0"}}><button className="cvi-button-secondary" onClick={()=>setBankOpen(false)}>انصراف</button><button className="cvi-button-primary" disabled={!bankSelection.length} onClick={addFromBank}>افزودن به واحد</button></div></div></div></div>}
        {[1, 2, 3].map(month => (
            <div key={month} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 7 }}>
                    {month === 1 ? "ماه اول" : month === 2 ? "ماه دوم" : "ماه سوم"}
                </div>
                <EditableList
                    items={(() => {
                        const scopedQuestions = questions.filter(q => q[scopeKey] === scopeId);
                        const totalWeight = scopedQuestions.reduce((sum, q) => sum + (Number(q.weight) || 0), 0);
                        return scopedQuestions.filter(q => q.month === month).map(q => ({
                            ...q,
                            computationalWeight: totalWeight > 0 ? ((Number(q.weight) || 0) / totalWeight * 100).toFixed(1) : "0.0"
                        }));
                    })()}
                    fields={fields}
                    onAdd={async f => {
                        try {
                            await createRoutineQuestionApi({ scope, ...scopePayload(), month, text: f.label, weight: Number(f.weight) || 0 });
                        } catch (error) { uxNotify(error.message || "ثبت سؤال وظایف مستمر انجام نشد"); }
                    }}
                    onUpdate={async (id, f) => {
                        try {
                            await updateRoutineQuestionApi(id, { scope, ...scopePayload(), month, text: f.label, weight: Number(f.weight) || 0 });
                        } catch (error) { uxNotify(error.message || "ویرایش سؤال وظایف مستمر انجام نشد"); }
                    }}
                    onDelete={async id => {
                        try { await archiveRoutineQuestionApi(id); }
                        catch (error) { uxNotify(error.message || "حذف سؤال وظایف مستمر انجام نشد"); }
                    }}
                    validate={f => !f.label?.trim() ? "متن سؤال الزامی است" : null} />
            </div>
        ))}
    </div>;
}

// بانک سؤالات عمومی: فقط الگوی قابل استفاده مجدد است و وارد ارزیابی نمی‌شود.
function OrgRoutineQuestionsEditor({ questions }) {
    const { createRoutineQuestionApi, updateRoutineQuestionApi, archiveRoutineQuestionApi } = useData();
    const importanceOptions = useImportanceScaleOptions();
    const fields = [
        { key: "label", label: "متن سؤال", placeholder: "مثال: رعایت مهلت‌های تعیین‌شده" },
        { key: "weight", label: "سطح اهمیت", type: "select", placeholder: "انتخاب سطح اهمیت", options: importanceOptions },
        { key: "computationalWeight", label: "وزن محاسباتی", computed: true, suffix: "٪" },
    ];
    return <div>
        {[1, 2, 3].map(month => (
            <div key={month} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 7 }}>{month === 1 ? "ماه اول" : month === 2 ? "ماه دوم" : "ماه سوم"}</div>
                <EditableList items={(() => {
                    const monthQuestions = questions.filter(q => q.month === month);
                    const totalWeight = monthQuestions.reduce((sum, q) => sum + (Number(q.weight) || 0), 0);
                    return monthQuestions.map(q => ({
                        ...q,
                        computationalWeight: totalWeight > 0 ? ((Number(q.weight) || 0) / totalWeight * 100).toFixed(1) : "0.0"
                    }));
                })()} fields={fields}
                    onAdd={async f => {
                        try { await createRoutineQuestionApi({ scope: "ORGANIZATION", month, text: f.label, weight: Number(f.weight) || 0 }); }
                        catch (error) { uxNotify(error.message || "ثبت سؤال وظایف مستمر انجام نشد"); }
                    }}
                    onUpdate={async (id, f) => {
                        try { await updateRoutineQuestionApi(id, { scope: "ORGANIZATION", month, text: f.label, weight: Number(f.weight) || 0 }); }
                        catch (error) { uxNotify(error.message || "ویرایش سؤال وظایف مستمر انجام نشد"); }
                    }}
                    onDelete={async id => {
                        try { await archiveRoutineQuestionApi(id); }
                        catch (error) { uxNotify(error.message || "حذف سؤال وظایف مستمر انجام نشد"); }
                    }}
                    validate={f => !f.label?.trim() ? "متن سؤال الزامی است" : null} />
            </div>
        ))}
    </div>;
}

function AdminRoutineQuestions() {
    const { orgUnits, routineQuestions, orgRoutineQuestions } = useData();
    const [level, setLevel] = useState("unit");
    const activeUnits = orgUnits.filter(unit => unit.isActive !== false);

    return (
        <div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>سؤالات وظایف مستمر</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, lineHeight: 1.7 }}>
                سؤالات ارزیابی برای هر واحد سازمانی تعریف می‌شوند. «بانک سؤالات» مجموعه‌ای از سؤال‌های آماده است که مستقیماً در ارزیابی محاسبه نمی‌شود و می‌توان از آن برای ساخت سریع سؤالات واحدها استفاده کرد.
            </div>

            <ProfessionalTabs
                items={[
                    { id: "unit", label: "واحدهای سازمانی", icon: "members", count: routineQuestions.length },
                    { id: "org", label: "بانک سؤالات", icon: "org", count: orgRoutineQuestions.length },
                ]}
                value={level}
                onChange={setLevel}
                ariaLabel="سطوح پرسش‌های وظایف مستمر"
            />

            <div style={{ display: level === "unit" ? "block" : "none" }} aria-hidden={level !== "unit"}>
                <ScopedRoutineQuestionsEditor scopeItems={activeUnits} scopeKey="orgUnitId"
                    questions={routineQuestions} scopeLabel="واحد سازمانی" questionBank={orgRoutineQuestions} />
            </div>
            <div style={{ display: level === "org" ? "block" : "none" }} aria-hidden={level !== "org"}>
                <OrgRoutineQuestionsEditor questions={orgRoutineQuestions} />
            </div>
        </div>
    );
}

// مشترک بین «برنامه‌ریزی دوره» (target) و «ثبت عملکرد» (actual)
function PeriodDataEditor({ field, title, subtitle, controlledPeriodId = null, onPeriodChange = null }) {
    const { periods, indicatorDefs, indicatorData, teams, departments, orgUnits,
        periodAudit, closePeriod, reopenPeriod, users, setIndicatorTargetApi, indicatorError,
        createPeriodApi, activatePeriodApi } = useData();
    const activePeriod = periods.find(p => p.active && p.status === "active") || null;
    const [localSelectedPeriodId, setLocalSelectedPeriodId] = useState(activePeriod?.id);
    const selectedPeriodId = controlledPeriodId || localSelectedPeriodId;
    const setSelectedPeriodId = (id) => { if (onPeriodChange) onPeriodChange(id); else setLocalSelectedPeriodId(id); };
    const [newPeriod, setNewPeriod] = useState({ label: "", start: "", end: "" });
    const [localValues, setLocalValues] = useState({});
    const [showNewPeriod, setShowNewPeriod] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const periodYear = p => String(p?.label || "").match(/(13|14)\d{2}/)?.[0] || String(p?.start || p?.startDate || "").match(/(13|14|20)\d{2}/)?.[0] || "سایر";
    const periodYears = [...new Set(periods.map(periodYear))].sort((a, b) => String(b).localeCompare(String(a), "fa"));
    const [selectedYear, setSelectedYear] = useState(() => periodYear(activePeriod) || periodYears[0] || "");

    const period = periods.find(p => p.id === selectedPeriodId) || activePeriod;
    const periodsInYear = periods.filter(p => periodYear(p) === selectedYear);
    useEffect(() => {
        if (!selectedYear && periodYears.length) setSelectedYear(periodYears[0]);
    }, [periods.length]);

    const scopeLabel = (def) => {
        if (def.level === "org_unit") return orgUnits.find(unit => unit.id === def.orgUnitId)?.label || "—";
        if (def.level === "team") return teams.find(t => t.id === def.teamId)?.label || "—";
        if (def.level === "dept") return departments.find(d => d.id === def.deptId)?.label || "—";
        return "کلان سازمان";
    };

    const dataFor = (indId) => period && indicatorData.find(d => d.periodId === period.id && d.indicatorId === indId);

    const getValue = (indId) => {
        const key = `${period?.id}_${indId}`;
        if (localValues[key] !== undefined) return localValues[key];
        return dataFor(indId)?.[field] ?? "";
    };
    const setValue = (indId, val) => {
        const key = `${period?.id}_${indId}`;
        setLocalValues(v => ({ ...v, [key]: val }));
    };
    const saveRow = async (indId) => {
        if (!period || period.status === "locked" || field === "actual") return;
        const key = period.id + "_" + indId;
        const val = localValues[key];
        if (val === undefined || val === "") return;
        try {
            await setIndicatorTargetApi(indId, period.id, Number(val), "period");
        } catch (error) {
            console.error("Indicator target save failed:", error);
            uxNotify(error.message || "ذخیره هدف شاخص انجام نشد");
        }
    };
    const activatePeriod = async (id) => {
        try {
            await activatePeriodApi(id);
            setSelectedPeriodId(id);
        } catch (error) { uxNotify(error.message || "فعال‌سازی دوره انجام نشد"); }
    };
    const addPeriod = async () => {
        if (!newPeriod.label.trim() || !newPeriod.start || !newPeriod.end) return;
        try {
            const created = await createPeriodApi(newPeriod);
            setSelectedPeriodId(created.id);
            setSelectedYear(periodYear(created));
            setNewPeriod({ label: "", start: "", end: "" });
            setShowNewPeriod(false);
        } catch (error) { uxNotify(error.message || "ثبت دوره انجام نشد"); }
    };

    const smallInput = {
        padding: "7px 10px", border: `1.5px solid ${C.borderMd}`,
        borderRadius: 7, fontSize: 12, direction: "inherit", fontFamily: "inherit"
    };
    const statusLabel = { draft: "آماده‌سازی", preparation: "آماده‌سازی", active: "فعال", finalizing: "در حال نهایی‌سازی", locked: "نهایی و قفل‌شده" };
    const isLocked = period?.status === "locked";
    const isPlanning = field === "target";
    const periodRows = period ? indicatorDefs.map(def => ({ def, data: dataFor(def.id) })) : [];
    const targetReady = periodRows.filter(row => row.data?.target !== null && row.data?.target !== undefined && row.data?.target !== "").length;
    const recorded = periodRows.filter(row => row.data?.actual !== null && row.data?.actual !== undefined && row.data?.actual !== "").length;
    const pendingApproval = periodRows.filter(row => ["submitted", "pending", "pending_approval"].includes(row.data?.status)).length;
    const missing = Math.max(0, indicatorDefs.length - recorded);
    const completion = indicatorDefs.length ? Math.round((recorded / indicatorDefs.length) * 100) : 0;
    const planningReady = indicatorDefs.length > 0 && targetReady === indicatorDefs.length;

    return (
        <div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>{subtitle}</div>
            {indicatorError && <div style={{ marginBottom: 10, color: C.red, fontSize: 11 }}>{indicatorError}</div>}

            <Card style={{ marginBottom: 16 }}>
                <div style={{ padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontSize: 10, color: C.hint, marginBottom: 3 }}>دوره انتخاب‌شده</div>
                            <div style={{ fontSize: 15, fontWeight: 700 }}>{period?.label || "هنوز دوره‌ای ایجاد نشده"}</div>
                            {period && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>{period.start || period.startDate || "—"} تا {period.end || period.endDate || "—"} · {statusLabel[period.status] || "آماده‌سازی"}</div>}
                        </div>
                        {isPlanning && <button className="cvi-button-secondary" onClick={() => setShowNewPeriod(v => !v)}>+ دوره جدید</button>}
                    </div>
                    {!!periods.length && <div style={{ display: "grid", gridTemplateColumns: "minmax(120px,.45fr) minmax(220px,1fr)", gap: 8, marginTop: 12, maxWidth: 520 }}>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>سال</span><select value={selectedYear} onChange={e => { const year = e.target.value; setSelectedYear(year); const first = periods.find(p => periodYear(p) === year); if (first) setSelectedPeriodId(first.id); }} style={{ width: "100%", padding: 9, border: `1px solid ${C.borderMd}`, borderRadius: 8, background: "#fff", fontFamily: "inherit" }}>{periodYears.map(year => <option key={year} value={year}>{year}</option>)}</select></label>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>فصل / دوره</span><select value={period?.id || ""} onChange={e => setSelectedPeriodId(e.target.value)} style={{ width: "100%", padding: 9, border: `1px solid ${C.borderMd}`, borderRadius: 8, background: "#fff", fontFamily: "inherit" }}>{periodsInYear.map(p => <option key={p.id} value={p.id}>{p.label}{p.active ? " — جاری" : ""}</option>)}</select></label>
                    </div>}
                    {isPlanning && period && !period.active && !isLocked && <div style={{ marginTop: 12 }}><button className="cvi-button-primary" onClick={() => activatePeriod(period.id)}>فعال‌سازی به‌عنوان دوره جاری</button></div>}
                    {isPlanning && showNewPeriod && <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12, paddingTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>عنوان دوره</span><input value={newPeriod.label} onChange={e => setNewPeriod(x => ({ ...x, label: e.target.value }))} placeholder="فصل چهارم ۱۴۰۴" style={smallInput} /></label>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>شروع</span><JalaliDatePicker value={newPeriod.start} onChange={value => setNewPeriod(x => ({ ...x, start: value }))} /></label>
                        <label><span style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>پایان</span><JalaliDatePicker value={newPeriod.end} onChange={value => setNewPeriod(x => ({ ...x, end: value }))} /></label>
                        <button className="cvi-button-primary" onClick={addPeriod}>ایجاد دوره</button>
                    </div>}
                </div>
            </Card>

            {period && <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
                <Card><div style={{ padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{isPlanning ? "آمادگی دوره" : "پیشرفت اجرای دوره"}</div>
                            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4, lineHeight: 1.8 }}>
                                {isPlanning
                                    ? "در این مرحله فقط آماده‌بودن دوره، اهداف شاخص‌ها و فعال‌سازی آن را کنترل کنید."
                                    : "در این مرحله وضعیت ثبت، تأیید و موارد باقی‌مانده را پایش کنید."}
                            </div>
                        </div>
                        <div style={{ minWidth: 110, textAlign: "center", padding: "8px 12px", borderRadius: 10, background: C.bg }}>
                            <div style={{ fontSize: 9.5, color: C.hint }}>{isPlanning ? "آمادگی" : "تکمیل ثبت"}</div>
                            <div style={{ fontSize: 22, fontWeight: 750, marginTop: 2 }}>{isPlanning ? (indicatorDefs.length ? Math.round((targetReady / indicatorDefs.length) * 100) : 0) : completion}٪</div>
                        </div>
                    </div>
                    <div style={{ height: 7, borderRadius: 999, background: C.bg, overflow: "hidden", marginTop: 14 }}>
                        <div style={{ width: `${isPlanning ? (indicatorDefs.length ? Math.round((targetReady / indicatorDefs.length) * 100) : 0) : completion}%`, height: "100%", background: C.navy, borderRadius: 999 }} />
                    </div>
                </div></Card>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 }}>
                    {(isPlanning
                        ? [["شاخص‌های دوره", indicatorDefs.length], ["اهداف آماده", `${targetReady} از ${indicatorDefs.length}`], ["وضعیت دوره", statusLabel[period.status] || "آماده‌سازی"]]
                        : [["شاخص‌های دوره", indicatorDefs.length], ["ثبت‌شده", recorded], ["در انتظار تأیید", pendingApproval], ["ثبت‌نشده", missing]]
                    ).map(([label, value]) => <Card key={label}><div style={{ padding: "12px 14px" }}><div style={{ fontSize: 9.5, color: C.hint }}>{label}</div><div style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{value}</div></div></Card>)}
                </div>

                {isPlanning ? (
                    <Card><div style={{ padding: 15, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontSize: 12.5, fontWeight: 650 }}>اهداف شاخص‌های مشارکت جمعی</div>
                            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>
                                {planningReady ? "همه اهداف این دوره تعیین شده‌اند." : `${Math.max(0, indicatorDefs.length - targetReady)} هدف هنوز نیاز به بازبینی یا تعیین دارد.`}
                            </div>
                        </div>
                        <button className="cvi-button-secondary" onClick={() => setShowDetails(v => !v)}>{showDetails ? "بستن جزئیات" : "بازبینی اهداف"}</button>
                    </div></Card>
                ) : (
                    <>
                        {(missing > 0 || pendingApproval > 0) && <Card><div style={{ padding: 15 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 650, marginBottom: 7 }}>نیازمند اقدام</div>
                            <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.9 }}>
                                {missing > 0 && <div>• {missing} شاخص هنوز ثبت نشده است.</div>}
                                {pendingApproval > 0 && <div>• {pendingApproval} شاخص در انتظار تأیید است.</div>}
                            </div>
                        </div></Card>}
                        <Card><div style={{ padding: 15, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                            <div>
                                <div style={{ fontSize: 12.5, fontWeight: 650 }}>جزئیات ثبت و تأیید</div>
                                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>فقط برای بررسی موردی، جزئیات شاخص‌ها را باز کنید.</div>
                            </div>
                            <button className="cvi-button-secondary" onClick={() => setShowDetails(v => !v)}>{showDetails ? "بستن جزئیات" : "مشاهده جزئیات"}</button>
                        </div></Card>
                    </>
                )}
            </div>}

            {showDetails && (indicatorDefs.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                    هنوز شاخصی تعریف نشده است. ابتدا از «مشارکت جمعی» شاخص‌های موردنیاز را تعریف کنید.
                </div>
            ) : !period ? (
                <div style={{ textAlign: "center", padding: 32, color: C.muted, fontSize: 12 }}>
                    هنوز دوره‌ای ثبت نشده است.
                </div>
            ) : (
                <Card>
                    <div style={{ padding: "11px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
                        {isPlanning ? "بازبینی اهداف شاخص‌ها" : "وضعیت ثبت و تأیید شاخص‌ها"}
                    </div>
                    {indicatorDefs.map((def, i) => (
                        <div key={def.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: i < indicatorDefs.length - 1 ? `1px solid ${C.bg}` : "none", flexWrap: "wrap" }}>
                            <div style={{ flex: 1, minWidth: 180 }}>
                                <div style={{ fontSize: 12, fontWeight: 600 }}>{def.label}</div>
                                <div style={{ fontSize: 10, color: C.hint, marginTop: 2 }}>{scopeLabel(def)} · {def.unit || "—"}</div>
                            </div>
                            <div style={{ fontSize: 10.5, color: C.muted, minWidth: 85, textAlign: "center" }}>{field === "target" ? `واقعی: ${dataFor(def.id)?.actual ?? "—"}` : `هدف: ${dataFor(def.id)?.target ?? "—"}`}</div>
                            <input type="number" value={getValue(def.id)}
                                onChange={e => field !== "actual" && setValue(def.id, e.target.value)}
                                onBlur={() => saveRow(def.id)}
                                disabled={isLocked || field === "actual"}
                                placeholder={field === "target" ? "هدف" : "واقعی"}
                                style={{ width: 90, padding: "7px 10px", border: `1.5px solid ${C.borderMd}`, borderRadius: 7, fontSize: 12, textAlign: "center" }} />
                            {field === "actual" && <div style={{ fontSize: 9.5, color: C.hint, minWidth: 130 }}>ثبت‌کننده: {users.find(u => u.id === def.registrarId)?.name || "تعیین نشده"}<br/>وضعیت: {indicatorStatusLabel(dataFor(def.id)?.status)}</div>}
                        </div>
                    ))}
                </Card>
            ))}
            {!isPlanning && period?.status === "active" && <Card style={{ marginTop: 12 }}><div style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div><div style={{ fontSize: 12.5, fontWeight: 600 }}>نهایی‌سازی دوره</div><div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>پس از تکمیل ثبت‌ها و تأییدها، نتایج نهایی محاسبه و دوره قفل می‌شود.</div></div>
                <button className="cvi-button-primary" onClick={async () => {
                    if (!await uxConfirm(`با نهایی‌سازی «${period.label}»، نتایج قفل می‌شود. ادامه می‌دهید؟`)) return;
                    try { await closePeriod(period.id, "ادمین"); uxNotify("دوره با موفقیت نهایی و قفل شد."); }
                    catch (error) {
                        const blockers = error?.blockers;
                        if (blockers) {
                            const parts = [];
                            if (blockers.incompleteIndicators?.length) parts.push(`شاخص‌های ناقص: ${blockers.incompleteIndicators.length}`);
                            if (blockers.pendingActivities) parts.push(`فعالیت‌های در انتظار: ${blockers.pendingActivities}`);
                            if (blockers.pendingPeerAssignments) parts.push(`بازخوردهای در انتظار: ${blockers.pendingPeerAssignments}`);
                            uxNotify(`دوره هنوز آماده نهایی‌سازی نیست. ${parts.join(" | ")}`);
                        } else uxNotify(error?.message || "نهایی‌سازی دوره انجام نشد.");
                    }
                }}>نهایی‌سازی و قفل دوره</button>
            </div></Card>}
            {!isPlanning && isLocked && <Card style={{ marginTop: 12 }}><div style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div><div style={{ fontSize: 12.5, fontWeight: 600 }}>دوره نهایی شده است</div><div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>اطلاعات فقط‌خواندنی است. بازگشایی فقط برای اصلاح ضروری و با ثبت دلیل انجام می‌شود.</div></div>
                <button className="cvi-button-secondary" onClick={async () => { const reason = await uxPrompt("دلیل بازگشایی دوره را وارد کنید:", ""); if (!reason) return; try { await reopenPeriod(period.id, "ادمین", reason); uxNotify("دوره بازگشایی شد."); } catch (error) { uxNotify(error?.message || "بازگشایی دوره انجام نشد."); } }}>بازگشایی برای اصلاح</button>
            </div></Card>}

            {period && periodAudit.some(e => e.periodId === period.id) && (
                <Card style={{ marginTop: 12 }}>
                    <div style={{ padding: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>تاریخچه تغییرات دوره</div>
                        {periodAudit.filter(e => e.periodId === period.id).slice().reverse().map(e => (
                            <div key={e.id} style={{ padding: "8px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 11 }}>
                                <div style={{ fontWeight: 500 }}>{e.type === "locked" ? "قفل دوره" : "بازگشایی دوره"} · {e.by}</div>
                                <div style={{ color: C.muted, marginTop: 2 }}>{e.date} — {e.note}</div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}

function AdminPeerFeedback() {
    const importanceOptions = useImportanceScaleOptions();
    const { users, orgUnits, peerQuestions, peerConfigs, setPeerConfigs, routineLevels,
        createPeerQuestionAdminApi, updatePeerQuestionAdminApi, archivePeerQuestionAdminApi,
        savePeerConfigAdminApi, createPeerAssignmentsAdminApi, activePeriod } = useData();
    const people = users.filter(u => u.role !== "admin");
    const [tab, setTab] = useState("questions");
    const [selectedUserId, setSelectedUserId] = useState(people[0]?.id || "");
    useEffect(() => {
        if (!people.some(person => person.id === selectedUserId)) setSelectedUserId(people[0]?.id || "");
    }, [people, selectedUserId]);
    const [bulkOrgUnit, setBulkOrgUnit] = useState("all");
    const [bulkRole, setBulkRole] = useState("all");
    const [reviewerCount, setReviewerCount] = useState(3);
    const [assignmentPreview, setAssignmentPreview] = useState([]);
    useEffect(() => {
        setPeerConfigs(prev => prev.map(c => {
            const target = users.find(u => u.id === c.userId);
            return target?.managerId && c.reviewerIds?.includes(target.managerId)
                ? { ...c, reviewerIds: c.reviewerIds.filter(id => id !== target.managerId) } : c;
        }));
    }, [users, setPeerConfigs]);
    const selectedUser = users.find(u => u.id === selectedUserId);
    const config = peerConfigs.find(c => c.userId === selectedUserId) || {
        userId: selectedUserId, enabled: false, reviewerIds: [], visibility: "anonymous", minResponses: 3
    };
    const updateConfig = patch => setPeerConfigs(prev => {
        const exists = prev.some(c => c.userId === selectedUserId);
        return exists ? prev.map(c => c.userId === selectedUserId ? { ...c, ...patch } : c) : [...prev, { ...config, ...patch }];
    });
    const toggleReviewer = id => updateConfig({
        reviewerIds: config.reviewerIds.includes(id)
            ? config.reviewerIds.filter(x => x !== id) : [...config.reviewerIds, id]
    });
    const parentUnitOf = p => orgUnits.find(unit => unit.id === p.orgUnitId)?.parentId || null;
    const generateAssignments = () => {
        const targetUnitIds = bulkOrgUnit === "all" ? null : orgUnitSubtreeIds(orgUnits, bulkOrgUnit);
        const targets = people.filter(p => (!targetUnitIds || targetUnitIds.includes(p.orgUnitId)) && (bulkRole === "all" || p.role === bulkRole));
        const load = {};
        peerConfigs.forEach(c => (c.reviewerIds || []).forEach(id => { load[id] = (load[id] || 0) + 1; }));
        const rows = targets.map(target => {
            const targetParent = parentUnitOf(target);
            const candidates = people.filter(p => p.id !== target.id && p.id !== target.managerId && p.role === target.role)
                .map(p => ({ person: p, tier: p.orgUnitId && p.orgUnitId === target.orgUnitId ? 0 : parentUnitOf(p) === targetParent ? 1 : 2 }))
                .sort((a, b) => a.tier - b.tier || (load[a.person.id] || 0) - (load[b.person.id] || 0) || a.person.name.localeCompare(b.person.name, "fa"));
            const selected = candidates.slice(0, Math.max(1, reviewerCount)).map(c => c.person.id);
            selected.forEach(id => { load[id] = (load[id] || 0) + 1; });
            return { userId: target.id, reviewerIds: selected, complete: selected.length >= reviewerCount };
        });
        setAssignmentPreview(rows);
    };
    const applyAssignments = () => {
        setPeerConfigs(prev => {
            const ids = new Set(assignmentPreview.map(r => r.userId));
            const untouched = prev.filter(c => !ids.has(c.userId));
            const generated = assignmentPreview.map(row => {
                const old = prev.find(c => c.userId === row.userId);
                return {
                    userId: row.userId, enabled: true, reviewerIds: row.reviewerIds,
                    visibility: old?.visibility || "anonymous", minResponses: reviewerCount
                };
            });
            return [...untouched, ...generated];
        });
        setAssignmentPreview([]);
    };
    const fields = [{ key: "label", label: "متن سؤال", placeholder: "مثال: همکاری مؤثر با دیگران" },
    { key: "weight", label: "سطح اهمیت", type: "select", placeholder: "انتخاب سطح اهمیت", options: importanceOptions },
    { key: "computationalWeight", label: "وزن محاسباتی", computed: true, suffix: "٪" }];
    const tabs = [{ id: "questions", label: "بانک سؤال", icon: "❓", count: peerQuestions.length },
    { id: "assignments", label: "تخصیص ارزیاب‌ها", icon: "members", count: peerConfigs.filter(c => c.enabled).length }];
    const applyAssignmentsServer = async () => {
        if (!assignmentPreview.length) return;
        try {
            for (const row of assignmentPreview) {
                const old = peerConfigs.find(c => c.userId === row.userId);
                const cfg = {
                    userId: row.userId,
                    enabled: true,
                    reviewerIds: row.reviewerIds,
                    visibility: old?.visibility === "identified" ? "identified" : "anonymous",
                    minResponses: reviewerCount,
                };
                await savePeerConfigAdminApi(row.userId, cfg);
                if (activePeriod?.id && row.reviewerIds.length) {
                    await createPeerAssignmentsAdminApi(row.userId, activePeriod.id, row.reviewerIds);
                }
            }
            setAssignmentPreview([]);
            uxNotify(activePeriod?.id ? "تنظیمات و تخصیص‌های دوره فعال ذخیره شد." : "تنظیمات ذخیره شد؛ برای ساخت Assignment ابتدا یک دوره را فعال کنید.");
        } catch (error) { uxNotify(error.message || "اعمال تخصیص‌ها انجام نشد"); }
    };

    return <div>
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>تنظیمات بازخورد همکاران</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>سؤال‌های مشترک، ارزیاب‌های هر فرد و سطح محرمانگی را مدیریت کنید.</div>
        <ProfessionalTabs items={tabs} value={tab} onChange={setTab} ariaLabel="تنظیمات بازخورد همکاران" />
        {tab === "questions" && <div>
            <EditableList items={(() => {
                const totalWeight = peerQuestions.reduce((sum, q) => sum + (Number(q.weight) || 0), 0);
                return peerQuestions.map(q => ({
                    ...q,
                    computationalWeight: totalWeight > 0 ? ((Number(q.weight) || 0) / totalWeight * 100).toFixed(1) : "0.0"
                }));
            })()} fields={fields}
                onAdd={async f => { try { await createPeerQuestionAdminApi({ text: f.label, weight: Number(f.weight) || 0 }); } catch (error) { uxNotify(error.message || "ثبت سؤال بازخورد انجام نشد"); } }}
                onUpdate={async (id, f) => { try { await updatePeerQuestionAdminApi(id, { text: f.label, weight: Number(f.weight) || 0 }); } catch (error) { uxNotify(error.message || "ویرایش سؤال بازخورد انجام نشد"); } }}
                onDelete={async id => { try { await archivePeerQuestionAdminApi(id); } catch (error) { uxNotify(error.message || "حذف سؤال بازخورد انجام نشد"); } }}
                validate={f => !f.label?.trim() ? "متن سؤال الزامی است" : null} />
            <div style={{ fontSize: 10, color: C.hint, marginTop: 8 }}>سطوح پاسخ از همان مقیاس ارزیابی وظایف مستمر استفاده می‌کنند ({routineLevels.length} سطح).</div>
        </div>}
        {tab === "assignments" && <div>
            <Card style={{ marginBottom: 12 }}><div style={{ padding: 14 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500, marginBottom: 4 }}>تخصیص خودکار گروهی</div>
                <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.7, marginBottom: 12 }}>
                    سیستم ابتدا همکاران هم‌سطح همان واحد، سپس واحدهای هم‌خانواده در ساختار سازمانی و در نهایت سایر همکاران هم‌سطح را انتخاب می‌کند. مدیر مستقیم همیشه حذف می‌شود و بار ارزیابی بین افراد متعادل خواهد شد.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8 }}>
                    <div><label style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>واحد سازمانی</label><select value={bulkOrgUnit} onChange={e => { setBulkOrgUnit(e.target.value); setAssignmentPreview([]); }}
                        style={{ width: "100%", padding: 8, border: `1px solid ${C.borderMd}`, borderRadius: 7, fontFamily: "inherit" }}><option value="all">همه واحدها</option>{orgUnits.filter(unit => unit.isActive !== false).map(unit => <option key={unit.id} value={unit.id}>{unit.label}</option>)}</select></div>
                    <div><label style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>نقش</label><select value={bulkRole} onChange={e => { setBulkRole(e.target.value); setAssignmentPreview([]); }}
                        style={{ width: "100%", padding: 8, border: `1px solid ${C.borderMd}`, borderRadius: 7, fontFamily: "inherit" }}><option value="all">همه نقش‌ها</option><option value="specialist">کارشناس</option><option value="manager">مدیر</option><option value="deputy">معاون</option><option value="ceo">مدیرعامل</option></select></div>
                    <div><label style={{ display: "block", fontSize: 9.5, color: C.hint, marginBottom: 4 }}>تعداد ارزیاب</label><input type="number" min="1" max="10" value={reviewerCount} onChange={e => { setReviewerCount(Math.max(1, Math.min(10, Number(e.target.value) || 1))); setAssignmentPreview([]); }}
                        style={{ width: "100%", boxSizing: "border-box", padding: 8, border: `1px solid ${C.borderMd}`, borderRadius: 7, fontFamily: "inherit" }} /></div>
                </div>
                <button onClick={generateAssignments} style={{ marginTop: 10, padding: "9px 14px", border: "none", borderRadius: 7, background: C.navy, color: "#fff", fontFamily: "inherit", cursor: "pointer" }}>پیشنهاد سیستمی ارزیاب‌ها</button>
            </div></Card>
            {assignmentPreview.length > 0 && <Card style={{ marginBottom: 12 }}><div style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}><div><div style={{ fontSize: 12.5, fontWeight: 500 }}>پیش‌نمایش تخصیص‌ها</div><div style={{ fontSize: 10, color: C.hint, marginTop: 2 }}>{assignmentPreview.length} نفر · {assignmentPreview.reduce((s, r) => s + r.reviewerIds.length, 0)} درخواست ارزیابی</div></div>
                    <button onClick={applyAssignmentsServer} style={{ padding: "8px 12px", border: "none", borderRadius: 7, background: C.teal, color: "#fff", fontFamily: "inherit", cursor: "pointer" }}>تأیید و اعمال همه</button></div>
                {assignmentPreview.map(row => {
                    const target = users.find(u => u.id === row.userId); return <div key={row.userId} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.bg}`, fontSize: 11 }}>
                        <span style={{ minWidth: 110, fontWeight: 500 }}>{target?.name}</span><span style={{ flex: 1, color: C.muted }}>{row.reviewerIds.map(id => users.find(u => u.id === id)?.name).filter(Boolean).join("، ") || "ارزیابی پیشنهاد نشد"}</span>
                        <Badge color={row.complete ? "teal" : "amber"} small>{row.complete ? "معتبر" : "نیازمند تکمیل دستی"}</Badge></div>;
                })}
            </div></Card>}
            <Card style={{ marginBottom: 12 }}><div style={{ padding: 14 }}>
                <label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>فرد مورد ارزیابی</label>
                <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} style={{
                    width: "100%", padding: 9,
                    border: `1px solid ${C.borderMd}`, borderRadius: 8, fontFamily: "inherit", background: "#fff"
                }}>
                    {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
            </div></Card>
            {selectedUser && <Card><div style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div><div style={{ fontSize: 13, fontWeight: 500 }}>{selectedUser.name}</div><div style={{ fontSize: 10, color: C.hint }}>فعال‌سازی بازخورد در عملکرد فردی</div></div>
                    <input type="checkbox" checked={config.enabled} onChange={e => updateConfig({ enabled: e.target.checked })} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10, marginBottom: 14 }}>
                    <div><label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>نمایش نتیجه</label>
                        <select value={config.visibility} onChange={e => updateConfig({ visibility: e.target.value })} style={{ width: "100%", padding: 8, border: `1px solid ${C.borderMd}`, borderRadius: 7, fontFamily: "inherit" }}>
                            <option value="anonymous">محرمانه — بدون نام</option><option value="identified">شفاف — همراه نام</option>
                        </select></div>
                    <div><label style={{ display: "block", fontSize: 10, color: C.hint, marginBottom: 5 }}>حداقل پاسخ برای نمایش</label>
                        <input type="number" min="1" value={config.minResponses} onChange={e => updateConfig({ minResponses: Math.max(1, Number(e.target.value) || 1) })}
                            style={{ width: "100%", boxSizing: "border-box", padding: 8, border: `1px solid ${C.borderMd}`, borderRadius: 7, fontFamily: "inherit" }} /></div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 8 }}>انتخاب ارزیاب‌ها ({config.reviewerIds.length})</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 7 }}>
                    {people.filter(p => p.id !== selectedUserId && p.id !== selectedUser.managerId).map(p => <label key={p.id} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: 9, border: `1px solid ${config.reviewerIds.includes(p.id) ? C.cyan : C.border}`, borderRadius: 8, fontSize: 11, cursor: "pointer"
                    }}>
                        <input type="checkbox" checked={config.reviewerIds.includes(p.id)} onChange={() => toggleReviewer(p.id)} />{p.name}
                        {p.role === selectedUser.role && p.orgUnitId && p.orgUnitId === selectedUser.orgUnitId && <span style={{ fontSize: 9, color: C.teal }}>هم‌واحدی</span>}
                    </label>)}
                </div>
                <div style={{ fontSize: 9.5, color: C.hint, marginTop: 8 }}>مدیر مستقیم «{users.find(u => u.id === selectedUser.managerId)?.name || "تعیین نشده"}» در این فهرست نمایش داده نمی‌شود.</div>
                {config.reviewerIds.length < (config.minResponses || 1) && <div style={{ color: C.amber, fontSize: 10.5, marginTop: 10 }}>تعداد ارزیاب‌ها از حداقل پاسخ لازم کمتر است.</div>}
                <button onClick={async () => {
                    try { await savePeerConfigAdminApi(selectedUserId, config); uxNotify("تنظیمات بازخورد ذخیره شد."); }
                    catch (error) { uxNotify(error.message || "ذخیره تنظیمات بازخورد انجام نشد"); }
                }} style={{ marginTop: 12, padding: "8px 13px", border: "none", borderRadius: 7, background: C.teal, color: "#fff", fontFamily: "inherit", cursor: "pointer" }}>ذخیره تنظیمات بازخورد</button>
            </div></Card>}
        </div>}
    </div>;
}

function AdminOrganization() {
    const { orgSettings, updateOrganizationSettingsApi } = useData();
    const [tab, setTab] = useState("identity");
    const [brandDraft, setBrandDraft] = useState({ ...orgSettings });
    const [brandSaving, setBrandSaving] = useState(false);
    useEffect(() => setBrandDraft({ ...orgSettings }), [orgSettings.name, orgSettings.logo, orgSettings.defaultLanguage, orgSettings.accentColor, orgSettings.timezone, orgSettings.dateFormat, orgSettings.calendar]);
    const saveBrandSettings = async () => {
        setBrandSaving(true);
        try {
            await updateOrganizationSettingsApi({
                organizationName: brandDraft.name || "CVI Organization", logoUrl: brandDraft.logo || null,
                defaultLanguage: brandDraft.defaultLanguage || "en", accentColor: brandDraft.accentColor || "#00C8E6",
                timezone: brandDraft.timezone || "UTC", dateFormat: brandDraft.dateFormat || "YYYY-MM-DD",
                calendar: brandDraft.calendar || "gregorian",
            });
            uxNotify("تنظیمات سازمان با موفقیت ذخیره شد", "success");
        } catch (error) { uxNotify(error.message || "ذخیره تنظیمات سازمان انجام نشد", "error"); }
        finally { setBrandSaving(false); }
    };
    return <div>
        <DashboardPageIntro title="سازمان" subtitle="اطلاعات سازمان، ساختار واحدها و سمت‌های شغلی را از یک بخش مدیریت کنید." />
        <ProfessionalTabs
            items={[
                { id: "identity", label: "اطلاعات و برند", icon: <UiIcon name="company" size={15} /> },
                { id: "structure", label: "ساختار سازمانی", icon: <UiIcon name="org" size={15} /> },
                { id: "positions", label: "سمت‌های شغلی", icon: <UiIcon name="people" size={15} /> },
            ]}
            value={tab}
            onChange={setTab}
        />
        <TabTransitionContent transitionKey={tab}>
        <div style={{ display: tab === "identity" ? "block" : "none" }} aria-hidden={tab !== "identity"}>
            <div className="cvi-brand-settings">
                <div className="cvi-settings-intro"><strong>هویت سازمان و تنظیمات منطقه‌ای</strong><span>نام، لوگو و نحوه نمایش تاریخ‌ها برای همه کاربران سازمان از اینجا مدیریت می‌شود.</span></div>
                <Card><div className="cvi-brand-settings-grid">
                    <div className="cvi-brand-logo-editor">
                        <div className="cvi-brand-logo-preview">{brandDraft.logo ? <img src={brandDraft.logo} alt="لوگوی سازمان" /> : <UiIcon name="building" size={28} />}</div>
                        <label className="cvi-button-secondary cvi-upload-button">{brandDraft.logo ? "تغییر لوگو" : "آپلود لوگو"}<input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={e => { const file=e.target.files?.[0]; if(!file) return; if(file.size > 750*1024){ uxNotify("حجم لوگو باید کمتر از ۷۵۰ کیلوبایت باشد", "error"); return; } const reader=new FileReader(); reader.onload=ev=>setBrandDraft(d=>({...d,logo:ev.target.result})); reader.readAsDataURL(file); e.target.value=""; }} /></label>
                        {brandDraft.logo && <button type="button" className="cvi-brand-remove" onClick={() => setBrandDraft(d => ({ ...d, logo: null }))}>حذف لوگو</button>}
                    </div>
                    <div className="cvi-brand-fields">
                        <label><span>نام سازمان</span><input value={brandDraft.name || ""} onChange={e => setBrandDraft(d=>({...d,name:e.target.value}))} placeholder="نام سازمان" /></label>
                        <label><span>زبان پیش‌فرض</span><select value={brandDraft.defaultLanguage || "en"} onChange={e=>setBrandDraft(d=>({...d,defaultLanguage:e.target.value}))}>{Object.entries(SUPPORTED_LANGUAGES).map(([code,info])=><option key={code} value={code}>{info.label}</option>)}</select></label>
                        <label><span>منطقه زمانی</span><select value={brandDraft.timezone || "UTC"} onChange={e=>setBrandDraft(d=>({...d,timezone:e.target.value}))}><option value="UTC">UTC</option><option value="America/Toronto">Canada — Eastern</option><option value="America/Vancouver">Canada — Pacific</option><option value="America/Edmonton">Canada — Mountain</option><option value="Asia/Tehran">Iran — Tehran</option></select></label>
                        <label><span>فرمت تاریخ</span><select value={brandDraft.dateFormat || "YYYY-MM-DD"} onChange={e=>setBrandDraft(d=>({...d,dateFormat:e.target.value}))}><option value="YYYY-MM-DD">YYYY-MM-DD</option><option value="DD/MM/YYYY">DD/MM/YYYY</option><option value="MM/DD/YYYY">MM/DD/YYYY</option></select></label>
                        <label><span>تقویم</span><select value={brandDraft.calendar || "gregorian"} onChange={e=>setBrandDraft(d=>({...d,calendar:e.target.value}))}><option value="gregorian">Gregorian</option><option value="persian">Persian / Jalali</option></select></label>
                        <label><span>رنگ تأکیدی</span><div className="cvi-color-field"><input type="color" value={brandDraft.accentColor || "#00C8E6"} onChange={e=>setBrandDraft(d=>({...d,accentColor:e.target.value.toUpperCase()}))}/><code>{brandDraft.accentColor || "#00C8E6"}</code></div></label>
                    </div>
                </div><div className="cvi-brand-save"><span></span><button type="button" className="cvi-button-primary" disabled={brandSaving || !brandDraft.name?.trim()} onClick={saveBrandSettings}>{brandSaving ? "در حال ذخیره..." : "ذخیره تنظیمات سازمان"}</button></div></Card>
            </div>
        </div>
        <div style={{ display: tab === "structure" || tab === "positions" ? "block" : "none" }} aria-hidden={tab === "identity"}>
            <FlexibleOrgPrototype
                initialTab={tab === "positions" ? "positions" : "org"}
                visibleTabs={[tab === "positions" ? "positions" : "org"]}
                embedded
            />
        </div>
        </TabTransitionContent>
    </div>;
}

function AdminMobileMore({ setNav }) {
    const items = [
        ["org", "سازمان", "org"], ["individual-performance", "عملکرد فردی", "performance"],
        ["collective-contribution", "مشارکت جمعی", "target"], ["settings", "تنظیمات پیشرفته", "settings"],
        ["profile", "پروفایل", "profile"],
    ];
    return <div className="cvi-mobile-more-page"><DashboardPageIntro title="بیشتر" subtitle="تنظیمات و بخش‌های مدیریتی" />
        <div className="cvi-mobile-more-grid">{items.map(([id,label,icon]) => <button key={id} type="button" onClick={() => setNav(id)}><span><UiIcon name={icon} size={19} /></span><strong>{label}</strong><UiIcon name="chevronDown" size={14} /></button>)}</div>
    </div>;
}


function AdminCycleManagement() {
    const { activePeriod, periods, createPeriodApi, activatePeriodApi, closePeriod, reopenPeriod } = useData();
    const preferredPeriodId = activePeriod?.id || periods.find(p => ["draft", "preparation"].includes(String(p.status).toLowerCase()))?.id || periods[0]?.id || "";
    const [selectedPeriodId, setSelectedPeriodId] = useState(preferredPeriodId);
    const [cycleStatus, setCycleStatus] = useState(null);
    const [cycleStatusLoading, setCycleStatusLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!periods.some(p => p.id === selectedPeriodId)) setSelectedPeriodId(preferredPeriodId);
    }, [periods, preferredPeriodId, selectedPeriodId]);

    const selectedPeriod = periods.find(p => p.id === selectedPeriodId) || activePeriod || periods[0] || null;

    useEffect(() => {
        let cancelled = false;
        if (!selectedPeriod?.id) { setCycleStatus(null); return undefined; }
        const token = sessionStorage.getItem("cvi_token");
        if (!token) return undefined;
        setCycleStatusLoading(true);
        fetch(`${API_BASE_URL}/api/periods/${encodeURIComponent(selectedPeriod.id)}/cycle-status`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(async response => {
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(payload.message || "دریافت وضعیت دوره انجام نشد");
                return payload;
            })
            .then(payload => { if (!cancelled) setCycleStatus(payload); })
            .catch(error => { if (!cancelled) console.error("Cycle status load failed:", error); })
            .finally(() => { if (!cancelled) setCycleStatusLoading(false); });
        return () => { cancelled = true; };
    }, [selectedPeriod?.id, refreshKey]);

    const createCycle = async form => {
        try {
            const created = await createPeriodApi(form);
            if (created?.id) setSelectedPeriodId(created.id);
            setRefreshKey(k => k + 1);
            uxNotify("دوره جدید ایجاد شد.", "success");
            return created;
        } catch (error) {
            uxNotify(error.message || "ایجاد دوره انجام نشد.");
            return null;
        }
    };

    const activateCycle = async id => {
        try {
            await activatePeriodApi(id);
            setSelectedPeriodId(id);
            setRefreshKey(k => k + 1);
            uxNotify("دوره فعال شد.", "success");
        } catch (error) { uxNotify(error.message || "فعال‌سازی دوره انجام نشد."); }
    };

    const finalizeCycle = async id => {
        if (!await uxConfirm("با نهایی‌سازی دوره، نتایج قفل می‌شوند. ادامه می‌دهید؟")) return;
        try {
            await closePeriod(id, "ادمین");
            setRefreshKey(k => k + 1);
            uxNotify("دوره با موفقیت نهایی و قفل شد.", "success");
        } catch (error) {
            uxNotify(error.message || "نهایی‌سازی دوره انجام نشد.");
        }
    };

    const reopenCycle = async id => {
        const reason = await uxPrompt("دلیل بازگشایی دوره را وارد کنید:", "");
        if (!reason) return;
        try {
            await reopenPeriod(id, "ادمین", reason);
            setRefreshKey(k => k + 1);
            uxNotify("دوره بازگشایی شد.", "success");
        } catch (error) { uxNotify(error.message || "بازگشایی دوره انجام نشد."); }
    };

    return <CycleManagement
        locale="fa"
        periods={periods}
        selectedPeriodId={selectedPeriod?.id || ""}
        onPeriodChange={setSelectedPeriodId}
        cycleStatus={cycleStatus}
        loading={cycleStatusLoading}
        onCreateCycle={createCycle}
        onActivate={activateCycle}
        onFinalize={finalizeCycle}
        onReopen={reopenCycle}
    />;
}

function AdminApp({ user, onLogout }) {
    const isMobile = useIsMobile();
    const { activePeriod } = useData();
    const [nav, setNav] = useState("overview");
    const [visitedNav, setVisitedNav] = useState(() => new Set(["overview"]));
    useEffect(() => {
        setVisitedNav(current => {
            if (current.has(nav)) return current;
            const next = new Set(current);
            next.add(nav);
            return next;
        });
    }, [nav]);
    // Mount destinations on first visit and keep them cached afterwards. This avoids
    // remount flashes without triggering hidden-page API effects at startup.

    const sidebarItems = [
        { id: "overview", label: "نمای کلی", icon: "overview", group: "مدیریت" },
        { id: "org", label: "سازمان", icon: "org", group: "مدیریت" },
        { id: "people", label: "افراد و دسترسی‌ها", icon: "people", group: "مدیریت" },

        { id: "individual-performance", label: "عملکرد فردی", icon: "performance", group: "مدل CVI" },
        { id: "collective-contribution", label: "مشارکت جمعی", icon: "target", group: "مدل CVI" },
        { id: "settings", label: "تنظیمات پیشرفته", icon: "settings", group: "مدل CVI" },

        { id: "cycle-management", label: "مدیریت دوره", icon: "planning", group: "دوره و اجرا" },

        { id: "help", label: "راهنما", icon: "help", section: "account" },

        { id: "profile", label: "پروفایل", icon: "profile", group: "حساب کاربری" },
    ];

    const mobileItems = [
        { id: "overview", label: "خانه", icon: "overview" },
        { id: "people", label: "افراد", icon: "people" },
        { id: "cycle-management", label: "دوره", icon: "planning" },
        { id: "more", label: "بیشتر", icon: "more" },
    ];

    const adminPages = [
        ["overview", <AdminOverview setNav={setNav} />],
        ["org", <AdminOrganization />],
        ["people", <AdminPeople />],
        ["help", <CviHelpCenter />],
        ["individual-performance", <AdminIndividualPerformance />],
        ["collective-contribution", <AdminCollectiveContribution />],
        ["settings", <AdminSettings />],
        ["cycle-management", <AdminCycleManagement />],
        ["goals", <IndividualGoalsPage user={user} />],
        ["profile", <PersonProfile user={user} />],
        ["more", <AdminMobileMore setNav={setNav} />],
    ];

    return (
        <CviAppShell
            isMobile={isMobile}
            header={<TopBar searchItems={sidebarItems} onSearch={setNav} right={<UserMenu user={user} onLogout={onLogout} onSettings={() => setNav("profile")} />} />}
            periodContext={<PeriodContextBar period={activePeriod} daysLeft={daysRemainingInPeriod(activePeriod)} />}
            sidebar={<Sidebar items={sidebarItems} nav={nav} onNav={setNav} onLogout={onLogout} />}
            bottomNav={<AppBottomNav items={mobileItems} active={nav} onNav={setNav} onLogout={onLogout} />}
        >
            <div className="cvi-admin-shell-scroll">
                <TabTransitionContent transitionKey={nav}>
                    {adminPages.filter(([id]) => visitedNav.has(id)).map(([id, page]) => (
                        <div key={id} style={{ display: nav === id ? "block" : "none" }} aria-hidden={nav !== id}>
                            {page}
                        </div>
                    ))}
                </TabTransitionContent>
            </div>
        </CviAppShell>
    );
}

// ── PLACEHOLDER (نقش‌هایی که هنوز پنل اختصاصی ندارند: مدیرعامل، معاون) ──
function PlaceholderPanel({ user, onLogout }) {
    const isMobile = useIsMobile();
    return (
        <div style={{
            display: "flex", flexDirection: "column", height: "100vh",
            fontFamily: "inherit", direction: "inherit", background: C.bg,
            maxWidth: isMobile ? 420 : "100%", margin: isMobile ? "0 auto" : 0,
            border: isMobile ? `1px solid ${C.border}` : "none",
            boxShadow: isMobile ? "0 4px 32px rgba(0,0,64,0.08)" : "none"
        }}>

            <TopBar right={
                <UserMenu user={user} onLogout={onLogout} onSettings={() => document.getElementById("cvi-profile")?.scrollIntoView({ behavior: "smooth" })} />
            } />

            <div style={{ flex: 1, overflowY: "auto" }}>
                <PersonProfile user={user} />
                <div style={{ padding: "0 16px 16px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
                        🚧 سایر بخش‌های پنل «{user.name}» در مراحل بعدی توسعه اضافه می‌شود
                    </div>
                    <button onClick={onLogout}
                        style={{
                            padding: "10px 20px", background: C.navy, color: "#fff",
                            border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13
                        }}>
                        خروج از سامانه
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── ROOT: لاگین واحد + مسیردهی بر اساس نقش ─────────────────────────────────────

function OrganizationThemeSync() {
    const { orgSettings } = useData();
    useEffect(() => {
        const accent = /^#[0-9A-Fa-f]{6}$/.test(orgSettings?.accentColor || "") ? orgSettings.accentColor : "#00C8E6";
        document.documentElement.style.setProperty("--cvi-cyan-500", accent);
    }, [orgSettings?.accentColor]);
    return null;
}

function AppRouter() {
    const { baseDataLoading, baseDataError, sessionDataReady, orgSettings, users, invalidateSessionData } = useData();
    const { language, setLanguage } = useI18n();
    const [user, setUser] = useState(() => {
        try {
            const savedUser = sessionStorage.getItem("cvi_user");
            return savedUser ? JSON.parse(savedUser) : null;
        } catch {
            return null;
        }
    });

    const onLogin = (loggedInUser, token) => {
        invalidateSessionData();
        setUser(loggedInUser);
        sessionStorage.setItem("cvi_user", JSON.stringify(loggedInUser));
        sessionStorage.setItem("cvi_token", token);
        window.dispatchEvent(new Event("cvi-auth-changed"));
    };

    const onLogout = () => {
        invalidateSessionData();
        setUser(null);
        sessionStorage.removeItem("cvi_user");
        sessionStorage.removeItem("cvi_token");
        window.dispatchEvent(new Event("cvi-auth-changed"));
    };

    useEffect(() => {
        const handleSessionExpired = () => setUser(null);
        window.addEventListener("cvi-session-expired", handleSessionExpired);
        return () => window.removeEventListener("cvi-session-expired", handleSessionExpired);
    }, []);

    useEffect(() => {
        if (!user) return;
        const preferred = user.preferredLanguage;
        const fallback = orgSettings?.defaultLanguage || "en";
        const resolved = SUPPORTED_LANGUAGES[preferred] ? preferred : SUPPORTED_LANGUAGES[fallback] ? fallback : "en";
        if (resolved !== language) setLanguage(resolved);
    }, [user?.id, user?.preferredLanguage, orgSettings?.defaultLanguage]);

    if (!user) return <LoginPage onLogin={onLogin} />;
    if (baseDataError) return <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}><div style={{ color: C.red }}>{baseDataError}</div><button onClick={() => window.dispatchEvent(new Event("cvi-auth-changed"))} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.borderMd}`, background: C.white, cursor: "pointer" }}>تلاش مجدد</button></div>;
    if (baseDataLoading || !sessionDataReady) return <AppSkeleton />;
    const appUser = users.find(item => item.id === user.id) || user;
    if (appUser.role === "specialist") return <SpecialistApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "manager") return <ManagerApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "deputy") return <DeputyApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "ceo") return <CeoApp user={appUser} onLogout={onLogout} />;
    if (appUser.role === "admin") return <AdminApp user={appUser} onLogout={onLogout} />;
    return <PlaceholderPanel user={appUser} onLogout={onLogout} />;
}

export default function App() {
    return (
        <I18nProvider>
            <UxProvider>
                <DataProvider>
                    <OrganizationThemeSync />
                    <AppRouter />
                </DataProvider>
            </UxProvider>
        </I18nProvider>
    );
}
